// src/App.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Card, Button } from 'react-bootstrap';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { keycloak, login as startLogin, logout as startLogout, getAccessToken, getTokenProfile, subscribeToAuth, AUTH_DISABLED, DEV_AUTH_ENABLED } from './auth/keycloak';
import { setAnalyticsUserId, trackEvent, trackPageView } from './analytics';
import './App.css';
import buildInfo from './buildInfo.json';
import authorPortraitManifest from './authorPortraitManifest.json';

// Prefer explicit API base (can be absolute or relative), otherwise build from ROOT_URL.
const ROOT_URL = process.env.REACT_APP_ROOT_URL;
// Build API bases from env; default to /api/v1 and /api/v2.
const rawApiBase = process.env.REACT_APP_API_BASE_URL || (ROOT_URL ? `${ROOT_URL}/api` : '/api');
const API_BASE_URL_V1 = /\/api\/v1\b/.test(rawApiBase)
  ? rawApiBase
  : `${rawApiBase.replace(/\/+$/, '')}/v1`;
const API_BASE_URL_V2 = /\/api\/v2\b/.test(rawApiBase)
  ? rawApiBase
  : `${rawApiBase.replace(/\/+$/, '')}/v2`;
const normalizeVersionLabel = (value) => String(value || '').trim().replace(/^v/i, '');
const normalizeReleaseLabel = (value) => String(value || '').trim();
const APP_VERSION = normalizeVersionLabel(process.env.REACT_APP_APP_VERSION) || normalizeVersionLabel(buildInfo.version) || 'unknown';
const APP_BUILD = Number.isFinite(Number(buildInfo.build)) ? Number(buildInfo.build) : null;
const APP_RELEASE = normalizeReleaseLabel(process.env.REACT_APP_RELEASE)
  || normalizeReleaseLabel(buildInfo.release)
  || (APP_BUILD !== null ? `build-${APP_BUILD}` : '');
const BUILD_TIME = buildInfo.generatedAt ? new Date(buildInfo.generatedAt).toLocaleString() : '';
const ENVIRONMENT = (process.env.REACT_APP_ENVIRONMENT || 'dev').toLowerCase();
const API_KEY = process.env.REACT_APP_AUGUSTINE_API_KEY;
const CHAT_STATUS_IDLE = {
  phase: 'idle',
  title: '',
  message: '',
  elapsedMs: 0,
  retryable: false,
  statusCode: null,
  retryAction: 'chat',
  retryLabel: 'Retry',
};
const CHAT_REQUEST_TIMEOUT_MS = 180000;
const CHAT_WAIT_NOTICE_MS = 30000;
const CHAT_WAIT_UPDATE_MS = 10000;

const apiV1 = axios.create({ baseURL: API_BASE_URL_V1 });
const apiV2 = axios.create({ baseURL: API_BASE_URL_V2 });
const addAuthHeaders = async (config) => {
  config.headers = config.headers || {};
  if (!config.headers['X-Request-ID']) {
    config.headers['X-Request-ID'] = `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
  const token = await getAccessToken();
  const tokenProfile = getTokenProfile();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (!token && DEV_AUTH_ENABLED && tokenProfile?.sub) {
    config.headers['X-Dev-Auth-Sub'] = tokenProfile.sub;
    if (Array.isArray(tokenProfile.roles) && tokenProfile.roles.length > 0) {
      config.headers['X-Dev-Auth-Roles'] = tokenProfile.roles.join(',');
    }
  }
  if (API_KEY) {
    config.headers['X-API-Key'] = API_KEY;
  }
  return config;
};
apiV1.interceptors.request.use(addAuthHeaders);
apiV2.interceptors.request.use(addAuthHeaders);

const reportClientFailure = (payload = {}) => {
  if (typeof window === 'undefined') {
    return;
  }
  const fieldText = (value, maxLength) => cleanText(value).slice(0, maxLength);
  const event = {
    source: 'frontend',
    severity: fieldText(payload.severity || 'error', 20),
    kind: fieldText(payload.kind || 'client_error', 80),
    phase: fieldText(payload.phase || '', 48),
    message: fieldText(payload.message || payload.error?.message || '', 1000),
    stack: fieldText(payload.stack || payload.error?.stack || '', 3000),
    request_id: fieldText(payload.request_id || '', 120),
    session_id: fieldText(payload.session_id || '', 80),
    user_id: fieldText(payload.user_id || '', 120),
    persona: fieldText(payload.persona || '', 80),
    route: fieldText(payload.route || window.location.pathname || '', 160),
    method: fieldText(payload.method || '', 16),
    status_code: Number.isFinite(Number(payload.status_code))
      && Number(payload.status_code) >= 100
      && Number(payload.status_code) <= 599
      ? Number(payload.status_code)
      : undefined,
    app_version: fieldText(APP_VERSION, 80),
    release: fieldText(APP_RELEASE, 80),
    user_agent: fieldText(window.navigator?.userAgent || '', 500),
    page_url: fieldText(window.location.href || '', 500),
  };
  void apiV2.post('/client-errors', event).catch(() => {
    // Failure collection must never cascade into the user-facing chat flow.
  });
};
if (typeof window !== 'undefined') {
  window.pericopeReportClientFailure = reportClientFailure;
}
const navigateToExternalUrl = (url) => {
  if (typeof window === 'undefined') {
    return;
  }
  if (typeof window.pericopeExternalNavigate === 'function') {
    window.pericopeExternalNavigate(url);
    return;
  }
  window.location.assign(url);
};

const GUIDED_PROMPT_SOURCE_LABELS = {
  daily_guidance: 'Start here',
  weekly_arc: 'Question to press',
  daily_profile: 'Another angle',
  why_selected: 'Context',
  content_bundle: 'Reading',
};
const FEATURED_AUTHOR_PRIORITY_SLUGS = [
  'augustine',
  'thomas_aquinas',
  'aquinas',
  'seneca',
  'marcus_aurelius',
  'epictetus',
  'musonius_rufus',
  'plato',
  'sigmund_freud',
  'teresa',
  'john_chrysostom',
  'irenaeus',
  'eusebius_pamphilus',
  'machiavelli',
];
const CLOCK_LAUNCH_SOURCE = 'solomonic_clock';
const CLOCK_LAUNCH_MENTOR_HINT = 'solomon';
const CLOCK_LAUNCH_STORAGE_PREFIX = 'pericope_clock_launch_state:';
const COUNSEL_SYNTHESIS_STORAGE_PREFIX = 'pericope_counsel_synthesis:';
const CLOCK_LAUNCH_QUERY_KEYS = ['mode', 'source', 'message', 'prompt_id', 'ctx'];
const SESSION_RESUME_QUERY_KEY = 'session_id';
const GUIDED_CLOCK_FALLBACK_MESSAGE = "Help me begin from today's guidance.";
const ASK_PROVERBS_ROUTE = '/ask-proverbs';
const ASK_PROVERBS_MODE = 'ask_proverbs';
const ASK_PROVERBS_PERSONA = 'solomon';
const ASK_PROVERBS_SOURCE_SCOPE = 'proverbs';
const ASK_PROVERBS_SOURCE = 'ask_proverbs';
const BILLING_ACCESS_STATE_LABELS = {
  unpaid: 'No paid subscription on file',
  checkout_pending: 'Checkout created; payment not yet completed',
  awaiting_role_sync: 'Payment captured; runtime access still waiting on role sync',
  paid_active: 'Payment and paid runtime access are active',
};
const BILLING_TIERS = [
  {
    slug: 'reader',
    label: 'Reader',
    price: '$9 / month',
    summary: 'Single-reader access for the public Pericope stack.',
    features: ['Public author access', 'Reader route and passage chat', 'Saved account history'],
  },
  {
    slug: 'scholar',
    label: 'Scholar',
    price: '$19 / month',
    summary: 'Deeper study lane for sustained reading and citation work.',
    features: ['Everything in Reader', 'Long-form study cadence', 'Priority research workflow'],
  },
  {
    slug: 'family_group',
    label: 'Family / Group',
    price: '$39 / month',
    summary: 'Shared access for household or guided study groups.',
    features: ['Everything in Scholar', 'Shared family or group lane', 'Coordinated study access'],
  },
  {
    slug: 'institution',
    label: 'Institution',
    price: 'Contact',
    summary: 'Institution-scale access and deployment coordination.',
    features: ['Everything in Family / Group', 'Institutional onboarding', 'Deployment coordination'],
  },
];
const GUEST_HOMEPAGE_AUTHOR_STORAGE_KEY = 'pericope_guest_homepage_author';
const GUEST_HISTORY_STORAGE_KEY = 'pericope:lastSessions:v1';
const GUEST_HISTORY_STORAGE_VERSION = 1;
const GUEST_HISTORY_MAX_SESSIONS = 12;
const VOICE_STATUS_IDLE = {
  phase: 'idle',
  message: '',
};
const getBrowserSpeechRecognition = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};
const runtimeDebugEnabled = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  const params = new URLSearchParams(window.location.search || '');
  return params.get('debug') === 'runtime'
    || params.get('runtimeDebug') === '1'
    || window.localStorage?.getItem('pericope:runtimeDebug') === '1';
};
const formatRuntimeDebugValue = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : '[]';
  }
  if (value && typeof value === 'object') {
    return JSON.stringify(value);
  }
  if (value === true) {
    return 'true';
  }
  if (value === false) {
    return 'false';
  }
  if (value == null || value === '') {
    return 'none';
  }
  return String(value);
};
const buildRuntimeDriftWarnings = (runtime) => {
  const warnings = [];
  const routeAuthor = cleanText(runtime?.routeAuthor).toLowerCase();
  const activeAuthor = cleanText(runtime?.activeAuthor).toLowerCase();
  const inferencePersona = cleanText(runtime?.inferencePersona).toLowerCase();
  const retrievalScope = normalizeAuthorSlugList(runtime?.retrievalScope || []);
  if (routeAuthor && activeAuthor && routeAuthor !== activeAuthor) {
    warnings.push(`route author ${routeAuthor} differs from active author ${activeAuthor}`);
  }
  if (inferencePersona && activeAuthor && inferencePersona !== activeAuthor) {
    warnings.push(`inference persona ${inferencePersona} differs from active author ${activeAuthor}`);
  }
  if (activeAuthor && retrievalScope.length > 0 && !retrievalScope.includes(activeAuthor)) {
    warnings.push(`retrieval scope missing active author ${activeAuthor}`);
  }
  if (runtime?.audioKey && runtime?.audioState === 'idle') {
    warnings.push('audio key is set while audio state is idle');
  }
  if (runtime?.chatStatusCode === 401 || runtime?.chatStatusCode === 403) {
    warnings.push(`chat auth state returned HTTP ${runtime.chatStatusCode}`);
  }
  return warnings;
};

function RuntimeDebugPanel({ runtime }) {
  const warnings = buildRuntimeDriftWarnings(runtime);
  const sections = [
    {
      label: 'Conversation',
      items: {
        conversation_id: runtime.conversationId,
        active_author: runtime.activeAuthor,
        active_author_name: runtime.activeAuthorName,
        route_author: runtime.routeAuthor,
        inference_persona: runtime.inferencePersona,
        model: runtime.model,
      },
    },
    {
      label: 'State',
      items: {
        authenticated: runtime.authenticated,
        auth_mode: runtime.authMode,
        chat_phase: runtime.chatPhase,
        chat_status_code: runtime.chatStatusCode,
        streaming: runtime.streaming,
      },
    },
    {
      label: 'Counsel',
      items: {
        matter_anchor: runtime.matterAnchor,
        counsel_count: runtime.counselCount,
        counselors: runtime.counselors,
        retrieval_scope: runtime.retrievalScope,
        latest_assistant_persona: runtime.latestAssistantPersona,
        reference_count: runtime.referenceCount,
      },
    },
    {
      label: 'Audio',
      items: {
        audio_state: runtime.audioState,
        audio_key: runtime.audioKey,
        audio_message: runtime.audioMessage,
        browser_fallback: runtime.browserFallback,
        fallback_reason: runtime.fallbackReason,
        tts_request_id: runtime.ttsRequestId,
        tts_session_id: runtime.ttsSessionId,
        current_chunk: runtime.currentChunk,
        tts_latency_ms: runtime.ttsLatencyMs,
        first_audio_ready_ms: runtime.firstAudioReadyMs,
        playback_start_ms: runtime.playbackStartMs,
        polls: runtime.polls,
      },
    },
    {
      label: 'Build',
      items: {
        environment: runtime.environment,
        app_version: runtime.appVersion,
        app_release: runtime.appRelease,
        build_time: runtime.buildTime,
      },
    },
  ];

  return (
    <section className="runtime-debug-panel" aria-label="Runtime diagnostics">
      <div className="runtime-debug-panel__header">
        <div>
          <span className="runtime-debug-panel__eyebrow">Runtime</span>
          <h2>Diagnostics</h2>
        </div>
        <span className={`runtime-debug-panel__badge${warnings.length > 0 ? ' runtime-debug-panel__badge--warn' : ''}`}>
          {warnings.length > 0 ? `${warnings.length} drift` : 'stable'}
        </span>
      </div>
      {warnings.length > 0 && (
        <ul className="runtime-debug-panel__warnings">
          {warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}
      <div className="runtime-debug-panel__sections">
        {sections.map((section) => (
          <div className="runtime-debug-panel__section" key={section.label}>
            <h3>{section.label}</h3>
            {Object.entries(section.items).map(([key, value]) => (
              <div className="runtime-debug-panel__row" key={key}>
                <span>{key}</span>
                <strong>{formatRuntimeDebugValue(value)}</strong>
              </div>
            ))}
          </div>
        ))}
      </div>
      <details className="runtime-debug-panel__snapshot">
        <summary>JSON snapshot</summary>
        <pre>{JSON.stringify({ ...runtime, driftWarnings: warnings }, null, 2)}</pre>
      </details>
    </section>
  );
}

function ReadAloudButton({
  text,
  readAloudKey,
  activeReadAloudKey,
  readAloudStatus,
  onStartReadAloud,
  onStopReadAloud,
  className = '',
  supported = true,
}) {
  const readableText = String(text || '').trim();
  if (!supported || !readAloudKey || !readableText || typeof onStartReadAloud !== 'function' || typeof onStopReadAloud !== 'function') {
    return null;
  }

  const isActive = activeReadAloudKey === readAloudKey;
  const phase = isActive ? readAloudStatus?.phase : '';
  const shouldStop = isActive && phase !== 'error';
  const label = phase === 'generating'
    ? 'Generating...'
    : phase === 'loading'
      ? 'Loading...'
      : shouldStop
        ? 'Stop'
        : 'Speak';

  return (
    <button
      type="button"
      className={`read-aloud-button${className ? ` ${className}` : ''}${isActive ? ' is-active' : ''}`}
      onClick={() => (shouldStop ? onStopReadAloud() : onStartReadAloud(readAloudKey, readableText))}
      aria-pressed={isActive}
    >
      {label}
    </button>
  );
}

function Message({
  role,
  message,
  persona,
  followUpQuestion,
  inlineReferences,
  onUseFollowUpQuestion,
  onSelectInlineReference,
  activeReadAloudKey,
  readAloudStatus,
  onStartReadAloud,
  onStopReadAloud,
  readAloudKey,
  readAloudSupported,
}) {
  const followUpText = cleanText(followUpQuestion?.question);
  const followUpIntent = cleanText(followUpQuestion?.intent).replace(/^probe_/i, '').replace(/_/g, ' ');
  const messageReferences = Array.isArray(inlineReferences)
    ? inlineReferences.filter((reference) => cleanText(reference?.displayLabel || reference?.book))
    : [];
  return (
    <div className={`message ${role === 'user' ? 'user-message' : 'assistant-message'}`}>
      <span className="message-role">{role === 'user' ? 'You' : persona}:</span>
      <p>{message}</p>
      {role !== 'user' && messageReferences.length > 0 && (
        <div className="message-footnotes" aria-label="Message footnotes">
          <span className="message-footnotes__label">References</span>
          <div className="message-footnotes__list">
            {messageReferences.map((reference, index) => (
              <button
                key={`${reference.displayLabel || reference.book}-${index}`}
                type="button"
                className="message-footnotes__button"
                onClick={() => onSelectInlineReference?.(index)}
              >
                {reference.displayLabel || reference.book}
              </button>
            ))}
          </div>
        </div>
      )}
      {role !== 'user' && followUpText && (
        <div className="message-follow-up" aria-label="Suggested follow-up question">
          <span className="message-follow-up__label">
            {followUpIntent ? `Press further · ${followUpIntent}` : 'Press further'}
          </span>
          <p className="message-follow-up__question">{followUpText}</p>
          <button
            type="button"
            className="message-follow-up__action"
            onClick={() => onUseFollowUpQuestion?.(followUpText)}
          >
            Use this question
          </button>
        </div>
      )}
      {role !== 'user' && (
        <ReadAloudButton
          text={message}
          readAloudKey={readAloudKey}
          activeReadAloudKey={activeReadAloudKey}
          readAloudStatus={readAloudStatus}
          onStartReadAloud={onStartReadAloud}
          onStopReadAloud={onStopReadAloud}
          className="read-aloud-button--message"
          supported={readAloudSupported}
        />
      )}
    </div>
  );
}

const normalizeBookKey = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const escapeRegex = (value) => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const formatProvenanceLabel = (value) =>
  String(value || '')
    .replace(/_/g, ' ')
    .trim()
    .toLowerCase();

const buildScriptureReference = (book, chapter, verse) => {
  const safeBook = String(book || '').trim();
  const safeChapter = String(chapter || '').trim();
  const parsedVerse = Number.parseInt(verse, 10);
  if (!safeBook || !safeChapter || Number.isNaN(parsedVerse)) {
    return '';
  }
  return `${safeBook} ${safeChapter}:${parsedVerse}`;
};

const formatSlugLabel = (value) =>
  String(value || '')
    .trim()
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getAuthorSlugFromPath = (pathname) => {
  const match = String(pathname || '').match(/^\/authors?\/([^/]+)/);
  return match ? match[1].toLowerCase() : '';
};

const isAskProverbsPath = (pathname) => String(pathname || '').replace(/\/+$/, '') === ASK_PROVERBS_ROUTE;

const formatSessionTimestamp = (value) => {
  if (!value) {
    return 'Unknown';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Unknown';
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const formatTrackerDate = (value) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return cleanText(value);
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
  }).format(date);
};

const cleanText = (value) => String(value || '').replace(/\s+/g, ' ').trim();
const DEFAULT_READER_WINDOW_SIZE = 6;
const parsePositiveInt = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};
const decodeRouteSegment = (value) => {
  try {
    return decodeURIComponent(String(value || ''));
  } catch (_error) {
    return String(value || '');
  }
};
const getAuthorWorkPrimaryLabel = (item, index = 0) =>
  cleanText(item?.book || item?.work || item?.source) || `Work ${index + 1}`;
const getAuthorWorkSecondaryLabels = (item, index = 0) => {
  const primary = getAuthorWorkPrimaryLabel(item, index);
  const labels = [];
  [item?.work, item?.source].forEach((value) => {
    const text = cleanText(value);
    if (!text || text === primary || labels.includes(text)) {
      return;
    }
    labels.push(text);
  });
  return labels;
};
const findAuthorWorkBySource = (works, targetSource) => {
  const normalizedTarget = normalizeBookKey(targetSource);
  if (!normalizedTarget) {
    return null;
  }
  return (Array.isArray(works) ? works : []).find((item) => (
    [item?.source, item?.book, item?.work]
      .map(normalizeBookKey)
      .filter(Boolean)
      .some((value) => value === normalizedTarget)
  )) || null;
};
const parseReaderRangeFromSearch = (search) => {
  const params = new URLSearchParams(search || '');
  const startPosition = parsePositiveInt(params.get('start'), 1);
  const requestedEnd = parsePositiveInt(params.get('end'), startPosition + DEFAULT_READER_WINDOW_SIZE - 1);
  return {
    startPosition,
    endPosition: requestedEnd >= startPosition ? requestedEnd : startPosition,
  };
};
const buildAuthorWorkReaderHref = ({
  authorSlug,
  source,
  startPosition = 1,
  endPosition = startPosition + DEFAULT_READER_WINDOW_SIZE - 1,
}) => {
  const normalizedSlug = cleanText(authorSlug).toLowerCase();
  const normalizedSource = cleanText(source);
  if (!normalizedSlug || !normalizedSource) {
    return '/authors';
  }
  const safeStart = parsePositiveInt(startPosition, 1);
  const safeEnd = Math.max(safeStart, parsePositiveInt(endPosition, safeStart));
  const params = new URLSearchParams({
    start: String(safeStart),
    end: String(safeEnd),
  });
  return `/authors/${normalizedSlug}/works/${encodeURIComponent(normalizedSource)}?${params.toString()}`;
};
const buildReferenceCatalogHref = (bookId = '') => {
  const normalizedBookId = cleanText(bookId).toUpperCase();
  return normalizedBookId ? `/references/${encodeURIComponent(normalizedBookId)}` : '/references';
};
const normalizeSourceContextPayload = (value) => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const authorSlug = cleanText(value.author_slug || value.authorSlug).toLowerCase();
  const book = cleanText(value.book);
  const work = cleanText(value.work);
  const source = cleanText(value.source);
  const startPosition = parsePositiveInt(value.start_position ?? value.startPosition, 0);
  const endPosition = parsePositiveInt(value.end_position ?? value.endPosition, 0);
  const reference = cleanText(value.reference);
  const content = String(value.content || '').trim();
  if (!authorSlug || !source || !book || !content || startPosition < 1 || endPosition < startPosition) {
    return null;
  }
  return {
    author_slug: authorSlug,
    book,
    ...(work ? { work } : {}),
    source,
    start_position: startPosition,
    end_position: endPosition,
    ...(reference ? { reference } : {}),
    content,
  };
};
const buildReaderSourceContext = ({
  authorSlug,
  workLabel,
  source,
  entries,
  fallbackReference = '',
}) => {
  const safeAuthorSlug = cleanText(authorSlug).toLowerCase();
  const safeWorkLabel = cleanText(workLabel);
  const safeSource = cleanText(source);
  const normalizedEntries = (Array.isArray(entries) ? entries : [])
    .map((entry) => ({
      position: parsePositiveInt(entry?.position, 0),
      reference: cleanText(entry?.reference),
      content: cleanText(entry?.content),
    }))
    .filter((entry) => entry.position > 0 && entry.content)
    .sort((left, right) => left.position - right.position);
  if (!safeAuthorSlug || !safeWorkLabel || !safeSource || normalizedEntries.length === 0) {
    return null;
  }
  const firstEntry = normalizedEntries[0];
  const lastEntry = normalizedEntries[normalizedEntries.length - 1];
  const referenceLabels = uniqueTextList(normalizedEntries.map((entry) => entry.reference));
  const referenceFallback = cleanText(fallbackReference) || safeWorkLabel;
  const reference = referenceLabels.length <= 1
    ? (referenceLabels[0] || referenceFallback)
    : `${referenceLabels[0]} to ${referenceLabels[referenceLabels.length - 1]}`;
  return normalizeSourceContextPayload({
    author_slug: safeAuthorSlug,
    book: safeWorkLabel,
    work: safeWorkLabel,
    source: safeSource,
    start_position: firstEntry.position,
    end_position: lastEntry.position,
    reference,
    content: normalizedEntries.map((entry) => entry.content).join('\n\n'),
  });
};
const getSourceContextDisplayLabel = (value) => {
  const normalized = normalizeSourceContextPayload(value);
  if (!normalized) {
    return '';
  }
  return cleanText(normalized.reference || normalized.work || normalized.book) || normalized.book;
};
const normalizeConversationStateList = (value, limit = 4) =>
  uniqueTextList(Array.isArray(value) ? value.map((item) => cleanText(item)).filter(Boolean) : []).slice(0, limit);
const buildEmptyConversationState = (sessionId = '', persona = '') => ({
  session_id: cleanText(sessionId),
  persona: cleanText(persona).toLowerCase(),
  current_goal: '',
  stage: '',
  open_questions: [],
  next_best_action: '',
  promises: [],
  rolling_summary: '',
  last_response_summary: '',
});
const normalizeConversationStatePayload = (value, fallback = {}) => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const normalized = buildEmptyConversationState(
    value.session_id || fallback.sessionId,
    value.persona || fallback.persona,
  );
  normalized.current_goal = cleanText(value.current_goal);
  normalized.stage = cleanText(value.stage);
  normalized.open_questions = normalizeConversationStateList(value.open_questions);
  normalized.next_best_action = cleanText(value.next_best_action);
  normalized.promises = normalizeConversationStateList(value.promises);
  normalized.rolling_summary = cleanText(value.rolling_summary);
  normalized.last_response_summary = cleanText(value.last_response_summary);
  return normalized;
};
const hasConversationStateContent = (state) => {
  const normalized = normalizeConversationStatePayload(state);
  if (!normalized) {
    return false;
  }
  return Boolean(
    normalized.current_goal
      || normalized.stage
      || normalized.open_questions.length > 0
      || normalized.next_best_action
      || normalized.promises.length > 0
      || normalized.rolling_summary
      || normalized.last_response_summary
  );
};
const formatConversationStageLabel = (value) => cleanText(value)
  .split('_')
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');
const buildEmptyRelationshipMemory = (persona = '') => ({
  persona: cleanText(persona).toLowerCase(),
  organization: '',
  project_type: '',
  timeline: '',
  constraints: [],
  prior_decisions: [],
  follow_up_commitments: [],
  notes: '',
});
const normalizeRelationshipMemoryPayload = (value, fallback = {}) => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const normalized = buildEmptyRelationshipMemory(value.persona || fallback.persona);
  normalized.organization = cleanText(value.organization);
  normalized.project_type = cleanText(value.project_type || value.projectType);
  normalized.timeline = cleanText(value.timeline);
  normalized.constraints = normalizeConversationStateList(value.constraints);
  normalized.prior_decisions = normalizeConversationStateList(value.prior_decisions || value.priorDecisions);
  normalized.follow_up_commitments = normalizeConversationStateList(
    value.follow_up_commitments || value.followUpCommitments,
  );
  normalized.notes = cleanText(value.notes);
  return normalized;
};
const hasRelationshipMemoryContent = (memory) => {
  const normalized = normalizeRelationshipMemoryPayload(memory);
  if (!normalized) {
    return false;
  }
  return Boolean(
    normalized.organization
      || normalized.project_type
      || normalized.timeline
      || normalized.constraints.length > 0
      || normalized.prior_decisions.length > 0
      || normalized.follow_up_commitments.length > 0
      || normalized.notes
  );
};
const formatElapsedSeconds = (milliseconds) => `${Math.max(1, Math.round(Number(milliseconds || 0) / 1000))}s`;
const buildChatWaitMessage = (elapsedMs = 0) => {
  if (elapsedMs >= CHAT_WAIT_NOTICE_MS) {
    return `Still waiting for the chat service... ${formatElapsedSeconds(elapsedMs)} elapsed.`;
  }
  return 'Waiting for response from the chat service...';
};
const sleep = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));
const VIBEVOICE_TTS_JOBS_API_ENDPOINT = '/api/vibevoice/tts/jobs';
const VIBEVOICE_TTS_SESSIONS_API_ENDPOINT = '/api/vibevoice/tts/sessions';
const VIBEVOICE_POLL_INTERVAL_MS = 3000;
const VIBEVOICE_MAX_POLLS = 80;
const VIBEVOICE_CHUNK_TARGET_CHARS = 650;
const VIBEVOICE_CHUNK_MAX_CHARS = 950;
const READ_ALOUD_AUDIO_CACHE_MAX_ENTRIES = 20;
const nowMs = () => (typeof performance !== 'undefined' && typeof performance.now === 'function'
  ? performance.now()
  : Date.now());
const roundMs = (value) => Math.round(Number(value || 0));
const createReadAloudStatus = (phase, key, message = '', timings = {}) => ({
  phase,
  key,
  message,
  timings,
  updatedAt: Date.now(),
});
const formatSpeechElapsed = (value) => {
  const milliseconds = Number(value || 0);
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) {
    return '';
  }
  if (milliseconds < 1000) {
    return `${Math.round(milliseconds)}ms`;
  }
  return `${(milliseconds / 1000).toFixed(milliseconds < 10000 ? 1 : 0)}s`;
};
const getReadAloudPhaseLabel = (phase) => {
  switch (phase) {
    case 'generating':
      return 'API synthesis';
    case 'loading':
      return 'Audio loading';
    case 'playing':
      return 'Playback';
    case 'error':
      return 'Voice error';
    default:
      return 'Voice';
  }
};
const buildReadAloudDetails = (status) => {
  const timings = status?.timings || {};
  const details = [];
  if (timings.cacheHit) {
    details.push('source: page cache');
  } else if (timings.browserFallback) {
    details.push('upstream: browser speech fallback');
  } else {
    details.push('upstream: VibeVoice API');
  }
  const elapsed = formatSpeechElapsed(
    timings.playStartedMs
      || timings.ttsCompleteMs
      || timings.firstAudioReadyMs
      || timings.lastPollMs
      || timings.jobAcceptedMs
      || timings.proxyCreateElapsedMs,
  );
  if (elapsed) {
    details.push(`elapsed: ${elapsed}`);
  }
  if (timings.currentChunk) {
    details.push(`chunk: ${timings.currentChunk}`);
  }
  if (timings.polls) {
    details.push(`polls: ${timings.polls}`);
  }
  if (timings.fallbackReason && (timings.browserFallbackAvailable || timings.browserFallback)) {
    details.push(`reason: ${timings.fallbackReason}`);
  }
  return details;
};
const createTtsRequestId = () => {
  const randomPart = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `web-${randomPart}`;
};
const splitSpeakTextIntoChunks = (value) => {
  const readableText = cleanText(value);
  if (!readableText) {
    return [];
  }
  if (readableText.length <= VIBEVOICE_CHUNK_MAX_CHARS) {
    return [readableText];
  }
  const sentences = readableText.match(/[^.!?;:]+[.!?;:]+["')\]]*|[^.!?;:]+$/g) || [readableText];
  const chunks = [];
  let current = '';

  sentences.forEach((sentence) => {
    const nextSentence = cleanText(sentence);
    if (!nextSentence) {
      return;
    }
    const proposed = current ? `${current} ${nextSentence}` : nextSentence;
    if (proposed.length <= VIBEVOICE_CHUNK_TARGET_CHARS) {
      current = proposed;
      return;
    }
    if (current) {
      chunks.push(current);
      current = '';
    }
    if (nextSentence.length <= VIBEVOICE_CHUNK_MAX_CHARS) {
      current = nextSentence;
      return;
    }
    for (let index = 0; index < nextSentence.length; index += VIBEVOICE_CHUNK_TARGET_CHARS) {
      const fragment = cleanText(nextSentence.slice(index, index + VIBEVOICE_CHUNK_TARGET_CHARS));
      if (fragment) {
        chunks.push(fragment);
      }
    }
  });

  if (current) {
    chunks.push(current);
  }
  return chunks.length > 0 ? chunks : [readableText];
};
const createReadAloudCacheKey = (targetKey, text) => `${targetKey}::${cleanText(text)}`;
const fetchVibeVoiceJson = async (endpoint, options = {}) => {
  const response = await fetch(endpoint, {
    ...options,
    headers: {
      Accept: 'application/json',
      ...(options.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.error || payload?.detail || `VibeVoice request failed (${response.status}).`);
    error.status = response.status;
    throw error;
  }
  return payload;
};

const fetchReadAloudPlaybackSource = async (audioUrl) => {
  const sourceUrl = String(audioUrl || '').trim();
  if (!sourceUrl || sourceUrl.startsWith('blob:') || typeof fetch !== 'function') {
    return { playbackUrl: sourceUrl, objectUrl: '' };
  }
  try {
    const response = await fetch(sourceUrl, {
      method: 'GET',
      cache: 'force-cache',
      headers: {
        Accept: 'audio/wav,audio/*;q=0.9,*/*;q=0.1',
      },
    });
    if (!response?.ok || typeof response.blob !== 'function' || typeof window?.URL?.createObjectURL !== 'function') {
      return { playbackUrl: sourceUrl, objectUrl: '' };
    }
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    return { playbackUrl: objectUrl, objectUrl };
  } catch {
    return { playbackUrl: sourceUrl, objectUrl: '' };
  }
};

const buildReadAloudCacheChunk = async ({
  audioUrl,
  chunkIndex = 0,
  chunkNumber = 1,
  requestId = '',
}) => {
  const sourceUrl = String(audioUrl || '').trim();
  const playbackSource = await fetchReadAloudPlaybackSource(sourceUrl);
  return {
    audioUrl: sourceUrl,
    playbackUrl: playbackSource.playbackUrl || sourceUrl,
    objectUrl: playbackSource.objectUrl || '',
    chunkIndex,
    chunkNumber,
    requestId,
  };
};

const releaseReadAloudCacheEntry = (entry) => {
  if (!entry || !Array.isArray(entry.chunks) || typeof window?.URL?.revokeObjectURL !== 'function') {
    return;
  }
  entry.chunks.forEach((chunk) => {
    if (chunk?.objectUrl) {
      window.URL.revokeObjectURL(chunk.objectUrl);
    }
  });
};

const pruneReadAloudCache = (cache) => {
  while (cache.size > READ_ALOUD_AUDIO_CACHE_MAX_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    const oldestEntry = cache.get(oldestKey);
    cache.delete(oldestKey);
    releaseReadAloudCacheEntry(oldestEntry);
  }
};

const getUrlPathForReport = (url) => {
  try {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return String(url || '');
  }
};

const probeFailedMediaUrl = async (url) => {
  try {
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
      headers: {
        Range: 'bytes=0-0',
      },
    });
    return {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      requestId: response.headers.get('X-Request-ID') || '',
      route: getUrlPathForReport(response.url || url),
    };
  } catch (error) {
    return {
      ok: false,
      status: undefined,
      statusText: error?.message || 'Media probe failed',
      requestId: '',
      route: getUrlPathForReport(url),
    };
  }
};

const vibeVoiceAudioManager = {
  audio: null,
  playbackResolver: null,
  stop() {
    const resolvePlayback = this.playbackResolver;
    this.playbackResolver = null;
    if (resolvePlayback) {
      resolvePlayback({ stopped: true });
    }
    if (!this.audio) {
      return;
    }
    const current = this.audio;
    this.audio = null;
    current.onended = null;
    current.onerror = null;
    try {
      current.pause();
    } catch (error) {
      console.warn('Unable to pause VibeVoice speech', error);
    }
    current.removeAttribute?.('src');
    try {
      current.load?.();
    } catch (error) {
      console.warn('Unable to reset VibeVoice speech element', error);
    }
  },
  play(url, handlers = {}) {
    this.stop();
    const audio = new Audio(url);
    this.audio = audio;
    audio.onended = handlers.onended || null;
    audio.onerror = handlers.onerror || null;
    return audio.play();
  },
  playUntilEnded(url, handlers = {}) {
    this.stop();
    const audio = new Audio(url);
    this.audio = audio;
    return new Promise((resolve, reject) => {
      let settled = false;
      const settle = (resolver, value) => {
        if (settled) {
          return;
        }
        settled = true;
        if (this.audio === audio) {
          this.playbackResolver = null;
        }
        resolver(value);
      };
      this.playbackResolver = (value) => settle(resolve, value || { stopped: true });
      audio.onended = () => {
        handlers.onended?.();
        settle(resolve, { ended: true });
      };
      audio.onerror = async (event) => {
        const probe = await probeFailedMediaUrl(url);
        const statusMessage = probe.status ? ` (${probe.status})` : '';
        const error = new Error(`Audio playback failed${statusMessage}.`);
        error.status = probe.status;
        error.route = probe.route;
        error.requestId = probe.requestId;
        error.audioUrl = url;
        reportClientFailure({
          kind: 'vibevoice_audio_load_failed',
          phase: 'audio_playback',
          message: `Unable to load VibeVoice audio${statusMessage}: ${probe.route}`,
          route: probe.route,
          method: 'GET',
          status_code: probe.status,
          request_id: probe.requestId,
          stack: error.stack || '',
        });
        handlers.onerror?.(event, error);
        settle(reject, error);
      };
      Promise.resolve(audio.play())
        .then(() => {
          handlers.onstarted?.();
        })
        .catch((error) => {
          reportClientFailure({
            kind: 'vibevoice_audio_play_failed',
            phase: 'audio_playback',
            message: error?.message || 'Unable to play VibeVoice audio.',
            route: getUrlPathForReport(url),
            method: 'GET',
            stack: error?.stack || '',
          });
          settle(reject, error);
        });
    });
  },
};
const browserSpeechManager = {
  utterance: null,
  playbackResolver: null,
  stop() {
    const resolvePlayback = this.playbackResolver;
    this.playbackResolver = null;
    if (resolvePlayback) {
      resolvePlayback({ stopped: true });
    }
    this.utterance = null;
    try {
      window.speechSynthesis?.cancel?.();
    } catch (error) {
      console.warn('Unable to stop browser speech', error);
    }
  },
  play(text, handlers = {}) {
    this.stop();
    if (typeof window === 'undefined' || typeof window.speechSynthesis?.speak !== 'function' || typeof window.SpeechSynthesisUtterance !== 'function') {
      return Promise.reject(new Error('Browser speech is not available.'));
    }
    const utterance = new window.SpeechSynthesisUtterance(text);
    this.utterance = utterance;
    return new Promise((resolve, reject) => {
      let settled = false;
      const settle = (resolver, value) => {
        if (settled) {
          return;
        }
        settled = true;
        if (this.utterance === utterance) {
          this.playbackResolver = null;
          this.utterance = null;
        }
        resolver(value);
      };
      this.playbackResolver = (value) => settle(resolve, value || { stopped: true });
      utterance.onend = () => {
        handlers.onended?.();
        settle(resolve, { ended: true });
      };
      utterance.onerror = (event) => {
        handlers.onerror?.(event);
        settle(reject, new Error('Browser speech failed.'));
      };
      try {
        window.speechSynthesis.speak(utterance);
        handlers.onstarted?.();
      } catch (error) {
        settle(reject, error);
      }
    });
  },
};
const normalizeAuthorSlugList = (values) => Array.from(
  new Set(
    (Array.isArray(values) ? values : [])
      .map((value) => cleanText(value).toLowerCase())
      .filter(Boolean),
  ),
);
const hasAuthorSlug = (authors, slug) => {
  const normalizedSlug = cleanText(slug).toLowerCase();
  return Boolean(
    normalizedSlug
    && Array.isArray(authors)
    && authors.some((author) => cleanText(author?.slug).toLowerCase() === normalizedSlug),
  );
};
const normalizeRoleList = (roles) => Array.from(
  new Set(
    (Array.isArray(roles) ? roles : [])
      .map((role) => cleanText(role).toLowerCase())
      .filter(Boolean),
  ),
);
const hasAnyRole = (roles, ...needed) => {
  const available = new Set(normalizeRoleList(roles));
  return needed.some((role) => available.has(cleanText(role).toLowerCase()));
};
const normalizeAuthorStatusValue = (value) => cleanText(value).toLowerCase().replace(/\s*\/\s*/g, '/').replace(/[-\s]+/g, '_');
const SECRET_SACRED_STATUS_VALUES = new Set(['secret', 'sacred', 'secret/sacred', 'secret_sacred']);
const isSecretSacredAuthor = (author) => {
  const values = [
    author?.acquisition_status,
    author?.status,
    author?.visibility,
    author?.access_tier,
  ].map(normalizeAuthorStatusValue);
  return values.some((value) => SECRET_SACRED_STATUS_VALUES.has(value));
};
const isPublicAuthor = (author) => Boolean(author && !author.hidden && !author.local_only && !isSecretSacredAuthor(author));
const buildCounselSynthesisReadAloudText = (synthesis) => (
  [
    cleanText(synthesis?.commonGround) ? `Common ground: ${synthesis.commonGround}` : '',
    cleanText(synthesis?.tensions) ? `Tensions: ${synthesis.tensions}` : '',
    cleanText(synthesis?.integratedPrinciple) ? `Integrated principle: ${synthesis.integratedPrinciple}` : '',
  ]
    .filter(Boolean)
    .join(' ')
);
const normalizeCounselSynthesisPayload = (payload) => {
  const synthesis = payload?.synthesis || payload || {};
  const commonGround = cleanText(synthesis?.common_ground || synthesis?.commonGround);
  const tensions = cleanText(synthesis?.tensions);
  const integratedPrinciple = cleanText(synthesis?.integrated_principle || synthesis?.integratedPrinciple);
  const renderedText = cleanText(payload?.rendered_text || payload?.renderedText)
    || buildCounselSynthesisReadAloudText({
      commonGround,
      tensions,
      integratedPrinciple,
    });

  if (!commonGround && !tensions && !integratedPrinciple) {
    return null;
  }

  return {
    matter: cleanText(payload?.matter || synthesis?.matter),
    counselsCount: Number.isFinite(Number(payload?.counsels_count || payload?.counselsCount))
      ? Number(payload.counsels_count || payload.counselsCount)
      : null,
    commonGround,
    tensions,
    integratedPrinciple,
    renderedText,
  };
};
const normalizeFollowUpQuestionPayload = (payload, authors = []) => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const question = cleanText(payload?.question);
  if (!question) {
    return null;
  }

  const authorSlug = cleanText(payload?.author_slug || payload?.authorSlug).toLowerCase();
  const author = (Array.isArray(authors) ? authors : []).find(
    (item) => cleanText(item?.slug).toLowerCase() === authorSlug,
  ) || null;

  return {
    authorSlug,
    authorName: cleanText(payload?.author_name || payload?.authorName || author?.name) || formatSlugLabel(authorSlug || 'author'),
    question,
    intent: cleanText(payload?.intent),
    matter: cleanText(payload?.matter),
    clockInfluenceWeight: Number.isFinite(Number(payload?.clock_influence_weight || payload?.clockInfluenceWeight))
      ? Number(payload.clock_influence_weight || payload.clockInfluenceWeight)
      : 0,
    influenceSources: Array.isArray(payload?.influence_sources || payload?.influenceSources)
      ? (payload.influence_sources || payload.influenceSources).map((item) => cleanText(item)).filter(Boolean)
      : [],
  };
};
const buildHistoryMessageCompanionMap = (payload, authors = []) => {
  if (!payload || typeof payload !== 'object') {
    return {};
  }

  return Object.entries(payload).reduce((acc, [messageId, companion]) => {
    const normalizedMessageId = cleanText(messageId);
    if (!normalizedMessageId || !companion || typeof companion !== 'object') {
      return acc;
    }

    const followUpQuestion = normalizeFollowUpQuestionPayload(
      companion.follow_up_question || companion.followUpQuestion,
      authors,
    );
    if (followUpQuestion) {
      acc[normalizedMessageId] = followUpQuestion;
    }
    return acc;
  }, {});
};
const buildCounselEntryId = (matter, authorSlug) => (
  `${cleanText(authorSlug).toLowerCase()}:${normalizeBookKey(matter)}`
);
const buildCounselStackSourceKey = (matter, counselEntries) => JSON.stringify({
  matter: cleanText(matter),
  entries: (Array.isArray(counselEntries) ? counselEntries : []).map((entry) => ({
    id: entry?.id,
    question: cleanText(entry?.question),
    response: cleanText(entry?.response),
  })),
});
const getCounselSynthesisStorageKey = (sessionId) => `${COUNSEL_SYNTHESIS_STORAGE_PREFIX}${sessionId}`;
const buildStoredCitationReference = (citation) => {
  const source = cleanText(citation?.source) || 'Reference';
  const citationText = cleanText(citation?.citation);
  const referenceMatch = citationText.match(/Reference\s+([^;]+)/i);
  const displayLabel = cleanText(referenceMatch?.[1]) || source;
  return {
    book: source,
    displayLabel,
    content: citationText,
    provenance: 'stored_citation',
  };
};
const buildHistoryMessageReferenceMap = (citations) => {
  const grouped = new Map();
  (Array.isArray(citations) ? citations : []).forEach((citation) => {
    const messageId = cleanText(citation?.message_id);
    if (!messageId) {
      return;
    }
    const nextReference = buildStoredCitationReference(citation);
    const referenceKey = normalizeBookKey(
      `${cleanText(nextReference.displayLabel)}|${cleanText(nextReference.book)}`,
    );
    if (!referenceKey) {
      return;
    }
    if (!grouped.has(messageId)) {
      grouped.set(messageId, new Map());
    }
    const messageReferences = grouped.get(messageId);
    if (!messageReferences.has(referenceKey)) {
      messageReferences.set(referenceKey, nextReference);
    }
  });

  return Object.fromEntries(
    Array.from(grouped.entries()).map(([messageId, messageReferences]) => [
      messageId,
      Array.from(messageReferences.values()),
    ]),
  );
};
const normalizeCounselReference = (reference) => {
  const book = cleanText(reference?.book || reference?.source) || 'Reference';
  const displayLabel = cleanText(reference?.display_label || reference?.displayLabel) || book;
  const content = cleanText(reference?.content || reference?.citation);

  if (!displayLabel && !content) {
    return null;
  }

  return {
    book,
    displayLabel,
    content,
    provenance: cleanText(reference?.provenance) || 'stored_citation',
  };
};
const persistCounselSynthesisSnapshot = (sessionId, sourceKey, synthesis) => {
  if (!sessionId || !sourceKey || !synthesis) {
    return;
  }

  try {
    window.localStorage.setItem(
      getCounselSynthesisStorageKey(sessionId),
      JSON.stringify({
        sourceKey,
        synthesis,
      }),
    );
  } catch {
    // Ignore storage failures so session resume remains non-fatal.
  }
};
const loadPersistedCounselSynthesisSnapshot = (sessionId, sourceKey) => {
  if (!sessionId || !sourceKey) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(getCounselSynthesisStorageKey(sessionId));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.sourceKey !== sourceKey) {
      return null;
    }
    return normalizeCounselSynthesisPayload(parsed.synthesis);
  } catch {
    return null;
  }
};
const normalizeCounselStackPayload = ({
  counselStack,
  authors,
}) => {
  if (!counselStack || typeof counselStack !== 'object') {
    return null;
  }

  const entries = (Array.isArray(counselStack?.entries) ? counselStack.entries : [])
    .map((entry) => {
      const authorSlug = cleanText(entry?.author_slug || entry?.authorSlug).toLowerCase();
      const matter = cleanText(entry?.matter);
      const response = cleanText(entry?.response);
      if (!authorSlug || !matter || !response) {
        return null;
      }

      const author = (Array.isArray(authors) ? authors : []).find(
        (item) => cleanText(item?.slug).toLowerCase() === authorSlug,
      ) || null;
      const references = (Array.isArray(entry?.references) ? entry.references : [])
        .map(normalizeCounselReference)
        .filter(Boolean);

      return {
        id: cleanText(entry?.id) || buildCounselEntryId(matter, authorSlug),
        authorSlug,
        authorName: cleanText(entry?.author_name || entry?.authorName || author?.name) || formatSlugLabel(authorSlug),
        author,
        matter,
        question: cleanText(entry?.question) || matter,
        response,
        references,
        metaInfo: [],
      };
    })
    .filter(Boolean);

  const matterAnchor = cleanText(counselStack?.matter) || cleanText(entries[0]?.matter);
  const selectedAuthors = Array.isArray(counselStack?.selected_authors)
    ? counselStack.selected_authors
    : counselStack?.selectedAuthors;
  const counselAuthorSlugs = normalizeAuthorSlugList([
    ...(Array.isArray(selectedAuthors) ? selectedAuthors : []),
    ...entries.map((entry) => entry.authorSlug),
  ]);

  if (!matterAnchor && entries.length === 0 && counselAuthorSlugs.length === 0) {
    return null;
  }

  return {
    matterAnchor,
    counselAuthorSlugs,
    counselEntries: entries,
  };
};
const rebuildCounselStackFromHistory = ({
  history,
  citations,
  authors,
  mode,
}) => {
  if (cleanText(mode) === ASK_PROVERBS_MODE) {
    return {
      matterAnchor: '',
      counselAuthorSlugs: [],
      counselEntries: [],
    };
  }

  const referenceMap = buildHistoryMessageReferenceMap(citations);

  const entries = [];
  let matterAnchor = '';
  let pendingQuestion = '';

  (Array.isArray(history) ? history : []).forEach((message) => {
    const role = cleanText(message?.role).toLowerCase();
    const text = cleanText(message?.message);
    if (role === 'user') {
      pendingQuestion = text;
      if (!matterAnchor && text) {
        matterAnchor = text;
      }
      return;
    }
    if (role !== 'assistant' || !text) {
      return;
    }

    const authorSlug = cleanText(message?.persona).toLowerCase();
    if (!authorSlug) {
      pendingQuestion = '';
      return;
    }

    const matter = matterAnchor || pendingQuestion;
    if (!matter) {
      pendingQuestion = '';
      return;
    }

    const author = (Array.isArray(authors) ? authors : []).find(
      (item) => cleanText(item?.slug).toLowerCase() === authorSlug,
    ) || null;
    const nextEntry = {
      id: buildCounselEntryId(matter, authorSlug),
      authorSlug,
      authorName: cleanText(author?.name) || formatSlugLabel(authorSlug),
      author,
      matter,
      question: pendingQuestion || matter,
      response: text,
      references: referenceMap[cleanText(message?.message_id)] || [],
      metaInfo: [],
    };
    const existingIndex = entries.findIndex((entry) => entry.id === nextEntry.id);
    if (existingIndex === -1) {
      entries.push(nextEntry);
    } else {
      entries[existingIndex] = {
        ...entries[existingIndex],
        ...nextEntry,
      };
    }
    pendingQuestion = '';
  });

  return {
    matterAnchor,
    counselAuthorSlugs: normalizeAuthorSlugList(entries.map((entry) => entry.authorSlug)),
    counselEntries: entries,
  };
};
const DEFAULT_TRACE_FILTERS = {
  service_id: '',
  service_version: '',
  session_id: '',
  user_id: '',
  limit: '25',
};
const normalizeTraceFilters = (filters) => {
  const parsedLimit = Number.parseInt(filters?.limit, 10);
  const limit = Number.isFinite(parsedLimit)
    ? Math.max(1, Math.min(parsedLimit, 200))
    : Number.parseInt(DEFAULT_TRACE_FILTERS.limit, 10);
  return {
    service_id: cleanText(filters?.service_id),
    service_version: cleanText(filters?.service_version),
    session_id: cleanText(filters?.session_id),
    user_id: cleanText(filters?.user_id),
    limit,
  };
};
const formatCrossrefConfidence = (value) => {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) {
    return '';
  }
  const normalized = Math.max(0, Math.min(1, parsed));
  if (normalized >= 0.999) {
    return '';
  }
  return `${Math.round(normalized * 100)}% confidence`;
};
const getCrossrefRelationshipLabel = (item) => {
  const relationship = cleanText(item?.relationship).toLowerCase();
  if (relationship === 'canonical_author') {
    return 'canonical author';
  }
  const relationshipLabels = {
    commentator_on: 'commentator on',
    dialogue_voice: 'dialogue voice',
    engages_tradition: 'engages tradition',
    philosophical_predecessor: 'philosophical predecessor',
    scriptural_authority: 'scriptural authority',
    student_of_and_critic_of: 'student of and critic of',
    teacher_of: 'teacher of',
  };
  if (relationshipLabels[relationship]) {
    return relationshipLabels[relationship];
  }
  const evidenceWork = cleanText(item?.evidence_work);
  if (evidenceWork) {
    return `via ${evidenceWork}`;
  }
  const sourceType = cleanText(item?.source_type).replace(/_/g, ' ');
  return sourceType || 'cross reference';
};
const getAuthorBiography = (author) => (
  author?.biography && typeof author.biography === 'object' && !Array.isArray(author.biography)
    ? author.biography
    : null
);
const getAuthorTaxonomy = (author) => (
  author?.taxonomy && typeof author.taxonomy === 'object' && !Array.isArray(author.taxonomy)
    ? author.taxonomy
    : {}
);
const getAuthorSummary = (author) => (
  cleanText(getAuthorBiography(author)?.summary)
  || cleanText(author?.description)
);
const getAuthorHistory = (author) => cleanText(getAuthorBiography(author)?.history);
const getAuthorHistoricalContext = (author) => (
  author?.historical_context && typeof author.historical_context === 'object' && !Array.isArray(author.historical_context)
    ? author.historical_context
    : null
);
const formatHistoricalContextPeriod = (context) => {
  const period = context?.period && typeof context.period === 'object' && !Array.isArray(context.period)
    ? context.period
    : null;
  if (!period) {
    return '';
  }
  const label = cleanText(period.label);
  const startYear = cleanText(period.start_year);
  const endYear = cleanText(period.end_year);
  const yearRange = startYear && endYear
    ? `${startYear}–${endYear}`
    : (startYear || endYear);
  return uniqueTextList([label, yearRange], 2).join(' · ');
};
const getHistoricalContextPlaces = (context) => (
  Array.isArray(context?.places)
    ? context.places
      .map((item) => uniqueTextList([cleanText(item?.name), cleanText(item?.role)], 2).join(' · '))
      .filter(Boolean)
    : []
);
const getHistoricalContextTimelineEntries = (items) => (
  Array.isArray(items)
    ? items
      .map((item) => ({
        dateLabel: cleanText(item?.date_label || item?.dateLabel),
        title: cleanText(item?.title),
        body: cleanText(item?.body),
      }))
      .filter((item) => item.title || item.body)
    : []
);
const getHistoricalContextLabeledEntries = (items) => (
  Array.isArray(items)
    ? items
      .map((item) => ({
        label: cleanText(item?.label),
        body: cleanText(item?.body),
      }))
      .filter((item) => item.label || item.body)
    : []
);
const getHistoricalContextStringList = (items) => (
  Array.isArray(items)
    ? items.map((item) => cleanText(item)).filter(Boolean)
    : []
);
const formatHistoricalContextProvenance = (context) => {
  const provenance = context?.provenance && typeof context.provenance === 'object' && !Array.isArray(context.provenance)
    ? context.provenance
    : null;
  if (!provenance) {
    return '';
  }
  const confidence = Number(provenance.confidence);
  const confidenceLabel = Number.isFinite(confidence)
    ? `${Math.round(confidence * 100)}% confidence`
    : '';
  return uniqueTextList([
    Number.isFinite(Number(provenance.source_count)) ? `${Number(provenance.source_count)} reviewed sources` : '',
    Number.isFinite(Number(provenance.claim_count)) ? `${Number(provenance.claim_count)} claims` : '',
    confidenceLabel,
    cleanText(provenance.version) ? `Snapshot ${cleanText(provenance.version)}` : '',
  ], 4).join(' · ');
};
const getAuthorNotable = (author) => (
  cleanText(getAuthorBiography(author)?.notable)
  || getAuthorSummary(author)
);
const getAuthorCatalogName = (author) => (
  cleanText(author?.catalog_name)
  || cleanText(author?.name)
  || formatSlugLabel(author?.slug)
);
const normalizeAuthorPortraitManifest = (payload) => {
  const items = payload && typeof payload === 'object' && !Array.isArray(payload)
    ? payload
    : {};
  return Object.fromEntries(
    Object.entries(items)
      .map(([slug, portrait]) => [cleanText(slug).toLowerCase(), portrait])
      .filter(([slug, portrait]) => slug && portrait && typeof portrait === 'object' && !Array.isArray(portrait)),
  );
};
const LOCAL_AUTHOR_PORTRAITS = normalizeAuthorPortraitManifest(authorPortraitManifest);
const applyLocalAuthorPortraitMetadata = (author) => {
  if (!author || typeof author !== 'object' || Array.isArray(author)) {
    return author;
  }
  const slug = cleanText(author.slug).toLowerCase();
  const portrait = LOCAL_AUTHOR_PORTRAITS[slug];
  if (!portrait) {
    return author;
  }

  const merged = { ...author };
  const authorPortrait = author.portrait && typeof author.portrait === 'object' && !Array.isArray(author.portrait)
    ? author.portrait
    : {};
  const mergedPortrait = { ...portrait, ...authorPortrait };
  if (cleanText(mergedPortrait.media_path || mergedPortrait.url)) {
    merged.portrait = mergedPortrait;
  }
  if (!cleanText(merged.catalog_name) && cleanText(portrait.catalog_name)) {
    merged.catalog_name = portrait.catalog_name;
  }
  if (!cleanText(merged.wikidata_id) && cleanText(portrait.wikidata_id)) {
    merged.wikidata_id = portrait.wikidata_id;
  }
  return merged;
};
const getAuthorPortrait = (author) => {
  const portrait = author?.portrait && typeof author.portrait === 'object' && !Array.isArray(author.portrait)
    ? author.portrait
    : {};
  const src = cleanText(
    portrait.media_path
    || portrait.url
    || author?.portrait_url
    || author?.portrait_media_path,
  );
  if (!src) {
    return null;
  }
  const catalogName = cleanText(portrait.catalog_name) || getAuthorCatalogName(author);
  return {
    src,
    alt: cleanText(portrait.alt) || `Portrait of ${catalogName}`,
  };
};

function AuthorPortrait({ author, className = '' }) {
  const portrait = getAuthorPortrait(author);
  if (!portrait) {
    return null;
  }

  return (
    <figure className={`author-portrait${className ? ` ${className}` : ''}`}>
      <img src={portrait.src} alt={portrait.alt} loading="lazy" />
    </figure>
  );
}

const GENERIC_AUTHOR_TRADITIONS = new Set([
  'biblical author',
  'christian',
  'current',
  'general',
  'modern psychology',
  'philosophy',
]);
const getAuthorBodyOfThought = (author) => {
  const biography = getAuthorBiography(author);
  const explicit = cleanText(biography?.body_of_thought || biography?.bodyOfThought);
  if (explicit) {
    return explicit;
  }

  const taxonomy = getAuthorTaxonomy(author);
  const category = cleanText(taxonomy.category);
  const family = cleanText(taxonomy.family);
  const group = cleanText(taxonomy.group);
  const tradition = cleanText(taxonomy.tradition);
  const normalizedTradition = tradition.toLowerCase();

  if (category === 'Scripture') {
    return uniqueTextList([group, family, tradition], 2).join(' · ') || 'Scriptural witness';
  }
  if (tradition && !GENERIC_AUTHOR_TRADITIONS.has(normalizedTradition)) {
    return uniqueTextList([tradition, family, group], 2).join(' · ');
  }
  return uniqueTextList([group, family, tradition, category], 2).join(' · ') || 'General tradition';
};
const getAuthorInfluenceEra = (author) => {
  const biography = getAuthorBiography(author);
  const explicit = cleanText(biography?.influence_era || biography?.influenceEra);
  if (explicit) {
    return explicit;
  }

  const taxonomy = getAuthorTaxonomy(author);
  const category = cleanText(taxonomy.category);
  const family = cleanText(taxonomy.family);
  const tradition = cleanText(taxonomy.tradition).toLowerCase();
  const era = cleanText(taxonomy.era);

  if (category === 'Scripture') {
    if (family === 'New Testament') {
      return 'The early Church and the long history of Christian doctrine.';
    }
    if (family === 'Old Testament') {
      return 'Ancient Israel, Second Temple Judaism, and later Jewish and Christian interpretation.';
    }
    if (family === 'Deuterocanonical / Apocrypha') {
      return 'Second Temple Judaism and later Jewish and Christian interpretation.';
    }
    if (family === 'Wisdom Tradition') {
      return 'Ancient sapiential reading and later Jewish and Christian moral reflection.';
    }
    return 'Ancient Jewish and Christian interpretation.';
  }
  if (category === 'Church Fathers') {
    return 'Late antiquity, medieval Christianity, and later doctrinal theology.';
  }
  if (category === 'Philosophy') {
    if (tradition.includes('platonic')) {
      return 'Late antiquity, Christian metaphysics, and the long philosophical tradition.';
    }
    if (tradition.includes('aristotelian')) {
      return 'Medieval scholasticism, early modern philosophy, and contemporary ethics and logic.';
    }
    if (tradition.includes('stoic')) {
      return 'Imperial Rome, Renaissance moral writing, and modern self-command.';
    }
    return 'Classical antiquity and the long philosophical tradition.';
  }
  if (category === 'Psychology') {
    return 'Modern psychotherapy, twentieth-century thought, and contemporary conversations about the self.';
  }
  if (category === 'Political Thought') {
    return 'Renaissance statecraft, early modern politics, and modern realism.';
  }
  if (category === 'Esoteric') {
    return 'Late antique esotericism, Renaissance revival, and modern occult traditions.';
  }
  if (category === 'Internal') {
    return 'Current product, systems, and operating practice inside this platform.';
  }
  return era
    ? `${era} and the traditions that continued to interpret it.`
    : 'The wider traditions that continued to receive this author.';
};
const getPrimaryAuthorWorkLabel = (works, author) => {
  const entries = Array.isArray(works) && works.length > 0
    ? works
    : (Array.isArray(author?.works_preview) ? author.works_preview : []);
  return entries
    .map((item) => cleanText(item?.book || item?.work || item?.source))
    .find(Boolean) || '';
};
const getAuthorIntroductoryPrompt = (author, works) => {
  const biography = getAuthorBiography(author);
  const explicit = cleanText(biography?.introductory_prompt || biography?.introductoryPrompt);
  if (explicit) {
    return explicit;
  }

  const displayName = cleanText(author?.name) || formatSlugLabel(author?.slug);
  const primaryWork = getPrimaryAuthorWorkLabel(works, author);
  const bodyOfThought = getAuthorBodyOfThought(author);

  if (primaryWork) {
    return `I'm new to ${displayName}. What should I understand first in your work, and why is ${primaryWork} the right place to start?`;
  }
  if (bodyOfThought) {
    return `I'm new to ${displayName}. What should I understand first about your body of thought, and where should I start in your works?`;
  }
  return `I'm new to ${displayName}. What is the best place to start in your work, and what question should I bring to it?`;
};
const isHomepagePath = (pathname) => {
  const normalized = String(pathname || '').trim();
  return normalized === '' || normalized === '/';
};

const readGuestHomepageAuthor = () => {
  try {
    return cleanText(window.sessionStorage.getItem(GUEST_HOMEPAGE_AUTHOR_STORAGE_KEY)).toLowerCase();
  } catch {
    return '';
  }
};

const writeGuestHomepageAuthor = (slug) => {
  try {
    const normalized = cleanText(slug).toLowerCase();
    if (!normalized) {
      window.sessionStorage.removeItem(GUEST_HOMEPAGE_AUTHOR_STORAGE_KEY);
      return;
    }
    window.sessionStorage.setItem(GUEST_HOMEPAGE_AUTHOR_STORAGE_KEY, normalized);
  } catch {
    // Ignore storage write failures; they should not break guest landing.
  }
};

const pickGuestHomepageAuthor = ({ browseItems, authors }) => {
  const visibleAuthorSlugs = new Set(
    (Array.isArray(authors) ? authors : [])
      .map((author) => cleanText(author?.slug).toLowerCase())
      .filter(Boolean),
  );

  const browseCandidates = (Array.isArray(browseItems) ? browseItems : []).filter((author) => {
    const slug = cleanText(author?.slug).toLowerCase();
    const summary = getAuthorSummary(author);
    const worksCount = Number(author?.works_count || 0);
    const hasWorksPreview = Array.isArray(author?.works_preview)
      && author.works_preview.some((item) => cleanText(item?.book || item?.work || item?.source));
    return Boolean(
      slug
      && visibleAuthorSlugs.has(slug)
      && isPublicAuthor(author)
      && summary
      && (worksCount > 0 || hasWorksPreview),
    );
  });

  if (browseCandidates.length > 0) {
    const storedSlug = readGuestHomepageAuthor();
    if (storedSlug && browseCandidates.some((author) => cleanText(author?.slug).toLowerCase() === storedSlug)) {
      return storedSlug;
    }
    const index = Math.floor(Math.random() * browseCandidates.length);
    return cleanText(browseCandidates[index]?.slug).toLowerCase();
  }

  const safeFallback = (Array.isArray(authors) ? authors : []).find((author) => {
    const slug = cleanText(author?.slug).toLowerCase();
    return Boolean(slug && isPublicAuthor(author));
  });
  return cleanText(safeFallback?.slug).toLowerCase();
};
const EMPTY_AUTHOR_PREFERENCES = Object.freeze({
  favorite_authors: [],
  default_author_slug: '',
  default_author_unavailable_slug: '',
  updated_at: '',
});

const normalizeAuthorPreferencesPayload = (payload) => {
  const favorite_authors = uniqueTextList(
    Array.isArray(payload?.favorite_authors) ? payload.favorite_authors : [],
  ).map((slug) => cleanText(slug).toLowerCase()).filter(Boolean);
  return {
    favorite_authors,
    default_author_slug: cleanText(payload?.default_author_slug).toLowerCase(),
    default_author_unavailable_slug: cleanText(payload?.default_author_unavailable_slug).toLowerCase(),
    updated_at: cleanText(payload?.updated_at),
  };
};

const formatPreferenceTimestamp = (value) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};
const decodeBase64UrlUtf8 = (value) => {
  if (!value) {
    return '';
  }

  const normalized = String(value)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const remainder = normalized.length % 4;
  const padded = remainder === 0
    ? normalized
    : `${normalized}${'='.repeat(4 - remainder)}`;
  const binary = window.atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

const parseClockContext = (rawValue) => {
  const encoded = cleanText(rawValue);
  if (!encoded) {
    return null;
  }

  try {
    const decoded = decodeBase64UrlUtf8(encoded);
    const parsed = JSON.parse(decoded);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const normalizeClockLaunchState = (value) => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const source = cleanText(value.source) || CLOCK_LAUNCH_SOURCE;
  const sourcePromptId = cleanText(value.sourcePromptId || value.source_prompt_id);
  const initialMessageInput = cleanText(value.initialMessage || value.initial_message || value.message);
  const initialMessage = initialMessageInput || GUIDED_CLOCK_FALLBACK_MESSAGE;
  const rawClockContext = value.clockContext ?? value.clock_context;
  const clockContext = rawClockContext && typeof rawClockContext === 'object' && !Array.isArray(rawClockContext)
    ? rawClockContext
    : null;

  return {
    source,
    sourcePromptId,
    mentorHint: cleanText(value.mentorHint || value.mentor_hint) || CLOCK_LAUNCH_MENTOR_HINT,
    initialMessage,
    clockContext,
  };
};

const buildClockLaunchStateFromUrl = (locationLike = window.location) => {
  const search = String(locationLike?.search || '');
  if (!search) {
    return null;
  }

  const params = new URLSearchParams(search);
  const hasLaunchSignal = CLOCK_LAUNCH_QUERY_KEYS.some((key) => params.has(key));
  if (!hasLaunchSignal) {
    return null;
  }

  return normalizeClockLaunchState({
    mode: params.get('mode'),
    source: params.get('source'),
    message: params.get('message'),
    source_prompt_id: params.get('prompt_id'),
    clock_context: parseClockContext(params.get('ctx')),
  });
};

const buildClockLaunchStateFromSessionPayload = (value) => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const source = cleanText(value.source).toLowerCase();
  const sourcePromptId = cleanText(value.sourcePromptId || value.source_prompt_id);
  const rawClockContext = value.clockContext ?? value.clock_context;
  const hasClockContext = Boolean(rawClockContext && typeof rawClockContext === 'object' && !Array.isArray(rawClockContext));
  if (source !== CLOCK_LAUNCH_SOURCE && !sourcePromptId && !hasClockContext) {
    return null;
  }

  const normalized = normalizeClockLaunchState(value);
  return normalized?.source === CLOCK_LAUNCH_SOURCE ? normalized : null;
};

const getResumeSessionIdFromUrl = (locationLike = window.location) => {
  const search = String(locationLike?.search || '');
  if (!search) {
    return '';
  }
  const params = new URLSearchParams(search);
  return cleanText(params.get(SESSION_RESUME_QUERY_KEY));
};

const scrubClockLaunchParamsFromUrl = () => {
  const url = new URL(window.location.href);
  let changed = false;

  CLOCK_LAUNCH_QUERY_KEYS.forEach((key) => {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  });

  if (!changed) {
    return;
  }

  const search = url.searchParams.toString();
  const nextUrl = `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
  window.history.replaceState(window.history.state, '', nextUrl);
};

const scrubResumeSessionParamFromUrl = () => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(SESSION_RESUME_QUERY_KEY)) {
    return;
  }
  url.searchParams.delete(SESSION_RESUME_QUERY_KEY);
  const search = url.searchParams.toString();
  const nextUrl = `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
  window.history.replaceState(window.history.state, '', nextUrl);
};

const getClockLaunchStorageKey = (sessionId) => `${CLOCK_LAUNCH_STORAGE_PREFIX}${sessionId}`;

const persistClockLaunchState = (sessionId, launchState) => {
  if (!sessionId || !launchState) {
    return;
  }

  try {
    window.localStorage.setItem(
      getClockLaunchStorageKey(sessionId),
      JSON.stringify({
        source: launchState.source,
        sourcePromptId: launchState.sourcePromptId,
        mentorHint: launchState.mentorHint,
        initialMessage: launchState.initialMessage,
        clockContext: launchState.clockContext,
      }),
    );
  } catch {
    // Ignore storage failures so launch bootstrap remains non-fatal.
  }
};

const loadPersistedClockLaunchState = (sessionId) => {
  if (!sessionId) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(getClockLaunchStorageKey(sessionId));
    if (!raw) {
      return null;
    }
    return normalizeClockLaunchState(JSON.parse(raw));
  } catch {
    return null;
  }
};

const buildGuestHistoryPreview = (messages, matterAnchor = '') => {
  const explicitMatter = cleanText(matterAnchor);
  if (explicitMatter) {
    return explicitMatter;
  }

  const availableMessages = Array.isArray(messages) ? messages : [];
  for (let index = availableMessages.length - 1; index >= 0; index -= 1) {
    const candidate = availableMessages[index];
    if (cleanText(candidate?.role).toLowerCase() === 'user') {
      const messageText = cleanText(candidate?.message);
      if (messageText) {
        return messageText;
      }
    }
  }

  for (let index = availableMessages.length - 1; index >= 0; index -= 1) {
    const messageText = cleanText(availableMessages[index]?.message);
    if (messageText) {
      return messageText;
    }
  }

  return '';
};

const normalizeGuestHistoryMessagePayload = (message, authors = []) => {
  const role = cleanText(message?.role).toLowerCase();
  const text = cleanText(message?.message);
  if (!role || !text) {
    return null;
  }

  return {
    role,
    message: text,
    persona: cleanText(message?.persona),
    followUpQuestion: normalizeFollowUpQuestionPayload(
      message?.followUpQuestion || message?.follow_up_question,
      authors,
    ),
    references: (Array.isArray(message?.references) ? message.references : [])
      .map(normalizeCounselReference)
      .filter(Boolean),
    metaInfo: Array.isArray(message?.metaInfo) ? message.metaInfo : [],
  };
};

const normalizeGuestHistoryCounselEntry = (entry) => {
  const id = cleanText(entry?.id);
  const authorSlug = cleanText(entry?.authorSlug || entry?.author_slug).toLowerCase();
  const response = cleanText(entry?.response);
  if (!id && !authorSlug && !response) {
    return null;
  }

  return {
    id,
    authorSlug,
    authorName: cleanText(entry?.authorName || entry?.author_name)
      || formatSlugLabel(authorSlug || 'author'),
    author: entry?.author && typeof entry.author === 'object' ? entry.author : null,
    matter: cleanText(entry?.matter),
    question: cleanText(entry?.question),
    response,
    references: (Array.isArray(entry?.references) ? entry.references : [])
      .map(normalizeCounselReference)
      .filter(Boolean),
    metaInfo: Array.isArray(entry?.metaInfo) ? entry.metaInfo : [],
  };
};

const normalizeGuestHistorySession = (session, authors = []) => {
  const sessionId = cleanText(session?.session_id || session?.sessionId);
  if (!sessionId) {
    return null;
  }

  const normalizedMessages = (Array.isArray(session?.messages) ? session.messages : [])
    .map((message) => normalizeGuestHistoryMessagePayload(message, authors))
    .filter(Boolean);
  const preview = cleanText(session?.preview)
    || buildGuestHistoryPreview(normalizedMessages, session?.matterAnchor || session?.matter_anchor);
  const startTime = cleanText(session?.start_time || session?.startTime);
  const lastActive = cleanText(session?.last_active || session?.lastActive || startTime);

  return {
    session_id: sessionId,
    persona: cleanText(session?.persona).toLowerCase(),
    mode: cleanText(session?.mode) || 'conversation',
    start_time: startTime || lastActive || '',
    last_active: lastActive || startTime || '',
    preview,
    messages: normalizedMessages,
    references: (Array.isArray(session?.references) ? session.references : [])
      .map(normalizeCounselReference)
      .filter(Boolean),
    metaInfo: Array.isArray(session?.metaInfo) ? session.metaInfo : [],
    matterAnchor: cleanText(session?.matterAnchor || session?.matter_anchor),
    counselAuthorSlugs: normalizeAuthorSlugList(
      session?.counselAuthorSlugs || session?.counsel_author_slugs,
    ),
    counselEntries: (Array.isArray(session?.counselEntries || session?.counsel_entries)
      ? (session.counselEntries || session.counsel_entries)
      : [])
      .map(normalizeGuestHistoryCounselEntry)
      .filter(Boolean),
    counselSynthesis: normalizeCounselSynthesisPayload(
      session?.counselSynthesis || session?.counsel_synthesis,
    ),
    launchState: normalizeClockLaunchState(session?.launchState || session?.launch_state),
  };
};

const compareGuestHistorySessions = (left, right) => {
  const leftTime = new Date(left?.last_active || left?.start_time || 0).getTime();
  const rightTime = new Date(right?.last_active || right?.start_time || 0).getTime();
  return rightTime - leftTime;
};

const loadPersistedGuestHistorySessions = (authors = []) => {
  try {
    const raw = window.localStorage.getItem(GUEST_HISTORY_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== GUEST_HISTORY_STORAGE_VERSION || !Array.isArray(parsed.sessions)) {
      return [];
    }
    return parsed.sessions
      .map((session) => normalizeGuestHistorySession(session, authors))
      .filter(Boolean)
      .sort(compareGuestHistorySessions)
      .slice(0, GUEST_HISTORY_MAX_SESSIONS);
  } catch {
    return [];
  }
};

const loadPersistedGuestHistorySession = (sessionId, authors = []) => {
  const normalizedSessionId = cleanText(sessionId);
  if (!normalizedSessionId) {
    return null;
  }
  return loadPersistedGuestHistorySessions(authors).find(
    (session) => session.session_id === normalizedSessionId,
  ) || null;
};

const persistGuestHistorySession = (session) => {
  const normalized = normalizeGuestHistorySession(session);
  if (!normalized || normalized.messages.length === 0) {
    return;
  }

  try {
    const current = loadPersistedGuestHistorySessions().filter(
      (item) => item.session_id !== normalized.session_id,
    );
    const nextSessions = [normalized, ...current]
      .sort(compareGuestHistorySessions)
      .slice(0, GUEST_HISTORY_MAX_SESSIONS);
    window.localStorage.setItem(
      GUEST_HISTORY_STORAGE_KEY,
      JSON.stringify({
        version: GUEST_HISTORY_STORAGE_VERSION,
        sessions: nextSessions,
      }),
    );
  } catch {
    // Ignore storage failures so guest history remains additive only.
  }
};

const getClockLaunchChips = (launchState) => {
  if (!launchState) {
    return [];
  }

  const clockContext = launchState.clockContext || {};
  const chips = [
    {
      label: 'Day',
      value: cleanText(clockContext?.daily_guidance?.day),
    },
    {
      label: 'Focus',
      value: cleanText(clockContext?.daily_profile?.focus || clockContext?.weekly_arc?.focus),
    },
    {
      label: 'Domain',
      value: cleanText(clockContext?.daily_profile?.life_domain_focus),
    },
    {
      label: 'Anchor',
      value: cleanText(clockContext?.content_bundle?.wisdom?.ref),
    },
  ].filter((item) => item.value);

  if (chips.length === 0 && cleanText(launchState.sourcePromptId)) {
    chips.push({
      label: 'Prompt',
      value: cleanText(launchState.sourcePromptId),
    });
  }

  return chips.slice(0, 4);
};

const getClockLaunchSummary = (launchState) => {
  const tone = cleanText(launchState?.clockContext?.daily_guidance?.tone);
  if (tone) {
    return tone;
  }

  return 'Clock context is attached to this conversation.';
};

const stripParenthetical = (value) => cleanText(value).replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
const toOptionalInt = (value) => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};
const formatReferenceLabel = (book, chapter, verseStart, verseEnd) => {
  const safeBook = book || 'Reference';
  const chapterText = chapter && !String(chapter).toLowerCase().includes('unknown')
    ? String(chapter)
    : '';
  const start = toOptionalInt(verseStart);
  const end = toOptionalInt(verseEnd);
  if (chapterText && start != null) {
    if (end != null && end !== start) {
      return `${safeBook} ${chapterText}:${start}-${end}`;
    }
    return `${safeBook} ${chapterText}:${start}`;
  }
  if (chapterText) {
    return `${safeBook} ${chapterText}`;
  }
  return safeBook;
};

const REFERENCE_PROVENANCE_PRIORITY = {
  retrieved: 0,
  inferred_from_excerpt: 1,
  inferred_from_answer: 2,
};

const getReferenceProvenancePriority = (value) => {
  const key = cleanText(value).toLowerCase();
  return Object.prototype.hasOwnProperty.call(REFERENCE_PROVENANCE_PRIORITY, key)
    ? REFERENCE_PROVENANCE_PRIORITY[key]
    : 9;
};

const parseReferenceLabel = (value) => {
  const text = cleanText(value);
  if (!text) {
    return null;
  }
  const match = text.match(/^(.+?)\s+(\d{1,3})(?::(\d{1,3})(?:-(\d{1,3}))?)?$/);
  if (!match) {
    return null;
  }
  return {
    book: cleanText(match[1]),
    chapter: cleanText(match[2]),
    verseStart: toOptionalInt(match[3]),
    verseEnd: toOptionalInt(match[4]) ?? toOptionalInt(match[3]),
  };
};

const buildReferenceDedupKey = (ref) => {
  const targetType = cleanText(ref?.targetType || ref?.target_type || 'bible').toLowerCase();
  if (targetType === 'author') {
    return `author:${normalizeBookKey(ref?.targetSlug || ref?.displayLabel || ref?.book)}`;
  }
  if (targetType === 'work') {
    return `work:${normalizeBookKey(ref?.displayLabel || ref?.book)}`;
  }
  const chapter = cleanText(ref?.chapter);
  if (chapter) {
    return `bible:${normalizeBookKey(ref?.book)}:${chapter}:${toOptionalInt(ref?.verseStart) ?? ''}:${toOptionalInt(ref?.verseEnd) ?? ''}`;
  }
  return `book:${normalizeBookKey(ref?.displayLabel || ref?.book)}`;
};

const getReferenceRichness = (ref) => {
  let score = 0;
  if (ref?.expandable) {
    score += 4;
  }
  if (ref?.targetSlug || ref?.targetAuthorSlug) {
    score += 2;
  }
  if (ref?.position != null) {
    score += 1;
  }
  if (cleanText(ref?.content) && !/inferred .* answer text/i.test(cleanText(ref?.content))) {
    score += 1;
  }
  return score;
};

const preferIncomingReference = (current, incoming) => {
  const currentPriority = getReferenceProvenancePriority(current?.provenance);
  const incomingPriority = getReferenceProvenancePriority(incoming?.provenance);
  if (incomingPriority !== currentPriority) {
    return incomingPriority < currentPriority;
  }
  const currentRichness = getReferenceRichness(current);
  const incomingRichness = getReferenceRichness(incoming);
  if (incomingRichness !== currentRichness) {
    return incomingRichness > currentRichness;
  }
  return cleanText(incoming?.content).length > cleanText(current?.content).length;
};

const mergeReferenceRows = (current, incoming) => {
  if (!current) {
    return incoming;
  }
  const preferred = preferIncomingReference(current, incoming) ? incoming : current;
  const fallback = preferred === incoming ? current : incoming;
  return {
    ...fallback,
    ...preferred,
    content: cleanText(preferred?.content) || cleanText(fallback?.content) || 'Reference available.',
    displayLabel: cleanText(preferred?.displayLabel) || cleanText(fallback?.displayLabel) || '',
    book: cleanText(preferred?.book) || cleanText(fallback?.book) || '',
    source: cleanText(preferred?.source) || cleanText(fallback?.source) || '',
    chapter: cleanText(preferred?.chapter) || cleanText(fallback?.chapter) || '',
    page: cleanText(preferred?.page) || cleanText(fallback?.page) || '',
    position: preferred?.position ?? fallback?.position ?? null,
    verseStart: preferred?.verseStart ?? fallback?.verseStart ?? null,
    verseEnd: preferred?.verseEnd ?? fallback?.verseEnd ?? null,
    provenance: cleanText(preferred?.provenance) || cleanText(fallback?.provenance) || 'retrieved',
    targetType: cleanText(preferred?.targetType) || cleanText(fallback?.targetType) || 'bible',
    targetSlug: cleanText(preferred?.targetSlug) || cleanText(fallback?.targetSlug) || '',
    targetAuthorSlug: cleanText(preferred?.targetAuthorSlug) || cleanText(fallback?.targetAuthorSlug) || '',
    authorSlug: cleanText(preferred?.authorSlug) || cleanText(fallback?.authorSlug) || '',
    detector: cleanText(preferred?.detector) || cleanText(fallback?.detector) || '',
    confidence: preferred?.confidence ?? fallback?.confidence ?? null,
    expanded: preferred?.expandable ? Boolean(preferred?.expanded) : Boolean(fallback?.expanded),
    expandable: Boolean(preferred?.expandable || fallback?.expandable),
  };
};

const uniqueTextList = (items, limit = Infinity) => {
  const seen = new Set();
  const results = [];
  items.forEach((item) => {
    const value = cleanText(item);
    if (!value) {
      return;
    }
    const key = value.toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    results.push(value);
  });
  return results.slice(0, limit);
};

const getTaxonomyTrail = (taxonomy) => uniqueTextList([
  taxonomy?.category,
  taxonomy?.family,
  taxonomy?.group,
  taxonomy?.tradition,
  taxonomy?.era,
], 5);

const getWorksPreviewLabel = (works) => {
  if (!Array.isArray(works) || works.length === 0) {
    return 'No works metadata available yet.';
  }
  const names = works
    .map((item) => cleanText(item?.book || item?.work || item?.source))
    .filter(Boolean)
    .slice(0, 4);
  if (names.length === 0) {
    return 'No works metadata available yet.';
  }
  return names.join(' • ');
};

const getCrossrefSummaryLabel = (crossrefs) => {
  const counts = crossrefs?.counts || {};
  const books = Number(counts.books || 0);
  const testaments = Number(counts.testaments || 0);
  if (books <= 0) {
    return 'No cross citations mapped yet.';
  }
  if (testaments > 0) {
    return `${books} mapped scriptural references across ${testaments} testament bucket${testaments === 1 ? '' : 's'}.`;
  }
  return `${books} mapped scriptural references.`;
};

const getClockGuidedPromptsUrl = () => (
  ROOT_URL ? `${ROOT_URL}/api/pericope/guided-prompts` : '/api/pericope/guided-prompts'
);

const getTimeBucket = (date = new Date()) => {
  const hour = date.getHours();
  if (hour < 11) {
    return { id: 'morning', label: 'Morning' };
  }
  if (hour < 17) {
    return { id: 'midday', label: 'Midday' };
  }
  return { id: 'evening', label: 'Evening' };
};

const CHALDEAN_ORDER = ['Saturn', 'Jupiter', 'Mars', 'Sun', 'Venus', 'Mercury', 'Moon'];

const CLOCK_GUIDANCE_STUBS = [
  {
    day: 'Sunday',
    ruler: 'Sun',
    subjects: ['purpose', 'leadership', 'gratitude'],
    dailyGuidance: {
      tone: 'Visibility, gratitude, and rightly ordered ambition are favored.',
      activities: [
        'Set one public duty under a clean intention.',
        'Strengthen courage without drifting into vanity.',
        'Review whether your work is serving a worthy end.',
      ],
    },
    weeklyArc: {
      pentacle: 'Sun #3',
      focus: 'Public duty and favor',
      psalmRef: 'Psalm 19:1',
      wisdomRef: 'Proverbs 16:3',
    },
    dailyProfile: {
      activePentacle: 'Sun #6',
      focus: 'Public duty and clarity',
      color: 'Gold',
      metal: 'Gold',
      angel: 'Michael',
    },
    whySelected: {
      spirit: 'Leo 0-5 (Orias)',
      source: 'Key of Solomon, Book II',
      primaryCitation: 'Primary scripture citation: Psalm 19:1.',
    },
    contentBundle: {
      psalmText: 'The heavens declare the glory of God; and the firmament sheweth his handywork.',
      wisdomText: 'Commit thy works unto the LORD, and thy thoughts shall be established.',
      solomonicRef: 'Key of Solomon, Book II • Sun Pentacle #6',
      solomonicText: 'Purpose: honor, favor, and visible courage.',
    },
    promptSeeds: {
      action: 'public duty, ambition, and gratitude',
      weekly: 'What does public duty and favor require today?',
      profile: 'Why is public duty and clarity the suggested focus today?',
    },
  },
  {
    day: 'Monday',
    ruler: 'Moon',
    subjects: ['reflection', 'home', 'recollection'],
    dailyGuidance: {
      tone: 'Reflection, recollection, and the ordering of the inner life are favored.',
      activities: [
        'Make room for recollection before reacting.',
        'Repair disorder at home or in the household.',
        'Ask where your attention has become too diffuse.',
      ],
    },
    weeklyArc: {
      pentacle: 'Moon #2',
      focus: 'Domestic peace and inward order',
      psalmRef: 'Psalm 42:1',
      wisdomRef: 'Wisdom 8:1',
    },
    dailyProfile: {
      activePentacle: 'Moon #6',
      focus: 'Recollection and household peace',
      color: 'Pearl',
      metal: 'Silver',
      angel: 'Gabriel',
    },
    whySelected: {
      spirit: 'Cancer 10-15 (Vassago)',
      source: 'Key of Solomon, Book II',
      primaryCitation: 'Primary scripture citation: Psalm 42:1.',
    },
    contentBundle: {
      psalmText: 'As the hart panteth after the water brooks, so panteth my soul after thee, O God.',
      wisdomText: 'She reacheth from one end to another mightily: and sweetly doth she order all things.',
      solomonicRef: 'Key of Solomon, Book II • Moon Pentacle #6',
      solomonicText: 'Purpose: peace, dreams, and interior recollection.',
    },
    promptSeeds: {
      action: 'recollection, household peace, and inward order',
      weekly: 'What does domestic peace and inward order require today?',
      profile: 'Why is recollection and household peace the suggested focus today?',
    },
  },
  {
    day: 'Tuesday',
    ruler: 'Mars',
    subjects: ['courage', 'conflict', 'duty'],
    dailyGuidance: {
      tone: 'Courage, difficult action, and hard conversations are favored.',
      activities: [
        'Face one avoided task directly.',
        'Bring discipline to anger before using forceful speech.',
        'Do the hard duty without performative aggression.',
      ],
    },
    weeklyArc: {
      pentacle: 'Mars #4',
      focus: 'Conflict navigation with restraint',
      psalmRef: 'Psalm 18:39',
      wisdomRef: 'Proverbs 24:10',
    },
    dailyProfile: {
      activePentacle: 'Mars #2',
      focus: 'Courage under pressure',
      color: 'Scarlet',
      metal: 'Iron',
      angel: 'Samael',
    },
    whySelected: {
      spirit: 'Aries 15-20 (Marchosias)',
      source: 'Key of Solomon, Book II',
      primaryCitation: 'Primary scripture citation: Psalm 18:39.',
    },
    contentBundle: {
      psalmText: 'For thou hast girded me with strength unto the battle.',
      wisdomText: 'If thou faint in the day of adversity, thy strength is small.',
      solomonicRef: 'Key of Solomon, Book II • Mars Pentacle #2',
      solomonicText: 'Purpose: strength, courage, and disciplined force.',
    },
    promptSeeds: {
      action: 'anger, conflict, and difficult duty',
      weekly: 'What does conflict navigation with restraint require today?',
      profile: 'Why is courage under pressure the suggested focus today?',
    },
  },
  {
    day: 'Wednesday',
    ruler: 'Mercury',
    subjects: ['speech', 'study', 'judgment'],
    dailyGuidance: {
      tone: 'Study, right speech, and careful judgment are favored.',
      activities: [
        'Write or revise something with precision.',
        'Slow down before sending the sharp response.',
        'Use the day for reading, counsel, and tested words.',
      ],
    },
    weeklyArc: {
      pentacle: 'Mercury #3',
      focus: 'Judgment in speech and counsel',
      psalmRef: 'Psalm 119:66',
      wisdomRef: 'Proverbs 18:13',
    },
    dailyProfile: {
      activePentacle: 'Mercury #6',
      focus: 'Study and prudent speech',
      color: 'Amber',
      metal: 'Quicksilver',
      angel: 'Raphael',
    },
    whySelected: {
      spirit: 'Virgo 5-10 (Bifrons)',
      source: 'Key of Solomon, Book II',
      primaryCitation: 'Primary scripture citation: Psalm 119:66.',
    },
    contentBundle: {
      psalmText: 'Teach me good judgment and knowledge: for I have believed thy commandments.',
      wisdomText: 'He that answereth a matter before he heareth it, it is folly and shame unto him.',
      solomonicRef: 'Key of Solomon, Book II • Mercury Pentacle #6',
      solomonicText: 'Purpose: understanding, prudent counsel, and clear speech.',
    },
    promptSeeds: {
      action: 'study, prudent speech, and judgment',
      weekly: 'What does judgment in speech and counsel require today?',
      profile: 'Why is study and prudent speech the suggested focus today?',
    },
  },
  {
    day: 'Thursday',
    ruler: 'Jupiter',
    subjects: ['stewardship', 'justice', 'leadership'],
    dailyGuidance: {
      tone: 'Stewardship, generosity, and higher-order judgment are favored.',
      activities: [
        'Make one decision with justice instead of convenience.',
        'Use strength in service rather than self-display.',
        'Build something that will still matter later.',
      ],
    },
    weeklyArc: {
      pentacle: 'Jupiter #2',
      focus: 'Justice and magnanimity',
      psalmRef: 'Psalm 112:5',
      wisdomRef: 'Proverbs 11:25',
    },
    dailyProfile: {
      activePentacle: 'Jupiter #7',
      focus: 'Stewardship and enlargement',
      color: 'Royal blue',
      metal: 'Tin',
      angel: 'Sachiel',
    },
    whySelected: {
      spirit: 'Sagittarius 5-10 (Ipos)',
      source: 'Key of Solomon, Book II',
      primaryCitation: 'Primary scripture citation: Psalm 112:5.',
    },
    contentBundle: {
      psalmText: 'A good man sheweth favour, and lendeth: he will guide his affairs with discretion.',
      wisdomText: 'The liberal soul shall be made fat: and he that watereth shall be watered also himself.',
      solomonicRef: 'Key of Solomon, Book II • Jupiter Pentacle #7',
      solomonicText: 'Purpose: increase, justice, and wise administration.',
    },
    promptSeeds: {
      action: 'justice, generosity, and responsible leadership',
      weekly: 'What does justice and magnanimity require today?',
      profile: 'Why is stewardship and enlargement the suggested focus today?',
    },
  },
  {
    day: 'Friday',
    ruler: 'Venus',
    subjects: ['diplomacy', 'relationship', 'reconciliation'],
    dailyGuidance: {
      tone: 'Harmony, creativity, and relationship work are favored.',
      activities: [
        'Repair social friction with diplomacy.',
        'Make time for art or design.',
        'Strengthen key partnerships.',
      ],
    },
    weeklyArc: {
      pentacle: 'Mercury #5',
      focus: 'Negotiation success',
      psalmRef: 'Psalm 45:2',
      wisdomRef: 'Proverbs 15:1',
    },
    dailyProfile: {
      activePentacle: 'Mercury #7',
      focus: 'Safe travel',
      color: 'Emerald',
      metal: 'Copper',
      angel: 'Anael',
    },
    whySelected: {
      spirit: 'Gemini 10-15 (Eligos)',
      source: 'Key of Solomon, Book II',
      primaryCitation: 'Primary scripture citation: fallback psalm is used when this pentacle has no direct Psalm note.',
    },
    contentBundle: {
      psalmText: 'Thou art fairer than the children of men: grace is poured into thy lips.',
      wisdomText: 'A soft answer turneth away wrath: but grievous words stir up anger.',
      solomonicRef: 'Key of Solomon, Book II • Mercury Pentacle #7',
      solomonicText: 'Purpose: Safe travel.',
    },
    promptSeeds: {
      action: 'social friction and diplomacy',
      weekly: 'What does negotiation success require today?',
      profile: 'Why is safe travel the suggested focus today?',
    },
  },
  {
    day: 'Saturday',
    ruler: 'Saturn',
    subjects: ['discipline', 'inventory', 'endurance'],
    dailyGuidance: {
      tone: 'Discipline, order, and honest inventory are favored.',
      activities: [
        'Put one neglected structure back in order.',
        'Do the tedious work you have been postponing.',
        'Take stock of what must be cut, repaired, or endured.',
      ],
    },
    weeklyArc: {
      pentacle: 'Saturn #3',
      focus: 'Restraint and sober inventory',
      psalmRef: 'Psalm 90:12',
      wisdomRef: 'Ecclesiastes 7:8',
    },
    dailyProfile: {
      activePentacle: 'Saturn #7',
      focus: 'Discipline and endurance',
      color: 'Lead gray',
      metal: 'Lead',
      angel: 'Cassiel',
    },
    whySelected: {
      spirit: 'Capricorn 20-25 (Andromalius)',
      source: 'Key of Solomon, Book II',
      primaryCitation: 'Primary scripture citation: Psalm 90:12.',
    },
    contentBundle: {
      psalmText: 'So teach us to number our days, that we may apply our hearts unto wisdom.',
      wisdomText: 'Better is the end of a thing than the beginning thereof: and the patient in spirit is better than the proud in spirit.',
      solomonicRef: 'Key of Solomon, Book II • Saturn Pentacle #7',
      solomonicText: 'Purpose: endurance, boundaries, and severe honesty.',
    },
    promptSeeds: {
      action: 'discipline, inventory, and hard repair',
      weekly: 'What does restraint and sober inventory require today?',
      profile: 'Why is discipline and endurance the suggested focus today?',
    },
  },
];

const formatShortCalendarLabel = (date) => new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
}).format(date);

const getPlanetaryHourStub = (date, ruler) => {
  const startIndex = CHALDEAN_ORDER.indexOf(ruler);
  if (startIndex < 0) {
    return null;
  }
  const hourIndex = date.getHours();
  return {
    hourIndex,
    ruler: CHALDEAN_ORDER[(startIndex + hourIndex) % CHALDEAN_ORDER.length],
  };
};

const buildStubClockGuidance = ({ persona, personaName, authors }) => {
  const now = new Date();
  const bucket = getTimeBucket(now);
  const activeStub = CLOCK_GUIDANCE_STUBS[now.getDay()] || CLOCK_GUIDANCE_STUBS[0];
  const hourRule = getPlanetaryHourStub(now, activeStub.ruler);
  const promptGuideName = personaName || 'the selected author';

  const reasons = [
    `${activeStub.day} is ruled by ${activeStub.ruler}, so ${activeStub.ruler}-aligned intentions are prioritized.`,
    hourRule
      ? `Planetary hour proxy: local hour ${hourRule.hourIndex + 1} resolves to ${hourRule.ruler} in the Chaldean sequence.`
      : `Arrival cue: ${bucket.label.toLowerCase()} hours are foregrounding the day's practical emphasis.`,
    `Active spirit sector: ${activeStub.whySelected.spirit} informs the sign layer.`,
    `Active pentacle rule: ${activeStub.dailyProfile.activePentacle} (${activeStub.dailyProfile.focus}).`,
    activeStub.whySelected.primaryCitation,
  ];

  const guidedPrompts = [
    {
      id: normalizeBookKey(activeStub.promptSeeds.action),
      source: 'daily_guidance',
      source_label: GUIDED_PROMPT_SOURCE_LABELS.daily_guidance,
      text: `What should I do today about ${activeStub.promptSeeds.action}?`,
    },
    {
      id: normalizeBookKey(activeStub.weeklyArc.focus),
      source: 'weekly_arc',
      source_label: GUIDED_PROMPT_SOURCE_LABELS.weekly_arc,
      text: activeStub.promptSeeds.weekly,
    },
    {
      id: normalizeBookKey(activeStub.dailyProfile.focus),
      source: 'daily_profile',
      source_label: GUIDED_PROMPT_SOURCE_LABELS.daily_profile,
      text: activeStub.promptSeeds.profile,
    },
    {
      id: normalizeBookKey(`${activeStub.weeklyArc.wisdomRef}-${promptGuideName}`),
      source: 'content_bundle',
      source_label: GUIDED_PROMPT_SOURCE_LABELS.content_bundle,
      text: `Ask ${promptGuideName} how to apply ${activeStub.weeklyArc.wisdomRef} today.`,
    },
  ];

  const featureAuthors = [];
  const seenAuthors = new Set();
  const authorBySlug = new Map(
    authors.map((author) => [String(author?.slug || '').toLowerCase(), author]),
  );
  const pushAuthor = (author) => {
    const slug = String(author?.slug || '').toLowerCase();
    if (!slug || seenAuthors.has(slug) || featureAuthors.length >= 4) {
      return;
    }
    seenAuthors.add(slug);
    featureAuthors.push({
      slug,
      name: author?.name || formatSlugLabel(slug),
    });
  };

  const currentAuthor = authors.find((author) => author.slug === persona);
  if (currentAuthor) {
    pushAuthor(currentAuthor);
  }
  FEATURED_AUTHOR_PRIORITY_SLUGS.forEach((slug) => {
    pushAuthor(authorBySlug.get(slug));
  });

  return {
    hero: {
      eyebrow: 'Faith. Virtue. The Good Life.',
      title: 'Ask the Great Tradition',
      subtitle: 'Start with a live question, then press it with the voice best suited to the problem in front of you.',
      currentGuide: personaName,
      timeLabel: 'Suggested starting points for today',
      topics: activeStub.subjects,
      featuredAuthors: featureAuthors,
    },
    guidance: {
      as_of: now.toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      daily_guidance: {
        day: `${activeStub.day} (${activeStub.ruler})`,
        tone: activeStub.dailyGuidance.tone,
        activities: activeStub.dailyGuidance.activities,
      },
      weekly_arc: {
        date_label: formatShortCalendarLabel(now),
        ruler: activeStub.ruler,
        is_today: true,
        pentacle: activeStub.weeklyArc.pentacle,
        focus: activeStub.weeklyArc.focus,
        psalm_ref: activeStub.weeklyArc.psalmRef,
        wisdom_ref: activeStub.weeklyArc.wisdomRef,
      },
      daily_profile: {
        day_label: `${activeStub.day} ruled by ${activeStub.ruler}`,
        active_pentacle: activeStub.dailyProfile.activePentacle,
        focus: activeStub.dailyProfile.focus,
        color: activeStub.dailyProfile.color,
        metal: activeStub.dailyProfile.metal,
        angel: activeStub.dailyProfile.angel,
      },
      why_selected: {
        reasons,
        citation: `Citation source: ${activeStub.whySelected.source}`,
      },
      content_bundle: {
        psalm: {
          ref: activeStub.weeklyArc.psalmRef,
          text: activeStub.contentBundle.psalmText,
        },
        wisdom: {
          ref: activeStub.weeklyArc.wisdomRef,
          text: activeStub.contentBundle.wisdomText,
        },
        solomonic: {
          ref: activeStub.contentBundle.solomonicRef,
          text: activeStub.contentBundle.solomonicText,
        },
      },
      guided_prompts: guidedPrompts,
      source: {
        service: 'solomonic_clock_stub',
        derived_from: ['daily_guidance', 'weekly_arc', 'daily_profile', 'why_selected', 'content_bundle'],
      },
    },
  };
};

const normalizeGuidedPrompt = (prompt, index) => {
  const text = cleanText(prompt?.text);
  if (!text) {
    return null;
  }
  const source = cleanText(prompt?.source).toLowerCase().replace(/\s+/g, '_') || 'daily_guidance';
  return {
    id: cleanText(prompt?.id) || normalizeBookKey(`${source}-${text}-${index}`),
    source,
    source_label: cleanText(prompt?.source_label) || GUIDED_PROMPT_SOURCE_LABELS[source] || 'Guided prompt',
    text,
  };
};

const buildHeroTopicsFromGuidance = (guidance, fallbackTopics = []) => {
  const derivedTopics = uniqueTextList([
    guidance?.weekly_arc?.focus,
    guidance?.daily_profile?.focus,
    ...(Array.isArray(guidance?.daily_guidance?.activities) ? guidance.daily_guidance.activities : []),
  ], 4);
  return derivedTopics.length > 0 ? derivedTopics : fallbackTopics;
};

const buildHeroTimeLabelFromGuidance = (guidance, fallbackLabel = '') => {
  const day = stripParenthetical(guidance?.daily_guidance?.day);
  if (day) {
    return `${day} · suggested starting points`;
  }
  return fallbackLabel || 'Suggested starting points for today';
};

const buildLandingCuesFromGuidancePayload = ({ payload, fallbackCues }) => {
  if (!payload || typeof payload !== 'object') {
    return fallbackCues;
  }

  const fallbackGuidance = fallbackCues.guidance;
  const fallbackHero = fallbackCues.hero;
  const rawPrompts = Array.isArray(payload?.guided_prompts) ? payload.guided_prompts : [];
  const normalizedPrompts = rawPrompts
    .map((prompt, index) => normalizeGuidedPrompt(prompt, index))
    .filter(Boolean)
    .slice(0, 6);

  const guidance = {
    as_of: cleanText(payload?.as_of) || fallbackGuidance.as_of,
    timezone: cleanText(payload?.timezone) || fallbackGuidance.timezone,
    daily_guidance: {
      day: cleanText(payload?.daily_guidance?.day) || fallbackGuidance.daily_guidance.day,
      tone: cleanText(payload?.daily_guidance?.tone) || fallbackGuidance.daily_guidance.tone,
      activities: uniqueTextList(
        Array.isArray(payload?.daily_guidance?.activities)
          ? payload.daily_guidance.activities
          : fallbackGuidance.daily_guidance.activities,
        4,
      ),
    },
    weekly_arc: {
      date_label: cleanText(payload?.weekly_arc?.date_label) || fallbackGuidance.weekly_arc.date_label,
      ruler: cleanText(payload?.weekly_arc?.ruler) || fallbackGuidance.weekly_arc.ruler,
      is_today: typeof payload?.weekly_arc?.is_today === 'boolean'
        ? payload.weekly_arc.is_today
        : fallbackGuidance.weekly_arc.is_today,
      pentacle: cleanText(payload?.weekly_arc?.pentacle) || fallbackGuidance.weekly_arc.pentacle,
      focus: cleanText(payload?.weekly_arc?.focus) || fallbackGuidance.weekly_arc.focus,
      psalm_ref: cleanText(payload?.weekly_arc?.psalm_ref) || fallbackGuidance.weekly_arc.psalm_ref,
      wisdom_ref: cleanText(payload?.weekly_arc?.wisdom_ref) || fallbackGuidance.weekly_arc.wisdom_ref,
    },
    daily_profile: {
      day_label: cleanText(payload?.daily_profile?.day_label) || fallbackGuidance.daily_profile.day_label,
      active_pentacle: cleanText(payload?.daily_profile?.active_pentacle) || fallbackGuidance.daily_profile.active_pentacle,
      focus: cleanText(payload?.daily_profile?.focus) || fallbackGuidance.daily_profile.focus,
      color: cleanText(payload?.daily_profile?.color) || fallbackGuidance.daily_profile.color,
      metal: cleanText(payload?.daily_profile?.metal) || fallbackGuidance.daily_profile.metal,
      angel: cleanText(payload?.daily_profile?.angel) || fallbackGuidance.daily_profile.angel,
    },
    why_selected: {
      reasons: uniqueTextList(
        Array.isArray(payload?.why_selected?.reasons)
          ? payload.why_selected.reasons
          : fallbackGuidance.why_selected.reasons,
        6,
      ),
      citation: cleanText(payload?.why_selected?.citation) || fallbackGuidance.why_selected.citation,
    },
    content_bundle: {
      psalm: {
        ref: cleanText(payload?.content_bundle?.psalm?.ref) || fallbackGuidance.content_bundle.psalm.ref,
        text: cleanText(payload?.content_bundle?.psalm?.text) || fallbackGuidance.content_bundle.psalm.text,
      },
      wisdom: {
        ref: cleanText(payload?.content_bundle?.wisdom?.ref) || fallbackGuidance.content_bundle.wisdom.ref,
        text: cleanText(payload?.content_bundle?.wisdom?.text) || fallbackGuidance.content_bundle.wisdom.text,
      },
      solomonic: {
        ref: cleanText(payload?.content_bundle?.solomonic?.ref) || fallbackGuidance.content_bundle.solomonic.ref,
        text: cleanText(payload?.content_bundle?.solomonic?.text) || fallbackGuidance.content_bundle.solomonic.text,
      },
    },
    guided_prompts: normalizedPrompts.length > 0 ? normalizedPrompts : fallbackGuidance.guided_prompts,
    source: {
      service: cleanText(payload?.source?.service) || fallbackGuidance.source.service,
      derived_from: uniqueTextList(
        Array.isArray(payload?.source?.derived_from)
          ? payload.source.derived_from
          : fallbackGuidance.source.derived_from,
        8,
      ),
    },
  };

  return {
    hero: {
      ...fallbackHero,
      timeLabel: buildHeroTimeLabelFromGuidance(guidance, fallbackHero.timeLabel),
      topics: buildHeroTopicsFromGuidance(guidance, fallbackHero.topics),
    },
    guidance,
  };
};

function PersonaContextPanel({
  persona,
  setPersona,
  authors,
  profile,
  loading,
  error,
  invalidSlug,
  isAuthenticated,
  favoriteAuthorSlugs,
  defaultAuthorSlug,
  preferencesSaving,
  preferencesError,
  onToggleFavorite,
  onSetDefaultAuthor,
}) {
  const books = Array.isArray(profile?.books) ? profile.books : [];
  const hasKnownPersona = authors.some((author) => author.slug === persona);
  const selectedAuthor = authors.find((author) => author.slug === persona) || null;
  const favoriteAuthorSet = useMemo(
    () => new Set((favoriteAuthorSlugs || []).map((slug) => cleanText(slug).toLowerCase()).filter(Boolean)),
    [favoriteAuthorSlugs],
  );
  const favoriteAuthors = useMemo(
    () => authors.filter((author) => favoriteAuthorSet.has(cleanText(author?.slug).toLowerCase())),
    [authors, favoriteAuthorSet],
  );
  const [authorQuery, setAuthorQuery] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerBlurTimeoutRef = useRef(null);
  const normalizedQuery = String(authorQuery || '').trim().toLowerCase();

  useEffect(() => {
    setAuthorQuery('');
    setPickerOpen(false);
  }, [persona]);

  useEffect(() => () => {
    if (pickerBlurTimeoutRef.current) {
      window.clearTimeout(pickerBlurTimeoutRef.current);
    }
  }, []);

  const visibleAuthors = useMemo(() => {
    const scored = authors
      .map((author) => {
        const slug = String(author?.slug || '').trim().toLowerCase();
        const name = String(author?.name || '').trim() || formatSlugLabel(slug);
        const catalogName = cleanText(author?.catalog_name);
        if (!slug) {
          return null;
        }

        const lowerName = [name, catalogName].filter(Boolean).join(' ').toLowerCase();
        let score = slug === persona ? -1 : 10;
        if (normalizedQuery) {
          if (slug === normalizedQuery || lowerName === normalizedQuery) {
            score = 0;
          } else if (lowerName.startsWith(normalizedQuery)) {
            score = 1;
          } else if (slug.startsWith(normalizedQuery)) {
            score = 2;
          } else if (lowerName.includes(normalizedQuery)) {
            score = 3;
          } else if (slug.includes(normalizedQuery)) {
            score = 4;
          } else {
            return null;
          }
        }

        return {
          ...author,
          name,
          slug,
          score,
          isFavorite: favoriteAuthorSet.has(slug),
        };
      })
      .filter(Boolean)
      .sort(
        (left, right) => (
          Number(right.isFavorite) - Number(left.isFavorite)
          || left.score - right.score
          || left.name.localeCompare(right.name)
        ),
      );

    return scored;
  }, [authors, favoriteAuthorSet, persona, normalizedQuery]);

  const pickerAuthors = visibleAuthors.slice(0, normalizedQuery ? 10 : 8);
  const hasMoreMatches = visibleAuthors.length > pickerAuthors.length;
  const selectedSlug = cleanText(selectedAuthor?.slug).toLowerCase();
  const isFavoriteSelection = Boolean(selectedSlug && favoriteAuthorSet.has(selectedSlug));
  const isDefaultSelection = Boolean(selectedSlug && selectedSlug === cleanText(defaultAuthorSlug).toLowerCase());
  const contextAuthor = useMemo(
    () => ({ ...(selectedAuthor || {}), ...(profile || {}) }),
    [profile, selectedAuthor],
  );

  const handleAuthorPickerFocus = () => {
    if (pickerBlurTimeoutRef.current) {
      window.clearTimeout(pickerBlurTimeoutRef.current);
      pickerBlurTimeoutRef.current = null;
    }
    setPickerOpen(true);
  };

  const handleAuthorPickerBlur = () => {
    pickerBlurTimeoutRef.current = window.setTimeout(() => {
      setPickerOpen(false);
      pickerBlurTimeoutRef.current = null;
    }, 120);
  };

  const handleAuthorPick = (slug) => {
    if (!slug) {
      return;
    }
    setAuthorQuery('');
    setPickerOpen(false);
    setPersona(slug);
  };

  return (
    <div className="persona-context-panel">
      <div className="persona-context-panel__header">
        <div className="persona-context-control persona-context-control--stacked">
          <span className="persona-context-label">Persona</span>
          <span className="persona-context-current">
            {selectedAuthor
              ? `Current: ${selectedAuthor.name}`
              : invalidSlug
                ? `Unavailable: ${formatSlugLabel(invalidSlug)}`
                : 'Select an author'}
          </span>
          <div className="persona-context-picker">
            <label htmlFor="personaSearch" className="visually-hidden">Search authors</label>
            <input
              id="personaSearch"
              type="text"
              value={authorQuery}
              onChange={(e) => {
                setAuthorQuery(e.target.value);
                setPickerOpen(true);
              }}
              onFocus={handleAuthorPickerFocus}
              onBlur={handleAuthorPickerBlur}
              placeholder={hasKnownPersona ? 'Search authors by name or slug' : 'Find an author'}
              autoComplete="off"
              spellCheck="false"
            />
            {(pickerOpen || normalizedQuery) && (
              <div className="persona-context-results" aria-label="Author results">
                {pickerAuthors.length > 0 ? (
                  <>
                    {pickerAuthors.map((author) => (
                      <button
                        key={author.slug}
                        type="button"
                        className={`persona-context-option${author.slug === persona ? ' persona-context-option--active' : ''}`}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => handleAuthorPick(author.slug)}
                      >
                        <AuthorPortrait author={author} className="persona-context-option__portrait" />
                        <span className="persona-context-option__text">
                          <span className="persona-context-option__name">{author.name}</span>
                          <span className="persona-context-option__slug">{formatSlugLabel(author.slug)}</span>
                        </span>
                      </button>
                    ))}
                    {hasMoreMatches && (
                      <p className="persona-context-results__hint">
                        Keep typing to narrow {visibleAuthors.length} matching authors.
                      </p>
                    )}
                  </>
                ) : (
                  <p className="author-context-state">
                    No authors match <strong>{authorQuery.trim()}</strong>.
                  </p>
                )}
              </div>
            )}
          </div>
          <Link className="persona-context-browse-link" to="/authors">
            Browse all authors
          </Link>
          {isAuthenticated && favoriteAuthors.length > 0 && (
            <div className="persona-preferences-favorites">
              <span className="persona-context-label">Favorites</span>
              <div className="persona-preferences-favorites__list">
                {favoriteAuthors.map((author) => {
                  const slug = cleanText(author?.slug).toLowerCase();
                  const isDefault = slug && slug === cleanText(defaultAuthorSlug).toLowerCase();
                  return (
                    <button
                      key={slug}
                      type="button"
                      className={`persona-preferences-chip${slug === selectedSlug ? ' persona-preferences-chip--active' : ''}`}
                      onClick={() => handleAuthorPick(slug)}
                    >
                      <AuthorPortrait author={author} className="persona-preferences-chip__portrait" />
                      <span>{author.name || formatSlugLabel(slug)}</span>
                      {isDefault && <span className="persona-preferences-chip__badge">Default</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="persona-context-panel__body">
        <h2>Author Context</h2>
        {invalidSlug ? (
          <p className="author-context-state">
            Author slug <code>{invalidSlug}</code> is not available in this deployment. Select another persona.
          </p>
        ) : loading ? (
          <p className="author-context-state">Loading author profile…</p>
        ) : error ? (
          <p className="author-context-state">Unable to load author profile: {error}</p>
        ) : !profile ? (
          <p className="author-context-state">No author profile available for this selection.</p>
        ) : (
          <>
            <AuthorPortrait author={contextAuthor} className="author-context-portrait" />
            <div className="author-context-name">{profile.name || profile.slug}</div>
            <p className="author-context-description">
              {getAuthorSummary(profile) || 'No description available.'}
            </p>
            {isAuthenticated && selectedAuthor && (
              <div className="persona-preferences-actions">
                <button
                  type="button"
                  className={`persona-preferences-button${isFavoriteSelection ? ' persona-preferences-button--active' : ''}`}
                  onClick={() => onToggleFavorite(selectedSlug)}
                  disabled={preferencesSaving}
                >
                  {isFavoriteSelection ? 'Favorited' : 'Add to favorites'}
                </button>
                <button
                  type="button"
                  className={`persona-preferences-button${isDefaultSelection ? ' persona-preferences-button--active' : ''}`}
                  onClick={() => onSetDefaultAuthor(selectedSlug)}
                  disabled={preferencesSaving || !selectedSlug}
                >
                  {isDefaultSelection ? 'Default author' : 'Make default'}
                </button>
              </div>
            )}
            {isAuthenticated && preferencesError && (
              <p className="author-context-state">{preferencesError}</p>
            )}
            <details className="author-context-books" open={books.length <= 8}>
              <summary>Books / Works ({books.length})</summary>
              {books.length > 0 ? (
                <ul>
                  {books.map((item, index) => {
                    const bookName = getAuthorWorkPrimaryLabel(item, index);
                    const secondaryLabels = getAuthorWorkSecondaryLabels(item, index);
                    const readerHref = selectedSlug && cleanText(item?.source)
                      ? buildAuthorWorkReaderHref({
                        authorSlug: selectedSlug,
                        source: item.source,
                      })
                      : '';
                    return (
                      <li key={`${item.source || bookName}-${index}`} className="persona-context-books__item">
                        <div className="persona-context-books__copy">
                          <strong>{bookName}</strong>
                          {secondaryLabels.length > 0 && (
                            <span className="meta-label"> · {secondaryLabels.join(' · ')}</span>
                          )}
                        </div>
                        {readerHref && (
                          <Link
                            className="author-browse-card__button author-browse-card__button--secondary persona-context-books__read"
                            to={readerHref}
                          >
                            Read {bookName}
                          </Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="author-context-state">No books metadata available.</p>
              )}
            </details>
          </>
        )}
      </div>
    </div>
  );
}

function ChatInput({
  query,
  setQuery,
  onSubmit,
  loading,
  placeholder,
  voiceSupported = false,
  voiceStatus = VOICE_STATUS_IDLE,
  conversationActive = false,
  onStartVoiceInput,
  onStopVoiceInput,
  onToggleConversation,
}) {
  const listening = voiceStatus?.phase === 'listening';
  return (
    <form onSubmit={onSubmit} className={`input-form${conversationActive ? ' input-form--conversation' : ''}`}>
      <label htmlFor="chatQuestion" className="visually-hidden">Your message</label>
      <input
        id="chatQuestion"
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder || 'Ask a question...'}
        disabled={loading}
      />
      {voiceSupported && (
        <div className="input-form__voice-controls">
          <button
            type="button"
            className={`voice-control-button${listening && !conversationActive ? ' is-active' : ''}`}
            onClick={() => (listening ? onStopVoiceInput?.() : onStartVoiceInput?.())}
            disabled={loading && !listening}
            aria-pressed={listening && !conversationActive}
          >
            {listening && !conversationActive ? 'Stop' : 'Mic'}
          </button>
          <button
            type="button"
            className={`voice-control-button voice-control-button--conversation${conversationActive ? ' is-active' : ''}`}
            onClick={onToggleConversation}
            disabled={loading && !conversationActive}
            aria-pressed={conversationActive}
          >
            {conversationActive ? 'Pause' : 'Talk'}
          </button>
        </div>
      )}
      <button type="submit" disabled={loading}>Send</button>
    </form>
  );
}

function CounselEntryCard({
  entry,
  index,
  activePersonaSlug,
  activeReadAloudKey,
  readAloudStatus,
  onStartReadAloud,
  onStopReadAloud,
  readAloudSupported,
}) {
  const referencePreview = Array.isArray(entry?.references) ? entry.references.slice(0, 4) : [];
  const promptChanged = cleanText(entry?.question) && cleanText(entry?.question) !== cleanText(entry?.matter);

  return (
    <article className={`counsel-entry-card${entry?.authorSlug === activePersonaSlug ? ' counsel-entry-card--active' : ''}`}>
      <div className="counsel-entry-card__header">
        <div className="counsel-entry-card__identity">
          <AuthorPortrait author={entry?.author} className="counsel-entry-card__portrait" />
          <div className="counsel-entry-card__copy">
            <span className="counsel-entry-card__eyebrow">Counsel {index + 1}</span>
            <h3>{entry?.authorName || formatSlugLabel(entry?.authorSlug)}</h3>
            <p className="counsel-entry-card__prompt">
              {promptChanged ? `Prompt: ${entry.question}` : 'Asked directly from this matter.'}
            </p>
          </div>
        </div>
        {entry?.authorSlug === activePersonaSlug && (
          <span className="counsel-entry-card__badge">Active lens</span>
        )}
      </div>

      <p className="counsel-entry-card__response">{entry?.response}</p>
      <ReadAloudButton
        text={entry?.response}
        readAloudKey={`counsel:${entry?.id}`}
        activeReadAloudKey={activeReadAloudKey}
        readAloudStatus={readAloudStatus}
        onStartReadAloud={onStartReadAloud}
        onStopReadAloud={onStopReadAloud}
        className="read-aloud-button--detail"
        supported={readAloudSupported}
      />

      <div className="counsel-entry-card__references">
        <span className="counsel-entry-card__label">Mapped references</span>
        {referencePreview.length > 0 ? (
          <ul>
            {referencePreview.map((ref, refIndex) => (
              <li key={`${entry?.id}-${ref.displayLabel || ref.book}-${refIndex}`}>
                <strong>{ref.displayLabel || ref.book}</strong>
                {ref.provenance && (
                  <span className="meta-label"> [{formatProvenanceLabel(ref.provenance)}]</span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="counsel-entry-card__empty">No mapped references surfaced yet for this counsel.</p>
        )}
      </div>
    </article>
  );
}

function CounselSynthesisCard({
  synthesis,
  activeReadAloudKey,
  readAloudStatus,
  onStartReadAloud,
  onStopReadAloud,
  readAloudSupported,
}) {
  if (!synthesis) {
    return null;
  }

  const sections = [
    { label: 'Common Ground', value: synthesis.commonGround },
    { label: 'Tensions', value: synthesis.tensions },
    { label: 'Integrated Principle', value: synthesis.integratedPrinciple },
  ].filter((section) => cleanText(section.value));

  return (
    <article className="counsel-synthesis-card" aria-label="Counsel synthesis">
      <div className="counsel-synthesis-card__header">
        <div>
          <span className="counsel-synthesis-card__eyebrow">Synthesis</span>
          <h3>Integrated counsel for this matter</h3>
        </div>
        {synthesis.counselsCount > 0 && (
          <span className="counsel-synthesis-card__badge">{synthesis.counselsCount} counsels</span>
        )}
      </div>
      <div className="counsel-synthesis-card__sections">
        {sections.map((section) => (
          <div key={section.label} className="counsel-synthesis-card__section">
            <span className="counsel-synthesis-card__label">{section.label}</span>
            <p>{section.value}</p>
          </div>
        ))}
      </div>
      <ReadAloudButton
        text={synthesis.renderedText}
        readAloudKey={`synthesis:${normalizeBookKey(synthesis.matter || synthesis.renderedText)}`}
        activeReadAloudKey={activeReadAloudKey}
        readAloudStatus={readAloudStatus}
        onStartReadAloud={onStartReadAloud}
        onStopReadAloud={onStopReadAloud}
        className="read-aloud-button--detail"
        supported={readAloudSupported}
      />
    </article>
  );
}

function CounselStackPanel({
  matter,
  composerText,
  persona,
  authors,
  counselAuthorSlugs,
  counselEntries,
  counselSynthesis,
  onAddCounsel,
  onSynthesize,
  onRemoveCounselAuthor,
  getPersonaName,
  loading,
  synthesisLoading,
  activeReadAloudKey,
  readAloudStatus,
  onStartReadAloud,
  onStopReadAloud,
  readAloudSupported,
}) {
  const activePersonaName = getPersonaName(persona);
  const activeHasCounsel = Boolean(
    persona && (counselEntries || []).some((entry) => cleanText(entry?.authorSlug).toLowerCase() === cleanText(persona).toLowerCase()),
  );
  const selectedAuthors = useMemo(
    () => normalizeAuthorSlugList([
      ...((counselEntries || []).map((entry) => entry?.authorSlug)),
      ...(counselAuthorSlugs || []),
      persona,
    ])
      .map((slug) => {
        const normalized = cleanText(slug).toLowerCase();
        const author = authors.find((item) => cleanText(item?.slug).toLowerCase() === normalized);
        return normalized
          ? {
              slug: normalized,
              name: author?.name || formatSlugLabel(normalized),
              isActive: normalized === cleanText(persona).toLowerCase(),
            }
          : null;
      })
      .filter(Boolean),
    [authors, counselAuthorSlugs, counselEntries, persona],
  );
  const currentMatter = cleanText(matter) || cleanText(counselEntries[0]?.matter);
  const nextMatter = currentMatter || cleanText(composerText);
  const canSynthesize = counselEntries.length >= 2 && Boolean(currentMatter);
  const showStackDetails = counselEntries.length >= 2 || Boolean(counselSynthesis);

  return (
    <section className="counsel-stack-panel" aria-label="Counsel stack">
      <div className="counsel-stack-panel__header">
        <div>
          <span className="counsel-stack-panel__eyebrow">Counsel Stack</span>
          <h2>Add another author when a second perspective would help.</h2>
        </div>
        <div className="counsel-stack-panel__actions">
          <button
            type="button"
            className="counsel-stack-panel__add"
            onClick={onAddCounsel}
            disabled={loading || !persona || !nextMatter}
          >
            {activeHasCounsel ? `Refresh ${activePersonaName}'s counsel` : `Add Counsel from ${activePersonaName}`}
          </button>
          {(canSynthesize || synthesisLoading || counselSynthesis) && (
            <button
              type="button"
              className="counsel-stack-panel__synthesize"
              onClick={onSynthesize}
              disabled={loading || !canSynthesize}
            >
              {synthesisLoading ? 'Synthesizing…' : counselSynthesis ? 'Refresh Synthesis' : 'Synthesize'}
            </button>
          )}
        </div>
      </div>

      <div className="counsel-stack-panel__footer">
        <div className="counsel-stack-panel__chips" aria-label="Selected counsel authors">
          {selectedAuthors.length > 0 ? (
            selectedAuthors.map((author) => (
              <span
                key={author.slug}
                className={`counsel-chip${author.isActive ? ' counsel-chip--active' : ''}`}
              >
                <span>{author.name}</span>
                {author.isActive && <span className="counsel-chip__badge">Active</span>}
                <button
                  type="button"
                  onClick={() => onRemoveCounselAuthor(author.slug)}
                  disabled={loading || selectedAuthors.length <= 1 || author.isActive}
                  aria-label={`Remove ${author.name} from counsel`}
                >
                  ×
                </button>
              </span>
            ))
          ) : (
            <span className="counsel-stack-panel__empty">No counsel selected yet.</span>
          )}
        </div>
        <p>
          {counselEntries.length > 0
            ? `${counselEntries.length} counsels assembled. Change the active author and add another lens against the same matter.`
            : 'The first prompt becomes the shared matter for counsel and synthesis.'}
        </p>
      </div>

      {showStackDetails && (
        <div className="counsel-stack-panel__entries" aria-label="Counsel stack entries">
          <div className="counsel-stack-panel__entries-header">
            <span className="counsel-stack-panel__label">Counsel Stack</span>
            <p>{currentMatter}</p>
          </div>
          {counselEntries.map((entry, index) => (
            <CounselEntryCard
              key={entry.id}
              entry={entry}
              index={index}
              activePersonaSlug={cleanText(persona).toLowerCase()}
              activeReadAloudKey={activeReadAloudKey}
              readAloudStatus={readAloudStatus}
              onStartReadAloud={onStartReadAloud}
              onStopReadAloud={onStopReadAloud}
              readAloudSupported={readAloudSupported}
            />
          ))}
          {counselSynthesis && (
            <CounselSynthesisCard
              synthesis={counselSynthesis}
              activeReadAloudKey={activeReadAloudKey}
              readAloudStatus={readAloudStatus}
              onStartReadAloud={onStartReadAloud}
              onStopReadAloud={onStopReadAloud}
              readAloudSupported={readAloudSupported}
            />
          )}
        </div>
      )}
    </section>
  );
}

function LandingHero({ hero, persona, onSelectAuthor, compact }) {
  return (
    <section className={`landing-hero${compact ? ' landing-hero--compact' : ''}`}>
      <div className="landing-hero__copy">
        <p className="landing-hero__eyebrow">{hero.eyebrow}</p>
        <h1 className="landing-hero__title">{hero.title}</h1>
        <p className="landing-hero__subtitle">{hero.subtitle}</p>
      </div>
      <div className="landing-hero__current">
        <span className="landing-hero__current-label">Now speaking with</span>
        <strong>{hero.currentGuide}</strong>
        <span>{hero.timeLabel}</span>
      </div>
      {hero.featuredAuthors.length > 0 && (
        <div className="landing-hero__authors">
          {hero.featuredAuthors.map((author) => (
            <button
              key={author.slug}
              type="button"
              className={`landing-author-pill${author.slug === persona ? ' landing-author-pill--active' : ''}`}
              onClick={() => onSelectAuthor(author.slug)}
            >
              {author.name}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function EmptyChatState({ guidance, onPromptSelect }) {
  const primaryReading = cleanText(guidance?.content_bundle?.wisdom?.ref)
    ? guidance.content_bundle.wisdom
    : guidance.content_bundle.psalm;
  const secondaryReading = primaryReading?.ref === guidance?.content_bundle?.wisdom?.ref
    ? guidance.content_bundle.psalm
    : guidance.content_bundle.wisdom;
  const focusPrompt = guidance.guided_prompts[0]?.text
    || `What should I do today about ${cleanText(guidance?.daily_profile?.focus).toLowerCase()}?`;
  const themePrompt = guidance.guided_prompts[1]?.text
    || `What does ${cleanText(guidance?.weekly_arc?.focus).toLowerCase()} require today?`;
  const readingPrompt = guidance.guided_prompts.find((prompt) => prompt?.source === 'content_bundle')?.text
    || `How should I apply ${primaryReading.ref} today?`;

  return (
    <div className="landing-empty-state">
      <div className="landing-arrival-grid">
        <button
          type="button"
          className="landing-summary-card landing-focus-card"
          onClick={() => onPromptSelect(focusPrompt)}
        >
          <p className="landing-focus-card__eyebrow">Start here</p>
          <h2>Begin with a question that matters.</h2>
          <p>{guidance.daily_guidance.tone}</p>
          <ul className="landing-bullet-list">
            {guidance.daily_guidance.activities.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <span className="landing-summary-card__cta">Ask this</span>
        </button>
        <div className="landing-detail-stack">
          <button
            type="button"
            className="landing-summary-card landing-mini-card"
            onClick={() => onPromptSelect(themePrompt)}
          >
            <p className="landing-mini-card__eyebrow">Today&apos;s theme</p>
            <h3>{guidance.weekly_arc.focus}</h3>
            <p className="landing-mini-card__line">Suggested angle: {guidance.daily_profile.focus}</p>
            <p className="landing-mini-card__meta">{guidance.weekly_arc.wisdom_ref} • {guidance.weekly_arc.psalm_ref}</p>
            <span className="landing-summary-card__cta">Ask this</span>
          </button>
          <button
            type="button"
            className="landing-summary-card landing-mini-card landing-mini-card--reading"
            onClick={() => onPromptSelect(readingPrompt)}
          >
            <p className="landing-mini-card__eyebrow">Reading for today</p>
            <h3>{primaryReading.ref}</h3>
            <p className="landing-mini-card__focus">{primaryReading.text}</p>
            {cleanText(secondaryReading?.ref) && (
              <p className="landing-mini-card__meta">Pair with {secondaryReading.ref}</p>
            )}
            <span className="landing-summary-card__cta">Ask this</span>
          </button>
        </div>
      </div>
      <div className="landing-prompt-grid">
        {guidance.guided_prompts.map((prompt) => (
          <button
            key={prompt.id}
            type="button"
            className="landing-prompt-card"
            onClick={() => onPromptSelect(prompt.text)}
          >
            <span className="landing-prompt-card__category">{prompt.source_label}</span>
            <span className="landing-prompt-card__text">{prompt.text}</span>
            <span className="landing-prompt-card__cta">Start conversation</span>
          </button>
        ))}
      </div>
      <p className="landing-empty-state__note">
        Choose one of these to begin, or ignore them and ask your own question.
      </p>
    </div>
  );
}

function AskProverbsEmptyState({
  content,
  loading,
  error,
  onPromptSelect,
}) {
  const examples = Array.isArray(content?.examples) ? content.examples.filter(cleanText).slice(0, 4) : [];
  const anchors = Array.isArray(content?.anchors) ? content.anchors.filter((anchor) => cleanText(anchor?.ref)).slice(0, 4) : [];

  return (
    <div className="ask-proverbs-empty-state">
      <div className="ask-proverbs-prompt-grid">
        {examples.map((example) => (
          <button
            key={example}
            type="button"
            className="ask-proverbs-prompt-card"
            onClick={() => onPromptSelect(example)}
          >
            <span>{example}</span>
          </button>
        ))}
      </div>
      {loading && <p className="author-context-state">Loading Proverbs anchors...</p>}
      {!loading && error && (
        <p className="author-context-state">Unable to load Proverbs anchors: {error}</p>
      )}
      {anchors.length > 0 && (
        <div className="ask-proverbs-anchor-grid" aria-label="Proverbs anchors">
          {anchors.map((anchor) => (
            <article key={anchor.ref} className="ask-proverbs-anchor-card">
              <strong>{anchor.ref}</strong>
              <p>{anchor.text}</p>
              {Array.isArray(anchor.tags) && anchor.tags.length > 0 && (
                <div className="ask-proverbs-tags">
                  {anchor.tags.slice(0, 3).map((tag) => (
                    <span key={`${anchor.ref}:${tag}`}>{tag}</span>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function AskProverbsSidebarPanel({ content, loading, error }) {
  const anchors = Array.isArray(content?.anchors) ? content.anchors.filter((anchor) => cleanText(anchor?.ref)).slice(0, 6) : [];

  return (
    <div className="ask-proverbs-side-panel">
      <h2>Proverbs Anchors</h2>
      {loading && <p className="author-context-state">Loading anchors...</p>}
      {!loading && error && <p className="author-context-state">{error}</p>}
      {!loading && !error && anchors.length === 0 && (
        <p className="author-context-state">No Proverbs anchors are available.</p>
      )}
      {anchors.length > 0 && (
        <ul>
          {anchors.map((anchor) => (
            <li key={anchor.ref}>
              <strong>{anchor.ref}</strong>
              <span>{anchor.text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ClockLaunchBanner({ launchState }) {
  if (!launchState || launchState.source !== CLOCK_LAUNCH_SOURCE) {
    return null;
  }

  const chips = getClockLaunchChips(launchState);
  return (
    <section className="clock-launch-banner" aria-label="Launch context">
      <p className="clock-launch-banner__eyebrow">Launch context</p>
      <h2 className="clock-launch-banner__title">Clock context is attached</h2>
      <p className="clock-launch-banner__copy">{getClockLaunchSummary(launchState)}</p>
      {chips.length > 0 && (
        <div className="clock-launch-banner__chips">
          {chips.map((chip) => (
            <span key={`${chip.label}:${chip.value}`} className="clock-launch-chip">
              <span className="clock-launch-chip__label">{chip.label}</span>
              <span className="clock-launch-chip__value">{chip.value}</span>
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

function TopNav({
  userProfile,
  sessionId,
  onNewSession,
  onSignIn,
  onSignOut,
  isAuthenticated,
  authDisabled,
  canInspectTraceAudit,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const tokenProfile = getTokenProfile() || {};
  const fallbackName = tokenProfile.given_name || tokenProfile.name || tokenProfile.email || '';
  const greeting = userProfile?.first_name
    ? `Welcome ${userProfile.first_name}`
    : (isAuthenticated ? `Welcome ${fallbackName || 'User'}` : 'Welcome Guest');

  const handleNewChat = () => {
    onNewSession();
    if (location.pathname !== '/' && location.pathname !== '/chat') {
      navigate('/chat');
    }
  };

  return (
    <nav className="top-nav">
      <div className="top-nav__info">
        <span className="nav-greeting">{greeting}</span>
        {sessionId && <span className="nav-meta">Chat ID: {sessionId}</span>}
      </div>
      <div className="nav-actions">
        <Link className="nav-link-btn" to="/ask-proverbs">
          Ask Proverbs
        </Link>
        <Link className="nav-link-btn" to="/authors">
          Authors
        </Link>
        <Link className="nav-link-btn" to="/references">
          References
        </Link>
        <Link className="nav-link-btn" to="/pricing">
          Pricing
        </Link>
        <Link className="nav-link-btn" to="/author-acquisition">
          Acquisition
        </Link>
        {canInspectTraceAudit && (
          <Link className="nav-link-btn" to="/governance/traces">
            Audit
          </Link>
        )}
        <Link className="nav-link-btn" to="/history">
          History
        </Link>
        {isAuthenticated && (
          <Link className="nav-link-btn" to="/user/profile/home">
            Profile
          </Link>
        )}
        <Button size="sm" variant="primary" onClick={handleNewChat}>New Chat</Button>
        {!authDisabled && (
          <>
            <button
              type="button"
              className="nav-link-btn"
              onClick={isAuthenticated ? onSignOut : onSignIn}
            >
              {isAuthenticated ? 'Sign Out' : 'Sign In'}
            </button>
            {!isAuthenticated && (
              <button type="button" className="nav-link-btn" onClick={onSignIn}>
                Quick Sign In
              </button>
            )}
          </>
        )}
      </div>
    </nav>
  );
}

function AnalyticsRouteTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView({
      pagePath: `${location.pathname}${location.search}${location.hash}`,
      pageTitle: document.title,
    });
  }, [location.pathname, location.search, location.hash]);

  return null;
}

function AuthMethodsPage({ onStartEmail, onStartGoogle }) {
  return (
    <Card className="form-card">
      <Card.Body>
        <Card.Title>Choose a Sign In Method</Card.Title>
        <p>Select how you would like to authenticate with Pericope AI.</p>
        <div className="auth-method">
          <div className="auth-method__info">
            <h5>Authenticate with Email</h5>
            <p className="auth-method__description">
              Use your Pericope AI credentials via our secure Keycloak login portal.
            </p>
          </div>
          <Button variant="primary" onClick={onStartEmail}>
            Continue
          </Button>
        </div>
        <div className="auth-method">
          <div className="auth-method__info">
            <h5>Authenticate with Google</h5>
            <p className="auth-method__description">
              Sign in quickly using Keycloak’s Google federation.
            </p>
          </div>
          <Button variant="outline-light" onClick={onStartGoogle}>
            Continue with Google
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}

function HistoryPage({ onLoadSession, getPersonaName }) {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadingSessionId, setLoadingSessionId] = useState('');
  const isAuthenticated = keycloak.authenticated;

  useEffect(() => {
    let cancelled = false;
    const loadHistory = async () => {
      setLoading(true);
      setError('');
      if (!isAuthenticated) {
        if (!cancelled) {
          setSessions(loadPersistedGuestHistorySessions());
          setLoading(false);
        }
        return;
      }
      try {
        const res = await apiV1.get('/history');
        if (!cancelled) {
          setSessions(res.data?.sessions || []);
        }
      } catch (err) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          setError(detail || err.message || 'Failed to load history');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const handleSelectSession = async (sessionId) => {
    setLoadingSessionId(sessionId);
    const ok = await onLoadSession(sessionId);
    setLoadingSessionId('');
    if (ok) {
      navigate('/');
    }
  };

  return (
    <Card className="form-card">
      <Card.Body>
        <Card.Title>Chat History</Card.Title>
        <Card.Text className="history-note">
          {isAuthenticated
            ? 'Resume a saved account session.'
            : 'Guest sessions are stored only on this device until you sign in.'}
        </Card.Text>
        {loading && <p>Loading your sessions…</p>}
        {!loading && error && <p className="history-note">Error: {error}</p>}
        {!loading && !error && sessions.length === 0 && (
          <p className="history-note">
            {isAuthenticated
              ? 'No chat history yet.'
              : 'No chat history saved on this device yet.'}
          </p>
        )}
        {!loading && !error && sessions.length > 0 && (
          <ul className="history-list">
            {sessions.map((session, index) => (
              <li key={session.session_id || `${session.start_time || 'session'}-${index}`}>
                <div className="history-session-card">
                  <div className="history-session-content">
                    <div className="history-session-id">
                      {session.preview
                        ? session.preview.slice(0, 80)
                        : session.session_id}
                    </div>
                    <div className="history-meta">
                      Persona: {getPersonaName(session.persona || '')} · {session.mode || 'conversation'}
                    </div>
                    <div className="history-meta">
                      Last updated: {formatSessionTimestamp(session.last_active || session.start_time)}
                    </div>
                    <div className="history-meta">
                      Started: {formatSessionTimestamp(session.start_time)}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="history-session-action"
                    onClick={() => handleSelectSession(session.session_id)}
                    disabled={!!loadingSessionId}
                  >
                    {loadingSessionId === session.session_id ? 'Opening…' : 'Resume session'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card.Body>
    </Card>
  );
}

function PricingPage({ isAuthenticated, onSignIn }) {
  const navigate = useNavigate();
  const [billingStatus, setBillingStatus] = useState(null);
  const [dummyAccount, setDummyAccount] = useState(null);
  const [loading, setLoading] = useState(Boolean(isAuthenticated));
  const [error, setError] = useState('');
  const [busyTier, setBusyTier] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadBillingContext = async () => {
      if (!isAuthenticated) {
        setBillingStatus(null);
        setDummyAccount(null);
        setLoading(false);
        setError('');
        return;
      }
      setLoading(true);
      setError('');
      const [statusResult, dummyResult] = await Promise.allSettled([
        apiV1.get('/billing/status'),
        apiV1.get('/billing/test/dummy-account'),
      ]);
      if (cancelled) {
        return;
      }
      if (statusResult.status === 'fulfilled') {
        setBillingStatus(statusResult.value?.data || null);
      } else {
        const detail = cleanText(statusResult.reason?.response?.data?.detail) || statusResult.reason?.message || 'Failed to load billing status';
        setError(detail);
      }
      if (dummyResult.status === 'fulfilled') {
        setDummyAccount(dummyResult.value?.data || null);
      } else {
        setDummyAccount(null);
      }
      setLoading(false);
    };
    void loadBillingContext();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const handleCheckout = async (tier) => {
    if (!isAuthenticated) {
      onSignIn();
      return;
    }
    setBusyTier(tier);
    setError('');
    try {
      const successUrl = `${window.location.origin}/billing/success`;
      const cancelUrl = `${window.location.origin}/pricing`;
      const res = await apiV1.post('/billing/checkout/session', {
        tier,
        success_url: successUrl,
        cancel_url: cancelUrl,
      });
      trackEvent('billing_checkout_created', {
        tier,
        provider: res.data?.provider || 'unknown',
      });
      if (cleanText(res.data?.provider).toLowerCase() === 'fixture') {
        const params = new URLSearchParams({
          session_id: cleanText(res.data?.session_id),
          provider: cleanText(res.data?.provider) || 'fixture',
          tier: cleanText(res.data?.subscription_tier) || tier,
        });
        navigate(`/billing/success?${params.toString()}`);
        return;
      }
      if (res.data?.checkout_url) {
        navigateToExternalUrl(res.data.checkout_url);
      }
    } catch (err) {
      const detail = cleanText(err?.response?.data?.detail) || err?.message || 'Failed to create billing checkout session';
      setError(detail);
      trackEvent('billing_checkout_failed', {
        tier,
        status_code: err?.response?.status || 0,
      });
    } finally {
      setBusyTier('');
    }
  };

  const handleCreatePortalSession = async () => {
    if (!isAuthenticated) {
      onSignIn();
      return;
    }
    setPortalLoading(true);
    setError('');
    try {
      const res = await apiV1.post('/billing/customer-portal/session', {
        return_url: `${window.location.origin}/user/profile/home`,
      });
      const portalUrl = cleanText(res.data?.portal_url);
      if (!portalUrl) {
        throw new Error('Customer Portal session did not return a URL.');
      }
      navigateToExternalUrl(portalUrl);
    } catch (err) {
      const detail = cleanText(err?.response?.data?.detail) || err?.message || 'Failed to create customer portal session';
      setError(detail);
    } finally {
      setPortalLoading(false);
    }
  };

  const billingAccessCopy = BILLING_ACCESS_STATE_LABELS[cleanText(billingStatus?.access_state).toLowerCase()]
    || 'No billing activity has been recorded for this account yet.';
  const currentTier = cleanText(billingStatus?.subscription_tier_label)
    || (cleanText(billingStatus?.subscription_tier) ? formatSlugLabel(billingStatus.subscription_tier) : 'Free');

  return (
    <section className="pricing-page">
      <div className="pricing-page__hero">
        <p className="pricing-page__eyebrow">Commercial lane</p>
        <h1>Pricing & Access</h1>
        <p className="pricing-page__copy">
          Paid public access is separate from invite-only sacred access. Checkout covers Reader-class tiers only;
          restricted authors still require explicit runtime entitlements.
        </p>
      </div>

      {isAuthenticated ? (
        <div className="pricing-page__status-card">
          <div>
            <p className="pricing-page__eyebrow">Current account</p>
            <h2>{currentTier}</h2>
            <p>{billingAccessCopy}</p>
          </div>
          <div className="pricing-page__status-facts">
            <span>Status: {cleanText(billingStatus?.subscription_status) || 'inactive'}</span>
            <span>Provider: {cleanText(billingStatus?.provider) || 'fixture'}</span>
            <span>Roles expected after payment: {(billingStatus?.expected_runtime_roles || []).join(', ') || 'none'}</span>
          </div>
          {billingStatus?.has_active_subscription && (
            <button
              type="button"
              className="author-browse-card__button author-browse-card__button--secondary"
              onClick={handleCreatePortalSession}
              disabled={portalLoading}
            >
              {portalLoading ? 'Opening...' : 'Manage billing'}
            </button>
          )}
          {loading && <p className="history-note">Loading billing status…</p>}
          {!loading && error && <p className="history-note">{error}</p>}
        </div>
      ) : (
        <div className="pricing-page__status-card">
          <div>
            <p className="pricing-page__eyebrow">Sign in first</p>
            <h2>Start from an account session</h2>
            <p>Checkout is attached to the authenticated Pericope account so the runtime role sync has a target user.</p>
          </div>
          <Button variant="primary" onClick={onSignIn}>Sign In To Subscribe</Button>
        </div>
      )}

      {isAuthenticated && cleanText(billingStatus?.provider).toLowerCase() === 'fixture' && dummyAccount && (
        <div className="pricing-page__dummy-card">
          <p className="pricing-page__eyebrow">Dummy account path</p>
          <strong>{dummyAccount.first_name} {dummyAccount.last_name}</strong>
          <span>{dummyAccount.email}</span>
          <span>Starting roles: {(dummyAccount.starting_roles || []).join(', ')}</span>
        </div>
      )}

      <div className="pricing-page__grid">
        {BILLING_TIERS.map((tier) => (
          <article key={tier.slug} className="pricing-tier-card">
            <p className="pricing-page__eyebrow">{tier.label}</p>
            <h3>{tier.price}</h3>
            <p>{tier.summary}</p>
            <ul className="pricing-tier-card__features">
              {tier.features.map((feature) => (
                <li key={`${tier.slug}:${feature}`}>{feature}</li>
              ))}
            </ul>
            <button
              type="button"
              className="author-browse-card__button"
              onClick={() => handleCheckout(tier.slug)}
              disabled={busyTier === tier.slug}
            >
              {busyTier === tier.slug ? 'Preparing checkout…' : `Start ${tier.label}`}
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}

function BillingSuccessPage({ isAuthenticated, onSignIn }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search || '');
  const sessionId = cleanText(params.get('session_id'));
  const provider = cleanText(params.get('provider')).toLowerCase();
  const [billingStatus, setBillingStatus] = useState(null);
  const [loading, setLoading] = useState(Boolean(isAuthenticated));
  const [error, setError] = useState('');
  const [completing, setCompleting] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalUrl, setPortalUrl] = useState('');

  const loadBillingStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setBillingStatus(null);
      setLoading(false);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await apiV1.get('/billing/status');
      setBillingStatus(res.data || null);
    } catch (err) {
      const detail = cleanText(err?.response?.data?.detail) || err?.message || 'Failed to load billing status';
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void loadBillingStatus();
  }, [loadBillingStatus]);

  const handleCompleteDummyCheckout = async () => {
    setCompleting(true);
    setError('');
    try {
      const res = await apiV1.post('/billing/test/complete-checkout', {
        session_id: sessionId || null,
      });
      trackEvent('billing_dummy_checkout_completed', {
        provider: res.data?.provider || 'fixture',
        tier: res.data?.subscription_tier || 'reader',
      });
      await loadBillingStatus();
    } catch (err) {
      const detail = cleanText(err?.response?.data?.detail) || err?.message || 'Failed to complete dummy payment';
      setError(detail);
    } finally {
      setCompleting(false);
    }
  };

  const handleCreatePortalSession = async () => {
    setPortalLoading(true);
    setError('');
    try {
      const res = await apiV1.post('/billing/customer-portal/session', {
        return_url: `${window.location.origin}/user/profile/home`,
      });
      setPortalUrl(cleanText(res.data?.portal_url));
    } catch (err) {
      const detail = cleanText(err?.response?.data?.detail) || err?.message || 'Failed to create customer portal session';
      setError(detail);
    } finally {
      setPortalLoading(false);
    }
  };

  const accessCopy = BILLING_ACCESS_STATE_LABELS[cleanText(billingStatus?.access_state).toLowerCase()]
    || 'Checkout is ready to be completed.';
  const showDummyAction = isAuthenticated
    && provider === 'fixture'
    && sessionId
    && cleanText(billingStatus?.subscription_status).toLowerCase() !== 'active';

  return (
    <Card className="form-card billing-success-card">
      <Card.Body>
        <Card.Title>Billing Success</Card.Title>
        <Card.Text>
          Checkout has returned to Pericope. This screen verifies the subscription state and, in fixture mode,
          lets the dummy account complete the payment capture step.
        </Card.Text>
        {!isAuthenticated && (
          <>
            <p className="history-note">Sign in to inspect or complete the returned billing session.</p>
            <Button variant="primary" onClick={onSignIn}>Sign In</Button>
          </>
        )}
        {loading && <p className="history-note">Loading billing status…</p>}
        {!loading && error && <p className="history-note">{error}</p>}
        {!loading && billingStatus && (
          <div className="billing-status-card">
            <p><strong>Provider:</strong> {cleanText(billingStatus.provider) || 'fixture'}</p>
            <p><strong>Subscription:</strong> {cleanText(billingStatus.subscription_tier_label) || 'Free'}</p>
            <p><strong>Status:</strong> {cleanText(billingStatus.subscription_status) || 'inactive'}</p>
            <p><strong>Access:</strong> {accessCopy}</p>
            <p><strong>Expected roles:</strong> {(billingStatus.expected_runtime_roles || []).join(', ') || 'none'}</p>
          </div>
        )}
        {showDummyAction && (
          <Button variant="primary" onClick={handleCompleteDummyCheckout} disabled={completing}>
            {completing ? 'Completing dummy payment…' : 'Complete Dummy Payment'}
          </Button>
        )}
        {!showDummyAction && billingStatus?.has_active_subscription && (
          <div className="billing-success-card__actions">
            <Button variant="outline-light" onClick={handleCreatePortalSession} disabled={portalLoading}>
              {portalLoading ? 'Creating portal link…' : 'Create Customer Portal Link'}
            </Button>
            {portalUrl && (
              <a className="author-browse-card__button author-browse-card__button--secondary" href={portalUrl} target="_blank" rel="noreferrer">
                Open Portal
              </a>
            )}
            <Link className="author-browse-card__button author-browse-card__button--secondary" to="/user/profile/home">
              Go To Profile
            </Link>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

function ProfileHome({ userProfile, authorPreferences, authors }) {
  const [billingStatus, setBillingStatus] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingError, setBillingError] = useState('');
  const [portalUrl, setPortalUrl] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadBillingStatus = async () => {
      setBillingLoading(true);
      setBillingError('');
      try {
        const res = await apiV1.get('/billing/status');
        if (!cancelled) {
          setBillingStatus(res.data || null);
        }
      } catch (err) {
        if (!cancelled) {
          const detail = cleanText(err?.response?.data?.detail) || err?.message || 'Failed to load billing status';
          setBillingError(detail);
        }
      } finally {
        if (!cancelled) {
          setBillingLoading(false);
        }
      }
    };
    void loadBillingStatus();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCreatePortalSession = async () => {
    setPortalLoading(true);
    setBillingError('');
    try {
      const res = await apiV1.post('/billing/customer-portal/session', {
        return_url: `${window.location.origin}/user/profile/home`,
      });
      setPortalUrl(cleanText(res.data?.portal_url));
    } catch (err) {
      const detail = cleanText(err?.response?.data?.detail) || err?.message || 'Failed to create customer portal session';
      setBillingError(detail);
    } finally {
      setPortalLoading(false);
    }
  };

  const billingAccessCopy = BILLING_ACCESS_STATE_LABELS[cleanText(billingStatus?.access_state).toLowerCase()]
    || 'Billing is not active for this account.';

  if (!userProfile) {
    return (
      <Card className="form-card">
        <Card.Body>
          <Card.Title>Your Profile</Card.Title>
          <Card.Text>No saved profile data yet.</Card.Text>
        </Card.Body>
      </Card>
    );
  }

  const favoriteAuthors = (authorPreferences?.favorite_authors || [])
    .map((slug) => authors.find((author) => cleanText(author?.slug).toLowerCase() === cleanText(slug).toLowerCase()) || {
      slug,
      name: formatSlugLabel(slug),
    });
  const defaultAuthorSlug = cleanText(authorPreferences?.default_author_slug).toLowerCase();
  const unavailableDefault = cleanText(authorPreferences?.default_author_unavailable_slug);
  const updatedLabel = formatPreferenceTimestamp(authorPreferences?.updated_at);

  return (
    <Card className="form-card">
      <Card.Body>
        <Card.Title>Your Profile</Card.Title>
        <p><strong>First name:</strong> {userProfile.first_name}</p>
        <p><strong>Last name:</strong> {userProfile.last_name}</p>
        <p><strong>Email:</strong> {userProfile.email}</p>
        <hr />
        <p><strong>Default author:</strong> {defaultAuthorSlug ? formatSlugLabel(defaultAuthorSlug) : 'Not set'}</p>
        <p>
          <strong>Favorites:</strong>{' '}
          {favoriteAuthors.length > 0
            ? favoriteAuthors.map((author) => author.name || formatSlugLabel(author.slug)).join(' · ')
            : 'No favorite authors yet'}
        </p>
        {unavailableDefault && (
          <p className="history-note">
            Your saved default author <strong>{formatSlugLabel(unavailableDefault)}</strong> is no longer available. Choose a new default from the author catalog or chat panel.
          </p>
        )}
        {updatedLabel && (
          <p className="history-note">Author preferences updated: {updatedLabel}</p>
        )}
        <hr />
        <p><strong>Billing tier:</strong> {cleanText(billingStatus?.subscription_tier_label) || 'Free'}</p>
        <p><strong>Billing status:</strong> {cleanText(billingStatus?.subscription_status) || 'inactive'}</p>
        <p><strong>Access state:</strong> {billingAccessCopy}</p>
        {billingLoading && <p className="history-note">Loading billing status…</p>}
        {!billingLoading && billingError && <p className="history-note">{billingError}</p>}
        {Array.isArray(billingStatus?.expected_runtime_roles) && billingStatus.expected_runtime_roles.length > 0 && (
          <p><strong>Expected paid roles:</strong> {billingStatus.expected_runtime_roles.join(', ')}</p>
        )}
        <div className="profile-billing-actions">
          <Link className="author-browse-card__button author-browse-card__button--secondary" to="/pricing">
            Review Pricing
          </Link>
          {billingStatus?.has_active_subscription && (
            <button
              type="button"
              className="author-browse-card__button"
              onClick={handleCreatePortalSession}
              disabled={portalLoading}
            >
              {portalLoading ? 'Creating portal link…' : 'Create Customer Portal Link'}
            </button>
          )}
        </div>
        {portalUrl && (
          <p className="history-note">
            Portal link ready:
            {' '}
            <a href={portalUrl} target="_blank" rel="noreferrer">{portalUrl}</a>
          </p>
        )}
      </Card.Body>
    </Card>
  );
}

function AuthorsBrowsePage({
  items,
  loading,
  error,
  isAuthenticated,
  favoriteAuthorSlugs,
  defaultAuthorSlug,
  preferencesSaving,
  onToggleFavorite,
  onSetDefaultAuthor,
}) {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeFamily, setActiveFamily] = useState('All');
  const normalizedQuery = cleanText(query).toLowerCase();
  const favoriteAuthorSet = useMemo(
    () => new Set((favoriteAuthorSlugs || []).map((slug) => cleanText(slug).toLowerCase()).filter(Boolean)),
    [favoriteAuthorSlugs],
  );

  const categories = useMemo(() => [
    'All',
    ...uniqueTextList(items.map((item) => item?.taxonomy?.category).sort((left, right) => left.localeCompare(right))),
  ], [items]);

  const families = useMemo(() => {
    const scoped = items.filter((item) => activeCategory === 'All' || cleanText(item?.taxonomy?.category) === activeCategory);
    return [
      'All',
      ...uniqueTextList(scoped.map((item) => item?.taxonomy?.family).sort((left, right) => left.localeCompare(right))),
    ];
  }, [items, activeCategory]);

  useEffect(() => {
    if (activeFamily !== 'All' && !families.includes(activeFamily)) {
      setActiveFamily('All');
    }
  }, [activeFamily, families]);

  const filteredItems = useMemo(() => items.filter((item) => {
    const category = cleanText(item?.taxonomy?.category);
    const family = cleanText(item?.taxonomy?.family);
    if (activeCategory !== 'All' && category !== activeCategory) {
      return false;
    }
    if (activeFamily !== 'All' && family !== activeFamily) {
      return false;
    }
    if (!normalizedQuery) {
      return true;
    }

    const haystack = [
      item?.name,
      item?.catalog_name,
      item?.slug,
      item?.description,
      category,
      family,
      item?.taxonomy?.group,
      item?.taxonomy?.tradition,
      ...(Array.isArray(item?.works_preview)
        ? item.works_preview.map((work) => work?.book || work?.work || work?.source)
        : []),
      ...(Array.isArray(item?.crossrefs?.books_preview)
        ? item.crossrefs.books_preview.map((work) => work?.name || work?.book_id)
        : []),
    ].map((value) => cleanText(value).toLowerCase()).filter(Boolean).join(' ');

    return haystack.includes(normalizedQuery);
  }), [items, activeCategory, activeFamily, normalizedQuery]);

  const groupedItems = useMemo(() => {
    const groups = new Map();
    filteredItems.forEach((item) => {
      const category = cleanText(item?.taxonomy?.category) || 'Other';
      if (!groups.has(category)) {
        groups.set(category, []);
      }
      groups.get(category).push(item);
    });
    return Array.from(groups.entries()).map(([category, authors]) => ({
      category,
      authors: [...authors].sort((left, right) => (
        Number(favoriteAuthorSet.has(cleanText(right?.slug).toLowerCase()))
        - Number(favoriteAuthorSet.has(cleanText(left?.slug).toLowerCase()))
        || cleanText(left?.name).localeCompare(cleanText(right?.name))
      )),
    }));
  }, [favoriteAuthorSet, filteredItems]);

  return (
    <section className="authors-page">
      <header className="authors-hero">
        <div>
          <p className="authors-hero__eyebrow">Author Catalog</p>
          <h1>Browse the voices before you enter chat.</h1>
          <p className="authors-hero__copy">
            Start with taxonomy, works, and scriptural reference patterns. Use search only to narrow once you know the field.
          </p>
        </div>
        <div className="authors-hero__meta">
          <span>{items.length} authors indexed</span>
          <span>{filteredItems.length} visible in this view</span>
        </div>
      </header>

      <section className="authors-filters" aria-label="Author filters">
        <div className="authors-filter-block">
          <label htmlFor="authorsBrowseSearch">Search</label>
          <input
            id="authorsBrowseSearch"
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search authors, works, or reference books"
            autoComplete="off"
          />
        </div>
        <div className="authors-filter-block">
          <span>Category</span>
          <div className="authors-filter-pills">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                className={`authors-filter-pill${category === activeCategory ? ' authors-filter-pill--active' : ''}`}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
        <div className="authors-filter-block">
          <span>Family</span>
          <div className="authors-filter-pills">
            {families.map((family) => (
              <button
                key={family}
                type="button"
                className={`authors-filter-pill${family === activeFamily ? ' authors-filter-pill--active' : ''}`}
                onClick={() => setActiveFamily(family)}
              >
                {family}
              </button>
            ))}
          </div>
        </div>
      </section>

      {loading && <p className="authors-page__state">Loading author catalog…</p>}
      {!loading && error && <p className="authors-page__state">Unable to load author catalog: {error}</p>}
      {!loading && !error && groupedItems.length === 0 && (
        <p className="authors-page__state">No authors match the current filters.</p>
      )}

      {!loading && !error && groupedItems.map((group) => (
        <section key={group.category} className="authors-group" aria-label={group.category}>
          <div className="authors-group__header">
            <div>
              <p className="authors-group__eyebrow">Category</p>
              <h2>{group.category}</h2>
            </div>
            <span>{group.authors.length} author{group.authors.length === 1 ? '' : 's'}</span>
          </div>
          <div className="authors-grid">
            {group.authors.map((author) => {
              const taxonomyTrail = getTaxonomyTrail(author.taxonomy);
              const summary = getAuthorSummary(author);
              const history = getAuthorHistory(author);
              const crossrefBooks = Array.isArray(author?.crossrefs?.books_preview)
                ? author.crossrefs.books_preview
                : [];
              const normalizedSlug = cleanText(author?.slug).toLowerCase();
              const isFavorite = favoriteAuthorSet.has(normalizedSlug);
              const isDefault = normalizedSlug && normalizedSlug === cleanText(defaultAuthorSlug).toLowerCase();
              return (
                <article key={author.slug} className="author-browse-card">
                  <div className="author-browse-card__header">
                    <div className="author-browse-card__identity">
                      <AuthorPortrait author={author} className="author-browse-card__portrait" />
                      <div>
                        <p className="author-browse-card__slug">{author.slug}</p>
                        <h3>{author.name || formatSlugLabel(author.slug)}</h3>
                      </div>
                    </div>
                    <div className="author-browse-card__chips">
                      {taxonomyTrail.slice(0, 3).map((label) => (
                        <span key={label} className="author-taxonomy-chip">{label}</span>
                      ))}
                    </div>
                  </div>
                  <p className="author-browse-card__description">
                    {summary || 'No description available yet.'}
                  </p>
                  <dl className="author-browse-card__facts">
                    <div>
                      <dt>Works</dt>
                      <dd>{author.works_count || 0}</dd>
                    </div>
                    <div>
                      <dt>Crossrefs</dt>
                      <dd>{author?.crossrefs?.counts?.books || 0}</dd>
                    </div>
                    <div>
                      <dt>Family</dt>
                      <dd>{cleanText(author?.taxonomy?.family) || 'General'}</dd>
                    </div>
                  </dl>
                  <div className="author-browse-card__section">
                    <p className="author-browse-card__label">Works preview</p>
                    <p>{getWorksPreviewLabel(author.works_preview)}</p>
                  </div>
                  {history && (
                    <div className="author-browse-card__section">
                      <p className="author-browse-card__label">Historical context</p>
                      <p className="author-browse-card__history">{history}</p>
                    </div>
                  )}
                  <div className="author-browse-card__section">
                    <p className="author-browse-card__label">Cross citations</p>
                    <p>{getCrossrefSummaryLabel(author.crossrefs)}</p>
                    {crossrefBooks.length > 0 && (
                      <p className="author-browse-card__subtle">
                        {crossrefBooks
                          .map((item) => cleanText(item?.name || item?.book_id))
                          .filter(Boolean)
                          .slice(0, 4)
                          .join(' • ')}
                      </p>
                    )}
                  </div>
                  <div className="author-browse-card__actions">
                    {isAuthenticated && (
                      <>
                        <button
                          type="button"
                          className={`author-browse-card__button author-browse-card__button--secondary${isFavorite ? ' author-browse-card__button--active' : ''}`}
                          onClick={() => onToggleFavorite(normalizedSlug)}
                          disabled={preferencesSaving}
                        >
                          {isFavorite ? 'Favorited' : 'Favorite'}
                        </button>
                        <button
                          type="button"
                          className={`author-browse-card__button author-browse-card__button--secondary${isDefault ? ' author-browse-card__button--active' : ''}`}
                          onClick={() => onSetDefaultAuthor(normalizedSlug)}
                          disabled={preferencesSaving}
                        >
                          {isDefault ? 'Default author' : 'Make default'}
                        </button>
                      </>
                    )}
                    <Link className="author-browse-card__button author-browse-card__button--secondary" to={`/authors/${author.slug}`}>
                      View author
                    </Link>
                    <Link className="author-browse-card__button" to={`/author/${author.slug}`}>
                      Chat with this author
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </section>
  );
}

function ReferenceCatalogPage({
  items,
  loading,
  error,
  authors,
  browseItems,
}) {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeTestament, setActiveTestament] = useState('All');
  const [activeSection, setActiveSection] = useState('All');
  const [selectedBookDetail, setSelectedBookDetail] = useState(null);
  const [selectedBookLoading, setSelectedBookLoading] = useState(false);
  const [selectedBookError, setSelectedBookError] = useState('');
  const normalizedBookId = cleanText(bookId).toUpperCase();
  const normalizedQuery = cleanText(query).toLowerCase();

  const testamentOptions = useMemo(() => [
    'All',
    ...uniqueTextList(
      items
        .map((item) => cleanText(item?.testament).toUpperCase())
        .filter(Boolean)
        .sort((left, right) => left.localeCompare(right)),
    ),
  ], [items]);
  const sectionOptions = useMemo(() => {
    const scoped = items.filter((item) => activeTestament === 'All' || cleanText(item?.testament).toUpperCase() === activeTestament);
    return [
      'All',
      ...uniqueTextList(
        scoped
          .map((item) => cleanText(item?.section))
          .filter(Boolean)
          .sort((left, right) => left.localeCompare(right)),
      ),
    ];
  }, [items, activeTestament]);

  useEffect(() => {
    if (activeSection !== 'All' && !sectionOptions.includes(activeSection)) {
      setActiveSection('All');
    }
  }, [activeSection, sectionOptions]);

  const filteredItems = useMemo(() => items.filter((item) => {
    const testament = cleanText(item?.testament).toUpperCase();
    const section = cleanText(item?.section);
    if (activeTestament !== 'All' && testament !== activeTestament) {
      return false;
    }
    if (activeSection !== 'All' && section !== activeSection) {
      return false;
    }
    if (!normalizedQuery) {
      return true;
    }

    const haystack = [
      item?.book_id,
      item?.name,
      item?.author_name,
      item?.author_slug,
      testament,
      section,
    ].map((value) => cleanText(value).toLowerCase()).filter(Boolean).join(' ');
    return haystack.includes(normalizedQuery);
  }), [items, activeSection, activeTestament, normalizedQuery]);

  const selectedCatalogItem = useMemo(
    () => items.find((item) => normalizeBookKey(item?.book_id) === normalizeBookKey(normalizedBookId)) || null,
    [items, normalizedBookId],
  );
  const publicAuthorSlugs = useMemo(
    () => new Set(
      [...(Array.isArray(authors) ? authors : []), ...(Array.isArray(browseItems) ? browseItems : [])]
        .map((item) => cleanText(item?.slug).toLowerCase())
        .filter(Boolean),
    ),
    [authors, browseItems],
  );
  const totalTestamentCount = useMemo(
    () => new Set(items.map((item) => cleanText(item?.testament).toUpperCase()).filter(Boolean)).size,
    [items],
  );
  const totalSectionCount = useMemo(
    () => new Set(items.map((item) => cleanText(item?.section)).filter(Boolean)).size,
    [items],
  );

  useEffect(() => {
    let cancelled = false;

    const loadCrossrefBook = async () => {
      if (!normalizedBookId) {
        setSelectedBookDetail(null);
        setSelectedBookError('');
        setSelectedBookLoading(false);
        return;
      }

      setSelectedBookLoading(true);
      setSelectedBookError('');
      try {
        const res = await apiV1.get(`/crossrefs/books/${normalizedBookId}`);
        if (!cancelled) {
          setSelectedBookDetail(res.data || null);
        }
      } catch (err) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          setSelectedBookDetail(null);
          setSelectedBookError(detail || err.message || 'Unknown error');
        }
      } finally {
        if (!cancelled) {
          setSelectedBookLoading(false);
        }
      }
    };

    loadCrossrefBook();
    return () => {
      cancelled = true;
    };
  }, [normalizedBookId]);

  const selectedBookMeta = selectedBookDetail?.book || selectedCatalogItem || null;
  const selectedAuthorMeta = selectedBookDetail?.author || null;
  const referencingAuthors = Array.isArray(selectedBookDetail?.referencing_authors)
    ? selectedBookDetail.referencing_authors
    : [];
  const canonicalAuthor = referencingAuthors.find(
    (item) => cleanText(item?.relationship).toLowerCase() === 'canonical_author',
  ) || null;
  const relatedAuthors = referencingAuthors.filter(
    (item) => cleanText(item?.relationship).toLowerCase() !== 'canonical_author',
  );
  const relatedBooks = Array.isArray(selectedBookDetail?.related_books) ? selectedBookDetail.related_books : [];
  const canResetFilters = Boolean(normalizedQuery || activeTestament !== 'All' || activeSection !== 'All');

  const openBook = (nextBookId) => {
    navigate(buildReferenceCatalogHref(nextBookId));
  };

  return (
    <section className="reference-catalog-page">
      <header className="reference-catalog-hero">
        <div>
          <p className="authors-hero__eyebrow">Cross Reference Catalog</p>
          <h1>Scripture reference catalog</h1>
          <p className="authors-hero__copy">
            Browse the mapped books directly, then open a reverse lookup to see which authors and works in the corpus lean on that book.
          </p>
        </div>
        <div className="reference-catalog-hero__actions">
          <Link className="author-browse-card__button author-browse-card__button--secondary" to="/authors">
            Browse authors
          </Link>
          {selectedBookMeta?.book_id && (
            <Link className="author-browse-card__button" to={buildReferenceCatalogHref(selectedBookMeta.book_id)}>
              {selectedBookMeta.book_id}
            </Link>
          )}
        </div>
      </header>

      <section className="reference-catalog-summary" aria-label="Reference catalog summary">
        <article className="reference-catalog-summary__card">
          <span>Books mapped</span>
          <strong>{items.length}</strong>
        </article>
        <article className="reference-catalog-summary__card">
          <span>Visible now</span>
          <strong>{filteredItems.length}</strong>
        </article>
        <article className="reference-catalog-summary__card">
          <span>Testament groups</span>
          <strong>{totalTestamentCount}</strong>
        </article>
        <article className="reference-catalog-summary__card">
          <span>Sections</span>
          <strong>{totalSectionCount}</strong>
        </article>
      </section>

      <section className="reference-catalog-filters" aria-label="Reference catalog filters">
        <div className="authors-filter-block">
          <label htmlFor="referenceCatalogSearch">Search</label>
          <input
            id="referenceCatalogSearch"
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search scripture books, sections, or author names"
            autoComplete="off"
          />
        </div>
        <div className="authors-filter-block">
          <span>Testament</span>
          <div className="authors-filter-pills">
            {testamentOptions.map((testament) => (
              <button
                key={testament}
                type="button"
                className={`authors-filter-pill${testament === activeTestament ? ' authors-filter-pill--active' : ''}`}
                onClick={() => setActiveTestament(testament)}
              >
                {testament}
              </button>
            ))}
          </div>
        </div>
        <div className="authors-filter-block">
          <span>Section</span>
          <div className="authors-filter-pills">
            {sectionOptions.map((section) => (
              <button
                key={section}
                type="button"
                className={`authors-filter-pill${section === activeSection ? ' authors-filter-pill--active' : ''}`}
                onClick={() => setActiveSection(section)}
              >
                {section}
              </button>
            ))}
          </div>
        </div>
        {canResetFilters && (
          <div className="reference-catalog-filters__actions">
            <button
              type="button"
              className="author-browse-card__button author-browse-card__button--secondary"
              onClick={() => {
                setQuery('');
                setActiveTestament('All');
                setActiveSection('All');
              }}
            >
              Clear filters
            </button>
          </div>
        )}
      </section>

      <div className="reference-catalog-layout">
        <section className="author-detail-panel">
          <div className="author-detail-panel__header">
            <div>
              <p className="author-browse-card__label">Browse</p>
              <h2>Mapped scripture books</h2>
            </div>
            <span className="author-detail-panel__status">{filteredItems.length} visible</span>
          </div>
          {loading && <p className="authors-page__state">Loading scripture catalog…</p>}
          {!loading && error && <p className="authors-page__state">Unable to load scripture catalog: {error}</p>}
          {!loading && !error && filteredItems.length === 0 && (
            <p className="authors-page__state">No scripture books match the current filters.</p>
          )}
          {!loading && !error && filteredItems.length > 0 && (
            <ul className="reference-catalog-list">
              {filteredItems.map((item) => {
                const bookKey = cleanText(item?.book_id).toUpperCase();
                const isActive = normalizeBookKey(bookKey) === normalizeBookKey(normalizedBookId);
                return (
                  <li key={bookKey || cleanText(item?.name)}>
                    <button
                      type="button"
                      className={`reference-catalog-row${isActive ? ' reference-catalog-row--active' : ''}`}
                      onClick={() => openBook(bookKey)}
                    >
                      <span className="reference-catalog-row__eyebrow">
                        {uniqueTextList([bookKey, cleanText(item?.testament).toUpperCase()], 2).join(' · ') || 'Scripture'}
                      </span>
                      <strong>{cleanText(item?.name) || bookKey}</strong>
                      <span className="reference-catalog-row__meta">
                        {uniqueTextList([item?.section, item?.author_name], 2).join(' · ') || 'Canonical author entry'}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="author-detail-panel">
          <div className="author-detail-panel__header">
            <div>
              <p className="author-browse-card__label">Reverse lookup</p>
              <h2>{cleanText(selectedBookMeta?.name) || (normalizedBookId ? normalizedBookId : 'Select a book')}</h2>
            </div>
            {selectedBookMeta?.book_id && (
              <span className="author-detail-panel__status">
                {Number(selectedBookDetail?.counts?.referencing_authors || 0)} mapped author trail{Number(selectedBookDetail?.counts?.referencing_authors || 0) === 1 ? '' : 's'}
              </span>
            )}
          </div>
          {!normalizedBookId && (
            <p className="authors-page__state">
              Select a scripture book to inspect the canonical author, corpus references, and nearby related books.
            </p>
          )}
          {normalizedBookId && selectedBookLoading && (
            <p className="authors-page__state">Loading reverse lookup for {normalizedBookId}…</p>
          )}
          {normalizedBookId && !selectedBookLoading && selectedBookError && (
            <p className="authors-page__state">Unable to load reverse lookup: {selectedBookError}</p>
          )}
          {normalizedBookId && !selectedBookLoading && !selectedBookError && selectedBookMeta && (
            <>
              <p className="author-detail-panel__summary">
                {uniqueTextList([
                  selectedBookMeta.book_id,
                  selectedBookMeta.testament,
                  selectedBookMeta.section,
                  selectedAuthorMeta?.name ? `canonical author: ${selectedAuthorMeta.name}` : '',
                ], 4).join(' · ')}
              </p>
              <div className="reference-catalog-detail__chips">
                {uniqueTextList([
                  selectedBookMeta.testament,
                  selectedBookMeta.section,
                  selectedAuthorMeta?.name,
                ]).map((label) => (
                  <span key={label} className="author-taxonomy-chip">{label}</span>
                ))}
              </div>

              {canonicalAuthor && (
                <div className="reference-catalog-detail__section">
                  <p className="author-browse-card__label">Canonical author</p>
                  <div className="reference-catalog-author-card">
                    <div>
                      <strong>{cleanText(canonicalAuthor?.name) || cleanText(selectedAuthorMeta?.name) || 'Unknown author'}</strong>
                      <p className="crossref-evidence-meta">Receives this book as the canonical author.</p>
                    </div>
                    {publicAuthorSlugs.has(cleanText(canonicalAuthor?.slug).toLowerCase()) && (
                      <Link
                        className="author-browse-card__button author-browse-card__button--secondary"
                        to={`/authors/${cleanText(canonicalAuthor?.slug).toLowerCase()}`}
                      >
                        View author
                      </Link>
                    )}
                  </div>
                </div>
              )}

              <div className="reference-catalog-detail__section">
                <p className="author-browse-card__label">Referenced by authors in corpus</p>
                {relatedAuthors.length > 0 ? (
                  <ul className="reference-catalog-linked-authors">
                    {relatedAuthors.map((item, index) => {
                      const slug = cleanText(item?.slug).toLowerCase();
                      const confidenceLabel = formatCrossrefConfidence(item?.confidence);
                      return (
                        <li key={`${slug || cleanText(item?.name) || 'author'}-${index}`}>
                          <div className="reference-catalog-author-card">
                            <div>
                              <strong>{cleanText(item?.name) || formatSlugLabel(slug)}</strong>
                              <p className="crossref-evidence-meta">
                                {uniqueTextList([
                                  getCrossrefRelationshipLabel(item),
                                  cleanText(item?.evidence_work) ? `via ${cleanText(item.evidence_work)}` : '',
                                  confidenceLabel,
                                ], 3).join(' · ') || 'Mapped author reference'}
                              </p>
                            </div>
                            {publicAuthorSlugs.has(slug) && (
                              <Link className="author-browse-card__button author-browse-card__button--secondary" to={`/authors/${slug}`}>
                                View author
                              </Link>
                            )}
                          </div>
                          {cleanText(item?.evidence_snippet) && (
                            <p className="crossref-evidence-note">{cleanText(item.evidence_snippet)}</p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="authors-page__state">No additional author references are mapped for this book yet.</p>
                )}
              </div>

              {relatedBooks.length > 0 && (
                <div className="reference-catalog-detail__section">
                  <p className="author-browse-card__label">Nearby canonical books</p>
                  <div className="reference-catalog-related-books">
                    {relatedBooks.slice(0, 10).map((item) => {
                      const relatedBookId = cleanText(item?.book_id).toUpperCase();
                      const label = cleanText(item?.name) || relatedBookId;
                      return (
                        <button
                          key={relatedBookId || label}
                          type="button"
                          className="authors-filter-pill"
                          onClick={() => openBook(relatedBookId)}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </section>
  );
}

function AuthorAcquisitionTrackerPage() {
  const [payload, setPayload] = useState({ summary: null, groups: [], items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [activeGroup, setActiveGroup] = useState('all');
  const normalizedQuery = cleanText(query).toLowerCase();
  const groups = useMemo(
    () => (Array.isArray(payload?.groups) ? payload.groups : []),
    [payload?.groups],
  );
  const items = useMemo(
    () => (Array.isArray(payload?.items) ? payload.items : []),
    [payload?.items],
  );
  const summary = payload?.summary || {};

  useEffect(() => {
    let cancelled = false;

    const fetchTracker = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await apiV1.get('/author-acquisition');
        if (!cancelled) {
          setPayload({
            summary: res.data?.summary || {},
            groups: Array.isArray(res.data?.groups) ? res.data.groups : [],
            items: Array.isArray(res.data?.items) ? res.data.items : [],
          });
        }
      } catch (err) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          setPayload({ summary: null, groups: [], items: [] });
          setError(detail || err.message || 'Unknown error');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchTracker();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredItems = useMemo(() => items.filter((item) => {
    const statusGroup = cleanText(item?.status_group);
    if (activeGroup !== 'all' && statusGroup !== activeGroup) {
      return false;
    }
    if (!normalizedQuery) {
      return true;
    }

    const haystack = [
      item?.name,
      item?.slug,
      item?.status,
      item?.status_group_label,
      item?.persona_mode,
      item?.catalog_name,
      ...(Array.isArray(item?.notes) ? item.notes : []),
      ...(Array.isArray(item?.key_works) ? item.key_works : []),
      ...(Array.isArray(item?.works) ? item.works : []),
      ...(Array.isArray(item?.corpus_works_preview) ? item.corpus_works_preview : []),
    ].map((value) => cleanText(value).toLowerCase()).filter(Boolean).join(' ');

    return haystack.includes(normalizedQuery);
  }), [activeGroup, items, normalizedQuery]);

  const groupedItems = useMemo(() => {
    const orderLookup = new Map(groups.map((group, index) => [group.key, index]));
    const labelLookup = new Map(groups.map((group) => [group.key, group.label]));
    const grouped = new Map();

    filteredItems.forEach((item) => {
      const key = cleanText(item?.status_group) || 'other';
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          label: cleanText(item?.status_group_label) || labelLookup.get(key) || formatSlugLabel(key),
          items: [],
        });
      }
      grouped.get(key).items.push(item);
    });

    return Array.from(grouped.values()).sort((left, right) => (
      (orderLookup.get(left.key) ?? Number.MAX_SAFE_INTEGER)
      - (orderLookup.get(right.key) ?? Number.MAX_SAFE_INTEGER)
    ));
  }, [filteredItems, groups]);

  const completionPercent = Number.isFinite(Number(summary?.workflow_completion_percent))
    ? Number(summary.workflow_completion_percent)
    : 0;

  return (
    <section className="authors-page acquisition-page">
      <header className="authors-hero acquisition-hero">
        <div>
          <p className="authors-hero__eyebrow">Prod Operations</p>
          <h1>Author acquisition tracker</h1>
          <p className="authors-hero__copy">
            This is the live rollout ledger for author acquisition: what is on prod, what remains seeded legacy, and what is still blocked in the workflow.
          </p>
        </div>
        <div className="authors-hero__meta">
          <span>{items.length} tracked authors</span>
          <span>{filteredItems.length} visible in this view</span>
          <span>{summary.live_on_prod_count || 0} live on prod</span>
        </div>
      </header>

      <section className="acquisition-summary-grid" aria-label="Acquisition summary">
        <article className="acquisition-summary-card">
          <p className="acquisition-summary-card__label">Tracked authors</p>
          <strong>{summary.total_count || 0}</strong>
          <span>Full acquisition ledger.</span>
        </article>
        <article className="acquisition-summary-card">
          <p className="acquisition-summary-card__label">Live on prod</p>
          <strong>{summary.live_on_prod_count || 0}</strong>
          <span>Runtime wired and publicly shipped.</span>
        </article>
        <article className="acquisition-summary-card">
          <p className="acquisition-summary-card__label">Workflow completion</p>
          <strong>{completionPercent.toFixed(1)}%</strong>
          <span>{summary.workflow_complete_count || 0} of {summary.workflow_total_count || 0} workflow acquisitions closed.</span>
        </article>
        <article className="acquisition-summary-card">
          <p className="acquisition-summary-card__label">Repeatable recipes</p>
          <strong>{summary.repeatable_recipe_count || 0}</strong>
          <span>Recoverable from versioned source pulls.</span>
        </article>
      </section>

      <section className="acquisition-progress" aria-label="Workflow completion progress">
        <div className="acquisition-progress__bar" aria-hidden="true">
          <span className="acquisition-progress__fill" style={{ width: `${Math.max(0, Math.min(100, completionPercent))}%` }} />
        </div>
        <p className="acquisition-progress__caption">
          Workflow completion excludes the seeded baseline personas and measures only acquisition work executed through the repeatable pipeline.
        </p>
      </section>

      <section className="authors-filters" aria-label="Author acquisition filters">
        <div className="authors-filter-block">
          <label htmlFor="authorAcquisitionSearch">Search</label>
          <input
            id="authorAcquisitionSearch"
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search authors, works, notes, or status"
            autoComplete="off"
          />
        </div>
        <div className="authors-filter-block">
          <span>Status group</span>
          <div className="authors-filter-pills">
            <button
              type="button"
              className={`authors-filter-pill${activeGroup === 'all' ? ' authors-filter-pill--active' : ''}`}
              onClick={() => setActiveGroup('all')}
            >
              All
            </button>
            {groups.map((group) => (
              <button
                key={group.key}
                type="button"
                className={`authors-filter-pill${activeGroup === group.key ? ' authors-filter-pill--active' : ''}`}
                onClick={() => setActiveGroup(group.key)}
              >
                {group.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {loading && <p className="authors-page__state">Loading author acquisition tracker…</p>}
      {!loading && error && <p className="authors-page__state">Unable to load author acquisition tracker: {error}</p>}
      {!loading && !error && groupedItems.length === 0 && (
        <p className="authors-page__state">No authors match the current tracker filters.</p>
      )}

      {!loading && !error && groupedItems.map((group) => (
        <section key={group.key} className="authors-group acquisition-group" aria-label={group.label}>
          <div className="authors-group__header">
            <div>
              <p className="authors-group__eyebrow">Status group</p>
              <h2>{group.label}</h2>
            </div>
            <span>{group.items.length} author{group.items.length === 1 ? '' : 's'}</span>
          </div>
          <div className="authors-grid">
            {group.items.map((item) => {
              const displayName = cleanText(item?.catalog_name) || cleanText(item?.name) || formatSlugLabel(item?.slug);
              const loggedWorks = Array.isArray(item?.works) ? item.works : [];
              const keyWorks = Array.isArray(item?.key_works)
                ? item.key_works
                : [];
              const corpusWorksCount = Number.isFinite(Number(item?.corpus_works_count))
                ? Number(item.corpus_works_count)
                : Number(item?.works_count || 0);
              const trackerInventoryCount = Number.isFinite(Number(item?.tracker_inventory_count))
                ? Number(item.tracker_inventory_count)
                : 0;
              const inventoryVerified = Boolean(item?.inventory_verified);
              const inventoryGapCount = Number.isFinite(Number(item?.inventory_gap_count))
                ? Number(item.inventory_gap_count)
                : 0;
              const keyWorksGapCount = Number.isFinite(Number(item?.key_works_gap_count))
                ? Number(item.key_works_gap_count)
                : Math.max(corpusWorksCount - keyWorks.length, 0);
              const coverageNote = !inventoryVerified
                ? (corpusWorksCount > 0
                  ? `Tracker inventory missing; corpus currently contains ${corpusWorksCount} works.`
                  : 'Tracker inventory missing; this card is showing key works only.')
                : inventoryGapCount > 0
                  ? `Tracker inventory underreports the corpus by ${inventoryGapCount} work${inventoryGapCount === 1 ? '' : 's'}.`
                  : keyWorksGapCount > 0
                    ? `Key works are representative only; the corpus currently contains ${corpusWorksCount} works.`
                    : '';
              const timelineBits = uniqueTextList([
                item?.acquired_date ? `Acquired ${formatTrackerDate(item.acquired_date)}` : '',
                item?.queue_date ? `Queued ${formatTrackerDate(item.queue_date)}` : '',
                item?.persona_mode ? `Mode ${cleanText(item.persona_mode)}` : '',
              ], 3);
              return (
                <article key={`${item.slug}:${item.position}`} className="author-browse-card acquisition-card">
                  <div className="author-browse-card__header">
                    <div>
                      <p className="author-browse-card__slug">#{item.position} · {item.slug}</p>
                      <h3>
                        {item.available_in_catalog ? (
                          <Link className="author-browse-card__title-link" to={`/authors/${item.slug}`}>
                            {displayName}
                          </Link>
                        ) : (
                          item.name || formatSlugLabel(item.slug)
                        )}
                      </h3>
                    </div>
                    <div className="author-browse-card__chips acquisition-card__chips">
                      <span className={`author-taxonomy-chip acquisition-status-chip acquisition-status-chip--${item.status_group}`}>
                        {item.status_group_label}
                      </span>
                      {item.has_repeatable_recipe && (
                        <span className="author-taxonomy-chip acquisition-status-chip acquisition-status-chip--recipe">
                          Repeatable recipe
                        </span>
                      )}
                      {item.requires_invitation && (
                        <span className="author-taxonomy-chip acquisition-status-chip acquisition-status-chip--secret_sacred">
                          Invite only
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="author-browse-card__description">{item.status || 'No status recorded.'}</p>
                  <dl className="author-browse-card__facts">
                    <div>
                      <dt>Corpus works</dt>
                      <dd>{corpusWorksCount}</dd>
                    </div>
                    <div>
                      <dt>Inventory</dt>
                      <dd>{trackerInventoryCount}</dd>
                    </div>
                    <div>
                      <dt>Catalog</dt>
                      <dd>{item.public_visible ? 'Public' : item.available_in_catalog ? 'Private' : 'Not live'}</dd>
                    </div>
                    <div>
                      <dt>Mode</dt>
                      <dd>{cleanText(item.persona_mode) || 'Unspecified'}</dd>
                    </div>
                  </dl>
                  {timelineBits.length > 0 && (
                    <div className="author-browse-card__section">
                      <p className="author-browse-card__label">Timeline</p>
                      <p>{timelineBits.join(' · ')}</p>
                    </div>
                  )}
                  <div className="author-browse-card__section">
                    <p className="author-browse-card__label">Works</p>
                    {loggedWorks.length > 0 ? (
                      <div className="author-browse-card__chips">
                        {loggedWorks.slice(0, 6).map((work) => (
                          <span key={`${item.slug}:${work}`} className="author-taxonomy-chip">{work}</span>
                        ))}
                      </div>
                    ) : (
                      <p className="author-browse-card__subtle">No works inventory logged in the tracker yet.</p>
                    )}
                    {coverageNote && (
                      <p className="author-browse-card__subtle">{coverageNote}</p>
                    )}
                  </div>
                  {Array.isArray(item.notes) && item.notes.length > 0 && (
                    <div className="author-browse-card__section">
                      <p className="author-browse-card__label">Notes</p>
                      <p>{item.notes.slice(0, 3).join(' • ')}</p>
                    </div>
                  )}
                  <div className="author-browse-card__actions">
                    {item.available_in_catalog ? (
                      <>
                        <Link
                          className="author-browse-card__button author-browse-card__button--secondary"
                          to={`/authors/${item.slug}`}
                          aria-label={`View ${displayName} profile`}
                        >
                          View profile
                        </Link>
                        <Link
                          className="author-browse-card__button"
                          to={`/author/${item.slug}`}
                          aria-label={`Chat with ${displayName}`}
                        >
                          Open chat
                        </Link>
                      </>
                    ) : (
                      <span className="author-browse-card__subtle acquisition-card__note">
                        Not yet visible in the public author catalog.
                      </span>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </section>
  );
}

function ResponseTraceAuditPage({ canInspect }) {
  const [filters, setFilters] = useState(DEFAULT_TRACE_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(() => normalizeTraceFilters(DEFAULT_TRACE_FILTERS));
  const [governanceVersions, setGovernanceVersions] = useState(null);
  const [governanceLoading, setGovernanceLoading] = useState(false);
  const [governanceError, setGovernanceError] = useState('');
  const [governanceDiffs, setGovernanceDiffs] = useState(null);
  const [governanceDiffsLoading, setGovernanceDiffsLoading] = useState(false);
  const [governanceDiffsError, setGovernanceDiffsError] = useState('');
  const [traceItems, setTraceItems] = useState([]);
  const [traceListLoading, setTraceListLoading] = useState(false);
  const [traceListError, setTraceListError] = useState('');
  const [selectedTraceId, setSelectedTraceId] = useState('');
  const [selectedTrace, setSelectedTrace] = useState(null);
  const [selectedTraceLoading, setSelectedTraceLoading] = useState(false);
  const [selectedTraceError, setSelectedTraceError] = useState('');
  const [replayBundleExporting, setReplayBundleExporting] = useState(false);
  const [replayBundleError, setReplayBundleError] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!canInspect) {
      setGovernanceVersions(null);
      setGovernanceLoading(false);
      setGovernanceError('');
      setGovernanceDiffs(null);
      setGovernanceDiffsLoading(false);
      setGovernanceDiffsError('');
      return () => {
        cancelled = true;
      };
    }

    const loadGovernanceVersions = async () => {
      setGovernanceLoading(true);
      setGovernanceError('');
      try {
        const res = await apiV1.get('/governance/versions');
        if (!cancelled) {
          setGovernanceVersions(res.data || {});
        }
      } catch (err) {
        if (!cancelled) {
          const detail = cleanText(err?.response?.data?.detail) || err?.message || 'Failed to load governance versions';
          setGovernanceVersions(null);
          setGovernanceError(detail);
        }
      } finally {
        if (!cancelled) {
          setGovernanceLoading(false);
        }
      }
    };

    loadGovernanceVersions();
    return () => {
      cancelled = true;
    };
  }, [canInspect]);

  useEffect(() => {
    let cancelled = false;
    if (!canInspect) {
      setGovernanceDiffs(null);
      setGovernanceDiffsLoading(false);
      setGovernanceDiffsError('');
      return () => {
        cancelled = true;
      };
    }

    const promptVersion = cleanText(selectedTrace?.prompt_version) || cleanText(governanceVersions?.prompt_version);
    const configVersion = cleanText(selectedTrace?.config_version) || cleanText(governanceVersions?.config_version);
    if (!promptVersion && !configVersion) {
      setGovernanceDiffs(null);
      setGovernanceDiffsLoading(false);
      setGovernanceDiffsError('');
      return () => {
        cancelled = true;
      };
    }

    const loadGovernanceDiffs = async () => {
      setGovernanceDiffsLoading(true);
      setGovernanceDiffsError('');
      try {
        const params = {};
        if (promptVersion) {
          params.prompt_version = promptVersion;
        }
        if (configVersion) {
          params.config_version = configVersion;
        }
        const res = await apiV1.get('/governance/diffs', { params });
        if (!cancelled) {
          setGovernanceDiffs(res.data || null);
        }
      } catch (err) {
        if (!cancelled) {
          const detail = cleanText(err?.response?.data?.detail) || err?.message || 'Failed to load governance diff';
          setGovernanceDiffs(null);
          setGovernanceDiffsError(detail);
        }
      } finally {
        if (!cancelled) {
          setGovernanceDiffsLoading(false);
        }
      }
    };

    loadGovernanceDiffs();
    return () => {
      cancelled = true;
    };
  }, [
    canInspect,
    governanceVersions?.config_version,
    governanceVersions?.prompt_version,
    selectedTrace?.config_version,
    selectedTrace?.prompt_version,
  ]);

  useEffect(() => {
    let cancelled = false;
    if (!canInspect) {
      setTraceItems([]);
      setTraceListLoading(false);
      setTraceListError('');
      setSelectedTraceId('');
      setSelectedTrace(null);
      setSelectedTraceError('');
      setReplayBundleExporting(false);
      setReplayBundleError('');
      return () => {
        cancelled = true;
      };
    }

    const loadTraceItems = async () => {
      setTraceListLoading(true);
      setTraceListError('');
      try {
        const res = await apiV1.get('/response-traces', { params: appliedFilters });
        if (!cancelled) {
          const nextItems = Array.isArray(res.data?.items) ? res.data.items : [];
          setTraceItems(nextItems);
          setSelectedTrace(null);
          setSelectedTraceError('');
          setSelectedTraceId((current) => {
            if (current && nextItems.some((item) => item.trace_id === current)) {
              return current;
            }
            return nextItems[0]?.trace_id || '';
          });
        }
      } catch (err) {
        if (!cancelled) {
          const detail = cleanText(err?.response?.data?.detail) || err?.message || 'Failed to load response traces';
          setTraceItems([]);
          setSelectedTraceId('');
          setSelectedTrace(null);
          setTraceListError(detail);
        }
      } finally {
        if (!cancelled) {
          setTraceListLoading(false);
        }
      }
    };

    loadTraceItems();
    return () => {
      cancelled = true;
    };
  }, [appliedFilters, canInspect]);

  useEffect(() => {
    let cancelled = false;
    if (!canInspect || !selectedTraceId) {
      setSelectedTrace(null);
      setSelectedTraceLoading(false);
      setSelectedTraceError('');
      setReplayBundleExporting(false);
      setReplayBundleError('');
      return () => {
        cancelled = true;
      };
    }

    const loadTraceDetail = async () => {
      setSelectedTraceLoading(true);
      setSelectedTraceError('');
      try {
        const res = await apiV1.get(`/response-traces/${selectedTraceId}`);
        if (!cancelled) {
          setSelectedTrace(res.data || null);
        }
      } catch (err) {
        if (!cancelled) {
          const detail = cleanText(err?.response?.data?.detail) || err?.message || 'Failed to load trace detail';
          setSelectedTrace(null);
          setSelectedTraceError(detail);
        }
      } finally {
        if (!cancelled) {
          setSelectedTraceLoading(false);
        }
      }
    };

    loadTraceDetail();
    return () => {
      cancelled = true;
    };
  }, [canInspect, selectedTraceId]);

  const handleFilterChange = (field) => (event) => {
    const value = event?.target?.value ?? '';
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const handleApplyFilters = (event) => {
    event.preventDefault();
    setAppliedFilters(normalizeTraceFilters(filters));
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_TRACE_FILTERS);
    setAppliedFilters(normalizeTraceFilters(DEFAULT_TRACE_FILTERS));
  };

  const handleExportReplayBundle = async () => {
    if (!selectedTraceId || replayBundleExporting) {
      return;
    }

    setReplayBundleExporting(true);
    setReplayBundleError('');
    try {
      const res = await apiV1.get(`/response-traces/${selectedTraceId}/replay-bundle`);
      const payload = res?.data || {};
      const traceId = cleanText(payload?.trace?.trace_id) || selectedTraceId;
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: 'application/json',
      });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `pericope-replay-${traceId}.json`;
      link.click();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      const detail = cleanText(err?.response?.data?.detail) || err?.message || 'Failed to export replay bundle';
      setReplayBundleError(detail);
    } finally {
      setReplayBundleExporting(false);
    }
  };

  if (!canInspect) {
    return (
      <Card className="form-card">
        <Card.Body>
          <Card.Title>Response Trace Audit</Card.Title>
          <Card.Text>Admin or operator access is required to inspect governance traces.</Card.Text>
        </Card.Body>
      </Card>
    );
  }

  const summaryCards = [
    {
      label: 'Runtime',
      value: cleanText(governanceVersions?.runtime_version) || 'Unavailable',
      detail: 'Active service build stamped into traces.',
    },
    {
      label: 'Corpus',
      value: cleanText(governanceVersions?.corpus_version) || 'Unavailable',
      detail: cleanText(governanceVersions?.corpus_content_release_label) || 'Corpus release label unavailable.',
    },
    {
      label: 'Prompt',
      value: cleanText(governanceVersions?.prompt_version) || 'Unavailable',
      detail: 'Prompt manifest version used for response generation.',
    },
    {
      label: 'Config',
      value: cleanText(governanceVersions?.config_version) || 'Unavailable',
      detail: 'Active service configuration version.',
    },
  ];
  const governanceDiffCards = [
    {
      key: 'prompt',
      label: 'Prompt diff',
      payload: governanceDiffs?.prompt,
    },
    {
      key: 'config',
      label: 'Config diff',
      payload: governanceDiffs?.config,
    },
  ];

  return (
    <section className="audit-page">
      <header className="audit-hero">
        <div>
          <p className="audit-hero__eyebrow">Governance</p>
          <h1>Response trace audit</h1>
          <p className="audit-hero__copy">
            Inspect which service, corpus, prompt, and configuration versions produced a live answer, then drill down into the recorded retrieval sources for that response.
          </p>
        </div>
      </header>

      <section className="audit-summary-grid" aria-label="Governance versions">
        {summaryCards.map((card) => (
          <article key={card.label} className="audit-summary-card">
            <p className="audit-summary-card__label">{card.label}</p>
            <strong>{card.value}</strong>
            <span>{card.detail}</span>
          </article>
        ))}
      </section>

      {governanceLoading && <p className="audit-page__state">Loading governance versions…</p>}
      {!governanceLoading && governanceError && (
        <p className="audit-page__state">Unable to load governance versions: {governanceError}</p>
      )}

      <Card className="form-card audit-card">
        <Card.Body>
          <Card.Title>Trace filters</Card.Title>
          <form className="audit-filters" onSubmit={handleApplyFilters}>
            <label>
              <span>Service ID</span>
              <input type="text" value={filters.service_id} onChange={handleFilterChange('service_id')} placeholder="augustine.en" autoComplete="off" />
            </label>
            <label>
              <span>Service version</span>
              <input type="text" value={filters.service_version} onChange={handleFilterChange('service_version')} placeholder="1.0.0" autoComplete="off" />
            </label>
            <label>
              <span>Session ID</span>
              <input type="text" value={filters.session_id} onChange={handleFilterChange('session_id')} placeholder="session UUID" autoComplete="off" />
            </label>
            <label>
              <span>User ID</span>
              <input type="text" value={filters.user_id} onChange={handleFilterChange('user_id')} placeholder="user UUID" autoComplete="off" />
            </label>
            <label>
              <span>Limit</span>
              <input type="number" min="1" max="200" value={filters.limit} onChange={handleFilterChange('limit')} />
            </label>
            <div className="audit-filters__actions">
              <Button type="submit" variant="primary">Run trace search</Button>
              <button type="button" className="nav-link-btn audit-filters__clear" onClick={handleClearFilters}>
                Clear
              </button>
            </div>
          </form>
        </Card.Body>
      </Card>

      <section className="audit-layout" aria-label="Response trace inspection">
        <div className="audit-panel">
          <div className="audit-panel__header">
            <div>
              <p className="audit-panel__eyebrow">Trace results</p>
              <h2>Recent traces</h2>
            </div>
            <span>{traceItems.length} trace{traceItems.length === 1 ? '' : 's'}</span>
          </div>
          {traceListLoading && <p className="audit-page__state">Loading response traces…</p>}
          {!traceListLoading && traceListError && (
            <p className="audit-page__state">Unable to load response traces: {traceListError}</p>
          )}
          {!traceListLoading && !traceListError && traceItems.length === 0 && (
            <p className="audit-page__state">No traces match the current filters.</p>
          )}
          {!traceListLoading && !traceListError && traceItems.length > 0 && (
            <div className="audit-trace-list">
              {traceItems.map((item) => {
                const retrievalSources = Array.isArray(item?.retrieval_sources) ? item.retrieval_sources : [];
                const sourcePreview = retrievalSources
                  .map((source) => cleanText(source?.reference) || cleanText(source?.location) || cleanText(source?.id))
                  .filter(Boolean)
                  .slice(0, 3);
                return (
                  <button
                    key={item.trace_id}
                    type="button"
                    className={`audit-trace-row${selectedTraceId === item.trace_id ? ' audit-trace-row--active' : ''}`}
                    onClick={() => setSelectedTraceId(item.trace_id)}
                    aria-label={`Inspect trace ${item.trace_id}`}
                  >
                    <div className="audit-trace-row__header">
                      <strong>{cleanText(item?.persona) ? formatSlugLabel(item.persona) : 'Unknown persona'}</strong>
                      <span>{formatSessionTimestamp(item?.created_at)}</span>
                    </div>
                    <div className="audit-trace-row__meta">
                      <span>{cleanText(item?.service_id) || 'Unknown service'}</span>
                      <span>{cleanText(item?.service_version) || 'Unknown version'}</span>
                      <span>{cleanText(item?.session_id) || 'No session'}</span>
                    </div>
                    <p className="audit-trace-row__summary">
                      {sourcePreview.length > 0
                        ? `Sources: ${sourcePreview.join(' · ')}`
                        : 'No retrieval sources recorded for this trace.'}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="audit-panel audit-panel--detail">
          <div className="audit-panel__header">
            <div>
              <p className="audit-panel__eyebrow">Trace detail</p>
              <h2>{selectedTraceId || 'Select a trace'}</h2>
            </div>
            <div className="audit-panel__actions">
              {selectedTrace?.session_id && (
                <Link className="author-browse-card__button author-browse-card__button--secondary" to={`/chat?session_id=${encodeURIComponent(selectedTrace.session_id)}`}>
                  Open session
                </Link>
              )}
              {selectedTrace && (
                <button
                  type="button"
                  className="author-browse-card__button author-browse-card__button--secondary audit-panel__action-button"
                  onClick={handleExportReplayBundle}
                  disabled={replayBundleExporting}
                >
                  {replayBundleExporting ? 'Exporting…' : 'Export replay bundle'}
                </button>
              )}
            </div>
          </div>
          {selectedTraceLoading && <p className="audit-page__state">Loading trace detail…</p>}
          {!selectedTraceLoading && selectedTraceError && (
            <p className="audit-page__state">Unable to load trace detail: {selectedTraceError}</p>
          )}
          {!selectedTraceLoading && !selectedTraceError && replayBundleError && (
            <p className="audit-page__state">Unable to export replay bundle: {replayBundleError}</p>
          )}
          {!selectedTraceLoading && !selectedTraceError && !selectedTrace && (
            <p className="audit-page__state">Select a trace to inspect the recorded governance versions and retrieval sources.</p>
          )}
          {!selectedTraceLoading && !selectedTraceError && selectedTrace && (
            <>
              <div className="audit-detail-grid">
                <div>
                  <span className="audit-detail-grid__label">Service</span>
                  <strong>{cleanText(selectedTrace.service_id) || 'Unknown service'}</strong>
                  <span>{cleanText(selectedTrace.service_version) || 'Unknown version'}</span>
                </div>
                <div>
                  <span className="audit-detail-grid__label">Governance</span>
                  <strong>{cleanText(selectedTrace.corpus_version) || 'Unknown corpus'}</strong>
                  <span>{cleanText(selectedTrace.prompt_version) || 'Unknown prompt'} · {cleanText(selectedTrace.config_version) || 'Unknown config'}</span>
                </div>
                <div>
                  <span className="audit-detail-grid__label">Runtime</span>
                  <strong>{cleanText(selectedTrace.runtime_version) || 'Unknown runtime'}</strong>
                  <span>{cleanText(selectedTrace.service_schema_version) ? `Schema ${selectedTrace.service_schema_version}` : 'Schema unavailable'}</span>
                </div>
                <div>
                  <span className="audit-detail-grid__label">Session</span>
                  <strong>{cleanText(selectedTrace.session_id) || 'No session'}</strong>
                  <span>{cleanText(selectedTrace.user_id) || 'No user recorded'}</span>
                </div>
              </div>

              <dl className="audit-detail-facts">
                <div>
                  <dt>Trace ID</dt>
                  <dd><code>{selectedTrace.trace_id}</code></dd>
                </div>
                <div>
                  <dt>Assistant message</dt>
                  <dd>{cleanText(selectedTrace.assistant_message_id) || 'Unavailable'}</dd>
                </div>
                <div>
                  <dt>Recorded at</dt>
                  <dd>{formatSessionTimestamp(selectedTrace.created_at)}</dd>
                </div>
                <div>
                  <dt>Corpus API</dt>
                  <dd>{cleanText(selectedTrace.corpus_api_url) || 'Unavailable'}</dd>
                </div>
              </dl>

              <div className="audit-detail-section">
                <p className="audit-detail-section__label">Prompt and config changes</p>
                {governanceDiffsLoading && <p className="audit-page__state">Loading prompt and config changes…</p>}
                {!governanceDiffsLoading && governanceDiffsError && (
                  <p className="audit-page__state">Unable to load prompt and config changes: {governanceDiffsError}</p>
                )}
                {!governanceDiffsLoading && !governanceDiffsError && governanceDiffs && (
                  <div className="audit-diff-grid">
                    {governanceDiffCards.map((card) => {
                      const payload = card.payload || {};
                      const changes = Array.isArray(payload?.changes) ? payload.changes : [];
                      return (
                        <article key={card.key} className="audit-diff-card">
                          <div className="audit-diff-card__header">
                            <div>
                              <p className="audit-diff-card__eyebrow">{card.label}</p>
                              <strong>{cleanText(payload.target_version) || 'Unavailable'}</strong>
                            </div>
                            <span>
                              {cleanText(payload.previous_version)
                                ? `from ${payload.previous_version}`
                                : 'first recorded version'}
                            </span>
                          </div>
                          <p className="audit-diff-card__summary">
                            {cleanText(payload.summary) || 'No change summary recorded for this version.'}
                          </p>
                          {changes.length > 0 ? (
                            <ul className="audit-diff-list">
                              {changes.map((change, index) => (
                                <li key={`${card.key}:${cleanText(change?.title) || index}`}>
                                  <span className="audit-diff-list__area">{cleanText(change?.area) || 'general'}</span>
                                  <strong>{cleanText(change?.title) || 'Untitled change'}</strong>
                                  <p>{cleanText(change?.description) || 'No description recorded.'}</p>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="audit-page__state">No change entries are recorded for this version.</p>
                          )}
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="audit-detail-section">
                <p className="audit-detail-section__label">Retrieval sources</p>
                {Array.isArray(selectedTrace.retrieval_sources) && selectedTrace.retrieval_sources.length > 0 ? (
                  <ul className="audit-source-list">
                    {selectedTrace.retrieval_sources.map((source) => (
                      <li key={cleanText(source?.id) || `${source?.reference || 'source'}:${source?.location || ''}`}>
                        <strong>{cleanText(source?.reference) || cleanText(source?.location) || cleanText(source?.id) || 'Unnamed source'}</strong>
                        <span>
                          {uniqueTextList([
                            cleanText(source?.source),
                            cleanText(source?.work),
                            cleanText(source?.position),
                            cleanText(source?.provenance),
                          ]).join(' · ') || 'No source metadata recorded.'}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="audit-page__state">No retrieval sources were recorded on this trace.</p>
                )}
              </div>
            </>
          )}
        </div>
      </section>
    </section>
  );
}

function AuthorDetailPage({
  items,
  loading,
  error,
  persona,
  setPersona,
  authorProfile,
  authorProfileLoading,
  authorProfileError,
  authorCrossrefs,
  authorCrossrefsLoading,
  authorCrossrefsError,
  isAuthenticated,
  favoriteAuthorSlugs,
  defaultAuthorSlug,
  preferencesSaving,
  preferencesError,
  onToggleFavorite,
  onSetDefaultAuthor,
}) {
  const { slug } = useParams();
  const normalizedSlug = String(slug || '').trim().toLowerCase();

  useEffect(() => {
    if (normalizedSlug && normalizedSlug !== persona) {
      setPersona(normalizedSlug);
    }
  }, [normalizedSlug, persona, setPersona]);

  const browseItem = useMemo(
    () => items.find((item) => String(item?.slug || '').toLowerCase() === normalizedSlug) || null,
    [items, normalizedSlug],
  );
  const profileForSlug = cleanText(authorProfile?.slug).toLowerCase() === normalizedSlug ? authorProfile : null;
  const crossrefsForSlug = cleanText(authorCrossrefs?.author?.slug).toLowerCase() === normalizedSlug ? authorCrossrefs : null;
  const taxonomy = profileForSlug?.taxonomy || browseItem?.taxonomy || {};
  const taxonomyTrail = getTaxonomyTrail(taxonomy);
  const works = useMemo(
    () => (
      Array.isArray(profileForSlug?.books) && profileForSlug.books.length > 0
        ? profileForSlug.books
        : (Array.isArray(browseItem?.works_preview) ? browseItem.works_preview : [])
    ),
    [browseItem, profileForSlug],
  );
  const crossrefBooks = Array.isArray(crossrefsForSlug?.books) && crossrefsForSlug.books.length > 0
    ? crossrefsForSlug.books
    : (Array.isArray(browseItem?.crossrefs?.books_preview) ? browseItem.crossrefs.books_preview : []);
  const crossrefCounts = crossrefsForSlug?.counts || browseItem?.crossrefs?.counts || {};
  const displayName = profileForSlug?.name || browseItem?.name || formatSlugLabel(normalizedSlug);
  const biographySource = profileForSlug || browseItem;
  const portraitSource = useMemo(
    () => ({ ...(browseItem || {}), ...(profileForSlug || {}) }),
    [browseItem, profileForSlug],
  );
  const description = getAuthorSummary(biographySource) || 'No description available yet.';
  const history = getAuthorHistory(biographySource);
  const historicalContext = getAuthorHistoricalContext(profileForSlug);
  const historicalContextSummary = cleanText(historicalContext?.summary) || description;
  const historicalContextPeriod = formatHistoricalContextPeriod(historicalContext);
  const historicalContextPlaces = getHistoricalContextPlaces(historicalContext);
  const historicalContextTimeline = getHistoricalContextTimelineEntries(historicalContext?.timeline);
  const historicalContextWorld = getHistoricalContextLabeledEntries(historicalContext?.world_context);
  const historicalContextWorks = getHistoricalContextTimelineEntries(historicalContext?.works_chronology);
  const historicalContextReception = getHistoricalContextLabeledEntries(historicalContext?.reception);
  const historicalContextReceivedFrom = getHistoricalContextStringList(historicalContext?.influences?.received_from);
  const historicalContextContemporaries = getHistoricalContextStringList(historicalContext?.influences?.contemporaries);
  const historicalContextLaterReception = getHistoricalContextStringList(historicalContext?.influences?.later_reception);
  const historicalContextProvenance = formatHistoricalContextProvenance(historicalContext);
  const favoriteAuthorSet = useMemo(
    () => new Set((favoriteAuthorSlugs || []).map((value) => cleanText(value).toLowerCase()).filter(Boolean)),
    [favoriteAuthorSlugs],
  );
  const isFavorite = favoriteAuthorSet.has(normalizedSlug);
  const isDefault = normalizedSlug && normalizedSlug === cleanText(defaultAuthorSlug).toLowerCase();
  const notable = getAuthorNotable(biographySource) || description;
  const bodyOfThought = getAuthorBodyOfThought(biographySource);
  const influenceEra = getAuthorInfluenceEra(biographySource);
  const introductoryPrompt = getAuthorIntroductoryPrompt(
    biographySource || { slug: normalizedSlug, name: displayName, taxonomy },
    works,
  );
  const introductoryPromptHref = useMemo(() => {
    const params = new URLSearchParams({
      source: 'author_detail',
      message: introductoryPrompt,
    });
    return `/author/${normalizedSlug}?${params.toString()}`;
  }, [introductoryPrompt, normalizedSlug]);
  const notFound = !loading && !error && items.length > 0 && !browseItem;

  if (loading && !browseItem) {
    return <section className="authors-page"><p className="authors-page__state">Loading author profile…</p></section>;
  }

  if (!loading && error && items.length === 0) {
    return <section className="authors-page"><p className="authors-page__state">Unable to load author catalog: {error}</p></section>;
  }

  if (notFound) {
    return (
      <section className="authors-page">
        <div className="author-detail-panel">
          <h1>Author not found</h1>
          <p>The author <code>{normalizedSlug}</code> is not available in this deployment.</p>
          <Link className="author-browse-card__button" to="/authors">Return to author catalog</Link>
        </div>
      </section>
    );
  }

  return (
    <section className="authors-page author-detail-page">
      <div className="author-detail-hero">
        <div className="author-detail-hero__identity">
          <AuthorPortrait author={portraitSource} className="author-detail-hero__portrait" />
          <div className="author-detail-hero__copy">
            <p className="authors-hero__eyebrow">Author Profile</p>
            <h1>{displayName}</h1>
            <p className="author-detail-hero__description">{description}</p>
            <div className="author-detail-hero__chips">
              {taxonomyTrail.map((label) => (
                <span key={label} className="author-taxonomy-chip">{label}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="author-detail-hero__actions">
          {isAuthenticated && (
            <>
              <button
                type="button"
                className={`author-browse-card__button author-browse-card__button--secondary${isFavorite ? ' author-browse-card__button--active' : ''}`}
                onClick={() => onToggleFavorite(normalizedSlug)}
                disabled={preferencesSaving}
              >
                {isFavorite ? 'Favorited' : 'Favorite'}
              </button>
              <button
                type="button"
                className={`author-browse-card__button author-browse-card__button--secondary${isDefault ? ' author-browse-card__button--active' : ''}`}
                onClick={() => onSetDefaultAuthor(normalizedSlug)}
                disabled={preferencesSaving}
              >
                {isDefault ? 'Default author' : 'Make default'}
              </button>
            </>
          )}
          <Link className="author-browse-card__button author-browse-card__button--secondary" to="/authors">
            Back to authors
          </Link>
          <Link className="author-browse-card__button" to={`/author/${normalizedSlug}`}>
            Chat with {displayName}
          </Link>
        </div>
      </div>
      {isAuthenticated && preferencesError && (
        <p className="authors-page__state">{preferencesError}</p>
      )}

      <section className="author-detail-metrics" aria-label="Author summary">
        <div className="author-detail-metric">
          <span>Works in corpus</span>
          <strong>{works.length}</strong>
        </div>
        <div className="author-detail-metric">
          <span>Mapped references</span>
          <strong>{Number(crossrefCounts.books || 0)}</strong>
        </div>
        <div className="author-detail-metric">
          <span>Tradition</span>
          <strong>{cleanText(taxonomy.tradition) || cleanText(taxonomy.family) || 'General'}</strong>
        </div>
      </section>

      <section className="author-detail-guide" aria-label="Author orientation">
        <article className="author-detail-guide__card">
          <p className="author-browse-card__label">Notable for</p>
          <p className="author-detail-guide__text">{notable}</p>
        </article>
        <article className="author-detail-guide__card">
          <p className="author-browse-card__label">Body of thought</p>
          <p className="author-detail-guide__text">{bodyOfThought}</p>
        </article>
        <article className="author-detail-guide__card">
          <p className="author-browse-card__label">Influence era</p>
          <p className="author-detail-guide__text">{influenceEra}</p>
        </article>
        <article className="author-detail-guide__card author-detail-guide__card--prompt">
          <p className="author-browse-card__label">Introductory prompt</p>
          <p className="author-detail-guide__prompt">{introductoryPrompt}</p>
          <div className="author-browse-card__actions">
            <Link className="author-browse-card__button" to={introductoryPromptHref}>
              Start with this prompt
            </Link>
          </div>
        </article>
      </section>

      <div className="author-detail-layout">
        <section className="author-detail-panel">
          <div className="author-detail-panel__header">
            <div>
              <p className="author-browse-card__label">Biography</p>
              <h2>Historical context</h2>
            </div>
          </div>
          <p className="author-detail-panel__summary">{historicalContextSummary}</p>
          {(historicalContextPeriod || historicalContextPlaces.length > 0) && (
            <dl className="author-detail-meta">
              {historicalContextPeriod && (
                <>
                  <dt>Period</dt>
                  <dd>{historicalContextPeriod}</dd>
                </>
              )}
              {historicalContextPlaces.length > 0 && (
                <>
                  <dt>Places</dt>
                  <dd>{historicalContextPlaces.join(' · ')}</dd>
                </>
              )}
            </dl>
          )}
          {historicalContextTimeline.length > 0 && (
            <div className="author-detail-subsection">
              <p className="author-browse-card__label">Timeline</p>
              <ol className="author-detail-timeline">
                {historicalContextTimeline.map((item, index) => (
                  <li key={`${item.dateLabel || item.title || 'timeline'}-${index}`}>
                    <span className="author-detail-timeline__date">{item.dateLabel || 'Undated'}</span>
                    <strong>{item.title || 'Historical milestone'}</strong>
                    {item.body && <p>{item.body}</p>}
                  </li>
                ))}
              </ol>
            </div>
          )}
          {historicalContextWorld.length > 0 && (
            <div className="author-detail-subsection">
              <p className="author-browse-card__label">World context</p>
              <ul className="author-detail-bullet-list">
                {historicalContextWorld.map((item, index) => (
                  <li key={`${item.label || item.body || 'world'}-${index}`}>
                    <strong>{item.label || 'Context'}</strong>
                    {item.body && <span>{item.body}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {historicalContextWorks.length > 0 && (
            <div className="author-detail-subsection">
              <p className="author-browse-card__label">Works chronology</p>
              <ul className="author-detail-bullet-list">
                {historicalContextWorks.map((item, index) => (
                  <li key={`${item.dateLabel || item.title || 'work'}-${index}`}>
                    <strong>{uniqueTextList([item.dateLabel, item.title], 2).join(' · ') || 'Work'}</strong>
                    {item.body && <span>{item.body}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(historicalContextReceivedFrom.length > 0
            || historicalContextContemporaries.length > 0
            || historicalContextLaterReception.length > 0) && (
            <div className="author-detail-subsection">
              <p className="author-browse-card__label">Influence and reception</p>
              <div className="author-detail-tag-groups">
                {historicalContextReceivedFrom.length > 0 && (
                  <div className="author-detail-tag-group">
                    <span className="author-detail-tag-group__title">Received from</span>
                    <div className="author-detail-tag-list">
                      {historicalContextReceivedFrom.map((item) => (
                        <span key={`received-${item}`} className="author-taxonomy-chip">{item}</span>
                      ))}
                    </div>
                  </div>
                )}
                {historicalContextContemporaries.length > 0 && (
                  <div className="author-detail-tag-group">
                    <span className="author-detail-tag-group__title">Contemporaries</span>
                    <div className="author-detail-tag-list">
                      {historicalContextContemporaries.map((item) => (
                        <span key={`contemporary-${item}`} className="author-taxonomy-chip">{item}</span>
                      ))}
                    </div>
                  </div>
                )}
                {historicalContextLaterReception.length > 0 && (
                  <div className="author-detail-tag-group">
                    <span className="author-detail-tag-group__title">Later reception</span>
                    <div className="author-detail-tag-list">
                      {historicalContextLaterReception.map((item) => (
                        <span key={`reception-${item}`} className="author-taxonomy-chip">{item}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {historicalContextReception.length > 0 && (
            <div className="author-detail-subsection">
              <p className="author-browse-card__label">Reception</p>
              <ul className="author-detail-bullet-list">
                {historicalContextReception.map((item, index) => (
                  <li key={`${item.label || item.body || 'reception'}-${index}`}>
                    <strong>{item.label || 'Reception'}</strong>
                    {item.body && <span>{item.body}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!historicalContext && history && (
            <p className="author-detail-panel__bodycopy">{history}</p>
          )}
          {!historicalContext && !history && (
            <p className="author-detail-panel__bodycopy">Reviewed historical context is not available yet.</p>
          )}
          {historicalContextProvenance && (
            <p className="author-detail-panel__meta">{historicalContextProvenance}</p>
          )}
        </section>

        <section className="author-detail-panel">
          <div className="author-detail-panel__header">
            <div>
              <p className="author-browse-card__label">Works</p>
              <h2>Texts available for chat</h2>
            </div>
            {authorProfileLoading && <span className="author-detail-panel__status">Loading full list…</span>}
          </div>
          {authorProfileError && !profileForSlug && (
            <p className="authors-page__state">Unable to load full works list: {authorProfileError}</p>
          )}
          {works.length > 0 ? (
            <ul className="author-detail-list">
              {works.map((item, index) => {
                const primary = getAuthorWorkPrimaryLabel(item, index);
                const secondary = getAuthorWorkSecondaryLabels(item, index);
                const readerHref = cleanText(item?.source)
                  ? buildAuthorWorkReaderHref({
                    authorSlug: normalizedSlug,
                    source: item.source,
                  })
                  : '';
                return (
                  <li key={`${primary}-${index}`}>
                    <div className="author-detail-list__row">
                      <div className="author-detail-list__copy">
                        <strong>{primary}</strong>
                        {secondary.length > 0 && (
                          <span className="meta-label"> · {secondary.join(' · ')}</span>
                        )}
                      </div>
                      {readerHref && (
                        <Link
                          className="author-browse-card__button author-browse-card__button--secondary author-detail-list__action"
                          to={readerHref}
                        >
                          Read {primary}
                        </Link>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="authors-page__state">No works metadata available yet.</p>
          )}
        </section>

        <section className="author-detail-panel">
          <div className="author-detail-panel__header">
            <div>
              <p className="author-browse-card__label">Cross citations</p>
              <h2>Scripture reference map</h2>
            </div>
            <Link className="author-browse-card__button author-browse-card__button--secondary" to="/references">
              Browse scripture catalog
            </Link>
            {authorCrossrefsLoading && <span className="author-detail-panel__status">Loading reference map…</span>}
          </div>
          {authorCrossrefsError && !crossrefsForSlug && (
            <p className="authors-page__state">Unable to load reference map: {authorCrossrefsError}</p>
          )}
          <p className="author-detail-panel__summary">{getCrossrefSummaryLabel(crossrefsForSlug || browseItem?.crossrefs)}</p>
          {crossrefBooks.length > 0 ? (
            <ul className="author-detail-list">
              {crossrefBooks.map((item) => {
                const bookId = cleanText(item?.book_id) || cleanText(item?.name);
                const evidenceSnippet = cleanText(item?.evidence_snippet);
                const confidenceLabel = formatCrossrefConfidence(item?.confidence);
                return (
                  <li key={bookId}>
                    <div className="author-detail-list__row">
                      <div className="author-detail-list__copy">
                        <strong>{bookId}</strong>
                        {cleanText(item?.name) && <span className="meta-label"> · {cleanText(item.name)}</span>}
                        {cleanText(item?.section) && <span className="meta-label"> · {cleanText(item.section)}</span>}
                        {cleanText(item?.testament) && <span className="meta-label"> · {cleanText(item.testament)}</span>}
                        {cleanText(item?.evidence_work) && <span className="meta-label"> · via {cleanText(item.evidence_work)}</span>}
                        {evidenceSnippet && (
                          <p className="crossref-evidence-note">{evidenceSnippet}</p>
                        )}
                        {confidenceLabel && (
                          <p className="crossref-evidence-meta">{confidenceLabel}</p>
                        )}
                      </div>
                      {bookId && (
                        <Link
                          className="author-browse-card__button author-browse-card__button--secondary author-detail-list__action"
                          to={buildReferenceCatalogHref(bookId)}
                        >
                          View reverse lookup
                        </Link>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="authors-page__state">No mapped scriptural references yet.</p>
          )}
        </section>
      </div>
    </section>
  );
}

function AuthorWorkReaderPage({
  items,
  loading,
  error,
  persona,
  setPersona,
  submitPromptText,
  isBusy,
  chatStatus,
  authorProfile,
  authorProfileLoading,
  authorProfileError,
}) {
  const { slug, source: sourceParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const normalizedSlug = cleanText(slug).toLowerCase();
  const decodedSource = cleanText(decodeRouteSegment(sourceParam));
  const [readerData, setReaderData] = useState(null);
  const [readerLoading, setReaderLoading] = useState(false);
  const [readerError, setReaderError] = useState('');
  const [readerQuestion, setReaderQuestion] = useState('');
  const [selectedReaderPositions, setSelectedReaderPositions] = useState([]);
  const browseItem = useMemo(
    () => items.find((item) => cleanText(item?.slug).toLowerCase() === normalizedSlug) || null,
    [items, normalizedSlug],
  );
  const profileForSlug = cleanText(authorProfile?.slug).toLowerCase() === normalizedSlug ? authorProfile : null;
  const portraitSource = useMemo(
    () => ({ ...(browseItem || {}), ...(profileForSlug || {}) }),
    [browseItem, profileForSlug],
  );
  const displayName = profileForSlug?.name || browseItem?.name || formatSlugLabel(normalizedSlug);
  const description = getAuthorSummary(profileForSlug || browseItem) || 'No description available yet.';
  const works = useMemo(
    () => (
      Array.isArray(profileForSlug?.books) && profileForSlug.books.length > 0
        ? profileForSlug.books
        : (Array.isArray(browseItem?.works_preview) ? browseItem.works_preview : [])
    ),
    [browseItem, profileForSlug],
  );
  const selectedWork = useMemo(
    () => findAuthorWorkBySource(works, decodedSource),
    [works, decodedSource],
  );
  const requestedRange = useMemo(
    () => parseReaderRangeFromSearch(location.search),
    [location.search],
  );
  const workLabel = selectedWork
    ? getAuthorWorkPrimaryLabel(selectedWork)
    : formatSlugLabel(decodedSource || 'work');
  const workLookupPending = !selectedWork && authorProfileLoading;
  const notFound = !loading && !error && items.length > 0 && !browseItem;
  const currentWindowSize = Math.max(
    1,
    parsePositiveInt(readerData?.end_position, requestedRange.endPosition)
      - parsePositiveInt(readerData?.start_position, requestedRange.startPosition)
      + 1,
    Array.isArray(readerData?.entries) ? readerData.entries.length : 0,
  );
  const entries = useMemo(() => {
    if (Array.isArray(readerData?.entries) && readerData.entries.length > 0) {
      return readerData.entries;
    }
    const content = cleanText(readerData?.content);
    if (!content) {
      return [];
    }
    return [
      {
        position: parsePositiveInt(readerData?.start_position, requestedRange.startPosition),
        reference: cleanText(readerData?.reference) || workLabel,
        content,
      },
    ];
  }, [readerData, requestedRange.startPosition, workLabel]);
  const selectedEntries = useMemo(() => {
    const selectedPositionSet = new Set(
      (Array.isArray(selectedReaderPositions) ? selectedReaderPositions : [])
        .map((value) => parsePositiveInt(value, 0))
        .filter((value) => value > 0),
    );
    return entries.filter((entry) => selectedPositionSet.has(parsePositiveInt(entry?.position, 0)));
  }, [entries, selectedReaderPositions]);
  const selectedSourceContext = useMemo(
    () => buildReaderSourceContext({
      authorSlug: normalizedSlug,
      workLabel,
      source: cleanText(selectedWork?.source) || decodedSource,
      entries: selectedEntries,
      fallbackReference: cleanText(readerData?.reference) || workLabel,
    }),
    [decodedSource, normalizedSlug, readerData, selectedEntries, selectedWork, workLabel],
  );
  const sourceContextDisplayLabel = getSourceContextDisplayLabel(selectedSourceContext);
  const sourceContextReaderHref = selectedSourceContext
    ? buildAuthorWorkReaderHref({
      authorSlug: normalizedSlug,
      source: cleanText(selectedWork?.source) || decodedSource,
      startPosition: selectedSourceContext.start_position,
      endPosition: selectedSourceContext.end_position,
    })
    : '';

  useEffect(() => {
    if (normalizedSlug && normalizedSlug !== persona) {
      setPersona(normalizedSlug);
    }
  }, [normalizedSlug, persona, setPersona]);

  useEffect(() => {
    const nextSelection = uniqueTextList(
      entries
        .map((entry) => parsePositiveInt(entry?.position, 0))
        .filter((value) => value > 0),
    ).map((value) => parsePositiveInt(value, 0));
    setSelectedReaderPositions(nextSelection);
  }, [entries]);

  useEffect(() => {
    if (!normalizedSlug || !decodedSource || !selectedWork?.source) {
      setReaderData(null);
      setReaderLoading(false);
      setReaderError('');
      return;
    }

    let cancelled = false;
    const requestBody = {
      author_slug: normalizedSlug,
      book: getAuthorWorkPrimaryLabel(selectedWork),
      source: selectedWork.source,
      chapter: selectedWork.chapter || null,
      page: selectedWork.page || null,
      start_position: requestedRange.startPosition,
      end_position: requestedRange.endPosition,
    };

    const loadReaderRange = async () => {
      setReaderLoading(true);
      setReaderError('');
      try {
        const res = await apiV1.post('/book_partial', requestBody);
        if (!cancelled) {
          setReaderData(res.data || null);
          trackEvent('author_work_reader_loaded', {
            author_slug: normalizedSlug,
            source: cleanText(selectedWork.source),
            start_position: requestBody.start_position,
            end_position: requestBody.end_position,
            entry_count: Array.isArray(res.data?.entries) ? res.data.entries.length : 0,
          });
        }
      } catch (err) {
        if (!cancelled) {
          const detail = cleanText(err?.response?.data?.detail) || err?.message || 'Unknown error';
          setReaderData(null);
          setReaderError(detail);
          trackEvent('author_work_reader_failed', {
            author_slug: normalizedSlug,
            source: cleanText(selectedWork.source) || decodedSource,
            status_code: err?.response?.status || 0,
          });
        }
      } finally {
        if (!cancelled) {
          setReaderLoading(false);
        }
      }
    };

    loadReaderRange();
    return () => {
      cancelled = true;
    };
  }, [
    decodedSource,
    normalizedSlug,
    requestedRange.endPosition,
    requestedRange.startPosition,
    selectedWork,
  ]);

  const navigateToRange = useCallback((startPosition) => {
    if (!selectedWork?.source) {
      return;
    }
    navigate(buildAuthorWorkReaderHref({
      authorSlug: normalizedSlug,
      source: selectedWork.source,
      startPosition,
      endPosition: parsePositiveInt(startPosition, 1) + currentWindowSize - 1,
    }));
  }, [currentWindowSize, navigate, normalizedSlug, selectedWork]);
  const toggleReaderPosition = useCallback((position) => {
    const safePosition = parsePositiveInt(position, 0);
    if (safePosition < 1) {
      return;
    }
    setSelectedReaderPositions((prev) => {
      const existing = new Set((Array.isArray(prev) ? prev : []).map((value) => parsePositiveInt(value, 0)).filter((value) => value > 0));
      if (existing.has(safePosition)) {
        existing.delete(safePosition);
      } else {
        existing.add(safePosition);
      }
      return Array.from(existing).sort((left, right) => left - right);
    });
  }, []);
  const selectCurrentWindow = useCallback(() => {
    setSelectedReaderPositions(
      entries
        .map((entry) => parsePositiveInt(entry?.position, 0))
        .filter((value) => value > 0),
    );
  }, [entries]);
  const clearReaderSelection = useCallback(() => {
    setSelectedReaderPositions([]);
  }, []);
  const submitReaderQuestion = useCallback(async (event) => {
    event.preventDefault();
    if (!selectedSourceContext || !cleanText(readerQuestion) || isBusy) {
      return;
    }
    const didSubmit = await submitPromptText(readerQuestion, 'reader_passage', {
      source: 'reader',
      sourceContext: selectedSourceContext,
      personaOverride: normalizedSlug,
    });
    if (didSubmit) {
      setReaderQuestion('');
      navigate(`/author/${normalizedSlug}`);
    }
  }, [isBusy, navigate, normalizedSlug, readerQuestion, selectedSourceContext, submitPromptText]);

  if (loading && !browseItem) {
    return <section className="authors-page"><p className="authors-page__state">Loading reader…</p></section>;
  }

  if (!loading && error && items.length === 0) {
    return <section className="authors-page"><p className="authors-page__state">Unable to load author catalog: {error}</p></section>;
  }

  if (notFound) {
    return (
      <section className="authors-page">
        <div className="author-detail-panel">
          <h1>Author not found</h1>
          <p>The author <code>{normalizedSlug}</code> is not available in this deployment.</p>
          <Link className="author-browse-card__button" to="/authors">Return to author catalog</Link>
        </div>
      </section>
    );
  }

  if (!workLookupPending && decodedSource && works.length > 0 && !selectedWork) {
    return (
      <section className="authors-page">
        <div className="author-detail-panel">
          <h1>Work not found</h1>
          <p>
            The work source <code>{decodedSource}</code> is not available for {displayName}.
          </p>
          <div className="author-browse-card__actions">
            <Link className="author-browse-card__button author-browse-card__button--secondary" to={`/authors/${normalizedSlug}`}>
              Back to author
            </Link>
            <Link className="author-browse-card__button" to={`/author/${normalizedSlug}`}>
              Chat with {displayName}
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="authors-page author-reader-page">
      <div className="author-detail-hero">
        <div className="author-detail-hero__identity">
          <AuthorPortrait author={portraitSource} className="author-detail-hero__portrait" />
          <div className="author-detail-hero__copy">
            <p className="authors-hero__eyebrow">Author Work Reader</p>
            <h1>{workLabel}</h1>
            <p className="author-detail-hero__description">
              Read {displayName} in stable source-backed ranges, then return to the same author for counsel.
            </p>
            <div className="author-detail-hero__chips">
              <span className="author-taxonomy-chip">{displayName}</span>
              {cleanText(readerData?.reference) && (
                <span className="author-taxonomy-chip">{cleanText(readerData.reference)}</span>
              )}
              <span className="author-taxonomy-chip">Source: {cleanText(selectedWork?.source) || decodedSource}</span>
            </div>
          </div>
        </div>
        <div className="author-detail-hero__actions">
          <Link className="author-browse-card__button author-browse-card__button--secondary" to={`/authors/${normalizedSlug}`}>
            Back to author
          </Link>
          <Link className="author-browse-card__button" to={`/author/${normalizedSlug}`}>
            Chat with {displayName}
          </Link>
        </div>
      </div>

      <section className="author-detail-metrics" aria-label="Reader range summary">
        <div className="author-detail-metric">
          <span>Range</span>
          <strong>
            {parsePositiveInt(readerData?.start_position, requestedRange.startPosition)}
            {' '}to{' '}
            {parsePositiveInt(readerData?.end_position, requestedRange.endPosition)}
          </strong>
        </div>
        <div className="author-detail-metric">
          <span>Total positions</span>
          <strong>{parsePositiveInt(readerData?.total_positions, works.length > 0 ? works.length : 0)}</strong>
        </div>
        <div className="author-detail-metric">
          <span>Selected author</span>
          <strong>{displayName}</strong>
        </div>
      </section>

      <div className="author-detail-layout">
        <section className="author-detail-panel author-reader-panel">
          <div className="author-detail-panel__header">
            <div>
              <p className="author-browse-card__label">Source text</p>
              <h2>Reading window</h2>
            </div>
            {(readerLoading || workLookupPending) && (
              <span className="author-detail-panel__status">Loading passage…</span>
            )}
          </div>
          <p className="author-detail-panel__summary">{description}</p>
          <p className="author-reader-panel__note">
            Range navigation uses retrieval positions, not canonical verse numbers.
          </p>
          {authorProfileError && !profileForSlug && (
            <p className="authors-page__state">Unable to load full works list: {authorProfileError}</p>
          )}
          {readerError && (
            <p className="authors-page__state">Unable to load reading window: {readerError}</p>
          )}
          {!readerLoading && !readerError && entries.length === 0 && (
            <p className="authors-page__state">No source text is available for this range yet.</p>
          )}
          {entries.length > 0 && (
            <div className="author-reader-selection-toolbar">
              <div className="author-reader-selection-toolbar__copy">
                <strong>Selected passage</strong>
                <span className="meta-label">
                  {selectedEntries.length > 0
                    ? ` · ${selectedEntries.length} position${selectedEntries.length === 1 ? '' : 's'} anchored`
                    : ' · choose one or more positions to ask about'}
                </span>
              </div>
              <div className="author-reader-selection-toolbar__actions">
                <button
                  type="button"
                  className="history-session-action"
                  onClick={selectCurrentWindow}
                  disabled={readerLoading}
                >
                  Select range
                </button>
                <button
                  type="button"
                  className="history-session-action"
                  onClick={clearReaderSelection}
                  disabled={readerLoading || selectedEntries.length === 0}
                >
                  Clear
                </button>
              </div>
            </div>
          )}
          {entries.length > 0 && (
            <ol className="author-reader-entries" start={parsePositiveInt(readerData?.start_position, requestedRange.startPosition)}>
              {entries.map((entry) => (
                <li
                  key={`${entry.position}-${entry.reference}`}
                  className={`author-reader-entry${selectedEntries.some((item) => item.position === entry.position) ? ' author-reader-entry--selected' : ''}`}
                >
                  <div className="author-reader-entry__meta">
                    <div className="author-reader-entry__heading">
                      <strong>{cleanText(entry.reference) || workLabel}</strong>
                      <span className="meta-label">Position {parsePositiveInt(entry.position, 1)}</span>
                    </div>
                    <button
                      type="button"
                      className="author-browse-card__button author-browse-card__button--secondary author-reader-entry__toggle"
                      onClick={() => toggleReaderPosition(entry.position)}
                    >
                      {selectedEntries.some((item) => item.position === entry.position) ? 'Selected' : 'Select'}
                    </button>
                  </div>
                  <p>{cleanText(entry.content) || 'No content available.'}</p>
                </li>
              ))}
            </ol>
          )}
          <form className="author-reader-composer" onSubmit={submitReaderQuestion}>
            <label htmlFor="readerQuestion" className="author-reader-composer__label">
              Ask {displayName} about the selected passage
            </label>
            <textarea
              id="readerQuestion"
              value={readerQuestion}
              onChange={(event) => setReaderQuestion(event.target.value)}
              placeholder={selectedSourceContext
                ? `Ask ${displayName} about ${sourceContextDisplayLabel || 'the selected source text'}...`
                : 'Select one or more positions to anchor the question.'}
              rows={3}
              disabled={isBusy || !selectedSourceContext}
            />
            <div className="author-reader-composer__meta">
              <p className="author-reader-composer__summary">
                {selectedSourceContext
                  ? `Anchored to ${sourceContextDisplayLabel || workLabel} · positions ${selectedSourceContext.start_position}-${selectedSourceContext.end_position}.`
                  : 'No passage selected yet.'}
              </p>
              <button
                type="submit"
                className="author-browse-card__button"
                disabled={isBusy || !selectedSourceContext || !cleanText(readerQuestion)}
              >
                Ask in chat
              </button>
            </div>
          </form>
          {chatStatus.phase !== 'idle' && (
            <div
              className={`chat-status chat-status--${chatStatus.phase}`}
              role={chatStatus.phase === 'error' || chatStatus.phase === 'timeout' ? 'alert' : 'status'}
              aria-live={chatStatus.phase === 'error' || chatStatus.phase === 'timeout' ? 'assertive' : 'polite'}
            >
              <div className="chat-status__title">{chatStatus.title}</div>
              {chatStatus.message && <p>{chatStatus.message}</p>}
            </div>
          )}
          <div className="author-reader-actions">
            <button
              type="button"
              className="history-session-action"
              onClick={() => navigateToRange(readerData?.previous_position)}
              disabled={readerLoading || !readerData?.previous_position}
            >
              Previous range
            </button>
            <button
              type="button"
              className="history-session-action"
              onClick={() => navigateToRange(readerData?.next_position)}
              disabled={readerLoading || !readerData?.next_position}
            >
              Next range
            </button>
          </div>
        </section>

        <section className="author-detail-panel">
          <div className="author-detail-panel__header">
            <div>
              <p className="author-browse-card__label">Current scope</p>
              <h2>Reader contract</h2>
            </div>
          </div>
          <ul className="author-detail-list">
            <li>
              <div className="author-detail-list__copy">
                <strong>Work</strong>
                <span className="meta-label"> · {workLabel}</span>
              </div>
            </li>
            <li>
              <div className="author-detail-list__copy">
                <strong>Source key</strong>
                <span className="meta-label"> · {cleanText(selectedWork?.source) || decodedSource}</span>
              </div>
            </li>
            <li>
              <div className="author-detail-list__copy">
                <strong>Deep link</strong>
                <span className="meta-label"> · preserves author, source, and range state in the URL</span>
              </div>
            </li>
            <li>
              <div className="author-detail-list__copy">
                <strong>Selected anchor</strong>
                <span className="meta-label">
                  {selectedSourceContext
                    ? ` · ${sourceContextDisplayLabel || workLabel} · positions ${selectedSourceContext.start_position}-${selectedSourceContext.end_position}`
                    : ' · select positions to anchor the next same-author question'}
                </span>
              </div>
            </li>
            {sourceContextReaderHref && (
              <li>
                <div className="author-detail-list__row">
                  <div className="author-detail-list__copy">
                    <strong>Selected reader link</strong>
                    <span className="meta-label"> · opens the exact anchored range</span>
                  </div>
                  <Link
                    className="author-browse-card__button author-browse-card__button--secondary author-detail-list__action"
                    to={sourceContextReaderHref}
                  >
                    Open anchor
                  </Link>
                </div>
              </li>
            )}
          </ul>
        </section>
      </div>
    </section>
  );
}

function ConversationStateCard({
  state,
  loading = false,
  error = '',
  resetting = false,
  onReset = null,
}) {
  const normalizedState = normalizeConversationStatePayload(state);
  const hasState = hasConversationStateContent(normalizedState);
  const stageLabel = formatConversationStageLabel(normalizedState?.stage);
  const openQuestions = normalizedState?.open_questions || [];
  const promises = normalizedState?.promises || [];

  return (
    <div
      className={`conversation-state-card${error ? ' conversation-state-card--error' : ''}`}
      role={error ? 'alert' : undefined}
      aria-live="polite"
    >
      <div className="conversation-state-card__header">
        <div>
          <span className="conversation-state-card__eyebrow">Session memory</span>
          <h2>Conversation State</h2>
        </div>
        {typeof onReset === 'function' && (
          <button
            type="button"
            className="history-session-action"
            onClick={onReset}
            disabled={loading || resetting}
          >
            {resetting ? 'Resetting...' : 'Reset state'}
          </button>
        )}
      </div>
      {loading ? (
        <p className="conversation-state-card__empty">Loading saved state...</p>
      ) : error ? (
        <p className="conversation-state-card__empty">{error}</p>
      ) : !hasState ? (
        <p className="conversation-state-card__empty">
          No derived state yet. The first answered turn will establish the thread memory.
        </p>
      ) : (
        <>
          <div className="conversation-state-card__grid">
            {normalizedState?.current_goal && (
              <div className="conversation-state-card__section">
                <span className="conversation-state-card__label">Current goal</span>
                <p>{normalizedState.current_goal}</p>
              </div>
            )}
            {stageLabel && (
              <div className="conversation-state-card__section">
                <span className="conversation-state-card__label">Stage</span>
                <p>{stageLabel}</p>
              </div>
            )}
            {normalizedState?.next_best_action && (
              <div className="conversation-state-card__section">
                <span className="conversation-state-card__label">Next best action</span>
                <p>{normalizedState.next_best_action}</p>
              </div>
            )}
          </div>
          {openQuestions.length > 0 && (
            <div className="conversation-state-card__section">
              <span className="conversation-state-card__label">Open questions</span>
              <ul className="conversation-state-card__list">
                {openQuestions.map((question) => (
                  <li key={question}>{question}</li>
                ))}
              </ul>
            </div>
          )}
          {promises.length > 0 && (
            <div className="conversation-state-card__section">
              <span className="conversation-state-card__label">Promises already made</span>
              <ul className="conversation-state-card__list">
                {promises.map((promise) => (
                  <li key={promise}>{promise}</li>
                ))}
              </ul>
            </div>
          )}
          {normalizedState?.rolling_summary && (
            <div className="conversation-state-card__section">
              <span className="conversation-state-card__label">Rolling summary</span>
              <p>{normalizedState.rolling_summary}</p>
            </div>
          )}
          {normalizedState?.last_response_summary && (
            <div className="conversation-state-card__section">
              <span className="conversation-state-card__label">Latest response</span>
              <p>{normalizedState.last_response_summary}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RelationshipMemoryCard({
  memory,
  loading = false,
  error = '',
  resetting = false,
  onReset = null,
}) {
  const normalizedMemory = normalizeRelationshipMemoryPayload(memory);
  const hasMemory = hasRelationshipMemoryContent(normalizedMemory);
  const constraints = normalizedMemory?.constraints || [];
  const priorDecisions = normalizedMemory?.prior_decisions || [];
  const followUpCommitments = normalizedMemory?.follow_up_commitments || [];

  return (
    <div
      className={`conversation-state-card relationship-memory-card${error ? ' conversation-state-card--error' : ''}`}
      role={error ? 'alert' : undefined}
      aria-live="polite"
    >
      <div className="conversation-state-card__header">
        <div>
          <span className="conversation-state-card__eyebrow">Durable context</span>
          <h2>Relationship Memory</h2>
        </div>
        {typeof onReset === 'function' && (
          <button
            type="button"
            className="history-session-action"
            onClick={onReset}
            disabled={loading || resetting || !hasMemory}
          >
            {resetting ? 'Resetting...' : 'Reset memory'}
          </button>
        )}
      </div>
      {loading ? (
        <p className="conversation-state-card__empty">Loading durable memory...</p>
      ) : error ? (
        <p className="conversation-state-card__empty">{error}</p>
      ) : !hasMemory ? (
        <p className="conversation-state-card__empty">
          No durable relationship memory is stored for this author yet.
        </p>
      ) : (
        <>
          <div className="conversation-state-card__grid">
            {normalizedMemory?.organization && (
              <div className="conversation-state-card__section">
                <span className="conversation-state-card__label">Organization</span>
                <p>{normalizedMemory.organization}</p>
              </div>
            )}
            {normalizedMemory?.project_type && (
              <div className="conversation-state-card__section">
                <span className="conversation-state-card__label">Project type</span>
                <p>{normalizedMemory.project_type}</p>
              </div>
            )}
            {normalizedMemory?.timeline && (
              <div className="conversation-state-card__section">
                <span className="conversation-state-card__label">Timeline</span>
                <p>{normalizedMemory.timeline}</p>
              </div>
            )}
          </div>
          {constraints.length > 0 && (
            <div className="conversation-state-card__section">
              <span className="conversation-state-card__label">Constraints</span>
              <ul className="conversation-state-card__list">
                {constraints.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          {priorDecisions.length > 0 && (
            <div className="conversation-state-card__section">
              <span className="conversation-state-card__label">Prior decisions</span>
              <ul className="conversation-state-card__list">
                {priorDecisions.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          {followUpCommitments.length > 0 && (
            <div className="conversation-state-card__section">
              <span className="conversation-state-card__label">Follow-up commitments</span>
              <ul className="conversation-state-card__list">
                {followUpCommitments.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          {normalizedMemory?.notes && (
            <div className="conversation-state-card__section">
              <span className="conversation-state-card__label">Relationship summary</span>
              <p>{normalizedMemory.notes}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ChatPage({
  featureMode = '',
  proverbsContent = null,
  proverbsContentLoading = false,
  proverbsContentError = '',
  persona,
  setPersona,
  sessionId,
  launchState,
  authors,
  isAuthenticated,
  favoriteAuthorSlugs,
  defaultAuthorSlug,
  preferencesSaving,
  preferencesError,
  onToggleFavorite,
  onSetDefaultAuthor,
  onSelectMessageReference,
  authorProfile,
  authorProfileLoading,
  authorProfileError,
  crossrefAuthor,
  crossrefAuthorLoading,
  crossrefAuthorError,
  crossrefCatalogLoading,
  crossrefCatalogError,
  referencedCrossrefBooks,
  relatedAuthors,
  curatedRelatedReferences,
  metaInfo,
  messages,
  loading,
  synthesisLoading,
  chatStatus,
  conversationState,
  conversationStateLoading,
  conversationStateError,
  conversationStateResetting,
  onResetConversationState,
  relationshipMemory,
  relationshipMemoryLoading,
  relationshipMemoryError,
  relationshipMemoryResetting,
  onResetRelationshipMemory,
  activeSourceContext,
  onClearSourceContext,
  onRetryLastMessage,
  onRetrySynthesis,
  references,
  expandReference,
  error,
  query,
  setQuery,
  matterAnchor,
  counselAuthorSlugs,
  counselEntries,
  counselSynthesis,
  onAddCounsel,
  onSynthesizeCounsel,
  onRemoveCounselAuthor,
  submitPromptText,
  getPersonaName,
  messagesEndRef,
  runtimeDebug,
}) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const crossrefBookRequestRef = useRef(0);
  const authorCatalogCacheRef = useRef(new Map());
  const messagesContainerRef = useRef(null);
  const normalizedSlug = String(slug || '').trim().toLowerCase();
  const authorSlugSet = useMemo(
    () => new Set(authors.map((author) => String(author?.slug || '').toLowerCase()).filter(Boolean)),
    [authors],
  );
  const invalidAuthorRoute = Boolean(normalizedSlug) && authors.length > 0 && !authorSlugSet.has(normalizedSlug);
  const fallbackAuthorSlug = authors[0]?.slug || '';
  const [selectedCrossrefBookId, setSelectedCrossrefBookId] = useState('');
  const [selectedCrossrefBook, setSelectedCrossrefBook] = useState(null);
  const [selectedCrossrefBookLoading, setSelectedCrossrefBookLoading] = useState(false);
  const [selectedCrossrefBookError, setSelectedCrossrefBookError] = useState('');
  const [selectedCrossrefBookPreview, setSelectedCrossrefBookPreview] = useState(null);
  const [selectedCrossrefBookPreviewLoading, setSelectedCrossrefBookPreviewLoading] = useState(false);
  const [selectedCrossrefBookPreviewError, setSelectedCrossrefBookPreviewError] = useState('');
  const [selectedScriptureReferenceKey, setSelectedScriptureReferenceKey] = useState('');
  const [selectedScriptureVerse, setSelectedScriptureVerse] = useState(null);
  const [selectedScriptureVerseLoading, setSelectedScriptureVerseLoading] = useState(false);
  const [selectedScriptureVerseError, setSelectedScriptureVerseError] = useState('');
  const [landingApiCues, setLandingApiCues] = useState(null);
  const readAloudRequestRef = useRef(0);
  const readAloudCacheRef = useRef(new Map());
  const previousPersonaRef = useRef(persona);
  const [activeReadAloudKey, setActiveReadAloudKey] = useState('');
  const [readAloudStatus, setReadAloudStatus] = useState(createReadAloudStatus('idle', '', ''));
  const readAloudSupported = typeof window !== 'undefined'
    && typeof window.fetch === 'function'
    && typeof window.Audio === 'function';
  const speechRecognitionSupported = Boolean(getBrowserSpeechRecognition());
  const speechRecognitionRef = useRef(null);
  const speechFinalTranscriptRef = useRef('');
  const conversationActiveRef = useRef(false);
  const voiceTurnRef = useRef(0);
  const latestAssistantSpeechKeyRef = useRef('');
  const [conversationActive, setConversationActive] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState({ ...VOICE_STATUS_IDLE });
  const isAskProverbs = featureMode === ASK_PROVERBS_MODE;
  const normalizedSourceContext = useMemo(
    () => normalizeSourceContextPayload(activeSourceContext),
    [activeSourceContext],
  );
  const showConversationState = isAuthenticated
    && !isAskProverbs
    && (
      Boolean(conversationState)
      || conversationStateLoading
      || Boolean(conversationStateError)
      || messages.some((message) => cleanText(message?.role).toLowerCase() === 'assistant')
    );
  const showRelationshipMemory = isAuthenticated && !isAskProverbs;
  const activeSourceReaderHref = normalizedSourceContext
    ? buildAuthorWorkReaderHref({
      authorSlug: normalizedSourceContext.author_slug,
      source: normalizedSourceContext.source,
      startPosition: normalizedSourceContext.start_position,
      endPosition: normalizedSourceContext.end_position,
    })
    : '';
  const submitScopedPromptText = useCallback(
    (rawText, origin = 'composer') => submitPromptText(
      rawText,
      origin,
      isAskProverbs ? { mode: ASK_PROVERBS_MODE } : {},
    ),
    [isAskProverbs, submitPromptText],
  );
  const applyFollowUpQuestion = useCallback((questionText) => {
    const nextQuestion = cleanText(questionText);
    if (!nextQuestion) {
      return;
    }
    setQuery(nextQuestion);
    trackEvent('follow_up_question_selected', {
      persona,
      mode: isAskProverbs ? ASK_PROVERBS_MODE : 'conversation',
    });
    window.requestAnimationFrame(() => {
      document.getElementById('chatQuestion')?.focus();
    });
  }, [isAskProverbs, persona, setQuery]);

  const getCachedReadAloudEntry = useCallback((cacheKey, chunkCount) => {
    const cache = readAloudCacheRef.current;
    const entry = cache.get(cacheKey);
    const minimumChunkCount = Number(chunkCount || 0) > 0 ? 1 : 0;
    const hasExpectedChunks = entry
      && Array.isArray(entry.chunks)
      && entry.chunks.length >= minimumChunkCount
      && entry.chunks.every((chunk) => chunk?.playbackUrl);
    if (!hasExpectedChunks) {
      if (entry) {
        cache.delete(cacheKey);
        releaseReadAloudCacheEntry(entry);
      }
      return null;
    }
    cache.delete(cacheKey);
    cache.set(cacheKey, entry);
    return entry;
  }, []);

  const storeReadAloudCacheEntry = useCallback((cacheKey, entry) => {
    if (!cacheKey || !entry || !Array.isArray(entry.chunks) || entry.chunks.length === 0) {
      return;
    }
    const cache = readAloudCacheRef.current;
    const previous = cache.get(cacheKey);
    if (previous && previous !== entry) {
      releaseReadAloudCacheEntry(previous);
    }
    cache.set(cacheKey, entry);
    pruneReadAloudCache(cache);
  }, []);

  const removeReadAloudCacheEntry = useCallback((cacheKey) => {
    const cache = readAloudCacheRef.current;
    const entry = cache.get(cacheKey);
    if (entry) {
      cache.delete(cacheKey);
      releaseReadAloudCacheEntry(entry);
    }
  }, []);

  const clearReadAloudCache = useCallback(() => {
    readAloudCacheRef.current.forEach((entry) => releaseReadAloudCacheEntry(entry));
    readAloudCacheRef.current.clear();
  }, []);

  const stopReadAloud = useCallback(() => {
    readAloudRequestRef.current += 1;
    setActiveReadAloudKey('');
    setReadAloudStatus(createReadAloudStatus('idle', '', ''));
    vibeVoiceAudioManager.stop();
    browserSpeechManager.stop();
  }, []);

  const startBrowserReadAloudFallback = useCallback(async (targetKey, text, reason = 'Manual browser fallback') => {
    const readableText = String(text || '').trim();
    if (!targetKey || !readableText) {
      return false;
    }

    readAloudRequestRef.current += 1;
    const requestId = readAloudRequestRef.current;
    const timings = {
      requestedAt: nowMs(),
      requestId: createTtsRequestId(),
      browserFallback: true,
      fallbackReason: reason,
    };
    vibeVoiceAudioManager.stop();
    browserSpeechManager.stop();
    setActiveReadAloudKey(targetKey);
    setReadAloudStatus(createReadAloudStatus('loading', targetKey, 'Using browser voice fallback...', timings));
    trackEvent('vibevoice_speech_browser_fallback_started', {
      request_id: timings.requestId,
      reason,
      elapsed_ms: 0,
      manual: true,
    });

    try {
      await browserSpeechManager.play(readableText, {
        onstarted: () => {
          if (readAloudRequestRef.current !== requestId) {
            return;
          }
          timings.playStartedMs = timings.playStartedMs || roundMs(nowMs() - timings.requestedAt);
          setReadAloudStatus(createReadAloudStatus('playing', targetKey, 'Playing speech with browser voice.', timings));
        },
        onended: () => {
          if (readAloudRequestRef.current === requestId) {
            stopReadAloud();
          }
        },
        onerror: () => {
          if (readAloudRequestRef.current === requestId) {
            setReadAloudStatus(createReadAloudStatus('error', targetKey, 'Browser speech failed.', timings));
          }
        },
      });
      return true;
    } catch (error) {
      console.warn('Unable to play browser speech fallback', error);
      trackEvent('vibevoice_speech_browser_fallback_failed', {
        request_id: timings.requestId,
        reason: error?.message || error?.name || 'unknown',
        elapsed_ms: roundMs(nowMs() - timings.requestedAt),
        manual: true,
      });
      setReadAloudStatus(createReadAloudStatus('error', targetKey, 'Browser speech failed.', timings));
      return false;
    }
  }, [stopReadAloud]);

  const startReadAloud = useCallback(async (targetKey, text) => {
    const readableText = String(text || '').trim();
    if (!targetKey || !readableText) {
      return false;
    }

    readAloudRequestRef.current += 1;
    const requestId = readAloudRequestRef.current;
    const chunks = splitSpeakTextIntoChunks(readableText);
    const cacheKey = createReadAloudCacheKey(targetKey, readableText);
    const cachedEntry = getCachedReadAloudEntry(cacheKey, chunks.length);
    const chunkCount = cachedEntry?.chunks?.length || chunks.length;
    const ttsRequestId = cachedEntry?.requestId || createTtsRequestId();
    const timings = {
      requestedAt: nowMs(),
      requestId: ttsRequestId,
      chunkCount,
      chunkIndex: 0,
      currentChunk: chunkCount > 1 ? `0/${chunkCount}` : '',
      cacheHit: Boolean(cachedEntry),
    };
    vibeVoiceAudioManager.stop();
    browserSpeechManager.stop();
    setActiveReadAloudKey(targetKey);
    setReadAloudStatus(createReadAloudStatus(
      cachedEntry ? 'loading' : 'generating',
      targetKey,
      cachedEntry
        ? (chunkCount > 1 ? 'Loading cached speech chunks...' : 'Loading cached speech...')
        : (chunks.length > 1 ? `Sending speech request 1/${chunks.length}...` : 'Sending speech request...'),
      timings,
    ));

    const voiceOffline = (error) => {
      const message = String(error?.message || '');
      return [502, 503, 504].includes(Number(error?.status || 0))
        || /VibeVoice service .*offline|VibeVoice service .*unavailable|Connection refused|Failed to fetch/i.test(message);
    };

    try {
      const generatedCacheChunks = [];
      const storeGeneratedCacheIfComplete = () => {
        const expectedChunkCount = Math.max(1, Number(timings.chunkCount || chunks.length || 1));
        const complete = Array.from({ length: expectedChunkCount })
          .every((_, index) => generatedCacheChunks[index]?.playbackUrl);
        if (!complete) {
          return;
        }
        storeReadAloudCacheEntry(cacheKey, {
          requestId: timings.requestId || ttsRequestId,
          targetKey,
          text: readableText,
          createdAt: Date.now(),
          chunks: Array.from({ length: expectedChunkCount }, (_, index) => ({ ...generatedCacheChunks[index] })),
        });
      };
      const cacheChunkForPlayback = async (chunkResult) => {
        const cachedChunk = await buildReadAloudCacheChunk(chunkResult);
        generatedCacheChunks[chunkResult.chunkIndex] = cachedChunk;
        storeGeneratedCacheIfComplete();
        return cachedChunk;
      };
      const playCachedSpeech = async () => {
        trackEvent('vibevoice_speech_cache_hit', {
          request_id: timings.requestId || ttsRequestId,
          chunk_count: cachedEntry.chunks.length,
        });
        try {
          for (let chunkIndex = 0; chunkIndex < cachedEntry.chunks.length; chunkIndex += 1) {
            const cachedChunk = cachedEntry.chunks[chunkIndex];
            timings.chunkIndex = cachedChunk.chunkNumber || chunkIndex + 1;
            timings.currentChunk = cachedEntry.chunks.length > 1 ? `${timings.chunkIndex}/${cachedEntry.chunks.length}` : '';
            timings.currentChunkRequestId = cachedChunk.requestId || '';
            timings.ttsCompleteMs = 0;
            setReadAloudStatus(createReadAloudStatus(
              'loading',
              targetKey,
              cachedEntry.chunks.length > 1
                ? `Loading cached speech chunk ${timings.chunkIndex}/${cachedEntry.chunks.length}...`
                : 'Loading cached speech...',
              timings,
            ));
            await vibeVoiceAudioManager.playUntilEnded(cachedChunk.playbackUrl, {
              onstarted: () => {
                if (readAloudRequestRef.current !== requestId) {
                  return;
                }
                timings.playStartedMs = timings.playStartedMs || roundMs(nowMs() - timings.requestedAt);
                setReadAloudStatus(createReadAloudStatus(
                  'playing',
                  targetKey,
                  cachedEntry.chunks.length > 1
                    ? `Playing cached speech chunk ${timings.chunkIndex}/${cachedEntry.chunks.length}.`
                    : 'Playing cached speech.',
                  timings,
                ));
                if (chunkIndex === 0) {
                  trackEvent('vibevoice_speech_replay_started', {
                    request_id: timings.requestId || ttsRequestId,
                    play_started_ms: timings.playStartedMs || 0,
                    chunk_count: cachedEntry.chunks.length,
                  });
                }
              },
              onerror: () => {
                if (readAloudRequestRef.current === requestId) {
                  setReadAloudStatus(createReadAloudStatus('error', targetKey, 'Cached audio playback failed.', timings));
                }
              },
            });
            if (readAloudRequestRef.current !== requestId) {
              return false;
            }
          }
          if (readAloudRequestRef.current === requestId) {
            stopReadAloud();
          }
          return true;
        } catch (cacheError) {
          removeReadAloudCacheEntry(cacheKey);
          throw cacheError;
        }
      };
      if (cachedEntry) {
        return await playCachedSpeech();
      }

      const applySessionTimings = (session, chunk = null) => {
        timings.sessionId = session?.session_id || timings.sessionId || '';
        timings.chunkCount = session?.chunk_count || timings.chunkCount;
        if (session?.timings?.first_audio_ready_ms != null) {
          timings.firstAudioReadyMs = session.timings.first_audio_ready_ms;
        }
        if (session?.timings?.complete_ms != null) {
          timings.sessionCompleteMs = session.timings.complete_ms;
        }
        if (chunk) {
          timings.chunkIndex = chunk.chunk_number || (Number(chunk.index || 0) + 1);
          timings.currentChunk = timings.chunkCount > 1 ? `${timings.chunkIndex}/${timings.chunkCount}` : '';
          timings.currentChunkRequestId = chunk.request_id || '';
          timings.proxyCreateElapsedMs = chunk.proxy_timings?.proxy_create_elapsed_ms || timings.proxyCreateElapsedMs || '';
          timings.proxyPollElapsedMs = chunk.proxy_timings?.proxy_poll_elapsed_ms || timings.proxyPollElapsedMs || '';
        }
      };
      const pollSessionUntilChunkReady = async (sessionId, chunkIndexToWaitFor) => {
        let session = null;
        for (let poll = 0; poll < VIBEVOICE_MAX_POLLS; poll += 1) {
          if (readAloudRequestRef.current !== requestId) {
            return null;
          }
          session = await fetchVibeVoiceJson(
            `${VIBEVOICE_TTS_SESSIONS_API_ENDPOINT}/${encodeURIComponent(sessionId)}?request_id=${encodeURIComponent(ttsRequestId)}`,
          );
          const chunk = Array.isArray(session?.chunks)
            ? session.chunks.find((item) => Number(item?.index) === chunkIndexToWaitFor)
            : null;
          applySessionTimings(session, chunk);
          timings.polls = (timings.polls || 0) + 1;
          timings.lastPollMs = roundMs(nowMs() - timings.requestedAt);
          if (session?.status === 'failed' || chunk?.status === 'failed') {
            throw new Error(chunk?.detail || session?.detail || 'VibeVoice session failed');
          }
          if (chunk?.proxy_audio_url || chunk?.audio_url) {
            return { session, chunk };
          }
          setReadAloudStatus(createReadAloudStatus(
            'generating',
            targetKey,
            `Generating speech chunk ${chunkIndexToWaitFor + 1}/${timings.chunkCount}... ${timings.lastPollMs}ms`,
            timings,
          ));
          await sleep(VIBEVOICE_POLL_INTERVAL_MS);
        }
        throw new Error('VibeVoice session timed out');
      };
      const playSessionSpeech = async () => {
        let session = await fetchVibeVoiceJson(VIBEVOICE_TTS_SESSIONS_API_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text: readableText,
            request_id: ttsRequestId,
            chunk_target_chars: VIBEVOICE_CHUNK_TARGET_CHARS,
            chunk_max_chars: VIBEVOICE_CHUNK_MAX_CHARS,
          }),
        });
        applySessionTimings(session);
        if (!timings.sessionId) {
          throw new Error('VibeVoice session did not return a session id.');
        }
        setReadAloudStatus(createReadAloudStatus(
          'generating',
          targetKey,
          `Generating speech chunk 1/${timings.chunkCount}...`,
          timings,
        ));

        let nextChunkReady = null;
        for (let chunkIndex = 0; chunkIndex < timings.chunkCount; chunkIndex += 1) {
          const initialChunk = Array.isArray(session?.chunks)
            ? session.chunks.find((item) => Number(item?.index) === chunkIndex)
            : null;
          const ready = initialChunk?.proxy_audio_url || initialChunk?.audio_url
            ? { session, chunk: initialChunk }
            : await pollSessionUntilChunkReady(timings.sessionId, chunkIndex);
          if (!ready || readAloudRequestRef.current !== requestId) {
            return false;
          }
          session = ready.session;
          const chunk = ready.chunk;
          applySessionTimings(session, chunk);
          if (!timings.firstAudioReadyMs) {
            timings.firstAudioReadyMs = roundMs(nowMs() - timings.requestedAt);
          }
          if (chunkIndex === 0) {
            trackEvent('vibevoice_speech_first_chunk_ready', {
              request_id: timings.requestId || ttsRequestId,
              session_id: timings.sessionId,
              chunk_count: timings.chunkCount,
              first_audio_ready_ms: timings.firstAudioReadyMs,
            });
          }
          if (chunkIndex + 1 < timings.chunkCount) {
            nextChunkReady = pollSessionUntilChunkReady(timings.sessionId, chunkIndex + 1);
          }
          timings.ttsCompleteMs = roundMs(nowMs() - timings.requestedAt);
          setReadAloudStatus(createReadAloudStatus(
            'loading',
            targetKey,
            `Loading speech chunk ${chunkIndex + 1}/${timings.chunkCount}...`,
            timings,
          ));
          const cachedChunk = await cacheChunkForPlayback({
            audioUrl: chunk.proxy_audio_url || chunk.audio_url,
            chunkIndex,
            chunkNumber: chunk.chunk_number || chunkIndex + 1,
            requestId: chunk.request_id || timings.currentChunkRequestId,
          });
          await vibeVoiceAudioManager.playUntilEnded(cachedChunk.playbackUrl, {
            onstarted: () => {
              if (readAloudRequestRef.current !== requestId) {
                return;
              }
              timings.playStartedMs = timings.playStartedMs || roundMs(nowMs() - timings.requestedAt);
              setReadAloudStatus(createReadAloudStatus(
                'playing',
                targetKey,
                `Playing speech chunk ${chunkIndex + 1}/${timings.chunkCount}.`,
                timings,
              ));
              if (chunkIndex === 0) {
                trackEvent('vibevoice_speech_play_started', {
                  request_id: timings.requestId || ttsRequestId,
                  session_id: timings.sessionId,
                  tts_latency_ms: timings.ttsCompleteMs || 0,
                  first_audio_ready_ms: timings.firstAudioReadyMs || 0,
                  play_started_ms: timings.playStartedMs || 0,
                  polls: timings.polls || 0,
                  chunk_count: timings.chunkCount,
                });
              }
            },
            onerror: () => {
              if (readAloudRequestRef.current === requestId) {
                setReadAloudStatus(createReadAloudStatus('error', targetKey, 'Audio playback failed.', timings));
              }
            },
          });
          if (readAloudRequestRef.current !== requestId) {
            return false;
          }
          if (nextChunkReady && chunkIndex + 1 < timings.chunkCount) {
            const nextReady = await nextChunkReady;
            session = nextReady?.session || session;
            nextChunkReady = Promise.resolve(nextReady);
          }
        }
        if (readAloudRequestRef.current === requestId) {
          stopReadAloud();
        }
        return true;
      };

      if (chunks.length > 1) {
        try {
          return await playSessionSpeech();
        } catch (sessionError) {
          if (![404, 405].includes(Number(sessionError?.status || 0))) {
            throw sessionError;
          }
          timings.sessionFallback = true;
        }
      }

      const synthesizeChunk = async (chunkText, chunkIndex) => {
        const chunkNumber = chunkIndex + 1;
        const chunkRequestId = chunks.length > 1
          ? `${ttsRequestId}:chunk-${chunkNumber}-of-${chunks.length}`
          : ttsRequestId;
        timings.chunkIndex = chunkNumber;
        timings.currentChunk = chunks.length > 1 ? `${chunkNumber}/${chunks.length}` : '';
        timings.currentChunkRequestId = chunkRequestId;
        setReadAloudStatus(createReadAloudStatus(
          'generating',
          targetKey,
          chunks.length > 1
            ? `Generating speech chunk ${chunkNumber}/${chunks.length}...`
            : 'Generating speech...',
          timings,
        ));
        let job = await fetchVibeVoiceJson(VIBEVOICE_TTS_JOBS_API_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: chunkText, request_id: chunkRequestId }),
        });
        const returnedRequestId = String(job?.request_id || '').trim();
        if (returnedRequestId) {
          timings.currentChunkRequestId = returnedRequestId;
        }
        timings.jobAcceptedMs = roundMs(nowMs() - timings.requestedAt);
        timings.proxyCreateElapsedMs = job?.proxy_timings?.proxy_create_elapsed_ms || '';
        const jobId = String(job?.job_id || '').trim();
        if (!jobId) {
          throw new Error('VibeVoice did not return a job id.');
        }
        for (let poll = 0; poll < VIBEVOICE_MAX_POLLS && job?.status !== 'completed'; poll += 1) {
          if (job?.status === 'failed') {
            throw new Error(job?.detail || 'VibeVoice generation failed');
          }
          await sleep(VIBEVOICE_POLL_INTERVAL_MS);
          if (readAloudRequestRef.current !== requestId) {
            return null;
          }
          job = await fetchVibeVoiceJson(
            `${VIBEVOICE_TTS_JOBS_API_ENDPOINT}/${encodeURIComponent(jobId)}?request_id=${encodeURIComponent(timings.currentChunkRequestId || chunkRequestId)}`,
          );
          if (job?.request_id) {
            timings.currentChunkRequestId = String(job.request_id);
          }
          timings.polls = (timings.polls || 0) + 1;
          timings.lastPollMs = roundMs(nowMs() - timings.requestedAt);
          timings.proxyPollElapsedMs = job?.proxy_timings?.proxy_poll_elapsed_ms || '';
          setReadAloudStatus(createReadAloudStatus(
            'generating',
            targetKey,
            chunks.length > 1
              ? `Generating speech chunk ${chunkNumber}/${chunks.length}... ${timings.lastPollMs}ms`
              : `Generating speech... ${timings.lastPollMs}ms`,
            timings,
          ));
        }
        if (job?.status === 'failed') {
          throw new Error(job?.detail || 'VibeVoice generation failed');
        }
        const audioUrl = String(job?.proxy_audio_url || job?.audio_url || '').trim();
        if (job?.status !== 'completed' || !audioUrl) {
          throw new Error('VibeVoice generation timed out');
        }
        return {
          audioUrl,
          chunkIndex,
          chunkNumber,
          requestId: timings.currentChunkRequestId || chunkRequestId,
        };
      };

      const queueSynthesis = (chunkIndex) => synthesizeChunk(chunks[chunkIndex], chunkIndex)
        .then((result) => ({ result }))
        .catch((error) => ({ error }));
      let nextSynthesis = queueSynthesis(0);
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex += 1) {
        const { result: chunkResult, error: chunkError } = await nextSynthesis;
        if (chunkError) {
          throw chunkError;
        }
        if (!chunkResult || readAloudRequestRef.current !== requestId) {
          return false;
        }
        timings.chunkIndex = chunkResult.chunkNumber;
        timings.currentChunk = chunks.length > 1 ? `${chunkResult.chunkNumber}/${chunks.length}` : '';
        timings.currentChunkRequestId = chunkResult.requestId;
        if (!timings.firstAudioReadyMs) {
          timings.firstAudioReadyMs = roundMs(nowMs() - timings.requestedAt);
          trackEvent('vibevoice_speech_first_chunk_ready', {
            request_id: timings.requestId || ttsRequestId,
            chunk_count: chunks.length,
            first_audio_ready_ms: timings.firstAudioReadyMs,
          });
        }
        if (chunkIndex + 1 < chunks.length) {
          nextSynthesis = queueSynthesis(chunkIndex + 1);
        }
        timings.ttsCompleteMs = roundMs(nowMs() - timings.requestedAt);
        setReadAloudStatus(createReadAloudStatus(
          'loading',
          targetKey,
          chunks.length > 1
            ? `Loading speech chunk ${chunkResult.chunkNumber}/${chunks.length}...`
            : 'Loading audio...',
          timings,
        ));
        const cachedChunk = await cacheChunkForPlayback(chunkResult);
        await vibeVoiceAudioManager.playUntilEnded(cachedChunk.playbackUrl, {
          onstarted: () => {
            if (readAloudRequestRef.current !== requestId) {
              return;
            }
            timings.playStartedMs = timings.playStartedMs || roundMs(nowMs() - timings.requestedAt);
            setReadAloudStatus(createReadAloudStatus(
              'playing',
              targetKey,
              chunks.length > 1
                ? `Playing speech chunk ${chunkResult.chunkNumber}/${chunks.length}.`
                : 'Playing speech.',
              timings,
            ));
            if (chunkIndex === 0) {
              trackEvent('vibevoice_speech_play_started', {
                request_id: timings.requestId || ttsRequestId,
                tts_latency_ms: timings.ttsCompleteMs || 0,
                first_audio_ready_ms: timings.firstAudioReadyMs || 0,
                play_started_ms: timings.playStartedMs || 0,
                polls: timings.polls || 0,
                chunk_count: chunks.length,
              });
            }
          },
          onerror: () => {
            if (readAloudRequestRef.current === requestId) {
              setReadAloudStatus(createReadAloudStatus('error', targetKey, 'Audio playback failed.', timings));
            }
          },
        });
        if (readAloudRequestRef.current !== requestId) {
          return false;
        }
      }
      if (readAloudRequestRef.current === requestId) {
        stopReadAloud();
      }
    } catch (error) {
      if (readAloudRequestRef.current !== requestId || error?.name === 'AbortError') {
        trackEvent('vibevoice_speech_aborted', {
          request_id: timings.requestId || ttsRequestId,
          reason: error?.name || 'stale_request',
          elapsed_ms: roundMs(nowMs() - timings.requestedAt),
        });
        return false;
      }
      console.warn('Unable to play VibeVoice speech', error);
      trackEvent('vibevoice_speech_failed', {
        request_id: timings.requestId || ttsRequestId,
        reason: error?.message || error?.name || 'unknown',
        elapsed_ms: roundMs(nowMs() - timings.requestedAt),
        polls: timings.polls || 0,
      });
      if (voiceOffline(error)) {
        timings.browserFallbackAvailable = true;
        timings.fallbackReason = error?.message || error?.name || 'VibeVoice unavailable';
        timings.fallbackText = readableText;
        setReadAloudStatus(createReadAloudStatus(
          'error',
          targetKey,
          'VibeVoice API is unavailable. Browser fallback is available.',
          timings,
        ));
        return false;
      }
      setReadAloudStatus(createReadAloudStatus('error', targetKey, error?.message || 'Unable to play speech.', timings));
      stopReadAloud();
      return false;
    }
    return true;
  }, [
    getCachedReadAloudEntry,
    removeReadAloudCacheEntry,
    stopReadAloud,
    storeReadAloudCacheEntry,
  ]);

  const stopVoiceInput = useCallback((nextStatus = VOICE_STATUS_IDLE) => {
    const recognition = speechRecognitionRef.current;
    speechRecognitionRef.current = null;
    if (recognition) {
      recognition.onstart = null;
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      try {
        recognition.abort();
      } catch {
        // Ignore browser recognition cleanup failures.
      }
    }
    speechFinalTranscriptRef.current = '';
    setVoiceStatus({ ...nextStatus });
  }, []);

  const stopConversation = useCallback((message = '') => {
    conversationActiveRef.current = false;
    setConversationActive(false);
    voiceTurnRef.current += 1;
    stopVoiceInput({ phase: 'idle', message });
  }, [stopVoiceInput]);

  const startVoiceInput = useCallback((autoSubmit = false) => {
    const Recognition = getBrowserSpeechRecognition();
    if (!Recognition) {
      setVoiceStatus({
        phase: 'error',
        message: 'Speech recognition is not available in this browser.',
      });
      return false;
    }
    if (loading || synthesisLoading) {
      setVoiceStatus({
        phase: 'thinking',
        message: 'Wait for the current response to finish before speaking again.',
      });
      return false;
    }

    stopVoiceInput({ phase: 'idle', message: '' });
    stopReadAloud();
    speechFinalTranscriptRef.current = '';

    const recognition = new Recognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    speechRecognitionRef.current = recognition;

    recognition.onstart = () => {
      setVoiceStatus({
        phase: 'listening',
        message: autoSubmit ? 'Listening. Finish your thought and I will send it.' : 'Listening. Your words will appear in the composer.',
      });
    };

    recognition.onresult = (event) => {
      let interim = '';
      let finalText = '';
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const transcript = event.results[index]?.[0]?.transcript || '';
        if (event.results[index]?.isFinal) {
          finalText += transcript;
        } else {
          interim += transcript;
        }
      }
      const visibleTranscript = cleanText(finalText || interim);
      if (visibleTranscript) {
        setQuery(visibleTranscript);
      }
      if (finalText) {
        speechFinalTranscriptRef.current = cleanText(
          `${speechFinalTranscriptRef.current} ${finalText}`,
        );
      }
    };

    recognition.onerror = (event) => {
      const errorName = cleanText(event?.error);
      const recoverableSilence = errorName === 'no-speech' || errorName === 'aborted';
      if (conversationActiveRef.current && recoverableSilence) {
        setVoiceStatus({
          phase: 'listening',
          message: 'Still listening.',
        });
        return;
      }
      if (conversationActiveRef.current) {
        stopConversation(errorName ? `Voice input stopped: ${errorName}.` : 'Voice input stopped.');
        return;
      }
      setVoiceStatus({
        phase: 'error',
        message: errorName ? `Voice input failed: ${errorName}.` : 'Voice input failed.',
      });
    };

    recognition.onend = () => {
      if (speechRecognitionRef.current === recognition) {
        speechRecognitionRef.current = null;
      }
      const finalText = cleanText(speechFinalTranscriptRef.current);
      speechFinalTranscriptRef.current = '';

      if (autoSubmit && conversationActiveRef.current) {
        if (!finalText) {
          window.setTimeout(() => {
            if (conversationActiveRef.current && !loading && !synthesisLoading) {
              startVoiceInput(true);
            }
          }, 400);
          return;
        }

        const turnId = voiceTurnRef.current + 1;
        voiceTurnRef.current = turnId;
        setVoiceStatus({
          phase: 'thinking',
          message: 'Sending your turn.',
        });
        void submitScopedPromptText(finalText, 'voice_conversation').then((ok) => {
          if (!ok && conversationActiveRef.current && voiceTurnRef.current === turnId) {
            stopConversation('Conversation paused after the request failed.');
          }
        });
        return;
      }

      setVoiceStatus({
        phase: finalText ? 'idle' : 'idle',
        message: finalText ? 'Transcript ready. Send when you are ready.' : '',
      });
    };

    try {
      recognition.start();
      trackEvent(autoSubmit ? 'voice_conversation_listen_started' : 'voice_dictation_started', {
        persona,
      });
      return true;
    } catch (error) {
      speechRecognitionRef.current = null;
      setVoiceStatus({
        phase: 'error',
        message: error?.message || 'Unable to start voice input.',
      });
      return false;
    }
  }, [
    loading,
    persona,
    setQuery,
    stopConversation,
    stopReadAloud,
    stopVoiceInput,
    submitScopedPromptText,
    synthesisLoading,
  ]);

  const toggleConversation = useCallback(() => {
    if (conversationActiveRef.current) {
      stopConversation('Conversation paused.');
      stopReadAloud();
      return;
    }
    conversationActiveRef.current = true;
    setConversationActive(true);
    setVoiceStatus({
      phase: 'listening',
      message: 'Listening. Speak naturally.',
    });
    startVoiceInput(true);
  }, [startVoiceInput, stopConversation, stopReadAloud]);

  useEffect(() => {
    if (!conversationActive || loading || synthesisLoading) {
      return;
    }
    const lastIndex = messages.length - 1;
    const latest = lastIndex >= 0 ? messages[lastIndex] : null;
    if (!latest || latest.role !== 'assistant') {
      return;
    }
    const speechKey = `assistant:${lastIndex}:${normalizeBookKey(latest.message).slice(0, 80)}`;
    if (latestAssistantSpeechKeyRef.current === speechKey) {
      return;
    }
    latestAssistantSpeechKeyRef.current = speechKey;
    const turnId = voiceTurnRef.current;
    setVoiceStatus({
      phase: 'speaking',
      message: `${cleanText(latest.persona) || getPersonaName(persona)} is answering.`,
    });
    void startReadAloud(`conversation:${lastIndex}`, latest.message).then(() => {
      if (!conversationActiveRef.current || voiceTurnRef.current !== turnId) {
        return;
      }
      setVoiceStatus({
        phase: 'listening',
        message: 'Listening for your next turn.',
      });
      window.setTimeout(() => {
        if (conversationActiveRef.current && voiceTurnRef.current === turnId && !loading && !synthesisLoading) {
          startVoiceInput(true);
        }
      }, 250);
    });
  }, [
    conversationActive,
    getPersonaName,
    loading,
    messages,
    persona,
    startReadAloud,
    startVoiceInput,
    synthesisLoading,
  ]);

  useEffect(() => () => {
    stopConversation();
    stopReadAloud();
  }, [stopConversation, stopReadAloud]);

  useEffect(() => {
    if (!normalizedSlug) {
      return;
    }
    if (authors.length > 0 && !authorSlugSet.has(normalizedSlug)) {
      return;
    }
    if (normalizedSlug !== persona) {
      setPersona(normalizedSlug);
    }
  }, [normalizedSlug, authors.length, authorSlugSet, persona, setPersona]);

  useEffect(() => {
    if (isAskProverbs && persona !== ASK_PROVERBS_PERSONA) {
      setPersona(ASK_PROVERBS_PERSONA);
    }
  }, [isAskProverbs, persona, setPersona]);

  useEffect(() => {
    if (!selectedCrossrefBookId) {
      return;
    }
    const stillVisible = referencedCrossrefBooks.some(
      (item) => String(item.book_id || '').toUpperCase() === selectedCrossrefBookId,
    );
    if (!stillVisible) {
      setSelectedCrossrefBookId('');
      setSelectedCrossrefBook(null);
      setSelectedCrossrefBookError('');
      setSelectedCrossrefBookLoading(false);
      setSelectedCrossrefBookPreview(null);
      setSelectedCrossrefBookPreviewError('');
      setSelectedCrossrefBookPreviewLoading(false);
    }
  }, [referencedCrossrefBooks, selectedCrossrefBookId]);

  useEffect(() => () => {
    stopReadAloud();
    clearReadAloudCache();
  }, [clearReadAloudCache, stopReadAloud]);

  useEffect(() => {
    if (messages.length === 0) {
      if (activeReadAloudKey) {
        stopReadAloud();
      }
      clearReadAloudCache();
    }
  }, [messages.length, activeReadAloudKey, clearReadAloudCache, stopReadAloud]);

  useEffect(() => {
    if (previousPersonaRef.current !== persona) {
      previousPersonaRef.current = persona;
      stopReadAloud();
      clearReadAloudCache();
    }
  }, [clearReadAloudCache, persona, stopReadAloud]);

  useEffect(() => {
    if (!selectedScriptureReferenceKey) {
      return;
    }
    const stillVisible = references.some((ref) => {
      const refKey = normalizeBookKey(
        buildScriptureReference(ref?.book, ref?.chapter, ref?.verseStart),
      );
      return refKey === selectedScriptureReferenceKey;
    });
    if (!stillVisible) {
      setSelectedScriptureReferenceKey('');
      setSelectedScriptureVerse(null);
      setSelectedScriptureVerseError('');
      setSelectedScriptureVerseLoading(false);
    }
  }, [references, selectedScriptureReferenceKey]);

  const activePersonaName = getPersonaName(persona);
  const busy = loading || synthesisLoading;
  const runtimeCounselors = useMemo(
    () => normalizeAuthorSlugList([...(counselAuthorSlugs || []), persona]),
    [counselAuthorSlugs, persona],
  );
  const latestAssistantMessage = [...messages].reverse().find((message) => message?.role === 'assistant') || null;
  const runtimeDebugData = useMemo(() => ({
    conversationId: sessionId || 'unpersisted',
    activeAuthor: persona,
    activeAuthorName: activePersonaName,
    routeAuthor: normalizedSlug || '',
    inferencePersona: persona,
    authenticated: isAuthenticated,
    authMode: AUTH_DISABLED ? 'disabled' : (isAuthenticated ? 'authenticated' : 'guest'),
    chatPhase: chatStatus.phase,
    chatStatusCode: chatStatus.statusCode || '',
    streaming: loading || synthesisLoading,
    audioState: readAloudStatus.phase,
    audioKey: activeReadAloudKey,
    ttsRequestId: readAloudStatus.timings?.requestId || '',
    ttsSessionId: readAloudStatus.timings?.sessionId || '',
    ttsChunkRequestId: readAloudStatus.timings?.currentChunkRequestId || '',
    chunkIndex: readAloudStatus.timings?.chunkIndex || 0,
    chunkCount: readAloudStatus.timings?.chunkCount || 0,
    currentChunk: readAloudStatus.timings?.currentChunk || '',
    audioMessage: readAloudStatus.message,
    browserFallback: readAloudStatus.timings?.browserFallback || false,
    fallbackReason: readAloudStatus.timings?.fallbackReason || '',
    firstAudioReadyMs: readAloudStatus.timings?.firstAudioReadyMs || '',
    sessionCompleteMs: readAloudStatus.timings?.sessionCompleteMs || '',
    ttsLatencyMs: readAloudStatus.timings?.ttsCompleteMs || '',
    playbackStartMs: readAloudStatus.timings?.playStartedMs || '',
    proxyCreateMs: readAloudStatus.timings?.proxyCreateElapsedMs || '',
    proxyPollMs: readAloudStatus.timings?.proxyPollElapsedMs || '',
    polls: readAloudStatus.timings?.polls || 0,
    matterAnchor: cleanText(matterAnchor),
    counselCount: counselEntries.length,
    counselors: runtimeCounselors,
    retrievalScope: runtimeCounselors,
    latestAssistantPersona: cleanText(latestAssistantMessage?.persona),
    referenceCount: references.length,
    model: 'fortress-lan-mixtral',
    environment: ENVIRONMENT,
    appVersion: APP_VERSION,
    appRelease: APP_RELEASE,
    buildTime: BUILD_TIME,
  }), [
    activePersonaName,
    activeReadAloudKey,
    chatStatus.phase,
    chatStatus.statusCode,
    counselEntries.length,
    isAuthenticated,
    latestAssistantMessage,
    loading,
    matterAnchor,
    normalizedSlug,
    persona,
    readAloudStatus,
    references.length,
    runtimeCounselors,
    sessionId,
    synthesisLoading,
  ]);
  const stubLandingCues = useMemo(() => buildStubClockGuidance({
    persona,
    personaName: activePersonaName,
    authors,
  }), [persona, activePersonaName, authors]);
  const showAskProverbsState = isAskProverbs && messages.length === 0 && !loading;
  const showLandingState = messages.length === 0 && !loading && !launchState && !isAskProverbs;

  useEffect(() => {
    if (invalidAuthorRoute || !showLandingState) {
      setLandingApiCues(null);
      return undefined;
    }

    const endpoint = getClockGuidedPromptsUrl();
    if (!endpoint) {
      setLandingApiCues(null);
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;

    const loadLandingGuidance = async () => {
      try {
        const requestConfig = await addAuthHeaders({
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        });
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: requestConfig.headers,
          body: JSON.stringify({
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
            as_of: new Date().toISOString(),
            limit: 4,
            mode: 'landing',
            ...(persona ? { persona_hint: persona } : {}),
          }),
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`Guided prompts request failed with status ${response.status}`);
        }
        const payload = await response.json();
        if (!cancelled) {
          setLandingApiCues(buildLandingCuesFromGuidancePayload({
            payload,
            fallbackCues: stubLandingCues,
          }));
        }
      } catch (err) {
        if (!cancelled && err?.name !== 'AbortError') {
          setLandingApiCues(null);
        }
      }
    };

    loadLandingGuidance();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [invalidAuthorRoute, persona, showLandingState, stubLandingCues]);

  useEffect(() => {
    if (!showLandingState) {
      return;
    }
    if (typeof messagesContainerRef.current?.scrollTo === 'function') {
      messagesContainerRef.current.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [showLandingState, persona]);

  const clearSelectedCrossrefPreview = () => {
    setSelectedCrossrefBookPreview(null);
    setSelectedCrossrefBookPreviewError('');
    setSelectedCrossrefBookPreviewLoading(false);
  };

  const getAuthorCatalogBooks = useCallback(async (authorSlug) => {
    const slug = String(authorSlug || '').trim().toLowerCase();
    if (!slug) {
      return [];
    }
    if (authorCatalogCacheRef.current.has(slug)) {
      return authorCatalogCacheRef.current.get(slug) || [];
    }

    if (slug === persona && Array.isArray(authorProfile?.books)) {
      authorCatalogCacheRef.current.set(slug, authorProfile.books);
      return authorProfile.books;
    }

    const res = await apiV1.get(`/authors/${slug}/profile`);
    const books = Array.isArray(res.data?.books) ? res.data.books : [];
    authorCatalogCacheRef.current.set(slug, books);
    return books;
  }, [authorProfile, persona]);

  const resolveCatalogBook = useCallback(async ({ authorSlug, bookId, bookName }) => {
    const books = await getAuthorCatalogBooks(authorSlug);
    if (!books.length) {
      return null;
    }
    const targets = new Set(
      [bookId, bookName]
        .map(normalizeBookKey)
        .filter(Boolean),
    );
    if (!targets.size) {
      return null;
    }
    return books.find((item) => {
      const keys = [
        item?.book,
        item?.work,
        item?.source,
      ].map(normalizeBookKey).filter(Boolean);
      return keys.some((k) => targets.has(k));
    }) || null;
  }, [getAuthorCatalogBooks]);

  const loadCrossrefBookPreview = useCallback(async (crossrefPayload, requestId) => {
    const bookMeta = crossrefPayload?.book || {};
    const authorSlug = String(crossrefPayload?.author?.slug || bookMeta.author_slug || '').toLowerCase();
    const bookId = String(bookMeta.book_id || '').toUpperCase();
    const bookName = String(bookMeta.name || bookId || '').trim();

    if (!authorSlug || !bookName) {
      setSelectedCrossrefBookPreview(null);
      setSelectedCrossrefBookPreviewError('Missing author or book details for preview.');
      setSelectedCrossrefBookPreviewLoading(false);
      return;
    }

    setSelectedCrossrefBookPreviewLoading(true);
    setSelectedCrossrefBookPreviewError('');
    setSelectedCrossrefBookPreview(null);

    try {
      const catalogBook = await resolveCatalogBook({ authorSlug, bookId, bookName });
      if (requestId !== crossrefBookRequestRef.current) {
        return;
      }
      if (!catalogBook?.source) {
        throw new Error(`No source mapping found for ${bookId || bookName}.`);
      }

      const referenceMatch = references.find(
        (ref) => ref?.position != null && normalizeBookKey(ref.book) === normalizeBookKey(bookName),
      );
      const startPosition = referenceMatch?.position != null ? Number(referenceMatch.position) : 1;
      const endPosition = startPosition + 1;
      const res = await apiV1.post('/book_partial', {
        author_slug: authorSlug,
        book: catalogBook.book || bookName,
        source: catalogBook.source,
        chapter: referenceMatch?.chapter || catalogBook.chapter || null,
        page: referenceMatch?.page || catalogBook.page || null,
        start_position: startPosition,
        end_position: endPosition,
      });
      if (requestId !== crossrefBookRequestRef.current) {
        return;
      }
      setSelectedCrossrefBookPreview({
        displayLabel: res.data?.reference || (catalogBook.book || bookName),
        content: res.data?.content || 'No excerpt available.',
      });
      trackEvent('crossref_book_preview_loaded', {
        persona,
        author_slug: authorSlug,
        book_id: bookId || normalizeBookKey(bookName).toUpperCase(),
      });
    } catch (err) {
      if (requestId !== crossrefBookRequestRef.current) {
        return;
      }
      const detail = String(err?.response?.data?.detail || err?.message || 'Unknown error');
      setSelectedCrossrefBookPreview(null);
      setSelectedCrossrefBookPreviewError(detail);
      trackEvent('crossref_book_preview_failed', {
        persona,
        author_slug: authorSlug,
        book_id: bookId || normalizeBookKey(bookName).toUpperCase(),
        status_code: err?.response?.status || 0,
      });
    } finally {
      if (requestId === crossrefBookRequestRef.current) {
        setSelectedCrossrefBookPreviewLoading(false);
      }
    }
  }, [references, resolveCatalogBook, persona]);

  const setPersonaAndNavigate = (newPersona) => {
    const next = (newPersona || '').toLowerCase();
    if (!next) {
      return;
    }
    if (next && next !== persona) {
      trackEvent('persona_selected', { persona: next });
    }
    setPersona(next);
    navigate(`/author/${next}`);
  };

  const canOpenScriptureVerse = useCallback((ref) => {
    const chapter = String(ref?.chapter || '').trim();
    const verseStart = Number.parseInt(ref?.verseStart, 10);
    const verseEnd = Number.parseInt(ref?.verseEnd, 10);
    const isSingleVerse = Number.isNaN(verseEnd) || verseEnd === verseStart;
    return Boolean(ref?.book && chapter && !Number.isNaN(verseStart) && isSingleVerse);
  }, []);

  const openScriptureVerse = useCallback(async (ref) => {
    if (!canOpenScriptureVerse(ref)) {
      return;
    }
    const reference = buildScriptureReference(ref.book, ref.chapter, ref.verseStart);
    const key = normalizeBookKey(reference);
    if (!reference || !key) {
      return;
    }
    if (selectedScriptureReferenceKey === key && selectedScriptureVerse && !selectedScriptureVerseLoading) {
      setSelectedScriptureReferenceKey('');
      setSelectedScriptureVerse(null);
      setSelectedScriptureVerseError('');
      return;
    }

    setSelectedScriptureReferenceKey(key);
    setSelectedScriptureVerse(null);
    setSelectedScriptureVerseError('');
    setSelectedScriptureVerseLoading(true);
    try {
      const res = await apiV1.post('/scripture/verse', {
        reference,
        translation: 'kjv',
      });
      setSelectedScriptureVerse(res.data || null);
      trackEvent('scripture_bundle_loaded', {
        persona,
        reference,
        source_witness: res.data?.source_witness?.id || '',
        translation_count: Array.isArray(res.data?.translations) ? res.data.translations.length : 0,
      });
    } catch (err) {
      const detail = String(err?.response?.data?.detail || err?.message || 'Unknown error');
      setSelectedScriptureVerse(null);
      setSelectedScriptureVerseError(detail);
      trackEvent('scripture_bundle_failed', {
        persona,
        reference,
        status_code: err?.response?.status || 0,
      });
    } finally {
      setSelectedScriptureVerseLoading(false);
    }
  }, [
    canOpenScriptureVerse,
    persona,
    selectedScriptureReferenceKey,
    selectedScriptureVerse,
    selectedScriptureVerseLoading,
  ]);

  const openFallbackAuthor = () => {
    if (fallbackAuthorSlug) {
      setPersonaAndNavigate(fallbackAuthorSlug);
      return;
    }
    navigate('/');
  };

  const openCrossrefBook = async (bookId) => {
    const normalized = String(bookId || '').trim().toUpperCase();
    if (!normalized) {
      return;
    }
    if (selectedCrossrefBookId === normalized && selectedCrossrefBook && !selectedCrossrefBookLoading) {
      setSelectedCrossrefBookId('');
      setSelectedCrossrefBook(null);
      setSelectedCrossrefBookError('');
      clearSelectedCrossrefPreview();
      return;
    }

    const requestId = crossrefBookRequestRef.current + 1;
    crossrefBookRequestRef.current = requestId;
    setSelectedCrossrefBookId(normalized);
    setSelectedCrossrefBookLoading(true);
    setSelectedCrossrefBookError('');
    setSelectedCrossrefBook(null);
    clearSelectedCrossrefPreview();
    try {
      const res = await apiV1.get(`/crossrefs/books/${normalized}`);
      if (requestId !== crossrefBookRequestRef.current) {
        return;
      }
      setSelectedCrossrefBook(res.data || null);
      await loadCrossrefBookPreview(res.data || null, requestId);
      trackEvent('crossref_book_expanded', {
        persona,
        book_id: normalized,
        related_books: Array.isArray(res.data?.related_books) ? res.data.related_books.length : 0,
      });
    } catch (err) {
      if (requestId !== crossrefBookRequestRef.current) {
        return;
      }
      const detail = err?.response?.data?.detail;
      setSelectedCrossrefBookError(detail || err.message || 'Unknown error');
      clearSelectedCrossrefPreview();
      trackEvent('crossref_book_expand_failed', {
        persona,
        book_id: normalized,
        status_code: err?.response?.status || 0,
      });
    } finally {
      if (requestId === crossrefBookRequestRef.current) {
        setSelectedCrossrefBookLoading(false);
      }
    }
  };

  const landingCues = landingApiCues || stubLandingCues;
  const askProverbsAuthors = isAskProverbs
    ? authors.filter((author) => cleanText(author?.slug).toLowerCase() === ASK_PROVERBS_PERSONA)
    : authors;
  const askProverbsHero = {
    eyebrow: cleanText(proverbsContent?.headline) || 'Ask Proverbs',
    title: cleanText(proverbsContent?.headline) || 'Ask Proverbs',
    subtitle: cleanText(proverbsContent?.description)
      || 'Bring a question or situation and receive counsel anchored in Proverbs.',
    currentGuide: cleanText(proverbsContent?.author?.name) || 'Solomon',
    timeLabel: 'Proverbs',
    featuredAuthors: [],
  };
  const composerPlaceholder = isAskProverbs
    ? 'Ask for counsel about a decision, conflict, habit, or situation...'
    : launchState && messages.length === 0
    ? `Ask ${activePersonaName} anything. The clock context will stay light unless it helps.`
    : showLandingState
      ? `Ask ${activePersonaName} about desire, suffering, discipline, prayer, or the good life...`
      : `Continue with ${activePersonaName}...`;

  const applyLandingPrompt = async (promptText) => {
    trackEvent('landing_prompt_selected', {
      persona,
      prompt: promptText,
      guidance_source: isAskProverbs ? ASK_PROVERBS_SOURCE : (landingCues.guidance?.source?.service || 'unknown'),
    });
    const didSubmit = await submitScopedPromptText(promptText, 'landing');
    if (!didSubmit) {
      setQuery(promptText);
      window.requestAnimationFrame(() => {
        document.getElementById('chatQuestion')?.focus();
      });
    }
  };

  const handleScopedSubmit = async (event) => {
    event.preventDefault();
    await submitScopedPromptText(query, 'composer');
  };

  return (
    <section className="chat-page">
      <LandingHero
        hero={isAskProverbs ? askProverbsHero : landingCues.hero}
        persona={persona}
        onSelectAuthor={setPersonaAndNavigate}
        compact={!showLandingState && !showAskProverbsState}
      />
      <div className="chat-layout">
        <aside className="chat-side-panel">
        <PersonaContextPanel
          persona={persona}
          setPersona={isAskProverbs ? () => {} : setPersonaAndNavigate}
          authors={askProverbsAuthors}
          profile={authorProfile}
          loading={authorProfileLoading}
          error={authorProfileError}
          invalidSlug={invalidAuthorRoute ? normalizedSlug : ''}
          isAuthenticated={isAuthenticated}
          favoriteAuthorSlugs={favoriteAuthorSlugs}
          defaultAuthorSlug={defaultAuthorSlug}
          preferencesSaving={preferencesSaving}
          preferencesError={preferencesError}
          onToggleFavorite={onToggleFavorite}
          onSetDefaultAuthor={onSetDefaultAuthor}
        />

          {isAskProverbs && (
            <AskProverbsSidebarPanel
              content={proverbsContent}
              loading={proverbsContentLoading}
              error={proverbsContentError}
            />
          )}

          {!invalidAuthorRoute && metaInfo.length > 0 && (
            <div className="meta-panel">
              <h2>Source Info</h2>
              <ul>
                {metaInfo.map((item) => (
                  <li key={item.key}>
                    <strong>{item.book}</strong>
                    {item.section && (
                      <span className="meta-label"> · Section: {item.section}</span>
                    )}
                    {item.testament && (
                      <span className="meta-label"> · Testament: {item.testament}</span>
                    )}
                    {item.authorTraditional && (
                      <span className="meta-label"> · Traditional: {item.authorTraditional}</span>
                    )}
                    {item.authorCritical && (
                      <span className="meta-label"> · Critical: {item.authorCritical}</span>
                    )}
                    {item.sourceTheory && (
                      <span className="meta-label"> · Theory: {item.sourceTheory}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>

        <div className="chat-main-column">
          {invalidAuthorRoute ? (
            <div className="route-state-card" role="status">
              <h2>Author Not Available</h2>
              <p>
                The route <code>/author/{normalizedSlug}</code> does not match a production-visible persona.
              </p>
              <p>Select another persona from the panel, or open the default author to continue.</p>
              <button
                type="button"
                className="history-session-action"
                onClick={openFallbackAuthor}
              >
                {fallbackAuthorSlug ? `Open ${getPersonaName(fallbackAuthorSlug)}` : 'Return to chat'}
              </button>
            </div>
          ) : (
            <>
              <div className={`chat-container${showLandingState || showAskProverbsState ? ' chat-container--landing' : ''}`}>
                <div
                  ref={messagesContainerRef}
                  className={`messages${showLandingState || showAskProverbsState ? ' messages--empty' : ''}`}
                >
                  {launchState && !isAskProverbs && (
                    <ClockLaunchBanner launchState={launchState} />
                  )}
                  {showLandingState && (
                    <EmptyChatState
                      guidance={landingCues.guidance}
                      onPromptSelect={applyLandingPrompt}
                    />
                  )}
                  {showAskProverbsState && (
                    <AskProverbsEmptyState
                      content={proverbsContent}
                      loading={proverbsContentLoading}
                      error={proverbsContentError}
                      onPromptSelect={applyLandingPrompt}
                    />
                  )}
                  {messages.map((msg, index) => (
                    <Message
                      key={index}
                      role={msg.role}
                      message={msg.message}
                      persona={msg.persona}
                      followUpQuestion={msg.followUpQuestion}
                      inlineReferences={msg.references}
                      onUseFollowUpQuestion={applyFollowUpQuestion}
                      onSelectInlineReference={(referenceIndex) => onSelectMessageReference?.(
                        msg.references,
                        msg.metaInfo,
                        referenceIndex,
                      )}
                      readAloudKey={msg.role === 'assistant' ? `assistant:${index}` : ''}
                      activeReadAloudKey={activeReadAloudKey}
                      readAloudStatus={readAloudStatus}
                      onStartReadAloud={startReadAloud}
                      onStopReadAloud={stopReadAloud}
                      readAloudSupported={readAloudSupported}
                    />
                  ))}
                  {loading && (
                    <div className="message assistant-message">
                      <span className="message-role">{getPersonaName(persona)}:</span>
                      <p className="typing-indicator">Thinking...</p>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {!isAskProverbs && (
                  <CounselStackPanel
                    matter={matterAnchor}
                    composerText={query}
                    persona={persona}
                    authors={authors}
                    counselAuthorSlugs={counselAuthorSlugs}
                    counselEntries={counselEntries}
                    counselSynthesis={counselSynthesis}
                    onAddCounsel={onAddCounsel}
                    onSynthesize={onSynthesizeCounsel}
                    onRemoveCounselAuthor={onRemoveCounselAuthor}
                    getPersonaName={getPersonaName}
                    loading={busy}
                    synthesisLoading={synthesisLoading}
                    activeReadAloudKey={activeReadAloudKey}
                    readAloudStatus={readAloudStatus}
                    onStartReadAloud={startReadAloud}
                    onStopReadAloud={stopReadAloud}
                    readAloudSupported={readAloudSupported}
                  />
                )}
                {activeReadAloudKey && readAloudStatus.phase !== 'idle' && (
                  <div className={`read-aloud-status read-aloud-status--${readAloudStatus.phase}`} role="status" aria-live="polite">
                    <div className="read-aloud-status__body">
                      <span className="read-aloud-status__label">
                        {getReadAloudPhaseLabel(readAloudStatus.phase)}
                      </span>
                      <span className="read-aloud-status__message">
                        {readAloudStatus.message || 'Preparing speech...'}
                      </span>
                      <div className="read-aloud-status__details" aria-label="Speak diagnostics">
                        {buildReadAloudDetails(readAloudStatus).map((detail) => (
                          <span key={detail}>{detail}</span>
                        ))}
                      </div>
                    </div>
                    {readAloudStatus.timings?.browserFallbackAvailable && readAloudStatus.timings?.fallbackText ? (
                      <button
                        type="button"
                        className="read-aloud-status__fallback"
                        onClick={() => startBrowserReadAloudFallback(
                          activeReadAloudKey,
                          readAloudStatus.timings.fallbackText,
                          readAloudStatus.timings.fallbackReason || 'VibeVoice unavailable',
                        )}
                      >
                        Play browser fallback
                      </button>
                    ) : null}
                  </div>
                )}
                {(conversationActive || voiceStatus.phase !== 'idle' || voiceStatus.message) && (
                  <div
                    className={`voice-conversation-status voice-conversation-status--${voiceStatus.phase}${conversationActive ? ' is-active' : ''}`}
                    role={voiceStatus.phase === 'error' ? 'alert' : 'status'}
                    aria-live={voiceStatus.phase === 'error' ? 'assertive' : 'polite'}
                  >
                    <span className="voice-conversation-status__dot" aria-hidden="true" />
                    <span className="voice-conversation-status__label">
                      {conversationActive ? 'Conversation' : 'Voice'}
                    </span>
                    <span className="voice-conversation-status__message">
                      {voiceStatus.message || (conversationActive ? 'Listening.' : 'Ready.')}
                    </span>
                  </div>
                )}
                {normalizedSourceContext && !isAskProverbs && (
                  <div className="reader-source-context-banner" role="status" aria-live="polite">
                    <div className="reader-source-context-banner__body">
                      <span className="reader-source-context-banner__label">Selected source text</span>
                      <p className="reader-source-context-banner__summary">
                        <strong>{getSourceContextDisplayLabel(normalizedSourceContext) || getPersonaName(persona)}</strong>
                        <span className="meta-label">
                          {' '}· positions {normalizedSourceContext.start_position}-{normalizedSourceContext.end_position}
                        </span>
                      </p>
                      <p className="reader-source-context-banner__excerpt">{normalizedSourceContext.content}</p>
                    </div>
                    <div className="reader-source-context-banner__actions">
                      {activeSourceReaderHref && (
                        <Link
                          className="author-browse-card__button author-browse-card__button--secondary"
                          to={activeSourceReaderHref}
                        >
                          Return to reader
                        </Link>
                      )}
                      <button
                        type="button"
                        className="history-session-action"
                        onClick={onClearSourceContext}
                      >
                        Clear anchor
                      </button>
                    </div>
                  </div>
                )}
                {showConversationState && (
                  <ConversationStateCard
                    state={conversationState}
                    loading={conversationStateLoading}
                    error={conversationStateError}
                    resetting={conversationStateResetting}
                    onReset={onResetConversationState}
                  />
                )}
                {showRelationshipMemory && (
                  <RelationshipMemoryCard
                    memory={relationshipMemory}
                    loading={relationshipMemoryLoading}
                    error={relationshipMemoryError}
                    resetting={relationshipMemoryResetting}
                    onReset={onResetRelationshipMemory}
                  />
                )}
                <ChatInput
                  query={query}
                  setQuery={setQuery}
                  onSubmit={handleScopedSubmit}
                  loading={busy}
                  placeholder={composerPlaceholder}
                  voiceSupported={speechRecognitionSupported}
                  voiceStatus={voiceStatus}
                  conversationActive={conversationActive}
                  onStartVoiceInput={() => startVoiceInput(false)}
                  onStopVoiceInput={() => stopVoiceInput({ phase: 'idle', message: 'Voice input stopped.' })}
                  onToggleConversation={toggleConversation}
                />
              </div>

              {chatStatus.phase !== 'idle' && (
                <div
                  className={`chat-status chat-status--${chatStatus.phase}`}
                  role={chatStatus.phase === 'error' || chatStatus.phase === 'timeout' ? 'alert' : 'status'}
                  aria-live={chatStatus.phase === 'error' || chatStatus.phase === 'timeout' ? 'assertive' : 'polite'}
                >
                  <div className="chat-status__title">{chatStatus.title}</div>
                  {chatStatus.message && <p>{chatStatus.message}</p>}
                  <div className="chat-status__meta">
                    {chatStatus.statusCode ? <span>HTTP {chatStatus.statusCode}</span> : <span>Status</span>}
                    {chatStatus.elapsedMs ? <span>Elapsed {formatElapsedSeconds(chatStatus.elapsedMs)}</span> : null}
                    {chatStatus.retryable && (
                      <button
                        className="chat-status-retry"
                        type="button"
                        onClick={chatStatus.retryAction === 'synthesis' ? onRetrySynthesis : onRetryLastMessage}
                        disabled={busy}
                      >
                        {chatStatus.retryLabel || 'Retry'}
                      </button>
                    )}
                  </div>
                </div>
              )}
              {runtimeDebug && (
                <RuntimeDebugPanel runtime={runtimeDebugData} />
              )}
              {error && <p className="error">{error}</p>}
            </>
          )}
        </div>

        {!invalidAuthorRoute && references.length > 0 && (
          <aside className="references">
            <h2>{counselEntries.length > 0 ? 'Latest References' : 'References'}</h2>
            <ul>
              {references.map((ref, index) => {
                const authorPageSlug = cleanText(ref?.targetSlug || ref?.targetAuthorSlug).toLowerCase();
                const isDerivedExcerpt = ref?.provenance !== 'retrieved' && ref?.expandable;
                return (
                  <li key={index}>
                    <strong>{ref.displayLabel || ref.book}:</strong> {ref.content}
                    {ref.provenance && (
                      <span className="meta-label"> [{formatProvenanceLabel(ref.provenance)}]</span>
                    )}
                    {ref.expandable && !ref.expanded && (
                      <button
                        className="reference-expand"
                        type="button"
                        onClick={() => expandReference(index)}
                      >
                        {isDerivedExcerpt ? 'Open source excerpt' : 'Expand'}
                      </button>
                    )}
                    {authorPageSlug && (
                      <button
                        className="reference-expand"
                        type="button"
                        onClick={() => navigate(`/authors/${authorPageSlug}`)}
                      >
                        Author page
                      </button>
                    )}
                    {canOpenScriptureVerse(ref) && (
                      <button
                        className="reference-expand"
                        type="button"
                        onClick={() => openScriptureVerse(ref)}
                      >
                        {selectedScriptureReferenceKey === normalizeBookKey(
                          buildScriptureReference(ref.book, ref.chapter, ref.verseStart),
                        ) && selectedScriptureVerse
                          ? 'Hide verse bundle'
                          : 'See verse bundle'}
                      </button>
                    )}
                    {ref.expanded && (
                      <ReadAloudButton
                        text={ref.content}
                        readAloudKey={`reference:${index}`}
                        activeReadAloudKey={activeReadAloudKey}
                        readAloudStatus={readAloudStatus}
                        onStartReadAloud={startReadAloud}
                        onStopReadAloud={stopReadAloud}
                        className="read-aloud-button--reference"
                        supported={readAloudSupported}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
            {(selectedScriptureVerseLoading || selectedScriptureVerseError || selectedScriptureVerse) && (
              <div className="scripture-verse-detail">
                {selectedScriptureVerseLoading && (
                  <p className="author-context-state">Loading verse witness…</p>
                )}
                {!selectedScriptureVerseLoading && selectedScriptureVerseError && (
                  <p className="author-context-state">
                    Unable to load verse witness: {selectedScriptureVerseError}
                  </p>
                )}
                {!selectedScriptureVerseLoading && !selectedScriptureVerseError && selectedScriptureVerse && (
                  <>
                    <p className="crossref-summary">
                      <strong>{selectedScriptureVerse.reference}</strong>
                    </p>
                    {selectedScriptureVerse.notice && (
                      <p className="scripture-verse-notice">{selectedScriptureVerse.notice}</p>
                    )}
                    {selectedScriptureVerse.source_witness && (
                      <>
                        <h3>Source Text</h3>
                        <p className="crossref-summary">
                          <strong>{selectedScriptureVerse.source_witness.label}</strong>
                          {selectedScriptureVerse.source_witness.language && (
                            <span className="meta-label"> · {selectedScriptureVerse.source_witness.language}</span>
                          )}
                        </p>
                        <p className="crossref-preview-content">
                          {selectedScriptureVerse.source_witness.text || 'No source text available.'}
                        </p>
                      </>
                    )}
                    {Array.isArray(selectedScriptureVerse.translations)
                      && selectedScriptureVerse.translations.length > 0 && (
                        <>
                          <h3>Canonical Translations</h3>
                          <div className="scripture-translation-list">
                            {selectedScriptureVerse.translations.map((item) => (
                              <div key={item.id} className="scripture-translation-card">
                                <p className="crossref-summary">
                                  <strong>{item.label}</strong>
                                  {item.id === selectedScriptureVerse.preferred_translation && (
                                    <span className="meta-label"> · preferred</span>
                                  )}
                                </p>
                                <p className="crossref-preview-content">
                                  {item.text || 'No translation text available.'}
                                </p>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    {(selectedScriptureVerse.derived_insights?.literal_gloss
                      || Array.isArray(selectedScriptureVerse.derived_insights?.key_term_notes)) && (
                        <>
                          <h3>Derived Notes</h3>
                          <div className="scripture-derived-card">
                            {selectedScriptureVerse.derived_insights?.notice && (
                              <p className="scripture-derived-notice">
                                {selectedScriptureVerse.derived_insights.notice}
                              </p>
                            )}
                            {selectedScriptureVerse.derived_insights?.literal_gloss && (
                              <p className="crossref-preview-content">
                                <strong>Literal gloss:</strong>{' '}
                                {selectedScriptureVerse.derived_insights.literal_gloss}
                              </p>
                            )}
                            {Array.isArray(selectedScriptureVerse.derived_insights?.key_term_notes)
                              && selectedScriptureVerse.derived_insights.key_term_notes.length > 0 && (
                                <ul className="scripture-key-term-list">
                                  {selectedScriptureVerse.derived_insights.key_term_notes.map((item) => (
                                    <li key={`${item.lemma}-${item.term}`}>
                                      <strong>{item.term}</strong>
                                      {item.gloss && (
                                        <span className="meta-label"> · {item.gloss}</span>
                                      )}
                                      {item.note && (
                                        <p className="crossref-evidence-note">{item.note}</p>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              )}
                          </div>
                        </>
                      )}
                    {Array.isArray(selectedScriptureVerse.commentary_layers)
                      && selectedScriptureVerse.commentary_layers.length > 0 && (
                        <>
                          <h3>Commentary Layer</h3>
                          <div className="scripture-commentary-list">
                            {selectedScriptureVerse.commentary_layers.map((item) => (
                              <div
                                key={`${item.author_slug}-${item.work || item.snippet}`}
                                className="scripture-commentary-card"
                              >
                                <p className="crossref-summary">
                                  <strong>{item.author_name || item.author_slug}</strong>
                                  {item.work && (
                                    <span className="meta-label"> · {item.work}</span>
                                  )}
                                </p>
                                {item.snippet && (
                                  <p className="crossref-evidence-note">{item.snippet}</p>
                                )}
                                {item.author_slug && (
                                  <button
                                    className="reference-expand"
                                    type="button"
                                    onClick={() => navigate(`/authors/${item.author_slug}`)}
                                  >
                                    Author page
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                  </>
                )}
              </div>
            )}
          </aside>
        )}

        {!invalidAuthorRoute && (
          <aside className="crossref-panel">
          <h2>Related Authors / Works</h2>
          {(crossrefCatalogLoading || crossrefAuthorLoading) && (
            <p className="author-context-state">Loading cross references…</p>
          )}
          {!crossrefCatalogLoading && !crossrefAuthorLoading && (crossrefCatalogError || crossrefAuthorError) && (
            <p className="author-context-state">
              Unable to load cross references: {crossrefAuthorError || crossrefCatalogError}
            </p>
          )}
          {!crossrefCatalogLoading && !crossrefAuthorLoading && !crossrefCatalogError && !crossrefAuthorError && (
            <>
              {crossrefAuthor?.author?.name && (
                <p className="crossref-summary">
                  Catalog for <strong>{crossrefAuthor.author.name}</strong>
                  {' '}({crossrefAuthor?.counts?.books || 0} mapped works
                  {Number(crossrefAuthor?.counts?.related_references || 0) > 0
                    ? `, ${crossrefAuthor.counts.related_references} known links`
                    : ''}
                  )
                </p>
              )}
              {curatedRelatedReferences.length > 0 && (
                <>
                  <h3>Known Links</h3>
                  <ul className="crossref-linked-authors">
                    {curatedRelatedReferences.map((item) => {
                      const slug = item.target_slug;
                      const publicAuthor = slug && authorSlugSet.has(slug);
                      const key = [
                        item.target_type || 'reference',
                        slug || item.target_label,
                        cleanText(item.relationship),
                        item.evidence_work,
                      ].join(':');
                      return (
                        <li key={key}>
                          <p className="crossref-summary">
                            <strong>{item.target_label}</strong>
                            <span className="meta-label"> · {getCrossrefRelationshipLabel(item)}</span>
                          </p>
                          {item.evidence_work && (
                            <p className="crossref-evidence-meta">{item.evidence_work}</p>
                          )}
                          {item.evidence_snippet && (
                            <p className="crossref-evidence-note">{item.evidence_snippet}</p>
                          )}
                          {publicAuthor && (
                            <div className="crossref-linked-author__actions">
                              <button
                                className="reference-expand"
                                type="button"
                                onClick={() => navigate(`/authors/${slug}`)}
                              >
                                Author page
                              </button>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
              {relatedAuthors.length > 0 ? (
                <>
                  <h3>Referenced Authors</h3>
                  <ul>
                    {relatedAuthors.map((author) => (
                      <li key={author.slug}>
                        <strong>{author.name}</strong>
                        {author.relationship && (
                          <span className="meta-label"> · {author.relationship}</span>
                        )}
                        {author.sampleBook && (
                          <span className="meta-label"> · via {author.sampleBook}</span>
                        )}
                        <button
                          className="reference-expand"
                          type="button"
                          onClick={() => openCrossrefBook(author.sampleBookId)}
                          disabled={!author.sampleBookId}
                        >
                          Open
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="author-context-state">
                  No related author detected in current references yet.
                </p>
              )}
              {referencedCrossrefBooks.length > 0 && (
                <>
                  <h3>Matched Works</h3>
                  <ul>
                    {referencedCrossrefBooks.slice(0, 8).map((item) => (
                      <li key={item.book_id}>
                        <strong>{item.book_id}</strong>
                        {item.name && <span className="meta-label"> · {item.name}</span>}
                        {item.author_name && <span className="meta-label"> · {item.author_name}</span>}
                          <button
                            className="reference-expand"
                            type="button"
                            onClick={() => openCrossrefBook(item.book_id)}
                          >
                            {selectedCrossrefBookId === String(item.book_id || '').toUpperCase() && selectedCrossrefBook
                              ? 'Hide'
                              : 'Open'}
                          </button>
                      </li>
                    ))}
                  </ul>

                  {(selectedCrossrefBookLoading || selectedCrossrefBookError || selectedCrossrefBook) && (
                    <div className="crossref-book-detail">
                      {selectedCrossrefBookLoading && (
                        <p className="author-context-state">
                          Loading details for {selectedCrossrefBookId}…
                        </p>
                      )}
                      {!selectedCrossrefBookLoading && selectedCrossrefBookError && (
                        <p className="author-context-state">
                          Unable to load work details: {selectedCrossrefBookError}
                        </p>
                      )}
                      {!selectedCrossrefBookLoading && !selectedCrossrefBookError && selectedCrossrefBook && (
                        <>
                          <p className="crossref-summary">
                            <strong>{selectedCrossrefBook?.book?.book_id || selectedCrossrefBookId}</strong>
                            {selectedCrossrefBook?.book?.name && (
                              <span className="meta-label"> · {selectedCrossrefBook.book.name}</span>
                            )}
                            {selectedCrossrefBook?.author?.name && (
                              <span className="meta-label"> · {selectedCrossrefBook.author.name}</span>
                            )}
                          </p>
                          {selectedCrossrefBookPreviewLoading && (
                            <p className="author-context-state">Loading referenced work excerpt…</p>
                          )}
                          {!selectedCrossrefBookPreviewLoading && selectedCrossrefBookPreviewError && (
                            <p className="author-context-state">
                              Unable to load referenced work: {selectedCrossrefBookPreviewError}
                            </p>
                          )}
                          {!selectedCrossrefBookPreviewLoading
                            && !selectedCrossrefBookPreviewError
                            && selectedCrossrefBookPreview && (
                              <>
                                <h3>Referenced Work Excerpt</h3>
                                <p className="crossref-summary">
                                  <strong>{selectedCrossrefBookPreview.displayLabel}</strong>
                                </p>
                                <p className="crossref-preview-content">{selectedCrossrefBookPreview.content}</p>
                                <ReadAloudButton
                                  text={selectedCrossrefBookPreview.content}
                                  readAloudKey={`crossref-preview:${selectedCrossrefBookId || selectedCrossrefBookPreview.displayLabel}`}
                                  activeReadAloudKey={activeReadAloudKey}
                                  readAloudStatus={readAloudStatus}
                                  onStartReadAloud={startReadAloud}
                                  onStopReadAloud={stopReadAloud}
                                  className="read-aloud-button--detail"
                                  supported={readAloudSupported}
                                />
                              </>
                            )}
                          {Array.isArray(selectedCrossrefBook?.referencing_authors)
                            && selectedCrossrefBook.referencing_authors.length > 0 && (
                              <>
                                <h3>Referenced by</h3>
                                <ul className="crossref-linked-authors">
                                  {selectedCrossrefBook.referencing_authors.map((item) => {
                                    const slug = String(item?.slug || '').trim().toLowerCase();
                                    const evidenceSnippet = cleanText(item?.evidence_snippet);
                                    const confidenceLabel = formatCrossrefConfidence(item?.confidence);
                                    const publicAuthor = slug && authorSlugSet.has(slug);
                                    return (
                                      <li key={`${slug || 'unknown'}-${cleanText(item?.relationship) || 'reference'}`}>
                                        <p className="crossref-summary">
                                          <strong>{cleanText(item?.name) || formatSlugLabel(slug)}</strong>
                                          <span className="meta-label"> · {getCrossrefRelationshipLabel(item)}</span>
                                        </p>
                                        {evidenceSnippet && (
                                          <p className="crossref-evidence-note">{evidenceSnippet}</p>
                                        )}
                                        {confidenceLabel && (
                                          <p className="crossref-evidence-meta">{confidenceLabel}</p>
                                        )}
                                        {publicAuthor && (
                                          <div className="crossref-linked-author__actions">
                                            <button
                                              className="reference-expand"
                                              type="button"
                                              onClick={() => navigate(`/authors/${slug}`)}
                                            >
                                              Author page
                                            </button>
                                          </div>
                                        )}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </>
                            )}
                          {Array.isArray(selectedCrossrefBook?.related_books)
                            && selectedCrossrefBook.related_books.length > 0 && (
                              <>
                                <h3>Related Works by Same Author</h3>
                                <ul>
                                  {selectedCrossrefBook.related_books.slice(0, 8).map((work) => (
                                    <li key={work.book_id}>
                                      <strong>{work.book_id}</strong>
                                      {work.name && <span className="meta-label"> · {work.name}</span>}
                                      <button
                                        className="reference-expand"
                                        type="button"
                                        onClick={() => openCrossrefBook(work.book_id)}
                                      >
                                        Open
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </>
                            )}
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
          </aside>
        )}
      </div>
    </section>
  );
}

const AUTH_OFF = AUTH_DISABLED;

function App({ initialAuthReady = true, initialIsAuthenticated = false }) {
  const initialClockLaunchState = buildClockLaunchStateFromUrl(window.location);
  const initialResumeSessionId = getResumeSessionIdFromUrl(window.location);
  const initialPathname = window.location.pathname;
  const [authReady, setAuthReady] = useState(AUTH_OFF ? true : initialAuthReady);
  const [isAuthenticated, setIsAuthenticated] = useState(AUTH_OFF ? false : initialIsAuthenticated);
  const [userProfile, setUserProfile] = useState(null);
  const [authorPreferences, setAuthorPreferences] = useState(EMPTY_AUTHOR_PREFERENCES);
  const [authorPreferencesLoading, setAuthorPreferencesLoading] = useState(false);
  const [authorPreferencesError, setAuthorPreferencesError] = useState('');
  const [authorPreferencesSaving, setAuthorPreferencesSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [matterAnchor, setMatterAnchor] = useState('');
  const [counselAuthorSlugs, setCounselAuthorSlugs] = useState([]);
  const [counselEntries, setCounselEntries] = useState([]);
  const [counselSynthesis, setCounselSynthesis] = useState(null);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState('');
  const [chatStatus, setChatStatus] = useState({ ...CHAT_STATUS_IDLE });
  const [lastSubmittedMessage, setLastSubmittedMessage] = useState('');
  const [lastSubmittedMode, setLastSubmittedMode] = useState('conversation');
  const [lastSubmittedSourceContext, setLastSubmittedSourceContext] = useState(null);
  const [lastSubmittedSource, setLastSubmittedSource] = useState('');
  const [activeLaunchState, setActiveLaunchState] = useState(initialClockLaunchState);
  const [activeSourceContext, setActiveSourceContext] = useState(null);
  const [pendingResumeSessionId, setPendingResumeSessionId] = useState(initialResumeSessionId);

  useEffect(() => {
    const handleError = (event) => {
      reportClientFailure({
        kind: 'uncaught_browser_error',
        phase: 'runtime',
        message: event.message || event.error?.message || 'Uncaught browser error.',
        stack: event.error?.stack || '',
        route: window.location.pathname,
      });
    };
    const handleRejection = (event) => {
      const reason = event.reason;
      reportClientFailure({
        kind: 'unhandled_promise_rejection',
        phase: 'runtime',
        message: reason?.message || String(reason || 'Unhandled promise rejection.'),
        stack: reason?.stack || '',
        route: window.location.pathname,
      });
    };
    const handleClientFailure = (event) => {
      reportClientFailure(event.detail || {});
    };
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    window.addEventListener('pericope:client-failure', handleClientFailure);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
      window.removeEventListener('pericope:client-failure', handleClientFailure);
    };
  }, []);

  const getInitialPersona = () => {
    if (isAskProverbsPath(window.location.pathname)) {
      return ASK_PROVERBS_PERSONA;
    }
    const requestedAuthor = getAuthorSlugFromPath(window.location.pathname);
    return requestedAuthor || 'augustine';
  };
  const [persona, setPersona] = useState(getInitialPersona());
  const [authors, setAuthors] = useState([]);
  const [authorBrowseItems, setAuthorBrowseItems] = useState([]);
  const [authorBrowseLoading, setAuthorBrowseLoading] = useState(false);
  const [authorBrowseError, setAuthorBrowseError] = useState('');
  const [userId, setUserId] = useState(null);
  const [guestId] = useState(() => {
    try {
      const existing = localStorage.getItem('guest_user_id');
      if (existing) return existing;
      const id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      localStorage.setItem('guest_user_id', id);
      return id;
    } catch {
      return (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : null;
    }
  });
  const initialSessionId = () =>
    typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : null;
  const [sessionId, setSessionId] = useState(initialSessionId);
  const [loading, setLoading] = useState(false);
  const [synthesisLoading, setSynthesisLoading] = useState(false);
  const [conversationState, setConversationState] = useState(null);
  const [conversationStateLoading, setConversationStateLoading] = useState(false);
  const [conversationStateError, setConversationStateError] = useState('');
  const [conversationStateResetting, setConversationStateResetting] = useState(false);
  const [relationshipMemory, setRelationshipMemory] = useState(null);
  const [relationshipMemoryLoading, setRelationshipMemoryLoading] = useState(false);
  const [relationshipMemoryError, setRelationshipMemoryError] = useState('');
  const [relationshipMemoryResetting, setRelationshipMemoryResetting] = useState(false);
  const [references, setReferences] = useState([]);
  const [metaInfo, setMetaInfo] = useState([]);
  const [authorProfile, setAuthorProfile] = useState(null);
  const [authorProfileLoading, setAuthorProfileLoading] = useState(false);
  const [authorProfileError, setAuthorProfileError] = useState('');
  const [crossrefCatalog, setCrossrefCatalog] = useState([]);
  const [crossrefCatalogLoading, setCrossrefCatalogLoading] = useState(false);
  const [crossrefCatalogError, setCrossrefCatalogError] = useState('');
  const [authorCrossrefs, setAuthorCrossrefs] = useState(null);
  const [authorCrossrefsLoading, setAuthorCrossrefsLoading] = useState(false);
  const [authorCrossrefsError, setAuthorCrossrefsError] = useState('');
  const [proverbsContent, setProverbsContent] = useState(null);
  const [proverbsContentLoading, setProverbsContentLoading] = useState(false);
  const [proverbsContentError, setProverbsContentError] = useState('');
  const messagesEndRef = useRef(null);
  const launchUrlScrubbedRef = useRef(!initialClockLaunchState);
  const resumeUrlScrubbedRef = useRef(!initialResumeSessionId);
  const launchPrefillRef = useRef('');
  const resumeSessionBootstrapRef = useRef('');
  const guestHomepageSelectionRef = useRef(false);
  const savedDefaultLaunchKeyRef = useRef('');
  const synthesisSourceKeyRef = useRef('');
  const isBusy = loading || synthesisLoading;
  const clearConversationState = useCallback(() => {
    setConversationState(null);
    setConversationStateLoading(false);
    setConversationStateError('');
    setConversationStateResetting(false);
  }, []);
  const clearRelationshipMemory = useCallback(() => {
    setRelationshipMemory(null);
    setRelationshipMemoryLoading(false);
    setRelationshipMemoryError('');
    setRelationshipMemoryResetting(false);
  }, []);

  const loadConversationState = useCallback(async (targetSessionId, sessionPersona = '') => {
    if (!isAuthenticated || !targetSessionId) {
      clearConversationState();
      return null;
    }
    setConversationStateLoading(true);
    setConversationStateError('');
    try {
      const res = await apiV1.get(`/history/${targetSessionId}/state`);
      const normalized = normalizeConversationStatePayload(res.data?.state, {
        sessionId: targetSessionId,
        persona: res.data?.persona || sessionPersona,
      });
      const nextState = normalized || buildEmptyConversationState(
        targetSessionId,
        res.data?.persona || sessionPersona,
      );
      setConversationState(nextState);
      return nextState;
    } catch (err) {
      const detail = cleanText(err?.response?.data?.detail) || err?.message || 'Failed to load conversation state';
      setConversationState(null);
      setConversationStateError(detail);
      return null;
    } finally {
      setConversationStateLoading(false);
    }
  }, [clearConversationState, isAuthenticated]);

  const loadRelationshipMemory = useCallback(async (targetPersona) => {
    const personaSlug = cleanText(targetPersona).toLowerCase();
    if (!isAuthenticated || !personaSlug) {
      clearRelationshipMemory();
      return null;
    }
    setRelationshipMemoryLoading(true);
    setRelationshipMemoryError('');
    try {
      const params = new URLSearchParams({ persona: personaSlug });
      const res = await apiV1.get(`/memory/relationship?${params.toString()}`);
      const normalized = normalizeRelationshipMemoryPayload(res.data?.memory, {
        persona: res.data?.persona || personaSlug,
      });
      const nextMemory = normalized || buildEmptyRelationshipMemory(res.data?.persona || personaSlug);
      setRelationshipMemory(nextMemory);
      return nextMemory;
    } catch (err) {
      const detail = cleanText(err?.response?.data?.detail) || err?.message || 'Failed to load relationship memory';
      setRelationshipMemory(null);
      setRelationshipMemoryError(detail);
      return null;
    } finally {
      setRelationshipMemoryLoading(false);
    }
  }, [clearRelationshipMemory, isAuthenticated]);

  useEffect(() => {
    const normalizedSourceContext = normalizeSourceContextPayload(activeSourceContext);
    if (!normalizedSourceContext) {
      return;
    }
    if (normalizedSourceContext.author_slug !== cleanText(persona).toLowerCase()) {
      setActiveSourceContext(null);
    }
  }, [activeSourceContext, persona]);
  const synthesisSourceKey = useMemo(
    () => buildCounselStackSourceKey(matterAnchor, counselEntries),
    [matterAnchor, counselEntries],
  );

  useEffect(() => {
    if (!synthesisSourceKeyRef.current) {
      synthesisSourceKeyRef.current = synthesisSourceKey;
      return;
    }
    if (synthesisSourceKeyRef.current !== synthesisSourceKey) {
      synthesisSourceKeyRef.current = synthesisSourceKey;
      setCounselSynthesis(null);
    }
  }, [synthesisSourceKey]);

  useEffect(() => {
    if (isAuthenticated) {
      return;
    }
    clearConversationState();
  }, [clearConversationState, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      return;
    }
    clearRelationshipMemory();
  }, [clearRelationshipMemory, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    void loadRelationshipMemory(persona);
  }, [isAuthenticated, loadRelationshipMemory, persona]);

  useEffect(() => {
    if (!sessionId || !counselSynthesis) {
      return;
    }
    persistCounselSynthesisSnapshot(sessionId, synthesisSourceKey, counselSynthesis);
  }, [sessionId, synthesisSourceKey, counselSynthesis]);

  useEffect(() => {
    if (AUTH_OFF) {
      setAuthReady(true);
      setIsAuthenticated(DEV_AUTH_ENABLED);
      trackEvent('auth_state_changed', { authenticated: DEV_AUTH_ENABLED });
      return () => {};
    }
    const updateAuthState = (authenticated) => {
      const nextAuthenticated = Boolean(authenticated);
      trackEvent('auth_state_changed', { authenticated: nextAuthenticated });
      setIsAuthenticated(nextAuthenticated);
      setAuthReady(true);
    };
    const unsubscribe = subscribeToAuth(updateAuthState);
    updateAuthState(keycloak.authenticated);
    return unsubscribe;
  }, [persona]);

  useEffect(() => {
    const tokenSub = getTokenProfile()?.sub || null;
    const trackingUserId = isAuthenticated
      ? (userId || tokenSub || guestId)
      : guestId;
    setAnalyticsUserId(trackingUserId || null);
  }, [isAuthenticated, userId, guestId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const syncProfile = useCallback(async () => {
    if (!keycloak.authenticated) {
      setUserProfile(null);
      setUserId(null);
       setIsAuthenticated(false);
      trackEvent('profile_sync_skipped', { reason: 'not_authenticated' });
      return;
    }
    try {
      const res = await apiV1.post('/user/profile/sync', {});
      setUserProfile(res.data);
      setUserId(res.data?.user_id || null);
      setIsAuthenticated(true);
      trackEvent('profile_sync_success', { has_user_id: Boolean(res.data?.user_id) });
    } catch (err) {
      console.error('Error syncing profile from server:', err);
      trackEvent('profile_sync_failed', { message: err?.message || 'unknown_error' });
      const tokenProfile = getTokenProfile();
      if (tokenProfile.sub) {
        setUserProfile((prev) => prev || {
          user_id: tokenProfile.sub,
          first_name: tokenProfile.given_name || tokenProfile.name || null,
          last_name: tokenProfile.family_name || null,
          email: tokenProfile.email || null,
        });
        setUserId(tokenProfile.sub);
        setIsAuthenticated(true);
      }
    }
  }, []);

  useEffect(() => {
    if (AUTH_OFF) {
      return;
    }
    if (authReady && isAuthenticated) {
      syncProfile();
    } else if (authReady) {
      setUserProfile(null);
      setUserId(null);
    }
  }, [authReady, isAuthenticated, syncProfile]);

  useEffect(() => {
    let cancelled = false;

    if (!authReady) {
      return () => {
        cancelled = true;
      };
    }

    if (!isAuthenticated) {
      setAuthorPreferences(EMPTY_AUTHOR_PREFERENCES);
      setAuthorPreferencesLoading(false);
      setAuthorPreferencesSaving(false);
      setAuthorPreferencesError('');
      savedDefaultLaunchKeyRef.current = '';
      return () => {
        cancelled = true;
      };
    }

    const loadAuthorPreferences = async () => {
      setAuthorPreferencesLoading(true);
      setAuthorPreferencesError('');
      try {
        const res = await apiV1.get('/user/preferences/authors');
        if (!cancelled) {
          setAuthorPreferences(normalizeAuthorPreferencesPayload(res.data));
        }
      } catch (err) {
        if (!cancelled) {
          const detail = cleanText(err?.response?.data?.detail) || err?.message || 'Failed to load author preferences';
          setAuthorPreferences(EMPTY_AUTHOR_PREFERENCES);
          setAuthorPreferencesError(detail);
        }
      } finally {
        if (!cancelled) {
          setAuthorPreferencesLoading(false);
        }
      }
    };

    loadAuthorPreferences();
    return () => {
      cancelled = true;
    };
  }, [authReady, isAuthenticated]);

  useEffect(() => {
    const fetchAuthors = async () => {
      try {
        const res = await apiV1.get('/authors');
        const nextAuthors = (Array.isArray(res.data) ? res.data : []).map(applyLocalAuthorPortraitMetadata);
        const requestedAuthor = getAuthorSlugFromPath(window.location.pathname);
        const hasRequestedAuthorRoute = Boolean(requestedAuthor);
        setAuthors(nextAuthors);
        if (hasRequestedAuthorRoute) {
          if (nextAuthors.some((author) => author.slug === requestedAuthor) && requestedAuthor !== persona) {
            setPersona(requestedAuthor);
          }
          return;
        }
        if (!nextAuthors.some((author) => author.slug === persona)) {
          const first = nextAuthors[0];
          if (first) {
            setPersona(first.slug);
          }
        }
      } catch (err) {
        console.error('Error fetching authors:', err);
      }
    };
    fetchAuthors();
  }, [persona]);

  useEffect(() => {
    if (!authReady || isAuthenticated) {
      return;
    }
    if (guestHomepageSelectionRef.current) {
      return;
    }
    if (!isHomepagePath(initialPathname)) {
      guestHomepageSelectionRef.current = true;
      return;
    }
    if (getAuthorSlugFromPath(initialPathname)) {
      guestHomepageSelectionRef.current = true;
      return;
    }
    if (messages.length > 0) {
      guestHomepageSelectionRef.current = true;
      return;
    }
    if (authorBrowseItems.length === 0 && authorBrowseLoading) {
      return;
    }
    if (authors.length === 0) {
      return;
    }

    const storedGuestPersona = readGuestHomepageAuthor();
    const nextPersona = pickGuestHomepageAuthor({
      browseItems: authorBrowseItems,
      authors,
    });
    guestHomepageSelectionRef.current = true;

    if (!nextPersona) {
      return;
    }

    writeGuestHomepageAuthor(nextPersona);
    if (nextPersona !== persona) {
      trackEvent('guest_homepage_author_preselected', {
        persona: nextPersona,
        source: nextPersona === storedGuestPersona ? 'session' : 'catalog',
      });
      setPersona(nextPersona);
    }
  }, [
    authReady,
    isAuthenticated,
    initialPathname,
    messages.length,
    authorBrowseItems,
    authorBrowseLoading,
    authors,
    persona,
  ]);

  useEffect(() => {
    let cancelled = false;

    const fetchProverbsContent = async () => {
      setProverbsContentLoading(true);
      setProverbsContentError('');
      try {
        const res = await apiV1.get('/proverbs/content');
        if (!cancelled) {
          setProverbsContent(res.data || null);
        }
      } catch (err) {
        if (!cancelled) {
          const detail = cleanText(err?.response?.data?.detail) || err?.message || 'Failed to load Proverbs content';
          setProverbsContent(null);
          setProverbsContentError(detail);
        }
      } finally {
        if (!cancelled) {
          setProverbsContentLoading(false);
        }
      }
    };

    fetchProverbsContent();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchAuthorBrowse = async () => {
      setAuthorBrowseLoading(true);
      setAuthorBrowseError('');
      try {
        const res = await apiV1.get('/authors/browse');
        if (!cancelled) {
          const nextItems = (Array.isArray(res.data?.items) ? res.data.items : []).map(applyLocalAuthorPortraitMetadata);
          setAuthorBrowseItems(nextItems);
        }
      } catch (err) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          setAuthorBrowseItems([]);
          setAuthorBrowseError(detail || err.message || 'Unknown error');
        }
      } finally {
        if (!cancelled) {
          setAuthorBrowseLoading(false);
        }
      }
    };

    fetchAuthorBrowse();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchAuthorProfile = async () => {
      const selected = (persona || '').toLowerCase();
      if (!selected) {
        setAuthorProfile(null);
        setAuthorProfileError('');
        setAuthorProfileLoading(false);
        return;
      }

      const selectedInAuthors = hasAuthorSlug(authors, selected);
      const selectedInBrowse = hasAuthorSlug(authorBrowseItems, selected);
      if (
        authors.length > 0
        && !selectedInAuthors
        && !selectedInBrowse
        && authorBrowseLoading
        && authorBrowseItems.length === 0
      ) {
        setAuthorProfileLoading(true);
        setAuthorProfileError('');
        return;
      }

      if ((authors.length > 0 || authorBrowseItems.length > 0) && !selectedInAuthors && !selectedInBrowse) {
        setAuthorProfile(null);
        setAuthorProfileError('');
        setAuthorProfileLoading(false);
        return;
      }

      setAuthorProfileLoading(true);
      setAuthorProfileError('');
      try {
        const res = await apiV1.get(`/authors/${selected}/profile`);
        if (!cancelled) {
          setAuthorProfile(applyLocalAuthorPortraitMetadata(res.data || null));
        }
      } catch (err) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          setAuthorProfile(null);
          setAuthorProfileError(detail || err.message || 'Unknown error');
        }
      } finally {
        if (!cancelled) {
          setAuthorProfileLoading(false);
        }
      }
    };

    fetchAuthorProfile();
    return () => {
      cancelled = true;
    };
  }, [persona, authors, authorBrowseItems, authorBrowseLoading]);

  useEffect(() => {
    let cancelled = false;

    const fetchCrossrefCatalog = async () => {
      setCrossrefCatalogLoading(true);
      setCrossrefCatalogError('');
      try {
        const res = await apiV1.get('/crossrefs/books', { params: { limit: 500 } });
        if (!cancelled) {
          setCrossrefCatalog(Array.isArray(res.data?.items) ? res.data.items : []);
        }
      } catch (err) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          setCrossrefCatalog([]);
          setCrossrefCatalogError(detail || err.message || 'Unknown error');
        }
      } finally {
        if (!cancelled) {
          setCrossrefCatalogLoading(false);
        }
      }
    };

    fetchCrossrefCatalog();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchAuthorCrossrefs = async () => {
      const selected = (persona || '').toLowerCase();
      if (!selected) {
        setAuthorCrossrefs(null);
        setAuthorCrossrefsError('');
        setAuthorCrossrefsLoading(false);
        return;
      }

      const selectedInAuthors = hasAuthorSlug(authors, selected);
      const selectedInBrowse = hasAuthorSlug(authorBrowseItems, selected);
      if (
        authors.length > 0
        && !selectedInAuthors
        && !selectedInBrowse
        && authorBrowseLoading
        && authorBrowseItems.length === 0
      ) {
        setAuthorCrossrefsLoading(true);
        setAuthorCrossrefsError('');
        return;
      }

      if ((authors.length > 0 || authorBrowseItems.length > 0) && !selectedInAuthors && !selectedInBrowse) {
        setAuthorCrossrefs(null);
        setAuthorCrossrefsError('');
        setAuthorCrossrefsLoading(false);
        return;
      }

      setAuthorCrossrefsLoading(true);
      setAuthorCrossrefsError('');
      try {
        const res = await apiV1.get(`/crossrefs/authors/${selected}`);
        if (!cancelled) {
          setAuthorCrossrefs(res.data || null);
        }
      } catch (err) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          setAuthorCrossrefs(null);
          setAuthorCrossrefsError(detail || err.message || 'Unknown error');
        }
      } finally {
        if (!cancelled) {
          setAuthorCrossrefsLoading(false);
        }
      }
    };

    fetchAuthorCrossrefs();
    return () => {
      cancelled = true;
    };
  }, [persona, authors, authorBrowseItems, authorBrowseLoading]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!activeLaunchState || launchUrlScrubbedRef.current) {
      return;
    }

    scrubClockLaunchParamsFromUrl();
    launchUrlScrubbedRef.current = true;
    trackEvent('clock_launch_bootstrapped', {
      source: activeLaunchState.source,
      has_clock_context: Boolean(activeLaunchState.clockContext),
      has_message: Boolean(activeLaunchState.initialMessage),
    });
  }, [activeLaunchState]);

  useEffect(() => {
    if (!pendingResumeSessionId || resumeUrlScrubbedRef.current) {
      return;
    }

    scrubResumeSessionParamFromUrl();
    resumeUrlScrubbedRef.current = true;
    trackEvent('clock_session_resume_bootstrapped', {
      session_id: pendingResumeSessionId,
    });
  }, [pendingResumeSessionId]);

  useEffect(() => {
    if (!activeLaunchState || messages.length > 0) {
      return;
    }

    const launchKey = `${sessionId || 'session'}:${activeLaunchState.initialMessage}`;
    if (launchPrefillRef.current === launchKey) {
      return;
    }

    launchPrefillRef.current = launchKey;
    if (activeLaunchState.initialMessage) {
      setQuery(activeLaunchState.initialMessage);
    }
  }, [activeLaunchState, messages.length, sessionId]);

  useEffect(() => {
    if (chatStatus.phase !== 'success') {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setChatStatus((prev) => (prev.phase === 'success' ? { ...CHAT_STATUS_IDLE } : prev));
    }, 3000);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [chatStatus.phase]);

  const getPersonaName = (slug) => {
    const author = authors.find((a) => a.slug === slug);
    return author ? author.name : formatSlugLabel(slug || 'Unknown persona');
  };

  useEffect(() => {
    if (!persona) {
      return;
    }
    setCounselAuthorSlugs((prev) => {
      const allowed = new Set(authors.map((author) => cleanText(author?.slug).toLowerCase()).filter(Boolean));
      const normalized = normalizeAuthorSlugList(prev).filter((slug) => !allowed.size || allowed.has(slug));
      return normalized.length > 0 ? normalized : [persona];
    });
  }, [authors, persona]);

  const addCounselAuthor = (slug) => {
    const normalized = cleanText(slug || persona).toLowerCase();
    if (!normalized) {
      return;
    }
    setCounselAuthorSlugs((prev) => {
      const next = normalizeAuthorSlugList([...prev, normalized]);
      return next.length > 0 ? next : [normalized];
    });
    trackEvent('counsel_author_added', {
      persona,
      counsel_author: normalized,
    });
  };

  const removeCounselAuthor = (slug) => {
    const normalized = cleanText(slug).toLowerCase();
    if (!normalized) {
      return;
    }
    setCounselAuthorSlugs((prev) => {
      const next = normalizeAuthorSlugList(prev).filter((item) => item !== normalized);
      return next.length > 0 ? next : (persona ? [persona] : []);
    });
    setCounselEntries((prev) => prev.filter((entry) => cleanText(entry?.authorSlug).toLowerCase() !== normalized));
    trackEvent('counsel_author_removed', {
      persona,
      counsel_author: normalized,
    });
  };

  const authorNameBySlug = useMemo(() => {
    const map = new Map();
    authors.forEach((author) => {
      if (author?.slug) {
        map.set(author.slug, author.name || author.slug);
      }
    });
    return map;
  }, [authors]);

  const favoriteAuthorSlugs = useMemo(
    () => uniqueTextList(
      Array.isArray(authorPreferences?.favorite_authors) ? authorPreferences.favorite_authors : [],
    ).map((slug) => cleanText(slug).toLowerCase()).filter(Boolean),
    [authorPreferences],
  );
  const tokenProfile = getTokenProfile() || {};
  const tokenRoles = normalizeRoleList([
    ...(Array.isArray(tokenProfile.roles) ? tokenProfile.roles : []),
    ...(Array.isArray(keycloak?.tokenParsed?.realm_access?.roles) ? keycloak.tokenParsed.realm_access.roles : []),
  ]);
  const canInspectTraceAudit = isAuthenticated && hasAnyRole(tokenRoles, 'admin', 'operator');

  const savedDefaultAuthorSlug = cleanText(authorPreferences?.default_author_slug).toLowerCase();
  const availableSavedDefaultAuthorSlug = useMemo(() => {
    if (!savedDefaultAuthorSlug) {
      return '';
    }
    return authors.some((author) => cleanText(author?.slug).toLowerCase() === savedDefaultAuthorSlug)
      ? savedDefaultAuthorSlug
      : '';
  }, [authors, savedDefaultAuthorSlug]);

  const crossrefBookLookup = useMemo(() => {
    const lookup = new Map();
    crossrefCatalog.forEach((item) => {
      const byId = normalizeBookKey(item.book_id);
      if (byId && !lookup.has(byId)) {
        lookup.set(byId, item);
      }
      const byName = normalizeBookKey(item.name);
      if (byName && !lookup.has(byName)) {
        lookup.set(byName, item);
      }
    });
    return lookup;
  }, [crossrefCatalog]);

  const authorMentionCatalog = useMemo(() => {
    const bibleBookKeys = new Set(Array.from(crossrefBookLookup.keys()));
    return authors
      .map((author) => {
        const slug = cleanText(author?.slug).toLowerCase();
        const name = cleanText(author?.name) || formatSlugLabel(slug);
        const nameKey = normalizeBookKey(name);
        if (!slug || !name || slug === persona || name.length < 5 || bibleBookKeys.has(nameKey)) {
          return null;
        }
        return { slug, name, pattern: new RegExp(`\\b${escapeRegex(name)}\\b`, 'i') };
      })
      .filter(Boolean)
      .sort((left, right) => right.name.length - left.name.length);
  }, [authors, crossrefBookLookup, persona]);

  const workMentionCatalog = useMemo(() => {
    const bibleBookKeys = new Set(Array.from(crossrefBookLookup.keys()));
    const byKey = new Map();
    const registerWork = (label, authorSlug, authorName) => {
      const workLabel = cleanText(label);
      const slug = cleanText(authorSlug).toLowerCase();
      const authorLabel = cleanText(authorName) || formatSlugLabel(slug);
      const workKey = normalizeBookKey(workLabel);
      if (!workLabel || !slug || workLabel.length < 5 || bibleBookKeys.has(workKey) || byKey.has(workKey)) {
        return;
      }
      byKey.set(workKey, {
        label: workLabel,
        targetAuthorSlug: slug,
        targetAuthorName: authorLabel,
        pattern: new RegExp(`\\b${escapeRegex(workLabel)}\\b`, 'i'),
      });
    };

    authorBrowseItems.forEach((item) => {
      const slug = cleanText(item?.slug).toLowerCase();
      const authorName = cleanText(item?.name) || formatSlugLabel(slug);
      const works = Array.isArray(item?.works_preview) ? item.works_preview : [];
      works.forEach((work) => {
        registerWork(work?.book || work?.work || work?.source, slug, authorName);
      });
    });

    if (cleanText(authorProfile?.slug)) {
      const slug = cleanText(authorProfile.slug).toLowerCase();
      const authorName = cleanText(authorProfile?.name) || formatSlugLabel(slug);
      const books = Array.isArray(authorProfile?.books) ? authorProfile.books : [];
      books.forEach((work) => {
        registerWork(work?.book || work?.work || work?.source, slug, authorName);
      });
    }

    return Array.from(byKey.values()).sort((left, right) => right.label.length - left.label.length);
  }, [authorBrowseItems, authorProfile, crossrefBookLookup]);

  const persistAuthorPreferences = useCallback(async (payload) => {
    if (!isAuthenticated) {
      return EMPTY_AUTHOR_PREFERENCES;
    }
    setAuthorPreferencesSaving(true);
    setAuthorPreferencesError('');
    try {
      const res = await apiV1.put('/user/preferences/authors', payload);
      const normalized = normalizeAuthorPreferencesPayload(res.data);
      setAuthorPreferences(normalized);
      return normalized;
    } catch (err) {
      const detail = cleanText(err?.response?.data?.detail) || err?.message || 'Failed to save author preferences';
      setAuthorPreferencesError(detail);
      throw err;
    } finally {
      setAuthorPreferencesSaving(false);
    }
  }, [isAuthenticated]);

  const handleToggleFavoriteAuthor = useCallback(async (slug) => {
    const normalizedSlug = cleanText(slug).toLowerCase();
    if (!isAuthenticated || !normalizedSlug) {
      return;
    }
    const favoriteSet = new Set(favoriteAuthorSlugs);
    if (favoriteSet.has(normalizedSlug)) {
      favoriteSet.delete(normalizedSlug);
    } else {
      favoriteSet.add(normalizedSlug);
    }
    const nextFavorites = Array.from(favoriteSet);
    const nextDefault = cleanText(authorPreferences?.default_author_slug).toLowerCase() || null;
    await persistAuthorPreferences({
      favorite_authors: nextFavorites,
      default_author_slug: nextDefault,
    });
  }, [authorPreferences, favoriteAuthorSlugs, isAuthenticated, persistAuthorPreferences]);

  const handleSetDefaultAuthor = useCallback(async (slug) => {
    const normalizedSlug = cleanText(slug).toLowerCase();
    if (!isAuthenticated || !normalizedSlug) {
      return;
    }
    const favoriteSet = new Set(favoriteAuthorSlugs);
    favoriteSet.add(normalizedSlug);
    await persistAuthorPreferences({
      favorite_authors: Array.from(favoriteSet),
      default_author_slug: normalizedSlug,
    });
  }, [favoriteAuthorSlugs, isAuthenticated, persistAuthorPreferences]);

  const referencedCrossrefBooks = useMemo(() => {
    const labels = new Set();
    references.forEach((ref) => {
      if (ref?.book) {
        labels.add(ref.book);
      }
    });
    metaInfo.forEach((meta) => {
      if (meta?.book) {
        labels.add(meta.book);
      }
    });

    const matched = new Map();
    labels.forEach((label) => {
      const hit = crossrefBookLookup.get(normalizeBookKey(label));
      if (hit?.book_id && !matched.has(hit.book_id)) {
        matched.set(hit.book_id, hit);
      }
    });

    return Array.from(matched.values()).sort(
      (a, b) => Number(a.canonical_order || 9999) - Number(b.canonical_order || 9999),
    );
  }, [references, metaInfo, crossrefBookLookup]);

  const relatedAuthors = useMemo(() => {
    const bySlug = new Map();
    (Array.isArray(authorCrossrefs?.related_references) ? authorCrossrefs.related_references : []).forEach((item) => {
      const slug = String(item?.target_slug || '').toLowerCase();
      const targetType = cleanText(item?.target_type).toLowerCase();
      if (!slug || slug === persona || targetType !== 'author') {
        return;
      }
      if (!bySlug.has(slug)) {
        bySlug.set(slug, {
          slug,
          name: cleanText(item?.target_label) || authorNameBySlug.get(slug) || slug,
          sampleBookId: '',
          sampleBook: cleanText(item?.evidence_work),
          relationship: getCrossrefRelationshipLabel(item),
        });
      }
    });
    referencedCrossrefBooks.forEach((item) => {
      const slug = String(item.author_slug || '').toLowerCase();
      if (!slug || slug === persona) {
        return;
      }
      if (!bySlug.has(slug)) {
        bySlug.set(slug, {
          slug,
          name: item.author_name || authorNameBySlug.get(slug) || slug,
          sampleBookId: item.book_id || '',
          sampleBook: item.name || item.book_id || '',
          relationship: 'canonical author',
        });
      }
    });
    return Array.from(bySlug.values());
  }, [authorCrossrefs, referencedCrossrefBooks, persona, authorNameBySlug]);

  const curatedRelatedReferences = useMemo(() => (
    Array.isArray(authorCrossrefs?.related_references)
      ? authorCrossrefs.related_references
          .map((item) => ({
            ...item,
            target_label: cleanText(item?.target_label),
            target_slug: cleanText(item?.target_slug).toLowerCase(),
            target_type: cleanText(item?.target_type).toLowerCase(),
            evidence_work: cleanText(item?.evidence_work),
            evidence_snippet: cleanText(item?.evidence_snippet),
          }))
          .filter((item) => item.target_label)
      : []
  ), [authorCrossrefs]);

  const buildReferenceFromMeta = (meta) => {
    const position = parseInt(meta.position, 10);
    const excerpt = meta.excerpt || '';
    const book = meta.book || meta.source || 'Reference';
    const verseStart = toOptionalInt(meta.verse_start);
    const verseEnd = toOptionalInt(meta.verse_end);
    return {
      book,
      displayLabel: meta.reference || formatReferenceLabel(book, meta.chapter, verseStart, verseEnd),
      content: excerpt || 'Reference available.',
      source: meta.source,
      chapter: meta.chapter,
      page: meta.page,
      verseStart,
      verseEnd,
      position: Number.isNaN(position) ? null : position,
      expanded: false,
      expandable: Boolean(meta.source && !Number.isNaN(position)),
      authorTraditional: meta.author_traditional,
      authorCritical: meta.author_critical,
      sourceTheory: meta.source_theory,
      section: meta.section || '',
      testament: meta.testament || '',
      provenance: meta.provenance || 'retrieved',
      targetType: 'bible',
      targetSlug: '',
      targetAuthorSlug: '',
      authorSlug: persona,
      detector: '',
      confidence: null,
    };
  };

  const buildReferenceFromDerived = (row) => {
    const label = cleanText(row?.target_label || row?.target_value);
    const targetType = cleanText(row?.target_type).toLowerCase();
    const parsedTarget = targetType === 'bible' ? parseReferenceLabel(label) : null;
    const position = toOptionalInt(row?.source_position);
    const source = cleanText(row?.source_source);
    const sourceBook = cleanText(row?.source_book || row?.source_work);
    const chapter = cleanText(row?.source_chapter || parsedTarget?.chapter);
    const verseStart = parsedTarget?.verseStart ?? null;
    const verseEnd = parsedTarget?.verseEnd ?? null;
    if (!label || !targetType) {
      return null;
    }
    return {
      book: sourceBook || parsedTarget?.book || label,
      displayLabel: label,
      content: cleanText(row?.evidence_excerpt) || `Inferred ${targetType} reference from retrieved context.`,
      source,
      chapter,
      page: cleanText(row?.source_page),
      verseStart,
      verseEnd,
      position,
      expanded: !Boolean(source && position != null),
      expandable: Boolean(source && position != null),
      authorTraditional: '',
      authorCritical: '',
      sourceTheory: '',
      section: '',
      testament: '',
      provenance: cleanText(row?.provenance) || 'inferred_from_excerpt',
      targetType,
      targetSlug: cleanText(row?.target_slug),
      targetAuthorSlug: cleanText(row?.target_author_slug),
      authorSlug: cleanText(row?.source_author_slug) || persona,
      detector: cleanText(row?.detector),
      confidence: Number.isFinite(Number(row?.confidence)) ? Number(row.confidence) : null,
    };
  };

  const citationBookRegex = useMemo(() => {
    const names = Array.from(
      new Set(
        crossrefCatalog
          .map((item) => String(item?.name || '').trim())
          .filter(Boolean),
      ),
    )
      .sort((a, b) => b.length - a.length)
      .map(escapeRegex);
    if (!names.length) {
      return null;
    }
    return new RegExp(`\\b(${names.join('|')})\\s+(\\d{1,3})(?::(\\d{1,3})(?:-(\\d{1,3}))?)?\\b`, 'gi');
  }, [crossrefCatalog]);

  const extractInlineScriptureReferences = useCallback((rawText, provenance = 'inferred_from_answer') => {
    const text = String(rawText || '');
    if (!text || !citationBookRegex) {
      return [];
    }

    const found = [];
    const seen = new Set();
    let match;

    while ((match = citationBookRegex.exec(text)) !== null) {
      const rawBook = String(match[1] || '').trim();
      const chapter = String(match[2] || '').trim();
      const verseStart = toOptionalInt(match[3]);
      const verseEnd = toOptionalInt(match[4]) ?? verseStart;
      const item = crossrefBookLookup.get(normalizeBookKey(rawBook));
      const book = item?.name || rawBook;
      const label = formatReferenceLabel(book, chapter, verseStart, verseEnd);
      const key = normalizeBookKey(`${book}|${chapter}|${verseStart ?? ''}|${verseEnd ?? ''}`);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      found.push({
        book,
        displayLabel: label,
        content: provenance === 'inferred_from_excerpt'
          ? 'Referenced in retrieved source excerpt.'
          : 'Referenced in answer text.',
        source: '',
        chapter,
        page: '',
        verseStart,
        verseEnd,
        position: null,
        expanded: true,
        expandable: false,
        authorTraditional: '',
        authorCritical: '',
        sourceTheory: '',
        section: item?.section || '',
        testament: item?.testament || '',
        provenance,
        targetType: 'bible',
        targetSlug: '',
        targetAuthorSlug: '',
        authorSlug: persona,
        detector: 'regex',
        confidence: 1,
      });
    }

    return found;
  }, [citationBookRegex, crossrefBookLookup, persona]);

  const extractInlineBookMentions = useCallback((rawText, provenance = 'inferred_from_answer') => {
    const text = String(rawText || '');
    if (!text) {
      return [];
    }

    const mentionRegex = /\b(?:gospel of|book of|epistle(?:s)? of|letter to|according to)\s+([a-z0-9 ]{2,40})\b/gi;
    const found = [];
    const seen = new Set();
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      const candidate = String(match[1] || '').trim();
      const hit = crossrefBookLookup.get(normalizeBookKey(candidate));
      if (!hit?.name) {
        continue;
      }
      const book = String(hit.name).trim();
      const key = normalizeBookKey(book);
      if (!key || seen.has(key)) {
        continue;
      }
      seen.add(key);
      found.push({
        book,
        displayLabel: book,
        content: provenance === 'inferred_from_excerpt'
          ? 'Book mention inferred from retrieved source excerpt.'
          : 'Book mention inferred from answer text.',
        source: '',
        chapter: '',
        page: '',
        verseStart: null,
        verseEnd: null,
        position: null,
        expanded: true,
        expandable: false,
        authorTraditional: '',
        authorCritical: '',
        sourceTheory: '',
        section: hit.section || '',
        testament: hit.testament || '',
        provenance,
        targetType: 'bible',
        targetSlug: '',
        targetAuthorSlug: '',
        authorSlug: persona,
        detector: 'pattern_match',
        confidence: 0.7,
      });
    }

    return found;
  }, [crossrefBookLookup, persona]);

  const extractInlineAuthorMentions = useCallback((rawText, provenance = 'inferred_from_answer') => {
    const text = String(rawText || '');
    if (!text) {
      return [];
    }

    const found = [];
    const seen = new Set();
    authorMentionCatalog.forEach((item) => {
      if (!item.pattern.test(text) || seen.has(item.slug)) {
        return;
      }
      seen.add(item.slug);
      found.push({
        book: item.name,
        displayLabel: item.name,
        content: provenance === 'inferred_from_excerpt'
          ? 'Author mention inferred from retrieved source excerpt.'
          : 'Author mention inferred from answer text.',
        source: '',
        chapter: '',
        page: '',
        verseStart: null,
        verseEnd: null,
        position: null,
        expanded: true,
        expandable: false,
        authorTraditional: '',
        authorCritical: '',
        sourceTheory: '',
        section: '',
        testament: '',
        provenance,
        targetType: 'author',
        targetSlug: item.slug,
        targetAuthorSlug: item.slug,
        authorSlug: persona,
        detector: 'catalog_match',
        confidence: 0.7,
      });
    });
    return found;
  }, [authorMentionCatalog, persona]);

  const extractInlineWorkMentions = useCallback((rawText, provenance = 'inferred_from_answer') => {
    const text = String(rawText || '');
    if (!text) {
      return [];
    }

    const found = [];
    const seen = new Set();
    workMentionCatalog.forEach((item) => {
      if (!item.pattern.test(text)) {
        return;
      }
      const key = `${item.targetAuthorSlug}:${normalizeBookKey(item.label)}`;
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      found.push({
        book: item.label,
        displayLabel: item.label,
        content: provenance === 'inferred_from_excerpt'
          ? 'Work mention inferred from retrieved source excerpt.'
          : 'Work mention inferred from answer text.',
        source: '',
        chapter: '',
        page: '',
        verseStart: null,
        verseEnd: null,
        position: null,
        expanded: true,
        expandable: false,
        authorTraditional: '',
        authorCritical: '',
        sourceTheory: '',
        section: '',
        testament: '',
        provenance,
        targetType: 'work',
        targetSlug: '',
        targetAuthorSlug: item.targetAuthorSlug,
        authorSlug: persona,
        detector: 'catalog_match',
        confidence: 0.65,
      });
    });
    return found;
  }, [persona, workMentionCatalog]);

  const buildMetaSummary = (metadataList) => {
    const summary = new Map();
    metadataList.forEach((meta, idx) => {
      const book = meta.book || meta.work || meta.source || `Reference ${idx + 1}`;
      if (summary.has(book)) {
        return;
      }
      summary.set(book, {
        key: `${book}-${idx}`,
        book,
        authorTraditional: meta.author_traditional || '',
        authorCritical: meta.author_critical || '',
        sourceTheory: meta.source_theory || '',
        section: meta.section || '',
        testament: meta.testament || '',
      });
    });
    return Array.from(summary.values());
  };

  const buildReferenceBundle = (metadataList, answerText = '', derivedRefs = []) => {
    const base = Array.isArray(metadataList) ? metadataList.map(buildReferenceFromMeta) : [];
    const inferredFromAnswer = extractInlineScriptureReferences(answerText, 'inferred_from_answer');
    const inferredFromExcerpts = (Array.isArray(metadataList) ? metadataList : [])
      .flatMap((meta) => extractInlineScriptureReferences(meta?.excerpt || '', 'inferred_from_excerpt'));
    const inferredAuthorsFromAnswer = extractInlineAuthorMentions(answerText, 'inferred_from_answer');
    const inferredWorksFromAnswer = extractInlineWorkMentions(answerText, 'inferred_from_answer');
    const inferredMentionsFromAnswer = extractInlineBookMentions(answerText, 'inferred_from_answer');
    const inferredMentionsFromExcerpts = (Array.isArray(metadataList) ? metadataList : [])
      .flatMap((meta) => extractInlineBookMentions(meta?.excerpt || '', 'inferred_from_excerpt'));
    const candidates = [
      ...base,
      ...inferredFromAnswer,
      ...inferredFromExcerpts,
      ...inferredAuthorsFromAnswer,
      ...inferredWorksFromAnswer,
      ...inferredMentionsFromAnswer,
      ...inferredMentionsFromExcerpts,
      ...(Array.isArray(derivedRefs) ? derivedRefs.map(buildReferenceFromDerived).filter(Boolean) : []),
    ];
    const mergedByKey = new Map();
    candidates.forEach((ref) => {
      const key = buildReferenceDedupKey(ref);
      mergedByKey.set(key, mergeReferenceRows(mergedByKey.get(key), ref));
    });
    const merged = Array.from(mergedByKey.values());

    return {
      references: merged,
      metaInfo: buildMetaSummary(Array.isArray(metadataList) ? metadataList : []),
    };
  };

  const buildChatFailureStatus = (statusCode, errorObj) => {
    const detail = String(errorObj?.response?.data?.detail || '').trim();
    const fallback = String(errorObj?.message || '').trim();
    const text = detail || fallback || 'Unexpected request failure.';
    const timedOut = statusCode === 504 || /timeout/i.test(text) || errorObj?.code === 'ECONNABORTED';

    if (statusCode === 401) {
      return {
        phase: 'error',
        title: 'Authentication Required',
        message: 'Your session or API key is invalid/missing. Sign in again and retry.',
        retryable: true,
        statusCode: 401,
        retryAction: 'chat',
        retryLabel: 'Retry',
      };
    }
    if (statusCode === 403) {
      return {
        phase: 'error',
        title: 'Access Denied',
        message: 'Your account does not have permission for this action/profile.',
        retryable: false,
        statusCode: 403,
        retryAction: 'chat',
        retryLabel: 'Retry',
      };
    }
    if (timedOut) {
      const elapsedMs = Number(errorObj?.elapsedMs || 0);
      return {
        phase: 'timeout',
        title: 'Upstream Timeout',
        message: elapsedMs
          ? `The chat service did not answer within ${formatElapsedSeconds(elapsedMs)}. Retry this request.`
          : 'The upstream service timed out. Retry this request.',
        elapsedMs,
        retryable: true,
        statusCode: statusCode || 504,
        retryAction: 'chat',
        retryLabel: 'Retry',
      };
    }
    if (statusCode >= 500) {
      return {
        phase: 'error',
        title: 'Service Unavailable',
        message: 'The service failed upstream. Retry in a moment.',
        retryable: true,
        statusCode: statusCode,
        retryAction: 'chat',
        retryLabel: 'Retry',
      };
    }
    return {
      phase: 'error',
      title: 'Request Failed',
      message: text,
      retryable: true,
      statusCode: statusCode || 0,
      retryAction: 'chat',
      retryLabel: 'Retry',
    };
  };

  const buildSynthesisFailureStatus = (statusCode, errorObj) => {
    const detail = String(errorObj?.response?.data?.detail || '').trim();
    const base = buildChatFailureStatus(statusCode, errorObj);
    if (statusCode === 401 || statusCode === 403) {
      return {
        ...base,
        retryAction: statusCode === 401 ? 'synthesis' : 'chat',
      };
    }
    if (base.phase === 'timeout') {
      return {
        ...base,
        title: 'Synthesis Timed Out',
        message: 'The synthesis request did not finish in time. Retry to derive the integrated principle again.',
        retryAction: 'synthesis',
      };
    }
    return {
      ...base,
      title: 'Synthesis Failed',
      message: detail || 'The synthesis service failed. Retry in a moment.',
      retryAction: 'synthesis',
    };
  };

  const expandReference = async (index) => {
    const ref = references[index];
    if (!ref || !ref.expandable || ref.expanded || ref.position == null) {
      return;
    }
    try {
      const res = await apiV1.post('/book_partial', {
        author_slug: cleanText(ref?.authorSlug) || persona,
        book: ref.book,
        source: ref.source,
        chapter: ref.chapter,
        page: ref.page,
        start_position: ref.position,
        end_position: ref.position + 1,
      });
      setReferences((prev) =>
        prev.map((item, idx) =>
          idx === index
            ? {
                ...item,
                content: res.data.content,
                chapter: res.data.chapter || item.chapter,
                verseStart: toOptionalInt(res.data.verse_start) ?? item.verseStart,
                verseEnd: toOptionalInt(res.data.verse_end) ?? item.verseEnd,
                displayLabel: res.data.reference || formatReferenceLabel(
                  res.data.book || item.book,
                  res.data.chapter || item.chapter,
                  toOptionalInt(res.data.verse_start) ?? item.verseStart,
                  toOptionalInt(res.data.verse_end) ?? item.verseEnd,
                ),
                section: res.data.section || item.section,
                testament: res.data.testament || item.testament,
                expanded: true,
              }
            : item,
        )
      );
      trackEvent('reference_expanded', {
        persona,
        reference_book: ref.book,
      });
    } catch (err) {
      console.error('Error expanding book reference:', err);
      const status = err.response?.status;
      trackEvent('reference_expand_failed', {
        persona,
        status_code: status || 0,
      });
      if (status === 404) {
        setError('Book or chapter not found');
      } else if (status >= 500) {
        setError("I'm sorry, I can not connect right now.");
      } else {
        setError('Error fetching book reference');
      }
    }
  };

  const applyMessageReferenceSelection = (referenceList, metaInfoList = [], selectedIndex = 0) => {
    const referencesForMessage = Array.isArray(referenceList)
      ? referenceList.filter((reference) => cleanText(reference?.displayLabel || reference?.book))
      : [];
    if (!referencesForMessage.length) {
      return;
    }

    const safeIndex = Number.isFinite(Number(selectedIndex))
      ? Math.max(0, Math.min(Number(selectedIndex), referencesForMessage.length - 1))
      : 0;
    const prioritizedReferences = [
      referencesForMessage[safeIndex],
      ...referencesForMessage.filter((_reference, index) => index !== safeIndex),
    ];
    setReferences(prioritizedReferences);
    setMetaInfo(Array.isArray(metaInfoList) ? metaInfoList : []);
    trackEvent('message_reference_selected', {
      persona,
      reference_book: cleanText(prioritizedReferences[0]?.book),
      reference_label: cleanText(prioritizedReferences[0]?.displayLabel),
    });
  };

  const sendMessage = async (messageText, matterOverride = '', options = {}) => {
    const isAskProverbsRequest = cleanText(options?.mode) === ASK_PROVERBS_MODE;
    const priorPhase = chatStatus.phase;
    const requestStartedAt = nowMs();
    let waitTimer = null;
    setLoading(true);
    setChatStatus({
      phase: 'sending',
      title: 'Sending Request',
      message: buildChatWaitMessage(0),
      elapsedMs: 0,
      retryable: false,
      statusCode: null,
      retryAction: 'chat',
      retryLabel: 'Retry',
    });
    try {
      waitTimer = window.setInterval(() => {
        const elapsedMs = roundMs(nowMs() - requestStartedAt);
        setChatStatus((prev) => {
          if (prev.phase !== 'sending' || prev.retryAction !== 'chat') {
            return prev;
          }
          return {
            ...prev,
            message: buildChatWaitMessage(elapsedMs),
            elapsedMs,
          };
        });
      }, CHAT_WAIT_UPDATE_MS);
      const activeUserId = userId || guestId || getTokenProfile().sub || undefined;
      const requestedPersona = cleanText(options?.personaOverride || persona).toLowerCase();
      const activePersona = isAskProverbsRequest ? ASK_PROVERBS_PERSONA : (requestedPersona || persona);
      const chatMode = isAskProverbsRequest ? ASK_PROVERBS_MODE : 'conversation';
      const selectedAuthors = normalizeAuthorSlugList([...(isAskProverbsRequest ? [] : (counselAuthorSlugs || [])), activePersona]);
      const matter = isAskProverbsRequest ? '' : (cleanText(matterOverride) || cleanText(matterAnchor) || cleanText(messageText));
      const sourceContext = isAskProverbsRequest
        ? null
        : normalizeSourceContextPayload(options?.sourceContext || activeSourceContext);
      const requestSource = isAskProverbsRequest
        ? ASK_PROVERBS_SOURCE
        : (cleanText(options?.source) || cleanText(activeLaunchState?.source) || '');
      const res = await apiV2.post('/chat', {
        question: messageText,
        mode: chatMode,
        persona: activePersona,
        ...(isAskProverbsRequest ? { source_scope: ASK_PROVERBS_SOURCE_SCOPE } : {}),
        ...(matter ? { matter } : {}),
        ...(selectedAuthors.length > 0 ? { selected_authors: selectedAuthors } : {}),
        session_id: sessionId || undefined,
        user_id: activeUserId,
        ...(requestSource ? { source: requestSource } : {}),
        ...(!isAskProverbsRequest && activeLaunchState?.sourcePromptId ? { source_prompt_id: activeLaunchState.sourcePromptId } : {}),
        ...(!isAskProverbsRequest && activeLaunchState?.mentorHint ? { mentor_hint: activeLaunchState.mentorHint } : {}),
        ...(!isAskProverbsRequest && activeLaunchState?.clockContext ? { clock_context: activeLaunchState.clockContext } : {}),
        ...(!isAskProverbsRequest && sourceContext ? { source_context: sourceContext } : {}),
      }, { timeout: CHAT_REQUEST_TIMEOUT_MS });

      if (res.data.session_id) {
        setSessionId(res.data.session_id);
      }
      if (res.data.user_id && res.data.user_id !== userId) {
        setUserId(res.data.user_id);
      }

      const segments = res.data.segments || [];
      const answerSeg = segments.find((s) => s.type === 'answer');
      const followUpQuestion = normalizeFollowUpQuestionPayload(
        segments.find((segment) => segment.type === 'follow_up_question')?.payload,
        authors,
      );
      if (isAuthenticated) {
        const nextState = normalizeConversationStatePayload(res.data.state, {
          sessionId: res.data.session_id || sessionId,
          persona: res.data.persona || activePersona,
        }) || buildEmptyConversationState(
          res.data.session_id || sessionId,
          res.data.persona || activePersona,
        );
        setConversationState(nextState);
        setConversationStateError('');
        setConversationStateLoading(false);
      }
      const assistantMessageText = answerSeg?.payload?.answer || res.data.response || '';
      const referenceBundle = buildReferenceBundle(
        res.data.metadata || [],
        assistantMessageText,
        res.data.derived_references || [],
      );

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          message: assistantMessageText,
          persona: getPersonaName(res.data.persona || activePersona),
          followUpQuestion,
          references: referenceBundle.references,
          metaInfo: referenceBundle.metaInfo,
        },
      ]);
      setReferences(referenceBundle.references);
      setMetaInfo(referenceBundle.metaInfo);
      if (matter && !isAskProverbsRequest) {
        addCounselAuthor(activePersona);
        const authorRecord = authors.find((item) => cleanText(item?.slug).toLowerCase() === cleanText(activePersona).toLowerCase())
          || { slug: activePersona, name: getPersonaName(activePersona) };
        const nextEntry = {
          id: buildCounselEntryId(matter, activePersona),
          authorSlug: cleanText(activePersona).toLowerCase(),
          authorName: getPersonaName(activePersona),
          author: authorRecord,
          matter,
          question: messageText,
          response: assistantMessageText,
          references: referenceBundle.references,
          metaInfo: referenceBundle.metaInfo,
        };
        setCounselEntries((prev) => {
          const existingIndex = prev.findIndex((entry) => entry.id === nextEntry.id);
          if (existingIndex === -1) {
            return [...prev, nextEntry];
          }
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            ...nextEntry,
          };
          return updated;
        });
      }
      trackEvent('chat_response_received', {
        persona: activePersona,
        counsel_count: selectedAuthors.length,
        has_matter: Boolean(matter),
        has_source_context: Boolean(sourceContext),
        mode: chatMode,
        response_chars: assistantMessageText.length,
        metadata_count: Array.isArray(res.data.metadata) ? res.data.metadata.length : 0,
      });
      setChatStatus((prev) => ({
        phase: 'success',
        title: priorPhase === 'error' || priorPhase === 'timeout' ? 'Recovered' : 'Response Received',
        message: priorPhase === 'error' || priorPhase === 'timeout'
          ? 'The service recovered and returned a response.'
          : 'Response received successfully.',
        retryable: false,
        statusCode: 200,
        retryAction: 'chat',
        retryLabel: 'Retry',
      }));
      return true;
    } catch (error) {
      console.error('Error fetching response:', error);
      error.elapsedMs = error.elapsedMs || roundMs(nowMs() - requestStartedAt);
      const status = error.response?.status;
      const failureStatus = buildChatFailureStatus(status, error);
      const requestId = error.response?.headers?.['x-request-id']
        || error.config?.headers?.['X-Request-ID']
        || '';
      reportClientFailure({
        kind: 'chat_response_failed',
        phase: 'chat',
        message: error.response?.data?.detail || error.message || 'Chat request failed.',
        request_id: requestId,
        session_id: sessionId || '',
        user_id: userId || guestId || getTokenProfile().sub || '',
        persona: isAskProverbsRequest ? ASK_PROVERBS_PERSONA : persona,
        route: '/api/v2/chat',
        method: 'POST',
        status_code: status || 0,
        error,
      });
      trackEvent('chat_response_failed', {
        persona: isAskProverbsRequest ? ASK_PROVERBS_PERSONA : persona,
        counsel_count: normalizeAuthorSlugList([...(isAskProverbsRequest ? [] : (counselAuthorSlugs || [])), isAskProverbsRequest ? ASK_PROVERBS_PERSONA : persona]).length,
        has_matter: Boolean(cleanText(matterAnchor)),
        has_source_context: Boolean(normalizeSourceContextPayload(options?.sourceContext || activeSourceContext)),
        mode: isAskProverbsRequest ? ASK_PROVERBS_MODE : 'conversation',
        status_code: status || 0,
      });
      if (status === 401 && keycloak.authenticated) {
        startLogin({ prompt: 'login' });
      }
      setChatStatus(failureStatus);
      setError('');
      return false;
    } finally {
      if (waitTimer) {
        window.clearInterval(waitTimer);
      }
      setLoading(false);
    }
  };

  const synthesizeCounselStack = async () => {
    const matter = cleanText(matterAnchor);
    if (!matter || counselEntries.length < 2 || isBusy) {
      return false;
    }

    setSynthesisLoading(true);
    setError('');
    setChatStatus({
      phase: 'sending',
      title: 'Synthesizing Counsel',
      message: 'Deriving common ground, tensions, and an integrated principle…',
      retryable: false,
      statusCode: null,
      retryAction: 'synthesis',
      retryLabel: 'Retry synthesis',
    });

    try {
      const activeUserId = userId || guestId || getTokenProfile().sub || undefined;
      const res = await apiV2.post('/synthesis', {
        matter,
        counsels: counselEntries.map((entry) => ({
          author_slug: cleanText(entry?.authorSlug).toLowerCase(),
          author_name: cleanText(entry?.authorName) || getPersonaName(entry?.authorSlug),
          question: cleanText(entry?.question) || matter,
          response: cleanText(entry?.response),
          references: Array.isArray(entry?.references)
            ? entry.references
              .map((ref) => cleanText(ref?.displayLabel || ref?.book))
              .filter(Boolean)
              .slice(0, 6)
            : [],
        })),
        session_id: sessionId || undefined,
        user_id: activeUserId,
      });

      const normalized = normalizeCounselSynthesisPayload(res.data);
      if (!normalized) {
        throw new Error('Synthesis payload was incomplete.');
      }

      setCounselSynthesis(normalized);
      trackEvent('counsel_synthesis_received', {
        persona,
        counsel_count: counselEntries.length,
        response_chars: normalized.renderedText.length,
      });
      setChatStatus({
        phase: 'success',
        title: 'Synthesis Ready',
        message: 'The counsel stack was synthesized successfully.',
        retryable: false,
        statusCode: 200,
        retryAction: 'synthesis',
        retryLabel: 'Retry synthesis',
      });
      return true;
    } catch (error) {
      console.error('Error synthesizing counsel:', error);
      const status = error.response?.status;
      trackEvent('counsel_synthesis_failed', {
        persona,
        counsel_count: counselEntries.length,
        status_code: status || 0,
      });
      if (status === 401 && keycloak.authenticated) {
        startLogin({ prompt: 'login' });
      }
      setChatStatus(buildSynthesisFailureStatus(status, error));
      setError('');
      return false;
    } finally {
      setSynthesisLoading(false);
    }
  };

  const submitPromptText = async (rawText, origin = 'composer', options = {}) => {
    const messageText = String(rawText || '').trim();
    if (!messageText || isBusy) {
      return false;
    }

    const isAskProverbsRequest = cleanText(options?.mode) === ASK_PROVERBS_MODE;
    const nextSourceContext = isAskProverbsRequest ? null : normalizeSourceContextPayload(options?.sourceContext || activeSourceContext);
    const nextSource = isAskProverbsRequest
      ? ASK_PROVERBS_SOURCE
      : (cleanText(options?.source) || cleanText(activeLaunchState?.source) || '');
    setError('');
    setChatStatus({ ...CHAT_STATUS_IDLE });
    setLastSubmittedMessage(messageText);
    setLastSubmittedMode(isAskProverbsRequest ? ASK_PROVERBS_MODE : 'conversation');
    setLastSubmittedSourceContext(nextSourceContext);
    setLastSubmittedSource(nextSource);
    if (!isAskProverbsRequest) {
      setActiveSourceContext(nextSourceContext);
    }
    const activePersona = isAskProverbsRequest ? ASK_PROVERBS_PERSONA : (cleanText(options?.personaOverride || persona).toLowerCase() || persona);
    const selectedAuthors = normalizeAuthorSlugList([...(isAskProverbsRequest ? [] : (counselAuthorSlugs || [])), activePersona]);
    const matter = isAskProverbsRequest ? '' : (cleanText(matterAnchor) || messageText);
    if (!isAskProverbsRequest && !cleanText(matterAnchor)) {
      setMatterAnchor(messageText);
    }
    trackEvent('chat_prompt_submitted', {
      persona: activePersona,
      counsel_count: selectedAuthors.length,
      has_matter: Boolean(matter),
      has_source_context: Boolean(nextSourceContext),
      has_session_id: Boolean(sessionId),
      message_chars: messageText.length,
      origin,
      mode: isAskProverbsRequest ? ASK_PROVERBS_MODE : 'conversation',
      launch_source: nextSource,
    });
    setQuery('');
    setMessages((prev) => [...prev, {
      role: 'user',
      message: messageText,
      references: [],
      metaInfo: [],
      followUpQuestion: null,
    }]);

    await sendMessage(messageText, matter, options);
    return true;
  };

  const retryLastMessage = async () => {
    if (isBusy || !lastSubmittedMessage) {
      return;
    }
    setError('');
    await sendMessage(
      lastSubmittedMessage,
      lastSubmittedMode === ASK_PROVERBS_MODE ? '' : (cleanText(matterAnchor) || lastSubmittedMessage),
      lastSubmittedMode === ASK_PROVERBS_MODE
        ? { mode: ASK_PROVERBS_MODE }
        : {
          ...(lastSubmittedSource ? { source: lastSubmittedSource } : {}),
          ...(lastSubmittedSourceContext ? { sourceContext: lastSubmittedSourceContext } : {}),
        },
    );
  };

  const retrySynthesis = async () => {
    if (isBusy) {
      return;
    }
    setError('');
    await synthesizeCounselStack();
  };

  const addCounselFromMatter = async () => {
    const matter = cleanText(matterAnchor) || cleanText(query);
    if (!matter || isBusy) {
      return false;
    }
    const draftQuery = query;
    const didSubmit = await submitPromptText(matter, 'add_counsel');
    if (draftQuery && cleanText(draftQuery) !== matter) {
      setQuery(draftQuery);
    }
    return didSubmit;
  };

  useEffect(() => {
    if (!sessionId || !activeLaunchState) {
      return;
    }
    persistClockLaunchState(sessionId, activeLaunchState);
  }, [sessionId, activeLaunchState]);

  useEffect(() => {
    if (isAuthenticated || !sessionId) {
      return;
    }
    if (!Array.isArray(messages) || !messages.some((message) => cleanText(message?.role).toLowerCase() === 'assistant')) {
      return;
    }

    const existing = loadPersistedGuestHistorySession(sessionId);
    persistGuestHistorySession({
      session_id: sessionId,
      persona,
      mode: lastSubmittedMode === ASK_PROVERBS_MODE
        ? ASK_PROVERBS_MODE
        : (cleanText(activeLaunchState?.source) === CLOCK_LAUNCH_SOURCE ? 'guided' : 'conversation'),
      start_time: cleanText(existing?.start_time) || new Date().toISOString(),
      last_active: new Date().toISOString(),
      preview: buildGuestHistoryPreview(messages, matterAnchor),
      messages,
      references,
      metaInfo,
      matterAnchor,
      counselAuthorSlugs,
      counselEntries,
      counselSynthesis,
      launchState: activeLaunchState,
    });
  }, [
    activeLaunchState,
    counselAuthorSlugs,
    counselEntries,
    counselSynthesis,
    isAuthenticated,
    lastSubmittedMode,
    matterAnchor,
    messages,
    metaInfo,
    persona,
    references,
    sessionId,
  ]);

  const resumeSession = async (targetSessionId) => {
    setLoading(true);
    setSynthesisLoading(false);
    setError('');
    setChatStatus({ ...CHAT_STATUS_IDLE });
    clearConversationState();
    try {
      const res = await apiV1.get(`/history/${targetSessionId}`);
      const data = res.data || {};
      const sessionPersona = (data.persona || persona || '').toLowerCase();
      if (sessionPersona) {
        setPersona(sessionPersona);
      }
      setSessionId(targetSessionId);
      setActiveLaunchState(
        buildClockLaunchStateFromSessionPayload(data) || loadPersistedClockLaunchState(targetSessionId)
      );
      const history = data.messages || [];
      const historyMessageCompanions = buildHistoryMessageCompanionMap(
        data.message_companions || data.messageCompanions,
        authors,
      );
      const historyMessageReferences = buildHistoryMessageReferenceMap(data.citations);
      const restoredCounselState = normalizeCounselStackPayload({
        counselStack: data.counsel_stack || data.counselStack,
        authors,
      }) || rebuildCounselStackFromHistory({
        history,
        citations: data.citations,
        authors,
        mode: data.mode,
      });
      const nextSynthesisSourceKey = buildCounselStackSourceKey(
        restoredCounselState.matterAnchor,
        restoredCounselState.counselEntries,
      );
      const restoredSynthesis = restoredCounselState.counselEntries.length >= 2
        ? loadPersistedCounselSynthesisSnapshot(targetSessionId, nextSynthesisSourceKey)
        : null;
      setMessages(
        history.map((msg) => ({
          role: msg.role,
          message: msg.message,
          persona: msg.persona || sessionPersona,
          followUpQuestion: historyMessageCompanions[cleanText(msg.message_id)] || null,
          references: historyMessageReferences[cleanText(msg.message_id)] || [],
          metaInfo: [],
        }))
      );
      synthesisSourceKeyRef.current = nextSynthesisSourceKey;
      setMatterAnchor(restoredCounselState.matterAnchor);
      setCounselAuthorSlugs(restoredCounselState.counselAuthorSlugs);
      setCounselEntries(restoredCounselState.counselEntries);
      setCounselSynthesis(restoredSynthesis);
      setActiveSourceContext(null);
      setLastSubmittedSourceContext(null);
      setLastSubmittedSource('');
      const historyRefs = (data.citations || []).map(buildStoredCitationReference);
      setReferences(historyRefs);
      setMetaInfo([]);
      void loadConversationState(targetSessionId, sessionPersona);
      trackEvent('history_session_loaded', {
        persona: sessionPersona,
        message_count: history.length,
        counsel_count: restoredCounselState.counselEntries.length,
        synthesis_restored: Boolean(restoredSynthesis),
      });
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      trackEvent('history_session_load_failed', {
        status_code: err?.response?.status || 0,
      });
      setError(detail || err.message || 'Failed to load session history');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const resumeGuestHistorySession = async (targetSessionId) => {
    setLoading(true);
    setSynthesisLoading(false);
    setError('');
    setChatStatus({ ...CHAT_STATUS_IDLE });
    try {
      const snapshot = loadPersistedGuestHistorySession(targetSessionId, authors);
      if (!snapshot) {
        setError('Saved guest session not found.');
        return false;
      }

      if (snapshot.persona) {
        setPersona(snapshot.persona);
      }
      const nextSynthesisSourceKey = buildCounselStackSourceKey(
        snapshot.matterAnchor,
        snapshot.counselEntries,
      );
      setSessionId(snapshot.session_id);
      setActiveLaunchState(snapshot.launchState || null);
      setMessages(snapshot.messages);
      synthesisSourceKeyRef.current = nextSynthesisSourceKey;
      setMatterAnchor(snapshot.matterAnchor);
      setCounselAuthorSlugs(snapshot.counselAuthorSlugs);
      setCounselEntries(snapshot.counselEntries);
      setCounselSynthesis(snapshot.counselSynthesis);
      setReferences(snapshot.references);
      setMetaInfo(snapshot.metaInfo);
      clearConversationState();
      setActiveSourceContext(null);
      setLastSubmittedMessage('');
      setLastSubmittedMode(snapshot.mode === ASK_PROVERBS_MODE ? ASK_PROVERBS_MODE : 'conversation');
      setLastSubmittedSourceContext(null);
      setLastSubmittedSource('');
      setPendingResumeSessionId('');
      launchPrefillRef.current = '';
      resumeSessionBootstrapRef.current = '';
      trackEvent('guest_history_session_loaded', {
        persona: snapshot.persona,
        message_count: snapshot.messages.length,
        counsel_count: snapshot.counselEntries.length,
      });
      return true;
    } finally {
      setLoading(false);
    }
  };

  const loadSessionFromHistory = async (targetSessionId) => (
    isAuthenticated
      ? resumeSession(targetSessionId)
      : resumeGuestHistorySession(targetSessionId)
  );

  useEffect(() => {
    if (!pendingResumeSessionId || isBusy) {
      return;
    }
    if (!AUTH_OFF && (!authReady || !isAuthenticated)) {
      return;
    }
    if (resumeSessionBootstrapRef.current === pendingResumeSessionId) {
      return;
    }

    resumeSessionBootstrapRef.current = pendingResumeSessionId;
    void resumeSession(pendingResumeSessionId).then((ok) => {
      if (ok) {
        setPendingResumeSessionId('');
      } else if (resumeSessionBootstrapRef.current === pendingResumeSessionId) {
        resumeSessionBootstrapRef.current = '';
      }
    });
  // Resume bootstrap is intentionally keyed off auth/busy/session state only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, isAuthenticated, isBusy, pendingResumeSessionId]);

  useEffect(() => {
    if (!authReady || !isAuthenticated || authorPreferencesLoading) {
      return;
    }
    if (!availableSavedDefaultAuthorSlug) {
      return;
    }
    const currentPathname = window.location.pathname;
    if (!isHomepagePath(currentPathname) && currentPathname !== '/chat') {
      return;
    }
    if (getAuthorSlugFromPath(currentPathname)) {
      return;
    }
    if (messages.length > 0 || pendingResumeSessionId || activeLaunchState) {
      return;
    }
    if (authors.length === 0) {
      return;
    }

    const launchKey = [
      sessionId || 'session',
      currentPathname,
      pendingResumeSessionId || '',
      activeLaunchState ? 'launch' : 'plain',
    ].join(':');
    if (savedDefaultLaunchKeyRef.current === launchKey) {
      return;
    }
    savedDefaultLaunchKeyRef.current = launchKey;

    if (persona !== availableSavedDefaultAuthorSlug) {
      setPersona(availableSavedDefaultAuthorSlug);
    }
  }, [
    activeLaunchState,
    authReady,
    authorPreferencesLoading,
    authors,
    availableSavedDefaultAuthorSlug,
    isAuthenticated,
    messages.length,
    pendingResumeSessionId,
    persona,
    sessionId,
  ]);

  const newSession = () => {
    trackEvent('new_session_started', {
      persona,
      had_messages: messages.length > 0,
      had_clock_launch: Boolean(activeLaunchState),
    });
    const newId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setSessionId(newId);
    setMessages([]);
    setReferences([]);
    setMetaInfo([]);
    setMatterAnchor('');
    setCounselAuthorSlugs([]);
    setCounselEntries([]);
    setCounselSynthesis(null);
    setQuery('');
    setError('');
    setChatStatus({ ...CHAT_STATUS_IDLE });
    setLastSubmittedMessage('');
    setLastSubmittedMode('conversation');
    setLastSubmittedSourceContext(null);
    setLastSubmittedSource('');
    setSynthesisLoading(false);
    clearConversationState();
    setActiveLaunchState(null);
    setActiveSourceContext(null);
    setPendingResumeSessionId('');
    launchPrefillRef.current = '';
    resumeSessionBootstrapRef.current = '';
    savedDefaultLaunchKeyRef.current = '';
    if (isAuthenticated && availableSavedDefaultAuthorSlug) {
      setPersona(availableSavedDefaultAuthorSlug);
    }
  };

  const handleSignIn = () => {
    if (AUTH_OFF) return;
    trackEvent('auth_sign_in_clicked', { method: 'email' });
    startLogin();
  };

  const handleSignOut = async () => {
    trackEvent('auth_sign_out_clicked', { authenticated: isAuthenticated });
    setUserProfile(null);
    setUserId(null);
    setAuthorPreferences(EMPTY_AUTHOR_PREFERENCES);
    setAuthorPreferencesLoading(false);
    setAuthorPreferencesSaving(false);
    setAuthorPreferencesError('');
    setSessionId(initialSessionId());
    setMessages([]);
    setReferences([]);
    setMetaInfo([]);
    setMatterAnchor('');
    setCounselAuthorSlugs([]);
    setCounselEntries([]);
    setCounselSynthesis(null);
    setQuery('');
    setError('');
    setChatStatus({ ...CHAT_STATUS_IDLE });
    setLastSubmittedMessage('');
    setLastSubmittedMode('conversation');
    setLastSubmittedSourceContext(null);
    setLastSubmittedSource('');
    setSynthesisLoading(false);
    clearConversationState();
    clearRelationshipMemory();
    setActiveLaunchState(null);
    setActiveSourceContext(null);
    setPendingResumeSessionId('');
    launchPrefillRef.current = '';
    resumeSessionBootstrapRef.current = '';
    savedDefaultLaunchKeyRef.current = '';
    if (AUTH_OFF) {
      setIsAuthenticated(false);
      return;
    }
    try {
      await startLogout();
      trackEvent('auth_sign_out_completed', { status: 'success' });
    } catch (err) {
      console.error('Keycloak logout failed', err);
      trackEvent('auth_sign_out_completed', { status: 'failed' });
    } finally {
      setIsAuthenticated(false);
    }
  };

  const handleGoogleSignIn = () => {
    trackEvent('auth_sign_in_clicked', { method: 'google' });
    startLogin({ idpHint: 'google' });
  };

  if (!authReady) {
    return (
      <div className="App">
        <div className="auth-loading">Loading authentication...</div>
      </div>
    );
  }

  const runtimeDebug = runtimeDebugEnabled();
  const buildChatRouteElement = (featureMode = '') => (
    <ChatPage
      featureMode={featureMode}
      proverbsContent={proverbsContent}
      proverbsContentLoading={proverbsContentLoading}
      proverbsContentError={proverbsContentError}
      persona={persona}
      setPersona={setPersona}
      sessionId={sessionId}
      launchState={activeLaunchState}
      authors={authors}
      isAuthenticated={isAuthenticated}
      favoriteAuthorSlugs={favoriteAuthorSlugs}
      defaultAuthorSlug={availableSavedDefaultAuthorSlug || savedDefaultAuthorSlug}
      preferencesSaving={authorPreferencesSaving}
      preferencesError={authorPreferencesError}
      onToggleFavorite={handleToggleFavoriteAuthor}
      onSetDefaultAuthor={handleSetDefaultAuthor}
      onSelectMessageReference={applyMessageReferenceSelection}
      authorProfile={authorProfile}
      authorProfileLoading={authorProfileLoading}
      authorProfileError={authorProfileError}
      crossrefAuthor={authorCrossrefs}
      crossrefAuthorLoading={authorCrossrefsLoading}
      crossrefAuthorError={authorCrossrefsError}
      crossrefCatalogLoading={crossrefCatalogLoading}
      crossrefCatalogError={crossrefCatalogError}
      referencedCrossrefBooks={referencedCrossrefBooks}
      relatedAuthors={relatedAuthors}
      curatedRelatedReferences={curatedRelatedReferences}
      metaInfo={metaInfo}
      messages={messages}
      loading={loading}
      synthesisLoading={synthesisLoading}
      chatStatus={chatStatus}
      conversationState={conversationState}
      conversationStateLoading={conversationStateLoading}
      conversationStateError={conversationStateError}
      conversationStateResetting={conversationStateResetting}
      onResetConversationState={async () => {
        if (!isAuthenticated || !sessionId || conversationStateResetting) {
          return;
        }
        setConversationStateResetting(true);
        setConversationStateError('');
        try {
          await apiV1.post(`/history/${sessionId}/state/reset`);
          setConversationState(buildEmptyConversationState(sessionId, persona));
          trackEvent('conversation_state_reset', {
            persona,
            session_id: sessionId,
          });
        } catch (err) {
          const detail = cleanText(err?.response?.data?.detail) || err?.message || 'Failed to reset conversation state';
          setConversationStateError(detail);
          trackEvent('conversation_state_reset_failed', {
            persona,
            session_id: sessionId,
            status_code: err?.response?.status || 0,
          });
        } finally {
          setConversationStateResetting(false);
        }
      }}
      relationshipMemory={relationshipMemory}
      relationshipMemoryLoading={relationshipMemoryLoading}
      relationshipMemoryError={relationshipMemoryError}
      relationshipMemoryResetting={relationshipMemoryResetting}
      onResetRelationshipMemory={async () => {
        const personaSlug = cleanText(persona).toLowerCase();
        if (!isAuthenticated || !personaSlug || relationshipMemoryResetting) {
          return;
        }
        setRelationshipMemoryResetting(true);
        setRelationshipMemoryError('');
        try {
          const params = new URLSearchParams({ persona: personaSlug });
          await apiV1.post(`/memory/relationship/reset?${params.toString()}`);
          setRelationshipMemory(buildEmptyRelationshipMemory(personaSlug));
          trackEvent('relationship_memory_reset', {
            persona: personaSlug,
          });
        } catch (err) {
          const detail = cleanText(err?.response?.data?.detail) || err?.message || 'Failed to reset relationship memory';
          setRelationshipMemoryError(detail);
          trackEvent('relationship_memory_reset_failed', {
            persona: personaSlug,
            status_code: err?.response?.status || 0,
          });
        } finally {
          setRelationshipMemoryResetting(false);
        }
      }}
      activeSourceContext={activeSourceContext}
      onClearSourceContext={() => setActiveSourceContext(null)}
      onRetryLastMessage={retryLastMessage}
      onRetrySynthesis={retrySynthesis}
      references={references}
      expandReference={expandReference}
      error={error}
      query={query}
      setQuery={setQuery}
      matterAnchor={matterAnchor}
      counselAuthorSlugs={counselAuthorSlugs}
      counselEntries={counselEntries}
      counselSynthesis={counselSynthesis}
      onAddCounsel={addCounselFromMatter}
      onSynthesizeCounsel={synthesizeCounselStack}
      onRemoveCounselAuthor={removeCounselAuthor}
      submitPromptText={submitPromptText}
      getPersonaName={getPersonaName}
      messagesEndRef={messagesEndRef}
      runtimeDebug={runtimeDebug}
    />
  );
  const chatRouteElement = buildChatRouteElement();
  const askProverbsRouteElement = buildChatRouteElement(ASK_PROVERBS_MODE);

  const authorsBrowseRouteElement = (
    <AuthorsBrowsePage
      items={authorBrowseItems}
      loading={authorBrowseLoading}
      error={authorBrowseError}
      isAuthenticated={isAuthenticated}
      favoriteAuthorSlugs={favoriteAuthorSlugs}
      defaultAuthorSlug={availableSavedDefaultAuthorSlug || savedDefaultAuthorSlug}
      preferencesSaving={authorPreferencesSaving}
      onToggleFavorite={handleToggleFavoriteAuthor}
      onSetDefaultAuthor={handleSetDefaultAuthor}
    />
  );

  const authorDetailRouteElement = (
    <AuthorDetailPage
      items={authorBrowseItems}
      loading={authorBrowseLoading}
      error={authorBrowseError}
      persona={persona}
      setPersona={setPersona}
      submitPromptText={submitPromptText}
      isBusy={isBusy}
      chatStatus={chatStatus}
      authorProfile={authorProfile}
      authorProfileLoading={authorProfileLoading}
      authorProfileError={authorProfileError}
      authorCrossrefs={authorCrossrefs}
      authorCrossrefsLoading={authorCrossrefsLoading}
      authorCrossrefsError={authorCrossrefsError}
      isAuthenticated={isAuthenticated}
      favoriteAuthorSlugs={favoriteAuthorSlugs}
      defaultAuthorSlug={availableSavedDefaultAuthorSlug || savedDefaultAuthorSlug}
      preferencesSaving={authorPreferencesSaving}
      preferencesError={authorPreferencesError}
      onToggleFavorite={handleToggleFavoriteAuthor}
      onSetDefaultAuthor={handleSetDefaultAuthor}
    />
  );
  const referenceCatalogRouteElement = (
    <ReferenceCatalogPage
      items={crossrefCatalog}
      loading={crossrefCatalogLoading}
      error={crossrefCatalogError}
      authors={authors}
      browseItems={authorBrowseItems}
    />
  );
  const authorWorkReaderRouteElement = (
    <AuthorWorkReaderPage
      items={authorBrowseItems}
      loading={authorBrowseLoading}
      error={authorBrowseError}
      persona={persona}
      setPersona={setPersona}
      submitPromptText={submitPromptText}
      isBusy={isBusy}
      chatStatus={chatStatus}
      authorProfile={authorProfile}
      authorProfileLoading={authorProfileLoading}
      authorProfileError={authorProfileError}
    />
  );
  const authorAcquisitionRouteElement = <AuthorAcquisitionTrackerPage />;
  const traceAuditRouteElement = <ResponseTraceAuditPage canInspect={canInspectTraceAudit} />;
  const pricingRouteElement = (
    <PricingPage
      isAuthenticated={isAuthenticated}
      onSignIn={handleSignIn}
    />
  );
  const billingSuccessRouteElement = (
    <BillingSuccessPage
      isAuthenticated={isAuthenticated}
      onSignIn={handleSignIn}
    />
  );

  return (
    <Router>
      <AnalyticsRouteTracker />
      <div className="App">
        <TopNav
          userProfile={userProfile}
          sessionId={sessionId}
          onNewSession={newSession}
          onSignIn={handleSignIn}
          onSignOut={handleSignOut}
          isAuthenticated={isAuthenticated}
          authDisabled={AUTH_OFF}
          canInspectTraceAudit={canInspectTraceAudit}
        />
        <main className="app-main">
          <Routes>
            <Route path="/user/auth" element={<AuthMethodsPage onStartEmail={handleSignIn} onStartGoogle={handleGoogleSignIn} />} />
            <Route path="/history" element={<HistoryPage onLoadSession={loadSessionFromHistory} getPersonaName={getPersonaName} />} />
            {isAuthenticated && (
              <Route
                path="/user/profile/home"
                element={<ProfileHome userProfile={userProfile} authorPreferences={authorPreferences} authors={authors} />}
              />
            )}
            <Route path="/authors/:slug/works/:source" element={authorWorkReaderRouteElement} />
            <Route path="/authors/:slug" element={authorDetailRouteElement} />
            <Route path="/authors" element={authorsBrowseRouteElement} />
            <Route path="/references/:bookId" element={referenceCatalogRouteElement} />
            <Route path="/references" element={referenceCatalogRouteElement} />
            <Route path="/pricing" element={pricingRouteElement} />
            <Route path="/billing/success" element={billingSuccessRouteElement} />
            <Route path="/author-acquisition" element={authorAcquisitionRouteElement} />
            {isAuthenticated && <Route path="/governance/traces" element={traceAuditRouteElement} />}
            <Route path="/ask-proverbs" element={askProverbsRouteElement} />
            <Route path="/author/:slug" element={chatRouteElement} />
            <Route path="/chat" element={chatRouteElement} />
            <Route path="/" element={chatRouteElement} />
          </Routes>
        </main>
        <footer className="build-tag">
          {ENVIRONMENT.toUpperCase()}
          {APP_VERSION !== 'unknown' ? ` · v${APP_VERSION}` : ''}
          {APP_RELEASE ? ` · release ${APP_RELEASE}` : ''}
          {BUILD_TIME ? ` · ${BUILD_TIME}` : ''}
        </footer>
      </div>
    </Router>
  );
}

export default App;
